import { Children, FormEvent, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Download,
  Eye,
  Pencil,
  Square,
  Lock,
  LogIn,
  MessageCircle,
  Mic,
  Search,
  ShieldCheck,
  Sparkles,
  Menu,
  X,
  // HP2 card icons + HP3/HP4 rail and menu icons, in the mockup's line style.
  ChevronDown,
  Droplets,
  Camera,
  ImagePlus,
} from "lucide-react";
import { combineName, splitName, withNameParts } from "./nameParts.js";
import { featureEntitlements, featureLockAfterAnswer, featureOutcome, opensSurface } from "./featureAccess.js";
// HER FUNNEL-CONTAINMENT COHORT TEST. The SAME law her counter and her wall are drawn from, so the
// chrome and the meter can never disagree about whether she is mid-trial (R28/R29).
import { freeTurnView } from "./lalaFreeTurns.js";
// L14 — the grace law owns which query flag means "open card update" and how it is swept.
import { billingRequested, addressWithoutBillingFlag } from "./paymentGrace.js";
// R22: the mobile shell, and N9-1's tab-highlight law with it — hookless, so a gate CALLS them.
import { MobileTabBar, MobileRoomsSheet, announceOverlayTab, OVERLAY_TAB_EVENT, CLOSE_OVERLAYS_EVENT, mobileHelpLabel } from "./components/MobileNav";
import { NavPinWatchdog } from "./components/NavPinWatchdog";
import { SidebarFooterNav } from "./components/SidebarFooterNav";
import { SiteFooter } from "./components/SiteFooter";
import { HomePage, GeneratorPage } from "./components/RoomPages";
import { FunnelPage } from "./components/FunnelPage";  // F1 — hookless, so a gate CALLS it
// R8: G1-PART-B's server poll and BOTH generation surfaces — the in-room strip and the site-wide dock
// — moved to src/components/GenerationStrips.tsx. They are a PARTITION (one draws a room's own work,
// the other everything else). R222's `useModalBusy` is there too, beside the ONE poller it reads.
import { useActiveGenerations, useModalBusy, usePhotoBusy, GenerationFollower, GenerationQueueStrip, type RecentGeneration } from "./components/GenerationStrips";
import { AttachButton } from "./components/AttachButton";
import { FunnelCapture } from "./components/FunnelCapture";  // F2/F3 — the capture; it holds the state so the page above does not
// HER CONTINUITY LAW, CLIENT HALF — what the bridge's token becomes on the front door's own form.
// The clocks disagree (the funnel is 24-hour, the front door is 12-hour + AM/PM) and that module is
// where the conversion lives, so a gate RUNS it over all 1,440 minutes rather than reading it here.
import { prefillPatch, prefillHasSomething, addressWithoutFunnelHandoff, joinRequested, readStashedPrefill, writeStashedPrefill, clearStashedPrefill, PREFILL_NOTE, FUNNEL_LEAD_PARAM } from "./lib/funnel-prefill";
import { PageOutlineTitle } from "./components/PageOutlineTitle";
import { ResultDestinations, NoHostageNote } from "./components/ResultDestinations";
import { AskLalaAboutButton, askLalaAboutReading, ASK_LALA_READING_EVENT } from "./components/AskLalaAboutButton";
import { DrawerTab } from "./components/DrawerTab";
import { GoToCauldronButton, consumeCauldronRequest, OPEN_CAULDRON_EVENT } from "./components/CauldronDoor";
import { RoomPanel } from "./components/RoomPanel";
import { FollowCircle } from "./components/FollowCircle";
import { FeaturedShelf } from "./components/FeaturedShelf";
// R5-2: the pure display formatters live in one module now, so a gate can load and assert them.
import {
  isPersonSubject,
  ordinalLabel,
  formatBirthday,
  stripMarkdown,
  memberFirstName,
  type PlanetPosition,
  shortDate,
} from "./lib/format";
// R5-3: the browser download path. `filenameFromDisposition` is the ONE filename law (T1-DL).
import { downloadFromUrl, downloadReportPdf } from "./lib/downloads";
// Her ruling, 2026-10-17: readings are opened in the house, never downloaded; the Magic Room still is.
import { pdfAllowed } from "./readingPdf.js";
// R5-4: the Sacred Texts catalog is data, and data lives in a data file.
import { books, type Book } from "./catalogs/sacred-text-books";
// R5-6 / W1-5 / 2026-08-18: what a tier is to this member and what its button says used to be read
// by three renderers here, then by ONE coded box. **Her ruling retires that box** — a live tester met
// it at a gated door on production — so there is NO tier renderer in this house at all: her artwork
// paints the ladder and the page maps controls onto it. App.tsx holds no copy of the law and no
// caller of it, which R5's own clause still asserts.
// R5-7: the country map. `countryCodeOf` takes a stored NAME or a stored CODE — both exist in the data.
import { COUNTRY_NAMES, COUNTRY_NAMES_BY_CODE, countryCodeOf } from "./countryCodes.js";
// R5-8: the words a member reads when a sign-in fails, when a reading is listed, while one is running.
import { authErrorMessage, elapsedLabel, newestFirst } from "./lib/labels";
// R90: the first read this site makes. A failed one used to draw the whole house as a visitor.
import { restoreSession, sessionRestoreNotice } from "./lib/session-restore";
import type { MemberSession } from "./lib/session-restore";
// R91: the read that says what she may open, asked three ways. A failed one forged a Free member.
import { readEntitlements, memberEntitlements, type MemberEntitlements } from "./lib/entitlements";
import { EventCountdownBanner, OwnerEventBar } from "./components/EventLive";
import { reportTypeLabel } from "./reportLabels.js";
import { landingAfterRoom, readTalkLanding, talkShowsCapture, talkStartPlan, writeTalkLanding } from "./talkDoor.js";
// R5-9: the master-number law. 11, 22 and 33 are never reduced.
import { reduceNumber, lifePathFromDate } from "./lib/numerology";
// R6-1: the route map (HP6). One map, both directions — every page owns a URL and every URL routes
// back to its page. The type comes with it, because every catalog below is typed by it.
import { PAGE_PATHS, pageFromPath, documentTitle, pageIsStandalone, type Page } from "./lib/pages";
import { sendPixelEvent } from "./vibePixel";
import { sendTtvEvent } from "./ttvTag";
import { reportPendingPurchases } from "./lib/pixel-purchases";
import { FloatingCloset } from "./components/FloatingCloset";
import { AccountMenuPortal } from "./components/AccountMenuPortal";
import { NavHeightVar } from "./components/NavHeight";
import { runInboxClose } from "./lib/inbox-return";
// ITEM 2, her defect 4 — ONE answer to *where does a close go*, one surface over (R28/R29).
import { stampMembershipFrom, runMembershipBack } from "./lib/return-path";
// L2.6 — the create-your-room box's whole visibility decision, and where her dismissal is kept.
import { showOnboarding, readDismissed, writeDismissed } from "./lib/onboarding";
import { HowThisWorks } from "./components/HowThisWorks";
// 14-B2: what the house makes for a new member and when — pure data, so a gate loads it (R37/R42).
import { signupSetReports, firstTapReports, onDemandReports, expectedReports, arrivalPointerShows, reportSlotState, slotClaimsReading, memberHasRoom, readingIsWritten } from "./lib/room-reports";
import { numerologyStages } from "./lib/numerology-stages";
import { EMPTY_PERSON, type PersonForm } from "./lib/person-form";
import { ASK_LALA_INTRO, ASK_LALA_GREETING_LINE_2, ASK_LALA_CHIP_POOL } from "./catalogs/ask-lala-copy";
// R6-2: the symbol tables the chart tables and report cards print.
// R10-3: ACG_THEME_LABELS left with the astrocartography views — it had no other reader here.
import { SIGN_GLYPHS, PLANET_GLYPHS, ROMAN_HOUSES, ZODIAC_MARKS, ASTRO_STAGE_THREADS, ASTRO_STAGE_PLANETS } from "./catalogs/glyphs";
// R6-3: the spreads she can draw and the spreads she can tell the analyzer she laid. Both carry the
// SERVER'S spread keys, and the gate holds them to the server's table.
import { TAROT_CATEGORIES } from "./catalogs/tarot-spreads";
import { TarotAnalyzePanel } from "./components/TarotAnalyze";
// R6-4: the Magic Room's browse ladder, held to the server's SPELL_BROWSE_CATEGORIES by the gate.
// R6-5: every offering the astrology / numerology / tarot rooms and the side menu are drawn from.
// `scripts/gate.mjs` reads THIS module for the A1-TILES roster now, not a slice of App.tsx.
import {
  chartCapabilities,
  astrologyOfferings,
  ASTROLOGY_OFFERINGS_HIDDEN,
  type AstrologyTool,
  type AstrologyOffering,
  type MenuCategory,
} from "./catalogs/astrology-menu";
// R7-2: the home page's room cards and the side menu's room list. They carry lucide JSX icons, which
// is why they could not move until R7-1 built the bundling loader that can read them.
import { generators, homeMenuItems } from "./catalogs/rooms";
// R7-5: the legal/FAQ markdown renderer (LG1). The first component out of this file.
// R7-6: the side menu's line icons (HP3), keyed by Page so a listed room cannot render iconless.
import { MenuIcon } from "./components/MenuIcon";
import { OPEN_SITE_MENU_EVENT, openSiteMenu } from "./components/MobileNav";
import { showsStrangerHome } from "./homeTapMap";
import { MasonryPanels } from "./components/MasonryPanels";
import { MyRoomReadingDoor } from "./components/MyRoomReadingDoor";
import { DailyCardModal } from "./components/DailyCardModal";
import { consumeMiniRequest, miniRequestPending, OPEN_MINI_READING_EVENT, writeStashedOrigin, readStashedOrigin, clearStashedOrigin } from "./roomOrigin.js";
import { READING_ICONS } from "./catalogs/reading-icons";
import { MR_BOOKS, MR_BOOKS_PORTRAIT, type MagicView, type MagicBook } from "./catalogs/magic-books";
// R8-1: the attachment file URL — the client's half of the server's auth-gated attachment route.
// W2-3: `stripAttachNote` moved beside it, and `AttachmentTray` (the row it feeds) into components.
import { stripAttachNote } from "./lib/attachments";
import { LANDING_REFERRER, LANDING_UTM, LANDING_FUNNEL, funnelParam } from "./lib/landing-source"; // the client's one reader of the address
import { funnelFromParam } from "./funnels.js"; // F1's roster: her three ad doors, resolved in ONE place
// LANDING v3: which landing door brought this arrival, kept across the signup navigation and the
// Google round trip. The LAW is in the tap map; this file holds no opinion about it.
import { readAskText, writeLandingArrival, readLandingArrival, clearLandingArrival } from "./landingTapMap.js";
import { AttachmentTray } from "./components/AttachmentTray";
// W2-3: the turn notice — ONE component above the text box on BOTH surfaces, and the WHEN of it.
import { LalaTurnNotice } from "./components/LalaTurnNotice";
import { applyTurnPayload, composerLock, type TurnNotice } from "./lib/lala-turns";
import { useFreeTurns } from "./lib/free-turns-state";
import { talkWindowClass, funnelArrivalMessages } from "./talkClose";
import { useAttachments } from "./lib/attachments-state";
// W1-13/W4-7: the ONE dictation engine and the ONE mic button, beside their pure laws. The seven mics
// on this site all reach them from here, and `useDictation` is imported as well as MicButton because
// two of the seven (the want-to-know-more ask and the Dream room) draw their own mic control.
import { useDictation, MicButton } from "./components/MicButton";
// W4-7: the live view, so a long dictation stays visible. `applyLiveView` is used directly on the two
// Lala composers because TYPING has to grow them too, not only speaking, and the mic route only fires
// on speech. One law, two triggers, no second copy of the geometry.
import { applyLiveView, dockClearance, limitsForWidth } from "./lib/live-view";
// W1-6: the bookmark laws. `Bookmark` is aliased: it is already the lucide icon inside the control.
import {
  toReaderIndex, toBookmarkPage, toImagePage, bookmarkWhere, listBookmarks,
  deleteBookmark, bookmarkNote, type Bookmark as SavedBookmark,
} from "./lib/bookmarks";
// R61: the bookmark bar, where a refused save used to close the form and leave nothing behind.
import BookmarkControls from "./components/BookmarkControls";
// R8-2: ONE journal title, so the two journal lists cannot drift apart on truncation again (N1-3).
import { JournalTitle } from "./components/JournalTitle";
import { PreviousDetailsBadge } from "./components/PreviousDetailsBadge";
import { UnwrittenBadge } from "./components/UnwrittenBadge";
import { FoundationalReportChip } from "./components/FoundationalReportChip";
// R35: the ONE way this house asks before it destroys something of hers. Every irreversible delete
// on this site goes through it, and the sentence is DERIVED from the kind, never typed at the call site.
import { DeleteControl, JournalEntryRow, JournalPanelItem, type JournalEntry } from "./components/DeleteControl";
// ITEM 7 (2026-09-03) — the contact form, out where a fixture can reach the door that produced
// ACK-NOACCOUNT-1's two silenced members.
import { ContactPage } from "./components/ContactPage";
// R38: the ONE way she renames something of hers, and the two routes it travels (six surfaces show a
// row of hers with a name on it; the pencil was at one). R39: W5-2's save chip, for a READING.
import { RenameControl } from "./components/RenameControl";
import { LalaRecentlyDeleted } from "./components/LalaRecentlyDeleted";
import { resumeTarget } from "./lib/lala-resume";
import { renameSavedReport, renameConversation } from "./lib/renames";
import { SaveReadingButton } from "./components/SaveReadingButton";
import { saveReadingToMyRoom, postReadingToRoom, saveReadingNote, saveReadingState } from "./lib/saved-readings";
import { postRoomState, getRoomState, readRoomAccess, roomStateFrom, roomStateNote, roomStateWrite, roomStateWriteNote, panelCollapsedIn, keepsDeviceCache, listRoomBackgrounds, roomBackgroundsNote } from "./lib/room-state";
import type { RoomArtRow } from "./lib/room-state";
// HER ORIENTATION LAW (2026-08-07): the ONE breakpoint the picker and the `<picture>` both read.
import { ROOM_ART_MOBILE_MEDIA, viewportFor } from "./roomArt.js";
import { RoomBackdrop, RoomArtPicker } from "./components/RoomArt";
import type { MyRoomState, RoomStateKey } from "./lib/room-state";
import { listPeople, addPerson, editPerson, deletePerson, personNote, personChartPath } from "./lib/people";
import { listSavedReports, fetchSavedReport, deleteSavedItem, openReportNote } from "./lib/saved-reports";
import { fetchRoomShelf, shelfItemsOr, shelfCountOr, shelfNote, SHELF_READ_COPY } from "./lib/room-shelf";
// R81 — the one thing every modal on this site is drawn inside. It holds two effects and moved
// anyway (R10-3, as R67 restated it): R40's print law is about this component and nothing could
// name it while it sat in here.
import { ModalBackdrop } from "./components/ModalBackdrop";
import { VideoPlayerModal, VideoCard, VideoShelf } from "./components/VideoShelf";
import { PageImageReader, Shelf } from "./components/BookShelf";
import { MagicScene } from "./components/MagicScene";
import { RoomBuildOverlay } from "./components/RoomBuildOverlay";
import { TalkPage } from "./components/TalkPage";
import { TalkCapture } from "./components/TalkCapture";
import { SiteInstructions } from "./components/SiteInstructions";
// L10 — the sign-in door, moved out with the round that gave it her lead's address (R8).
import { SignupPage } from "./components/SignupPage";
import { ClaimPage } from "./components/ClaimPage";
// C8b — lifted out to pay the R8 ceiling this round spent, and worth its own file: it is the one
// control on this site that shows a member her own EMAIL ADDRESS, behind her own deliberate tap.
import { SignInConfirmation } from "./components/SignInConfirmation";
// L5.1 paid the roadmap-7 ceiling with this: the purple box is its own file now (R10-3 — a hook only
// blocks a component a gate CALLS, and nothing calls this one).
import { MagicOverlay } from "./components/MagicOverlay";
import { MagicRoomInstructions } from "./components/MagicRoomInstructions";
import { FileCabinet } from "./components/FileCabinet";
import { MagicEdgeControls } from "./components/MagicEdgeControls";
import { useEscapeKey } from "./components/useEscapeKey";
import { EdgeDrawer } from "./components/EdgeDrawer";
import { listSavedVerses, saveVerse, deleteSavedVerse, verseNote, type SavedVerse } from "./lib/saved-verses";
import { listWatching, deleteWatching, saveVideoPosition, watchingMap, progressNote, type WatchedVideo } from "./lib/video-progress";
import { listJournalEntries, writeJournalEntry, removeJournalEntry, journalNote, isSessionOnlyId, sessionOnlyId, JOURNAL_CHANGED_EVENT, type JournalAnswer } from "./lib/journal";
import { listReadingMenu, readingMenuNote } from "./lib/reading-menus";
import { ReadingMenuSection } from "./components/ReadingMenuSection";
import { AkashicPage } from "./components/AkashicPage";
// R60: the last four fabricated collections. Three routes, one transport each, every answer RUN.
import {
  readLalaThread, readEarlierLalaMessages, prependEarlier, listLalaAttachments, attachToMessages, lalaThreadNote,
  listLalaConversations, createLalaConversation, deleteLalaConversation, lalaConversationNote, groupConversations,
  LALA_THREAD_COPY,
} from "./lib/lala-thread";
import TarotLibrary from "./components/TarotLibrary";
// R36: the two binding pages, and the ONE table of what each legal document is called.
import { LegalPage, FaqPage, LegalModal, LEGAL_DOC_TITLES, type ModalDoc } from "./components/LegalPages";
import { guidelinesMarkdown } from "./livingRoom.js";
// R8-3: what a sent message shows of its attachments. The type moves with the thing that renders it.
import { MessageAttachments, type LalaAttachment } from "./components/MessageAttachments";
// W1-6b: the not-yet-ingested book card. R8-4: N4-1 - the ONE card image, reversed on every surface.
import { ReaderPending } from "./components/ReaderPending";
import { useSavedShelf } from "./components/useSavedShelf";
import { BookReaderModal, useBookSearch, type ReaderState } from "./components/BookReaderModal";
// R8-5: the seal Lala hands over in chat. The type moves with the thing that renders it.
import { SealCard, type DeliveredSeal } from "./components/SealCard";
import { sealsOf } from "./lib/delivered-seals";
// R9-1: the shell EVERY narrative reading renders through, and the three types it defines. Out here a
// gate CALLS it — which is how W12-5's empty labelled field stops needing a paid run to catch.
import { NarrativeReportView, type NarrativeSection, type NarrativeReportData } from "./components/NarrativeReportView";
// R9-5: the chart report, and the helper that decides whether a section box is drawn at all. That
// helper's law was held by matching two strings in this file; out there the gate CALLS it.
import { ReportView, reportSectionCard, type ReportData } from "./components/ReportView";
// R10-1: the two Human Design report views and the two types only they name.
import { HumanDesignView, HumanDesignCompatView, type HumanDesignReportData, type HumanDesignCompatData } from "./components/HumanDesignView";
// R10-3: the three astrocartography report views and the seven types only they name.
// R11-1: the two tarot report views, the two card controls they are built from, and their types.
import {
  TarotFlipCard, CardZoomOverlay, TarotReadingView, TarotSpreadView,
  type TarotCard, type TarotReadingData, type TarotSpreadData,
} from "./components/TarotViews";
// R11-2: the deep astrology reading and the petition paper, each with the type only it renders.
import { DeepReportView, type DeepReportData } from "./components/DeepReportView";
import { PetitionPaperView, type PetitionPaperData } from "./components/PetitionPaperView";
// R12-1: the three field blocks every birth form on this site is built from, and the shape they
// patch. They moved so `CreateRoomForm` — the front door — could follow them out and be CALLED.
import {
  NameFields, BirthTimeFields, BirthPlaceFields, UnknownTimeCheck, birthplaceResultLabel, NAME_AT_BIRTH_NOTE,
  initialBirthChart, personToChart, type BirthChartForm, type BirthplaceResult,
} from "./components/BirthFields";
// R111: the numerology popup's own field block, rendered twice per popup — hookless, so a gate CALLS
// it and reads its date label back in both languages rather than parsing the call site's props.
import { NumberFields } from "./components/NumberFields";
// R8/ITEM 2: the anywhere-place picker moved to its own file so a gate can IMPORT it beside her
// /talk capture's picker -- two renderers, one place law (src/lib/place-search.ts).
import { PlaceSearch, type AcgPlace } from "./components/PlaceSearch";
// R12: the front door itself, and the only hookless component in it — so a gate CALLS it.
import { CreateRoomForm } from "./components/CreateRoomForm";
// R11-3: THE DISPATCHER — which reading type reaches which view — and the union it switches on.
// Every generated surface on this site is delivered through it, and out there a gate CALLS it.
import { ReportBody, type AnyReport } from "./components/ReportBody";
// R67: the whole delivery block — the dismissable shell, the reading with its print copy, the
// destinations row and the save note. R48 derived it; R67 moved it where a gate can CALL it.
// `ReportPrintPortal` is imported by name because the two tarot readers keep their OWN layout
// (14-B6a) and so mount the print copy themselves rather than through `ReportWithPrint`.
import { ClosableReport, ReportPrintPortal, ReportWithPrint, DeliveredReading, DeliveredReadingBody } from "./components/DeliveredReading";
// W10-3: the cards Lala pulls render as CARDS in the conversation, both doors, through N4-1's one image.
import { ChatCards, type ChatCard } from "./components/ChatCards";
// ITEM 3c — the product manual handed over in the thread, as a real file the member taps.
import { ManualHandoffCard, CompilationCard, type ManualHandoff, type Compilation } from "./components/ManualHandoff";
// W1-1 / W1-5: the CODED membership box, the one component the locked-door popup and the membership
// page share. Her artwork is the spec for it and is no longer the delivery of it.
// R73/2026-08-18: the ladder card TYPE moved to `src/lib/tier-catalog.ts` with the payload it
// describes when her ruling retired the popup that rendered it.
// R27: which card the box OPENS ON. On a phone the picker shows one card at a time, so this is not
// decoration — it is the tier she is being shown when she is refused.
// L5.1 — a pay prompt has THREE answers, and one of them is "we have not looked yet". Her live walk:
// a PAID member tapped My Room, met the membership popup, and was admitted anyway behind it.
import { membershipKnown } from "./lib/pay-prompt";
// W1-4: the glowing Upgrade button in the nav, and the condition that decides who sees it.
import { NavUpgrade } from "./components/NavUpgrade";
// ITEM G3 — THE STORE APP (her box, 2026-10-13): the first screen is sign-in (R2), nothing is sold (R3).
import { APP_MODE } from "./lib/appMode";
import { captureTruthLinks } from "./storeApp/truthDoor";
import { OpenTruth } from "./storeApp/OpenTruth";
import { AppSignIn } from "./storeApp/AppSignIn";
import { NavDashboard } from "./components/NavDashboard";
import { OwnerTruth } from "./components/OwnerTruth";
// W5-2: the shelf laws (how a title becomes a spine, how a catalogue becomes shelves) and the save,
// which is now ONE button on four surfaces — the two shelves and the two readers.
import {
  CATEGORY_GLYPHS, shelfSlug, fitSpine, buildShelves, shelfBooksFrom,
  type ShelfBook, type FeaturedBook, bookSlug,
} from "./lib/shelves";
import { SAVED_BOOKS_URL, saveMethod, saveButtonLabel, readerKindFor, listSavedBooks, listFeaturedBooks } from "./lib/saved-books";
import { cleanBookParagraphs } from "./lib/library-text";
import { ownsVideoProduct, videoState } from "./lib/video-access";
import { VideoCoverModal } from "./components/VideoCoverModal";
import { shelfActivityAt, byNewestActivity, type ShelfItem } from "./lib/shelf-order"; // W8-2: a re-run files as newest
import { SaveToRoomButton } from "./components/SaveToRoomButton";
import { WatchList, resumeAt } from "./components/WatchList";
import { copyText, copyState, copyLabel, copyWords, COPY_FLASH_MS, type CopyState } from "./lib/clipboard";
// ITEM 4(c) — the profile photo: the crop, the refusal, the removal and the pointer, in one file.
import { ProfilePhoto } from "./components/ProfilePhoto";
// ITEM 6 — themes. The law is shared with the server; the control is hookless so a gate can call it.
import { ThemeChoice } from "./components/ThemeChoice";
import { applyTheme, storeTheme, readStoredTheme, themeFromState } from "./theme.js";
// ITEM 14 — her language, on the account, in the same shape as her theme. The account menu panel came
// out of this file to hold the switcher, and is where the strings layer meets a real surface.
import { AccountMenuPanel } from "./components/AccountMenuPanel";
import { LalaMemoryPanel } from "./components/LalaMemoryPanel";
import { ReconsentModal } from "./components/ReconsentModal";
// 14-B2: the switcher itself, because signup is the one screen a member reaches before she has an
// account menu to open — and it is the screen where choosing a language decides everything after it.
import { LanguageChoice } from "./components/LanguageChoice";
import { applyLanguage, storeLanguage, readStoredLanguage, languageFromState, DEFAULT_LANGUAGE } from "./language.js";
import { t } from "./strings.js";
import { saveCopy } from "./saveCopy.js";
// T8-3's locked-door interceptor — extracted so a gate can run it against a fake window (R46).
import { LOCKED_DOOR_EVENT, raiseLockedDoor, watchLockedDoors, spendGesture, askedByHer } from "./lib/locked-door";
import { READING_UNREACHABLE, readingAnswer, readingStatusLine } from "./lib/reading-answer";
import { OPEN_SHELVES_EVENT, openShelves, shelvesRequestFrom, canOpenItem } from "./lib/shelves-door";
// R46: the astrology reading order and the copy that fills it from her chart, where I6's stamp lives.
import { readingFormFromChart, convertToTwentyFourHour, type AstrologyReadingForm } from "./lib/reading-form";
// D2 — owner admin powers: the grants tab, and the members-menu block that had to learn what a gift is.
import { OwnerGrants } from "./components/OwnerGrants";import { OwnerTicketsTab } from "./components/OwnerTickets";
// LR1 — the Living Room and her moderation seat, both whole in their own files.
import { LivingRoomPage } from "./components/LivingRoom";
import { LiveKillSwitch } from "./components/LiveRail";
import { HouseInbox } from "./components/HouseInbox";
import { InboxScreen } from "./components/LivingRoomInbox";
import { OwnerLivingRoom } from "./components/OwnerLivingRoom";
import { OwnerTriage } from "./components/OwnerTriage";
import { OwnerOverview, OwnerDelivery, OwnerChecks, RangePicker } from "./components/OwnerOverview";
import { OwnerMessages } from "./components/OwnerMessages";
import type { OwnerContactMessage } from "./components/OwnerMessages";
import { EMPTY_SUPPORT_REPLY, supportReplyNext, supportReplyWho } from "./lib/support-reply";
import type { SupportReplyState } from "./lib/support-reply";
import OwnerBroadcasts from "./components/OwnerBroadcasts";
import { OwnerTeaching } from "./components/OwnerTeaching";
import { pickSome } from "./lib/pick-some";
// A4: `OwnerCatalogs` and its two types are DELETED with the view — see `src/commandCenter.js`.
import { UpdateBanner } from "./components/UpdateBanner";
import { PullToRefresh, useRefresh } from "./components/PullToRefresh";
// R8 — M4-1's phone rail toggle and its two-listener law, out where an instrument can reach them.
import { MobileDrawerToggle } from "./components/MobileDrawerToggle";
import { InstallPrompt } from "./components/InstallPrompt";
import { PhonePrompt } from "./components/PhonePrompt";
import { useShelfOverflow } from "./lib/shelf-overflow";
import { PushOptIn } from "./components/PushOptIn";
// R217: the ten seal-form laws left with `SealCreatorPage` (R8's ledger rule — an extraction that
// leaves its imports behind pays the ceiling). `SEAL_MAX_AFFIRMATION_LENGTH` and
// `overlongAffirmations` went with them and were ALREADY dead here before this round: `affTooLongArgs`
// reads both inside `magicRoom.js`, and nothing in this file ever named either.
import { hidesMemberIdentity, outputNoun, magicBodyHeading, CRAFT_DRAWERS, CRAFT_FEATURES, OIL_TRADITIONS, PETITION_DIVINE_NAMES, magicOverlayLabel, cauldronKindLabel } from "./magicRoom.js";
import { sendToCauldron, cauldronSendNote, cauldronSendState } from "./lib/cauldron-send";
// R70: ONE read of the Spellbook's index, and it carries the LOCK the client is never allowed to guess.
import { beginRead, readMayPaint, closeRead } from "./lib/latest-read";
import { SendToCauldronButton } from "./components/SendToCauldronButton";
import { SealShelfView } from "./components/SealShelfView";
import { SpellbookExperience, type SpellLayering } from "./components/SpellbookExperience";
import { usePrefersReducedMotion } from "./components/usePrefersReducedMotion";
// R97: one lookup law for both readers of /api/astrology/geo-search, so a refusal cannot be a
// no-match in one field and a refusal in the other.
import { searchPlaces, lookupStateFor, placeNote, type PlaceLookupState } from "./lib/place-search";
// R98: one send law for the drawer and the page, so a refusal cannot arrive as three dots on either.
import { sendToLala, lalaSendTurn, lalaSendState, newestStoredId, awaitDroppedReply } from "./lib/lala-send";
import { readAngelDirectory, angelDirectoryNote } from "./lib/angel-directory";  // R99
import { readProfile, roomIsMissing, roomUnreachable } from "./lib/profile-read";
import { identityKey, roomProfileAction, resetMemberState, funnelPrefillSurvives } from "./sessionReset.js";
import { readMembershipStatus, membershipStatusNote, type MembershipAnswer } from "./lib/membership-status";
import { readTierCatalog, tierCatalogNote, type LockInfo, type OneTimeProduct, type TierCatalog, type TierCard } from "./lib/tier-catalog";
import { readShowcase, showcaseNote, type ShowcaseBubble, type ShowcaseConfig } from "./lib/home-showcase";
import { readLineMeanings, lineMeaningsNote, type AcgLineMeaningPlanet } from "./lib/acg-lines";
import { readDailyGuidance, runDailyGuidance, readDailyCard, drawDailyCard, dailyNote, dailyIdFromSearch, type DailyAnswer, type DailyCard, type DailyGuidance } from "./lib/daily-room";
import { loadOwnerData, postOwnerAction, ownerLoadNote, ownerActionNote } from "./lib/owner-load";
import { type EvidenceEvent } from "./components/OwnerEvidence";
import { OwnerProbes } from "./components/OwnerProbes";
import { ComingSoonModal } from "./components/ComingSoonModal";
import { visibleProbes } from "./probeClears";
import { deskView } from "./supportDesk.js";
// R70: what a spell becomes when it leaves the book (the shelf row, the PDF, the Cauldron send) is a
// pure law, so it lives where a gate can RUN T9-2's no-body-no-box on the sections it builds.
// R69: the pot's three transports, its panel and the experience that drives them all live beside
// each other now. App.tsx keeps only the SHARED SENDER, which belongs to the eleven rooms that reach
// the Cauldron rather than to the Cauldron itself.
import { CauldronExperience } from "./components/CauldronExperience";
import { applyUpdate, checkForUpdate, watchForUpdates } from "./lib/pwa";
import { OwnerAnalytics } from "./components/OwnerAnalytics";
import { OwnerFinancials } from "./components/OwnerFinancials";
import { OwnerMembersTab, type OwnerMember } from "./components/OwnerMembersTab";
import { RANGED_TABS, DEFAULT_RANGE, DEFAULT_SECTION, defaultViewOf, goToView as goToViewLaw } from "./commandCenter.js";
import { OwnerNav } from "./components/OwnerNav";
import { OwnerMemberDetail, type MemberDetail } from "./components/OwnerMemberDetail";
import { OwnerReview } from "./components/OwnerReview";
import { OwnerVeil } from "./components/OwnerVeil";
import { OwnerReviews } from "./components/OwnerReviews";
import { OwnerAlerts } from "./components/OwnerAlerts";
import { MR_IMG_W, MR_IMG_H, MR_CAULDRON } from "./lib/magic-room-geometry";
import { type SubStatus } from "./components/AccountSubscription";
// R13 — the search hit. `useBookSearch` keeps its hooks here and imports the types back.
import { BookHitList, SnippetText, type BookHit } from "./components/BookHitList";
import { searchBooks, searchStateFrom, IDLE_SEARCH, type BookSearchState } from "./lib/library-search";
import { readLibraryShelf, shelfSource } from "./lib/library-shelf";
import { libraryEmptyNote, librarySearchCount } from "./lib/library-empty";
// R5-8: authErrorMessage / elapsedLabel / newestFirst -> src/lib/labels.ts; reportTypeLabel -> src/reportLabels.js (P2-4B, shared with the server); R5-9: numerology -> src/lib/numerology.ts.

import { ASK_LALA_READING_TYPES, readingToAskText, handoffKindFor, composeHandoffMessage, suggestedQuestionsOf } from "./askLalaHandoff.js";
import { preparePhotoForUpload } from "./photoUpload.js";
// R15: what the file picker OFFERS is the same law the server ENFORCES — one declaration, both sides.
import { photoAccept } from "./uploadTypes.js";
// R16: I6 Rule 3's warm prompt, and the three answers it shares with the server.
import { DuplicatePersonPrompt, type SubjectPickerPerson, type DuplicateChoice } from "./components/DuplicatePersonPrompt";
import { PersonEditModal } from "./components/PersonEditModal";
import { OwnerWordMapPanel } from "./components/OwnerWordMapPanel";
import { RESOLUTION_CANCEL } from "./personDuplicate.js";
import { ZODIAC_SIGNS, sunSignFromBirthDate, signLabel } from "./zodiac.js";
import { astrologyTileRoute } from "./astrologyTileRoutes.js";
import { shelfTitle, subjectReadingTitle, tarotShelfTitle, roomLabel, SHELF_ROOMS, ROOM_FOR_PAGE } from "./rooms.js";
import { acgRunState, astroRunState } from "./generations.js";
import { SaveSubjectOffer } from "./components/SaveSubjectOffer";
import { SealCreatorPage } from "./components/SealCreatorPage";
// IT #146 — her ruling: the angels she was recommended RIDE INTO the seal creator, through the ONE
// door that keeps the app alive (CauldronDoor's shape). The drawer, its card and the two types came
// out with it, because the surface her ticket is about was reachable here by a regex and nothing else.
import { AngelsDrawer } from "./components/AngelsDrawer";
// N7-4: the ONE title law for a thing a member MAKES. The server imports the same module, so the
// name field, the shelf and the saved row cannot disagree about what her creation is called.
import { creationTitle, cleanCreationName, MAX_CREATION_NAME } from "./creationName.js";
// LG1: the legal documents are owner-authored markdown at the repo root, bundled at build
// time so the pages need no server round trip. Editing the markdown updates the page.
import termsMarkdown from "../terms-of-service.md?raw";
import privacyMarkdown from "../privacy-policy.md?raw";
// R36: THE ONE MAP FROM A LEGAL DOCUMENT TO ITS TEXT. The `?raw` imports are a build-time feature and
// have to live in this file, but the mapping does not have to be typed twice — and it was, once at
// the router arm and once inside `LegalModal`, which is the popup she actually agrees at.
const LEGAL_DOC_SOURCES: Record<ModalDoc, string> = { terms: termsMarkdown, privacy: privacyMarkdown, rules: guidelinesMarkdown() };
// T2-2: the consent law, shared with the server so the version she agrees to is the version recorded.
import { TERMS_VERSION } from "./termsConsent.js";

// =============================================================================================
// T8-3 — EVERY TIER REFUSAL RAISES HER BOX. ONE INTERCEPTOR, NOT FIFTY REMEMBERED PATCHES.
//
// The owner's walk: send-to-Cauldron refused her with a bare line of text and no membership box at
// all. The audit that followed is the reason this is a fetch hook rather than a fix at that button:
// the client has roughly FIFTY call sites that can be handed a 402, and exactly ONE of them — the
// My Room report generator — knew what to do with it. Every other one printed its own sentence and
// left her looking at a dead end with nothing to buy. Patching fifty is fifty chances to get it
// wrong today and one certainty of forgetting the fifty-first tomorrow, which is the same reasoning
// that put the gate itself in one table on the server.
//
// So: every response the app receives passes through here, and a 402 carrying `locked` fires ONE
// event. The app root listens and opens the purple box from the server's own payload — the feature,
// the tier that opens it, the ladder, the free layer — so the box always names the door she
// actually touched. The response is CLONED before it is read, so the caller still gets its body and
// its own error path is untouched; the box simply appears over the top of it.
// =============================================================================================
// THE BOX ANSWERS A DOOR SHE TOUCHED, NEVER A PAGE THAT LOADED. Half the app's opening requests
// are gated — the daily card, the journal, the drawers — so a zero-tier member arriving anywhere
// would have been met by the purple box before she pressed a single thing. That is not a locked
// door, it is a pop-up. So the hook remembers when she last actually did something, measured AT THE
// MOMENT THE REQUEST IS SENT (not when it comes back, because a generation she asked for can take
// far longer than any window worth setting), and raises the box only for a request that followed a
// real gesture of hers.
// R46 (ITEM 6's currency): the event name, the gesture window, `raiseLockedDoor` and the fetch hook
// moved VERBATIM to `src/lib/locked-door.ts`, where a gate RUNS them against a fake window — they
// were pure side effects on `window`, so a live Chrome was the only instrument that could reach
// them (R26's position exactly). Hooked here, once, at module scope, as it always was.
watchLockedDoors();
import faqMarkdown from "../faq.md?raw";
// B1: room art is imported rather than referenced by a public/ path, so the build
// fingerprints the filename and a browser cannot serve the previous art from cache.
// Regenerate from the owner's raw PNGs with `npm run assets:backgrounds`.
import homeLogoUrl from "./assets/home-logo.webp";
import menuLogoUrl from "./assets/menu-logo.webp"; // MN2: the round 12th House logo for the menu top
// MR-ART/MR-ART2: the owner's Magic Room images ARE the room's clickable tiles (Vite-fingerprinted).
// The product shots (spellbook, oils, cauldron, rootbook) are cut out to transparent; the dark painted
// scenes (petition, keys, moses) are full-bleed. magic-book-open is the landscape open-book interior.
// N20-4: the site identity, one constant (src/siteMeta.js), imported by everything that says it.
import { SITE_NAME, SITE_TAGLINE } from "./siteMeta.js";
import { safeLocalStorage, safeSessionStorage } from "./lib/safe-storage";
import { takePendingCheckout, clearPendingCheckout, stashPayLink } from "./lib/pending-checkout";

// R6-1: `Page`, `PAGE_PATHS`, `PATH_PAGES` and `pageFromPath` (HP6) moved VERBATIM to
// `src/lib/pages.ts` — see the import at the top of this file.

// R7-2: the `Generator` type moved with its catalog to `src/catalogs/rooms.tsx`.

// R12-1: BirthChartForm moved with the fields that patch it (src/components/BirthFields.tsx) and is
// imported back — sixty-odd flows in this file are typed by it.

// R12-1: BirthplaceResult + birthplaceResultLabel moved with the field that searches places.

// R5-7: the country map (COUNTRY_CODES / COUNTRY_NAMES / COUNTRY_NAMES_BY_CODE / countryCodeOf)
// moved VERBATIM to `src/countryCodes.js` (A3 moved it out of `src/lib/` so the SERVER can read it
// too — that directory is CLIENT-ONLY). Do not redefine them here.

type ChartStatus = "idle" | "loading" | "success" | "error";
type ReadingStatus = "idle" | "loading" | "success" | "error";

// R47: the three sentences every room shows around a save. `src/lib/saved-readings.ts` imports
// nothing and so cannot read the catalog, which is why the copy is handed IN (saved-books.ts's
// fallback-parameter shape). The two rooms with their own words pass their own `failed` line.
// R6-5: AstrologyTool and AstrologyOffering moved with their catalogs to `src/catalogs/astrology-menu.ts`.

// R46: the `AstrologyReadingForm` type moved with the function that builds it (R13/R16's shape).

// R5-4: the `Book` type moved with its catalog to `src/catalogs/sacred-text-books.ts`.

// R7-2: the `HomeMenuItem` type moved with its catalog to `src/catalogs/rooms.tsx`.

// ITEM 0: `type MembershipMode = "login" | "subscribe"` is DELETED. Measured before the swap was
// written: `membershipMode` was SET in five places and READ IN NONE — the retired modal chose its own
// view from `!member` and never looked at the mode it was handed. A value collected and never
// asserted (R52/R138), and the "subscribe" half of it named a surface that no longer exists.
type RoomProfile = BirthChartForm & {
  createdAt: string;
  roomBackground?: string;
};

// R53: one name for a row of `/api/astrology/reports`, because four call sites now ask the shared
// transport for a list of them and an inline shape repeated four times is the thing this round is
// closing everywhere else.
// NIKA: `interpreted` is the row's own three-state answer to whether the engine ever wrote anything
// into it — true / false / null (the 1,959 rows that carry no such key). `readingIsWritten` is the
// only thing allowed to turn it into a verdict.
type SavedReport = { id: string; type: string; title: string; createdAt: string; updatedAt?: string | null; activityAt?: string | null; inMyRoom?: boolean; subject?: string | null; fromPreviousDetails?: boolean; interpreted?: boolean | null };

// R35: JournalEntry moved with JournalEntryRow to src/components/DeleteControl.tsx and is imported
// back (R13/R16's shape — a type moves with the component that renders it).

// R6-5: MenuCategory moved with `siteMenuCategories` to `src/catalogs/astrology-menu.ts`.


// R5-1: the hardcoded 13-city `birthplaceOptions` list, `countryOptions` and `birthplaceLabel` were
// removed here — dead since Geoapify autocomplete shipped (roadmap 2) and `birthplaceResultLabel`
// became the one label. Zero call sites repo-wide when removed. Do not re-add a fixed city list.

// R71: `convertToTwentyFourHour` — the arithmetic between her birth form and the cast — moved to
// `src/lib/reading-form.ts` (imported at the top), where a gate RUNS midnight and noon rather than
// leaving the only thing that ever executed it a member ordering a reading.

// R5-1: `normalizeTimeEntry` (free-text birth-time parsing) was removed here — dead since
// BirthTimeFields replaced the text box with Hour / Minute / AM-PM selects. Zero call sites.

// R46: `AstrologyReadingForm` and `readingFormFromChart` moved VERBATIM to `src/lib/reading-form.ts`
// (imported at the top), where a gate RUNS the copy rather than reading the object literal — the one
// place I6/R2's subject stamp has already fallen off a reading once. Do not redefine them here.

// R12-1: BirthTimeFields moved to src/components/BirthFields.tsx.

// R12-1: BirthPlaceFields moved to src/components/BirthFields.tsx.

// R5-2: formatLabel / formatReadableValue / formatPlanetPlacements / isPersonSubject /
// formatObjectSummary / formatResultSummary / formatReadingOutput moved VERBATIM to
// `src/lib/format.ts`, where a gate can finally load and assert them. Do not redefine them here.

// R5-3 / R56: escapePdfText / downloadReadingPdf / filenameFromDisposition / downloadFromUrl AND
// `downloadReportPdf` all live in `src/lib/downloads.ts` now. That last one waited five rounds on a
// blocker R11 had already removed, and moving it made the anchor block ONE law instead of two that
// had drifted. Do not redefine any of them here.

// R7-2: `generators` and `homeMenuItems`, with the `Generator` and `HomeMenuItem` types, moved
// VERBATIM to `src/catalogs/rooms.tsx` — the first extraction that carries JSX, and it waited for
// R7-1's bundling loader so the move did not put the home page beyond the gate. See the import above.

// R5-4: `sacredTextBooks` and `books` moved VERBATIM to `src/catalogs/sacred-text-books.ts`.


// R6-5: chartCapabilities / astrologyOfferings / ASTROLOGY_OFFERINGS_HIDDEN / astrologyMenuItems /
// numerologyCards / tarotCards / numerologyTools / tarotTools / siteMenuCategories, and the
// AstrologyTool / AstrologyOffering / MenuCategory types, moved VERBATIM to
// `src/catalogs/astrology-menu.ts` — see the import at the top of this file.

function App() {
  // HP6: the page comes from the URL, so a refresh restores exactly where you were.
  const [page, setPage] = useState<Page>(() => (typeof window === "undefined" ? "home" : pageFromPath(window.location.pathname)));
  const [birthChart, setBirthChart] = useState(initialBirthChart);
  // Did this signup arrive off the funnel's bridge carrying her details? Kept as its own fact rather
  // than inferred from a filled birth date, because a member who typed one herself must not be told
  // the house remembered something for her (R141: a receipt for something that did not happen).
  const [prefilled, setPrefilled] = useState(false);
  // L10 — **THE ADDRESS SHE ALREADY GAVE THE LANDING PAGE.** It is the one detail of hers the front
  // door asks for that lives OUTSIDE `birthChart`, because the sign-in form is a different form. Her
  // continuity clause is *everything they gave*, and making a stranger re-type the address she typed
  // ninety seconds ago on the email path is the same broken promise as an empty birth date.
  const [leadEmail, setLeadEmail] = useState("");
  // PN1 / DC-4 — is a new version of the site waiting, and has she put the notice away for now.
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [submittedChart, setSubmittedChart] = useState<BirthChartForm | null>(null);
  // HP1: a question asked from the home page search bar, handed to the Ask Lala page to submit on
  // arrival. Deliberately in MEMORY and not in the URL: a member's question is private, and a
  // query string would put it in their browser history and in any link they ever shared.
  const [pendingAsk, setPendingAsk] = useState("");
  // N17-2: what the pending ask IS, when it is a handed-off reading rather than a typed question.
  const [pendingHandoff, setPendingHandoff] = useState<{ type: string; kind: string; subject?: string } | null>(null);
  // T5-2: the intercept box between "Ask Lala" and the Ask Lala room. Null when no reading is waiting.
  const [handoffIntercept, setHandoffIntercept] = useState<null | { report: unknown; kind: string; subject: string; type: string; questions: string[] }>(null);
  const [chartStatus, setChartStatus] = useState<ChartStatus>("idle");
  const [chartError, setChartError] = useState("");
  const [planetPositions, setPlanetPositions] = useState<PlanetPosition[]>([]);
  const [activeBook, setActiveBook] = useState(books[0]);
  const [activePage, setActivePage] = useState(0);
  const [member, setMember] = useState<MemberSession | null>(null);
  // >>> HER CONVERSION SURFACE 2: THE SIGN-IN MODAL IS RETIRED AND THE DOOR IS A PAGE. <<<
  // Her order: *"EVERY SIGNUP TRIGGER SITE-WIDE LANDS HERE"*, and *"the LOGIN path lands here too,
  // with the existing-account path reachable so a returning member is never stranded"*. Her artwork
  // paints ONE card and one Continue, so the DOOR is the same for both and only the SENTENCE differs
  // -- `/api/auth/login` has always created a member on a new address and resumed her on a known one.
  const [signupIntent, setSignupIntent] = useState("join");

  /**
   * >>> THE ONE DOOR, AND IT KEEPS THE CONTINUATION THE MODAL HAD. <<<
   *
   * Every trigger that used to raise the modal already knew where the member was going next
   * (`pendingPage`), and that is what carries her onward once she is in -- so the page inherits it
   * rather than needing a second answer to *where does she land afterwards* (R28/R29). A trigger
   * with nowhere particular in mind sends her to her own room, which is what a new member wants and
   * what the modal did by opening onboarding over whatever she was looking at.
   */
  function openSignup(intent = "join", next = "my-room", originReading = "", landingTag = "") {
    setSignupIntent(intent);
    setPendingPage(next as Page);
    // >>> HER CORRECTION (2026-08-19) — THE CARD SHE CLICKED TRAVELS WITH HER. <<<
    //
    // *"the funnel/home-card reading they actually clicked to arrive."* This is the ONE place every
    // signup trigger on this site passes through, which is why the stash is written here rather than
    // at the eight call sites: seven of them are not cards and carry nothing, and the eighth would
    // otherwise be the only one that had to remember. The stash refuses anything that is not one of
    // her own built readings, so a trigger handing it noise clears it rather than keeping it.
    writeStashedOrigin(safeSessionStorage(), originReading);
    // LANDING v3's clause (f), here for the same reason the stash above it is: the ONE place every
    // signup trigger passes through. The law refuses a tag that is not hers and CLEARS on none.
    writeLandingArrival(safeSessionStorage(), { tag: landingTag });
    goTo("signup");
  }
  // L2.6 — her dismissal. Why sessionStorage, and why a throwing storage reads as NOT dismissed, are
  // argued in src/lib/onboarding.ts. The lazy initialiser is what stops the box flashing on a refresh.
  const [onboardDismissed, setOnboardDismissed] = useState(() =>
    readDismissed(safeSessionStorage()));
  const dismissOnboarding = useCallback(() => {
    setOnboardDismissed(true);
    writeDismissed(safeSessionStorage(), true);
  }, []);
  const [instructionsOpen, setInstructionsOpen] = useState(false); // SI1: Site Instructions guide
  // >>> **MEM-1 — WHAT LALA REMEMBERS. THE SHELL HOLDS THE FLAG AND NOTHING ELSE.** <<<
  //
  // The reads, the writes and every failure sentence live in `LalaMemoryPanel`, which is roadmap 7's
  // own direction and R8's ceiling in practice: this file's first draft of this feature added eighty
  // lines to a file the ratchet only lets get smaller. Out there a gate can IMPORT the panel and
  // drive its states; in here it would have been reachable by a regex over ten thousand lines.
  const [memoryOpen, setMemoryOpen] = useState(false);
  // X8-HOTFIX: a failed Google sign-in used to re-open this modal saying NOTHING — the reason
  // came back on the URL and was thrown away. From the member's seat that reads as "the button
  // does nothing", which is exactly how the live failure was reported.
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  // R90: the session read could not be ASKED. Kept apart from `authNotice`, which is about a
  // sign-in she just attempted — two different facts, and the one about her own act wins below.
  const [sessionNotice, setSessionNotice] = useState("");
  // HELD BY POSITION (R52): a sign-in she just attempted outranks a session we could not ask about.
  // Both are true at once only when a failed OAuth return ALSO failed to restore, and there the
  // reason she can act on is the one about her own tap.
  const signInNotice = authNotice || sessionNotice || null;
  // Sign-in identity confirmation: a member who lands in an account that is not the one they
  // expected must SEE it, not conclude their room emptied out. Accounts are islands (owner
  // ruling), so the fix for "my things are missing" is almost always "you are in the other
  // account" — which is only actionable if we say whose room this is the moment they arrive.
  const [signedInAs, setSignedInAs] = useState<{ room: string; email: string } | null>(null);
  const [justSignedIn, setJustSignedIn] = useState(false);
  const [pendingPage, setPendingPage] = useState<Page | null>(null);
  // >>> HER 2026-08-18 RULING: A REFUSED DOOR SENDS HER TO THE MEMBERSHIP PAGE, NEVER TO A POPUP.
  // `gateOpen` IS DELETED WITH THE BOX (DL-2/DT-2b/R181: a retired surface is removed at its own
  // declaration, never left standing beside its replacement). A flag nothing can raise is one line
  // away from a round that finds it and renders something. <<<
  //
  // What the server refused with: the feature, the tier that opens it, the ladder, the free layer.
  // Empty when the page was reached by a plain "choose a membership" tap rather than by a refusal.
  // It OUTLIVES the navigation on purpose -- the page she lands on is the surface that has to say
  // which door she touched, which is the whole of what the box was carrying.
  const [gateLock, setGateLock] = useState<LockInfo>({});
  const [membershipUnreadNote, setMembershipUnreadNote] = useState(false);
  const membershipUnreadRef = useRef(false);
  useEffect(() => {
    if (!membershipUnreadNote) return;
    const timer = window.setTimeout(() => setMembershipUnreadNote(false), 8000);
    return () => window.clearTimeout(timer);
  }, [membershipUnreadNote]);
  const pendingGateAction = useRef<null | (() => void)>(null);
  const [profileChecked, setProfileChecked] = useState(false);
  // IT #326: her profile read did not land (offline or refused), so My Room says it could not be
  // reached rather than asking her to create one. roomReadTry is the Try again: it re-runs the read.
  const [roomReadFailed, setRoomReadFailed] = useState(false);
  const [roomReadTry, setRoomReadTry] = useState(0);
  const [roomProfile, setRoomProfile] = useState<RoomProfile | null>(null);
  const [roomBuildStatus, setRoomBuildStatus] = useState<"idle" | "preparing" | "ready">("idle");
  const [roomBuildReports, setRoomBuildReports] = useState<Array<{ type: string; title?: string; ok: boolean }>>([]);

  // >>> BREACH-1 — THE IDENTITY GUARD. A NEW PERSON RENDERS NOTHING THE LAST ONE LEFT BEHIND. <<<
  // The whole reasoning is in `src/sessionReset.js`, where it is RUN. What a reader standing here
  // needs: this is DURING RENDER and not an effect on purpose — an effect runs after the paint, and
  // one frame of another member's birth date IS the incident. State, not a ref, so StrictMode is
  // correct; it fires only when the identity really moves.
  const [lastIdentity, setLastIdentity] = useState(() => identityKey(member));
  const currentIdentity = identityKey(member);
  // ONE clearer, called by this guard AND by `logOut` (R28/R29): two spellings of *empty this
  // screen* is how one of them comes to be missed, and sign-out was the one that was.
  const clearMemberState = () => resetMemberState({
    roomProfile: () => setRoomProfile(null),
    submittedChart: () => setSubmittedChart(null),
    birthChart: () => setBirthChart(initialBirthChart),
    signedInAs: () => setSignedInAs(null),
    profileChecked: () => setProfileChecked(false),
  });
  if (currentIdentity !== lastIdentity) {
    setLastIdentity(currentIdentity);
    clearMemberState();
    // >>> **L10's CONTINUITY LAW, RESTORED UNDER THE GUARD RATHER THAN BESIDE IT.** <<< The whole
    // reasoning is in `src/sessionReset.js`, where it is RUN. What a reader standing here needs:
    // the clear above is correct and stays, and this puts back the ONE thing the stash can prove is
    // hers — refusing outright when the address does not match, because that is BREACH-1's own
    // incident wearing this repair. The WHOLE object is set rather than a patch merged, so it does
    // not depend on the ordering of two setters in one render pass.
    const stashedForMe = readStashedPrefill(safeSessionStorage());
    if (funnelPrefillSurvives(stashedForMe, member)) {
      setBirthChart({ ...initialBirthChart, ...prefillPatch(stashedForMe) });
      setPrefilled(true);
    }
  }

  // T2-2 — THE CONSENT AT SIGNUP. She ticks the box before the button will fire, and the server
  // refuses a create that carries no consent, so this state is a courtesy to her, never the gate.
  const [termsAgreed, setTermsAgreed] = useState(false);
  // C8b — her phone number and her SMS decision, SIGNUP ONLY. Two pieces of state and not one,
  // because a number and a permission to broadcast to it are two facts (R18) and the whole of her
  // constraint (b) is that the second is never implied by the first.
  const [signupPhone, setSignupPhone] = useState("");
  const [signupSmsConsent, setSignupSmsConsent] = useState(false);
  const [signupPhoneNote, setSignupPhoneNote] = useState("");
  const [consentNote, setConsentNote] = useState("");
  // Which legal document the signup modal is showing, so she can READ it without leaving signup.
  const [legalDoc, setLegalDoc] = useState<null | ModalDoc>(null);
  // R2: the one-time re-consent for existing members. The SERVER decides when this is true (it stays
  // false for everyone until the owner declares the attorney-final Terms version).
  const [reconsentOpen, setReconsentOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journalTitle, setJournalTitle] = useState("");
  const [journalDraft, setJournalDraft] = useState("");
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  // X6: which saved entry the pad is currently editing (null = a fresh, unsaved entry). Clicking a
  // recent entry loads it here so Save UPDATES it in place; "New" clears it so Save creates a new one.
  const [journalEditingId, setJournalEditingId] = useState<string | null>(null);
  // R57: the one sentence her journal can say back to her, read by BOTH surfaces (the pad and the
  // My Room panel) so a refusal cannot be visible in one place and invisible in the other.
  const [journalNoteText, setJournalNoteText] = useState("");

  const activeGenerator = generators.find((generator) => generator.id === page);
  // Access model: a logged-in member gets their room, create-room, the three free
  // auto-reports, plus the journal and Ask Lala. Subscription (isMember) still gates
  // the premium tool rooms and any extra report generation. hasMemberAccess keeps its
  // original "is subscribed" meaning so premium-tool navigation does not regress.
  const isLoggedIn = Boolean(member);
  // Q4 — WHAT SHE HAS PAID FOR IS A FACT ON THE SERVER, NOT A FLAG IN A COOKIE.
  //
  // `member.subscribed` is written FALSE at sign-in and is never re-issued: the cookie lives seven
  // days, and paying does not touch it. So a member who signed in free and then paid $149 was still
  // a non-member to this app until she happened to sign out and back in — every client-side gate
  // fired at her, and tapping a video showed her "Choose your membership" on the tier she had just
  // bought. Measured on production with a real test-mode payment.
  //
  // The tier now comes from the server (`/api/membership/status`, read from her subscriptions row)
  // with the cookie flag kept only as the legacy fallback, so the answer is the same one the server
  // enforces with. Reproved on every load rather than remembered.
  const [serverTier, setServerTier] = useState<string | null>(null);
  // EV3 — is the house open right now. Read off the SAME entitlements payload the locks come from,
  // so the paywall and the doors can never disagree about whether an event is running (R28/R29).
  const [eventOpen, setEventOpen] = useState(false);
  // **L5.1 — THE TWO FACTS THIS APP NEVER HELD: HAS THE ANSWER COME BACK?** Both of the flags above
  // are derived from state that starts empty, so every pay-prompt decision in this file was reading
  // "we have not looked yet" as "she does not pay". `src/lib/pay-prompt.ts` is the law; these are the
  // only two inputs it needs, and they are set on the ANSWER rather than on a successful answer.
  const [sessionKnown, setSessionKnown] = useState(false);
  const [tierKnown, setTierKnown] = useState(false);
  // W1-1 — `isMember` IS A BINARY ("does she pay ANYTHING"), and that binary was the bug she walked
  // into: a Newbie is `isMember === true`, so the video player, the Ask Lala drawer, the book reader
  // and the seal generator all MOUNTED and the box arrived later off the server's 402. R20: this
  // holds the ROSTER too — `locks` alone cannot tell "may open it" from "no such key". featureAccess.js.
  const [featureEnt, setFeatureEnt] = useState<ReturnType<typeof featureEntitlements>>(null);
  // >>> **AND A DROPPED GATE MAKES HER A MEMBER FOR AS LONG AS IT IS OPEN.** <<<
  //
  // **HER EVENT, 2026-09-13. THIS ONE LINE SENT HER MEMBERS TO THE MEMBERSHIP PAGE AFTER EVERY OTHER
  // DOOR HAD BEEN OPENED.** Measured against production with a real free member's session: her
  // payload came back `eventOpen: true`, `locks: 0`, every feature true, 20 Lala turns. And she still
  // met the paywall, because `requireMembership` never asks about features at all: it asks THIS, and
  // this asked only about her TIER. `applyGateDrop` lifts features, locks and the allowance and
  // deliberately leaves `tier` alone (she really is still free, and the membership page must keep
  // telling her the truth about that), so `isMember` stayed false and every door behind
  // `requireMembership` threw her at the checkout inside an event she was told was free.
  //
  // **IT IS A SEPARATE NOTION FROM THE FEATURE GATES AND THAT IS WHY THREE ROUNDS OF FIXING THE
  // FEATURE GATES DID NOT TOUCH IT.** The entitlement payload already carries the answer; nothing
  // was reading it here.
  const isMember = Boolean(member?.subscribed) || Boolean(serverTier && serverTier !== "free") || eventOpen;
  // M3 (mobile app shell): the "Rooms" bottom-tab opens a full-screen sheet of rooms.
  const [roomsSheetOpen, setRoomsSheetOpen] = useState(false);
  // AU1-2: the account menu (who you are, My Room, Log out). Closes on an outside click or Escape
  // like every other menu in the house.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // T1 — THE MEMBERS DROPDOWN. What she is on, and whether there is billing to manage. Loaded when
  // the menu opens rather than on every page, because most visits never touch it.
  // D2-1: the shape moved to the component that renders it, `gift` included — a type belongs with
  // the thing that draws it, which is the rule every extraction in this repo has followed.
  const [subStatus, setSubStatus] = useState<SubStatus | null>(null);
  // R87 — **THE ANSWER, KEPT WHOLE AND KEPT APART FROM THE PAYLOAD.** `subStatus` is the last thing
  // the server actually said and it deliberately SURVIVES a later read that failed (R65: the last
  // good payload stands and the NOTE supersedes it); this is whether the most recent read landed.
  // `null` is "nothing has been asked yet", which is not a failure and says nothing.
  const [subAnswer, setSubAnswer] = useState<MembershipAnswer<SubStatus> | null>(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingNote, setBillingNote] = useState("");
  useEffect(() => {
    if (!accountMenuOpen || !isLoggedIn) return;
    let active = true;
    void readMembershipStatus<SubStatus>().then((answer) => {
      if (!active) return;
      // ADJACENCY (R53/R84): the answer is recorded before anything else is done with it, so no
      // later branch can strand the one sentence this panel has.
      setSubAnswer(answer);
      if (answer.status) setSubStatus(answer.status);
    });
    return () => { active = false; };
  }, [accountMenuOpen, isLoggedIn]);

  // Q4: her tier, read on every load and again the moment she lands back from checkout, so the app
  // knows what she owns without waiting for her to open a menu or sign in again.
  useEffect(() => {
    // L5.1: signed out, there is no tier to read and nothing to wait for — the box is the right
    // answer for a visitor immediately, which is what keeps this from turning every first tap into a
    // wait. `membershipKnown` says exactly that.
    // FRONT 1 (2026-10-02): and her DOORS go with her. The cached answer was the last seat's, so after
    // signing out Ask Lala read the owner's open doors, sent, and drew the server's "Sign in first."
    // instead of walking her to the tiers. Null re-asks at the next tap (requireFeature's fresh read).
    if (!isLoggedIn) { setServerTier(null); setTierKnown(false); setEventOpen(false); setFeatureEnt(null); membershipUnreadRef.current = false; return; }
    let active = true;
    // R87: the SAME read, on the same law. It was typed twice (R47's count-the-copies), and the two
    // copies were about to become three the moment the checkout re-poll needed its own answer.
    // **A FAILED READ MAY NOT MOVE `serverTier`.** Only the server says what she has paid for, so an
    // unanswered read leaves the tier exactly where it was — null on a first load, which W1-4's
    // NavUpgrade already reads as "not answered yet" rather than as "free".
    const readTier = () => {
      void readMembershipStatus<SubStatus & { tier?: string }>().then((answer) => {
        if (!active) return;
        setSubAnswer(answer);
        // **L5.1 — THE ANSWER LANDED, AND THAT IS TRUE EVEN OF A READ THAT COULD NOT REACH THE
        // SERVER.** Leaving this false on a failed read would leave a paying member's tap waiting
        // forever, which is the swallow L2.7 is about; what a failed read must not do is MOVE the
        // tier, and the line below already refuses that.
        setTierKnown(true);
        if (!answer.status) return;
        setServerTier(String(answer.status.tier || "free"));
        setSubStatus(answer.status);
      });
    };
    // W1-1: which doors are shut for her, and the box payload for each, straight from the server.
    // R91: a dropped read left R20's `pending` standing FOREVER and the bounce below opens
    // `if (!featureEnt) return;`, so W1-1's "nothing half-open" was broken by a packet.
    const readLocks = () => {
      void readEntitlements().then((answer) => {
        // ENTITLEMENT-UNREADABLE-Q1: whether the house could read her membership at all, for the doors that ask only "is she a member".
        if (active) membershipUnreadRef.current = answer.kind !== "payload" || (answer.payload as { membershipUnread?: unknown })?.membershipUnread === true;
        if (!active || answer.kind !== "payload") return;
        setFeatureEnt(featureEntitlements(answer.payload));
        // The drop makes her a member for as long as it runs. Presence, never truthiness.
        setEventOpen((answer.payload as { eventOpen?: unknown })?.eventOpen === true);
      });
    };
    readTier();
    readLocks();
    // EV3: when the drop opens or raises, her locks are re-read rather than left as they were drawn.
    const onDrop = () => { readTier(); readLocks(); };
    window.addEventListener(ENTITLEMENTS_CHANGED, onDrop);
    // Stripe's webhook and her redirect race each other, so the return from checkout re-reads a
    // moment later rather than trusting the first answer.
    const joined = new URLSearchParams(window.location.search).get("joined") === "1";
    const timer = joined ? window.setTimeout(() => { readTier(); readLocks(); }, 4000) : null;
    return () => {
      active = false;
      window.removeEventListener(ENTITLEMENTS_CHANGED, onDrop);
      if (timer) window.clearTimeout(timer);
    };
  }, [isLoggedIn]);

  // VIBE-1 — THE PURCHASE EVENT, AND IT IS FED BY THE WEBHOOK RATHER THAN BY THE REDIRECT.
  //
  // Her rule is "the true Stripe checkout SUCCESS moment, the same place the membership actually
  // activates". That place is the signed webhook: this file cannot see one, so the server records
  // what really happened and this reads it back. Three things follow that a `?joined=1` test could
  // not have given her:
  //   · A PAID UPGRADE IS COVERED. It returns from Stripe's BILLING PORTAL with no marker in the URL
  //     at all, so a redirect-keyed rule would have missed every upgrade with no symptom.
  //   · A FAILED OR ABANDONED CHECKOUT FIRES NOTHING, by construction rather than by a guard — no
  //     webhook, no row, nothing to report. `joined=0` is not a case that needs handling.
  //   · TEST SEATS AND OWNER SEATS FIRE NOTHING, because the server refuses to write the row at all.
  //
  // CLAIM FIRST, THEN FIRE — the order is the "exactly once" guarantee. The POST is an atomic
  // `UPDATE ... WHERE reported_at IS NULL RETURNING id`, so a reload, a back button, or two tabs open
  // on the same account all lose the race and are handed an empty list. Firing first and acking after
  // would double-report every purchase whose ack was lost, which is the direction that quietly
  // inflates the number her TV spend is optimised against.
  useEffect(() => {
    if (!isLoggedIn) return;
    let active = true;
    // The sequence itself is `src/lib/pixel-purchases.ts`, so a gate can RUN it (R8).
    const reportPurchases = () => reportPendingPurchases({ doFetch: fetch, win: window, stillActive: () => active });
    void reportPurchases();
    // Stripe's webhook and her redirect race each other, so a return from checkout looks again a
    // moment later rather than trusting the first answer — the same 4s the tier re-read above uses.
    const joined = new URLSearchParams(window.location.search).get("joined") === "1";
    const timer = joined ? window.setTimeout(() => { void reportPurchases(); }, 4000) : null;
    return () => { active = false; if (timer) window.clearTimeout(timer); };
  }, [isLoggedIn]);

  // Stripe hosts the billing screen: card, invoices, cancel, resume. We only open the door, and the
  // customer is read from her own row server-side, never sent from here.
  async function openBilling() {
    setBillingBusy(true);
    setBillingNote("");
    try {
      const res = await fetch("/api/membership/portal", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (data?.portalUrl) { window.location.href = data.portalUrl; return; }
      setBillingNote(data?.message || "Billing could not be opened just now.");
    } catch {
      setBillingNote("Billing could not be opened just now.");
    }
    setBillingBusy(false);
  }
  const accountMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!accountMenuOpen) return;
    // MR-MOB: the panel is PORTALLED, so it is not a descendant of this ref. Asking only the
    // wrapper would close the menu on the way to Log out. The portal is asked too, by its class.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePill = accountMenuRef.current?.contains(target);
      const insidePanel = (target as Element | null)?.nodeType === 1
        && Boolean((target as Element).closest?.(".account-menu__portal"));
      if (!insidePill && !insidePanel) setAccountMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setAccountMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [accountMenuOpen]);
  // The session is the only truth about who is signed in, so a session that ends (logout, expiry)
  // can never leave an account menu open over a signed-out house.
  useEffect(() => { if (!member) setAccountMenuOpen(false); }, [member]);
  // Cosmetic role flag (the server decides what is actually allowed).
  const isOwner = Boolean(member?.owner);
  const hasMemberAccess = isMember;

  // N1-7: the Rooms sheet is not an EdgeDrawer, so it listens for the same announcement itself.
  useEffect(() => {
    const step = () => setRoomsSheetOpen(false);
    window.addEventListener(CLOSE_OVERLAYS_EVENT, step);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, step);
  }, []);

  // M4-3 — THE MOBILE APP SHELL IS FOR EVERYONE NOW (owner approval granted, 2026-07-25). It shipped
  // behind an owner-only flag (M3) so she could live with it before anyone else saw it. She has, and she
  // approved the rollout, so the flag is gone: the bottom tab bar and the Rooms sheet are every member's.
  // Still phone-only — that is the CSS's job, not a flag's — so desktop is untouched.
  useEffect(() => {
    document.body.classList.add("m3-shell");
    return () => document.body.classList.remove("m3-shell");
  }, []);

  // N9-1 THE TAB HIGHLIGHT LAW: whichever tab's sheet is open owns the highlight. Overlays announce
  // themselves (announceOverlayTab) and the close-everything broadcast clears the lot, so the bar never
  // shows a stale light after a sheet has gone.
  const [openSheetTab, setOpenSheetTab] = useState<string | null>(null);
  useEffect(() => {
    const onTab = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      setOpenSheetTab((current) => (detail.open ? String(detail.tab) : current === detail.tab ? null : current));
    };
    const onCloseAll = () => setOpenSheetTab(null);
    window.addEventListener(OVERLAY_TAB_EVENT, onTab);
    window.addEventListener(CLOSE_OVERLAYS_EVENT, onCloseAll);
    return () => {
      window.removeEventListener(OVERLAY_TAB_EVENT, onTab);
      window.removeEventListener(CLOSE_OVERLAYS_EVENT, onCloseAll);
    };
  }, []);

  // R90 — THE FIRST READ THIS SITE MAKES, AND A FAILED ONE DREW THE WHOLE HOUSE AS A VISITOR.
  // It read `data.member` off an unchecked `response.json()` inside a catch whose comment was about
  // a developer's laptop, so a dropped request, a proxy page or the seconds an instance spends
  // restarting mid-deploy were byte-identical to a genuine signed-out visitor — including on the
  // Google return path, where the cookie is already set and she has just done the work.
  // `src/lib/session-restore.ts` holds R18's distinction and the free re-ask; here the only new
  // decision is that an UNREACHABLE answer is remembered rather than discarded.
  useEffect(() => {
    let active = true;
    restoreSession<MemberSession>().then((answer) => {
      if (!active) return;
      // **L5.1 — THE ANSWER LANDED, WHICHEVER ANSWER IT IS.** This is the fact the whole app was
      // missing: `member` starting null made "signed out" and "we have not looked yet" one value,
      // so a paying member's first tap on My Room met the sales panel. It is set for EVERY outcome,
      // including a read that could not reach the server — R18's third state is about whether we
      // have an answer, not about whether the answer was yes.
      setSessionKnown(true);
      setSessionNotice(sessionRestoreNotice(answer));
      if (answer.kind !== "member") return;
      setMember(answer.member);
      // R2: the server is the only thing that decides a member owes a re-consent, and it only
      // says yes once the owner has declared a new Terms version.
      if (answer.consent?.reconsentRequired) setReconsentOpen(true);
    });
    return () => { active = false; };
  }, []);

  // ITEM 6 — **HER THEME, AND IT IS THE ACCOUNT'S, NOT THE DEVICE'S.** The device cache is already
  // painted (see `src/main.tsx`) so there is no black flash; this is the correction, and it runs at
  // the ROOT rather than in My Room because a theme has to be right on the page she actually opened.
  // A deep link to /inbox never mounts My Room.
  //
  // The initial value is the CACHE, not the default: the control and the painted page must agree in
  // the moment before the account answers, or the chips say Dark over a white screen.
  const [theme, setTheme] = useState<string>(() => readStoredTheme(safeLocalStorage()));
  // ITEM 14 — HER LANGUAGE IS THE SAME FACT IN THE SAME PLACE, so it is read in the SAME round trip
  // rather than in a second one: two preferences, one `room_state`, one correction. The initial value
  // is the device cache and `readStoredLanguage` answers NULL when there is nothing cached, which is
  // not the same fact as English (R18) — the default is applied here, once, where a member must have
  // words on screen.
  const [language, setLanguage] = useState<string>(() => readStoredLanguage(safeLocalStorage()) || DEFAULT_LANGUAGE);
  // R87 — **THE ACCOUNT MENU'S ONE SENTENCE ABOUT THE READ THAT ANSWERS WHAT SHE HAS PAID FOR.**
  // Composed at RENDER rather than stored, so it follows a language switch for free without putting
  // `language` in the deps of a read that also arms the checkout re-poll. It sits here rather than
  // beside `subAnswer` because `language` is declared below that state and a law is not worth a
  // crash-risk forward reference.
  const subNote = membershipStatusNote(subAnswer, {
    signIn: t(language, "sub.signInAgain"),
    failed: t(language, "sub.readFailed"),
  });
  useEffect(() => {
    if (!member) return;
    let active = true;
    void getRoomState().then((state) => {
      if (!active || !state) return;
      const next = themeFromState(state);
      setTheme(next);
      applyTheme(document.documentElement, next);
      storeTheme(safeLocalStorage(), next);
      // `languageFromState` answers null only for a state that never arrived; inside this branch it
      // has, so an account that says nothing is an ANSWER and the answer is English.
      const lang = languageFromState(state) || DEFAULT_LANGUAGE;
      setLanguage(lang);
      applyLanguage(document.documentElement, lang);
      storeLanguage(safeLocalStorage(), lang);
    });
    return () => { active = false; };
  }, [member]);

  // Her tap: PAINT FIRST, then tell the account. A theme that waits on a round trip before the
  // screen changes reads as a control that did nothing, and she taps it again.
  //
  // **THE WRITE IS PARTIAL AND THAT IS THE POINT** — `{ state: { theme } }` and nothing else, because
  // `room_state` is a jsonb MERGE at the database (LR14's law, and ITEM 4(c)'s fix to this very
  // route). This surface has no business knowing what her background or her open panels are.
  //
  // R51 — AND THE DEVICE CACHE WAITS FOR THE ACCOUNT'S ANSWER. It used to be written before the
  // request and the request's answer was thrown away, so a theme the account REFUSED (ITEM 6's
  // refuse-don't-replace 400, or a write that touched no row) was remembered on the device forever
  // and the two disagreed on every load after it. `keepsDeviceCache` is the law: only a REFUSAL is
  // withheld, because only a refusal is a decision (R18 — offline and a gone session decided
  // nothing, and the device is then the only place her choice can live).
  const chooseTheme = useCallback((next: string) => {
    const applied = applyTheme(document.documentElement, next);
    setTheme(applied);
    void postRoomState({ theme: applied }).then((answer) => {
      if (keepsDeviceCache(answer)) storeTheme(safeLocalStorage(), applied);
    });
  }, []);

  // ITEM 14(c) — her language, the same act: PAINT FIRST (the attribute a screen reader takes its
  // pronunciation from, written for BOTH languages), tell the account with a PARTIAL write that says
  // language and nothing else, and cache it on the device unless the account refused it.
  const chooseLanguage = useCallback((next: string) => {
    const applied = applyLanguage(document.documentElement, next);
    setLanguage(applied);
    void postRoomState({ language: applied }).then((answer) => {
      if (keepsDeviceCache(answer)) storeLanguage(safeLocalStorage(), applied);
    });
  }, []);

  // Journal entries persist to the member's My Room (journals table). Load them
  // when a member is present; clear when signed out.
  useEffect(() => {
    if (!member) {
      setJournalEntries([]);
      return;
    }
    let active = true;
    // R57: the guard is BEFORE the paint and null is NOT an empty journal (R52/R53's position law).
    // The old `r.ok ? r.json() : { entries: [] }` fabricated an array, so the Array.isArray check
    // that looks like it is protecting her is what painted "No entries yet" over everything she
    // has ever written.
    void listJournalEntries<JournalEntry>().then((entries) => {
      if (!active) return;
      if (!entries) { setJournalNoteText(t(language, "journal.loadFailed")); return; }
      setJournalNoteText("");
      setJournalEntries(entries);
    });
    // IT #350/#351: a save from the Angel room or the file cabinet re-reads her list, so it is in the drawer the moment it is sent.
    // A failed re-read changes nothing on her screen (null is never an empty journal, R57).
    const reload = () => void listJournalEntries<JournalEntry>().then((fresh) => { if (active && fresh) setJournalEntries(fresh); });
    window.addEventListener(JOURNAL_CHANGED_EVENT, reload);
    return () => { active = false; window.removeEventListener(JOURNAL_CHANGED_EVENT, reload); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  // Name the room they actually landed in, once, right after a Google sign-in. Runs ONLY on a
  // fresh sign-in (not on every session restore), so it costs one request and never nags a
  // member who is simply moving around the house.
  // Once-only guard lives in a REF, not in the dependency array. Flipping `justSignedIn` from
  // inside the effect re-ran it, and the re-run's cleanup cancelled the still-in-flight request,
  // so the confirmation never appeared (measured: /api/room/state returned 200 and the result was
  // dropped). Deps must not be written by the effect that reads them.
  const signInConfirmed = useRef(false);
  useEffect(() => {
    if (!justSignedIn || !member || signInConfirmed.current) return;
    signInConfirmed.current = true;
    let active = true;
    void readRoomAccess().then(({ state, needsBirthDetails }) => {
      if (!active) return;
      // >>> BREACH-1 — THE SERVER'S REFUSAL IS OBEYED AT THE MOMENT OF HER INCIDENT. <<< This effect
      // fires on SIGN-IN. Her clause: *the client cannot skip what the server refuses* — so a
      // chartless account is emptied whatever the screen was holding, and the sacred flow does not
      // rest on the client half alone, which is one stale bundle away from the same incident.
      if (needsBirthDetails) setRoomProfile(null);
      // Her own name for her room comes first; otherwise the same "<First>'s room" the room header
      // itself shows, so the confirmation and the room agree on what this place is called. A read
      // that never landed lands here too, and it always did — the old catch composed this same line.
      const saved = roomStateFrom(state).roomName.trim();
      setSignedInAs({ room: saved || `${memberFirstName(member, null)}'s room`, email: member.email || "" });
    });
    return () => { active = false; };
  }, [justSignedIn, member]);

  // Hydrate a returning member's saved profile so their room and birth form are
  // pre-filled.
  useEffect(() => {
    if (!member) setRoomReadFailed(false);
    if (!member) { setProfileChecked(false); return; }
    let active = true;
    void readProfile<Partial<BirthChartForm> & { createdAt?: string }>().then((answer) => {
      if (!active) return;
      setRoomReadFailed(roomUnreachable(answer));
      // >>> BREACH-1 — THIS WAS A ONE-WAY WRITE AND THAT IS THE WHOLE INCIDENT. <<< It read
      // `if (p && p.birthDate)` with NO else, so an identity change to a chartless account ran
      // nothing and `roomProfile` kept the PREVIOUS person. Three answers now, in
      // `src/sessionReset.js` where they are RUN; the birth-date test is `roomIsMissing` (R71/R165).
      const p = answer.profile;
      const action = roomProfileAction({ known: answer.known, roomMissing: roomIsMissing(answer) });
      if (action === "set" && p) {
        setRoomProfile({ ...initialBirthChart, ...p, createdAt: p.createdAt || new Date().toISOString() });
        setBirthChart((current) => ({ ...current, ...p, intention: current.intention }));
      } else if (action === "clear") {
        setRoomProfile(null);
      }
      // R71: THE NOT-DISMISSIBLE CREATE-YOUR-ROOM MODAL OPENS ON A READ THAT LANDED AND ON NOTHING
      // ELSE. Held by POSITION (R52/R53): the flag that says we KNOW whether she has a house is set
      // inside the arm that learned it, so no later spelling can hoist it back into a `finally`
      // that fires on a refusal too — which is exactly what it used to be.
      if (answer.known) setProfileChecked(true);
    });
    return () => { active = false; };
  }, [member, roomReadTry]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get("auth");

    if (authStatus === "google-success") {
      // Session loads via loadSession; a new member then gets the onboarding step,
      // an existing member lands normally. No forced subscribe prompt.
      setJustSignedIn(true);
    }

    if (authStatus === "google-error") {
      // TRIGGER 1 of 5. This one is NOT a paywall and does not become the tier popup: sign-in
      // FAILED, and the honest surface for that is the sign-in door wearing the reason. Raising a
      // price list at somebody whose Google handoff just errored would answer a question she did not
      // ask (R18 — two facts, and only one of them is about money).
      setAuthNotice(authErrorMessage(params.get("reason")));
      // TRIGGER 1 of 7. Sign-in FAILED, so the honest surface is the door wearing the reason -- her
      // page carries the notice, exactly as the modal did.
      openSignup("login");
    }

    // R42: one act, written once. Scrubbing `?auth=` was the last line of BOTH branches above, and
    // the condition is those two values, so nothing else in the query is ever touched.
    if (authStatus === "google-success" || authStatus === "google-error") window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  // FREE-1 (her order, 2026-08-11) — **W1-1's ROUTING GATE IS RETIRED AT ITS OWN DECLARATION**
  // (DL-2/DT-2b/R181), not overridden below it: it read `featureForPage(nextPage)` here and refused.
  // **Nothing was given away by deleting it** — `server/route-features.js` is one table checked
  // centrally before dispatch, so this was the door's FACE and the server is the door. A free
  // explorer reaches a room with nothing generated in it, and the first engine call answers 402 into
  // T8-3's interceptor, which is the same box W1-1 used to raise at the threshold.
  function goTo(nextPage: Page) {
    enter(nextPage);
  }

  // ITEM 1: closing the inbox is a RETURN, not a destination — the law is `src/lib/inbox-return.ts`.
  const closeInbox = () => runInboxClose(window.history.state, Object.keys(PAGE_PATHS), { back: () => window.history.back(), go: goTo });

  // LANDING v3: her three ad doors, by KEY. A separate navigator because *which page* and *which
  // advertisement* are two questions (R18); the key is F1's roster's, never re-spelled here.
  function goToFunnel(key: string) {
    enter("funnel", `?f=${encodeURIComponent(funnelFromParam(key))}`);
  }

  function enter(nextPage: Page, query = "") {
    // The tap that moved her is SPENT by the move (an effect on `page` runs too late).
    spendGesture();
    setPage(nextPage);
    // HP6: push a real history entry, so the URL is shareable and the back button walks back
    // through where the member actually went. replaceState would silently overwrite the trail.
    const url = PAGE_PATHS[nextPage] || "/";
    // LANDING v3: a door with a KEY is a different address, so a query is ALWAYS pushed (two funnel
    // doors really are two entries) while the no-query path keeps its exact pathname test unchanged
    // (ITEM 12's neutrality). `/funnel` with no key resolves F1's FALLBACK, which files every paid
    // click under one advertisement with nothing red anywhere (R8-4).
    if (query) window.history.pushState({ page: nextPage }, "", `${url}${query}`);
    else if (window.location.pathname !== url) window.history.pushState({ page: nextPage }, "", url);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // HP6: back and forward. The browser changes the URL without telling React, so the page state
  // is read back off the path it landed on.
  useEffect(() => {
    const onPop = () => { spendGesture(); setPage(pageFromPath(window.location.pathname)); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // R42 — THE NAME THE BROWSER WRITES DOWN. `document.title` was set nowhere, so every tab, bookmark,
  // history entry and iOS share sheet wore index.html's one static title. `page` is where all five
  // ways in end up (goTo, enter, a typed URL, the back button, W1-1's tier bounce), so one effect on
  // it covers every route without a second roster of who must remember to set it.
  useEffect(() => { document.title = documentTitle(page, SITE_NAME, SITE_TAGLINE); }, [page]);


  // VIBE-1 — THE PIXEL'S page_view ON EVERY CLIENT-SIDE ROUTE CHANGE (her order: every room visit
  // counts as a view). This rides R42's own argument one line up rather than a second roster: `page`
  // is where ALL FIVE ways in end up — `goTo`, `enter`, a typed or shared URL, the back button, and
  // W1-1's tier bounce — so one effect on it covers every route, and a room added next year is
  // counted by EXISTING rather than by somebody remembering to instrument it.
  //
  // IT SKIPS ITS OWN FIRST RUN, AND THAT IS THE WHOLE CORRECTNESS OF THE PAIR. The verbatim snippet
  // in index.html's head already fires `page_view` for the document load, and an effect that fired on
  // mount too would report TWO views for every landing — inflating exactly the number the TV campaign
  // is measured on, in the direction nobody checks (a metric that is too high reads as success).
  // A ref and not state: state would re-render, and the value must survive the render it is read in.
  const pixelFirstRun = useRef(true);
  useEffect(() => {
    if (pixelFirstRun.current) { pixelFirstRun.current = false; return; }
    // A bare event: a page view carries no parameters at all, so there is nothing here that could
    // ever be a member fact (her clause 3, satisfied by construction rather than by filtering).
    sendPixelEvent(window, "page_view");
    // TTV-1 — THE TRUSTED TV TAG RIDES THE SAME EFFECT, AND SHARING IT IS THE POINT. Both tags are
    // answering one question — a route changed — so a second effect would be a second copy of the
    // skip-first-run law, and two copies of a law drift (R28/R29). One of them getting the skip
    // wrong has no symptom except a number that is quietly twice what it should be, in the direction
    // nobody questions. Its own door refuses an absent tag rather than throwing, so an ad blocker
    // stripping one vendor cannot take the other one down with it.
    sendTtvEvent(window, "PageView");
  }, [page]);

  // FREE-1 — **W1-1's OTHER END GOES WITH IT, AND MOVING ONLY ONE OF THE TWO IS WORSE THAN MOVING
  // NEITHER.** This effect caught the three ways in that skip `goTo` (a typed URL, the back button,
  // a tier that changed under her) and BOUNCED her home — so left standing it would throw a free
  // explorer out of the Tarot room on the back button, out of the house she was just invited into.

  // OD: the page-view ping. Instrumented early and deliberately, because analytics cannot
  // be backfilled: a day this does not record is a day the dashboard can never show. It
  // sends the room name and nothing else, and its failure is silent by design (a member's
  // page must never break because a count did not land).
  // NOT `keepalive`. It looked like the careful choice and was the opposite: keepalive exists to
  // let a beacon outlive a document being UNLOADED, and this fires on SPA navigation, where the
  // document never unloads, so it bought nothing. What it cost was real: Chrome routes keepalive
  // fetches through a path that never reports completion to CDP, so the request looked eternally
  // in-flight, `networkidle0` never fired, and the FULL SMOKE SUITE crashed on every run from the
  // moment this shipped. A ping that hides from our own instruments is worse than no ping.
  useEffect(() => {
    fetch("/api/events/page", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // SURFACE 2(a)/2(b) — where she came from and WHICH DOOR ride this ping, every one read ONCE at module load and resolved server-side
      body: JSON.stringify({ page, ref: LANDING_REFERRER, utm: LANDING_UTM, funnel: LANDING_FUNNEL }),
    })
      .then((r) => r.text()) // DRAIN the body: an unread response stream never completes
      .catch(() => {});
  }, [page]);

  // PN1 / DC-4, PIECE THREE — ASK THE BROWSER TO LOOK FOR A NEW VERSION on navigation and on
  // foreground. A registration checks on its own schedule (roughly daily), which on a phone somebody
  // never closes means a fix can sit undelivered for a day. Both moments are floored in the shared
  // law, so a burst of taps is not a burst of requests, and neither costs anything when there is
  // nothing new: it is one conditional GET of a small file.
  useEffect(() => { checkForUpdate("navigation"); }, [page]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") checkForUpdate("foreground"); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  // PIECE FOUR — the visible notice. Raised only when a new worker is WAITING; "Later" puts it away
  // for this visit and it returns on the next foreground check, because a member who is mid-seal is
  // not someone to nag and is also not someone to leave on a stale app forever.
  useEffect(() => { watchForUpdates((waiting) => { setUpdateWaiting(waiting); if (waiting) setUpdateDismissed(false); }); }, []);

  // Every page is viewable by anyone; navigation never opens the membership gate.
  // The gate fires on ACTIONS (pull cards, generate a reading, open a book, play a
  // video, receive a seal), handled where those actions live. The only navigation
  // rule is that My Room needs a login, so there is a member to attach the room to.
  function requestMemberAccess(nextPage?: Page) {
    if (!nextPage) {
      // AU1-1: the account entry point NEVER opens the membership modal over a member — the purple
      // panel the owner kept meeting after signing in, on the control that used to be the only place
      // Log out lived. Guarded at the source, on session state, rather than hidden in CSS.
      // FREE-1 — **TRIGGER 2 of 5 IS THAT SAME WALL, TAKEN DOWN FOR PAYING MEMBERS AND LEFT UP FOR
      // EVERYONE ELSE:** a signed-in member with no tier met the box on the ACCOUNT icon, so a free
      // account could reach neither Log out, her language, her theme nor her notifications. Her
      // clause 4 leaves one popup in a free member's life and this is not it; the way to membership
      // is the Upgrade control she chooses.
      if (isLoggedIn) {
        setAccountMenuOpen(true);
        return true;
      }
      // TRIGGER 2 of 7 -- the account icon on a signed-out browser. She is being asked to come in,
      // so this is the LOGIN sentence rather than the join one.
      openSignup("login");
      return false;
    }
    // **L5.1 — HER LIVE DEFECT WAS THIS BRANCH:** as a PAID member she tapped My Room, met the
    // membership popup, and was admitted anyway behind it, because `isLoggedIn` is `Boolean(member)`
    // and `member` is null until `restoreSession` answers — *"signed out"* and *"we have not looked
    // yet"* were one value. **Its `wait` survives FREE-1 and is REMEMBERED, never dropped** (L2.7).
    //
    // FREE-1 — **THE MY ROOM WALL IS GONE.** TRIGGER 3 raised the box with
    // `openLockedDoor({ feature: "room:my" })` and **`room:my` IS ON NO ROSTER** — nowhere in
    // `server/tiers.js`, so `lockPayloads` never emitted a row for it (R20): an unconditional *"you
    // have not paid"* wearing a key that does not exist, which is her clause 4's *"My Room class"*.
    // **THE SIGN-IN IS KEPT AND IS A DIFFERENT QUESTION (R18)**, and L5.1's `wait` with it.
    if (nextPage === "my-room" && !isLoggedIn) {
      setPendingPage("my-room");
      if (!membershipKnown({ sessionKnown, tierKnown, isLoggedIn })) return false;
      // TRIGGER 3 of 7 -- My Room needs a session for the room to be attached to.
      openSignup("join", "my-room");
      return false;
    }
    goTo(nextPage);
    return true;
  }

  // **L5.1 — THE WAIT ALWAYS RESOLVES, WHICH IS WHAT KEEPS IT FROM BEING L2.7's SWALLOW.** A tap
  // taken while the session was still loading is REMEMBERED (`pendingPage`) and this is the half
  // that finishes it the moment the answer lands; without it, waiting rather than prompting is just
  // a tap that did nothing. `completeMembership` keeps its own resolution below for the OTHER door
  // and the two cannot fight, because this only ever runs while the modal is shut.
  useEffect(() => {
    // >>> A SIGNED-IN MEMBER IS NEVER LEFT STANDING AT THE DOOR, AND THE RENDERED WALK IS WHY. <<<
    // Every trigger carries a `pendingPage`, so the branch below walks her on — but she can arrive at
    // `/signup` with NO trigger behind her: a direct link, a bookmark, her mailed bridge. Measured at
    // coordinates: she typed her name, pressed her own Continue, the member row was created, and she
    // was **left sitting on the signup page** with nothing red anywhere. Her own room is where a new
    // member wants to be, and it is what the modal did by opening onboarding over the page she was on.
    if (isLoggedIn && page === "signup") {
      const next = pendingPage || "my-room";
      setPendingPage(null);
      goTo(next);
      return;
    }
    if (!pendingPage) return;
    // FREE-1 — TRIGGER 4 of 5 moves WITH trigger 3, or a tap taken mid-load lands on the removed box.
    if (!membershipKnown({ sessionKnown, tierKnown, isLoggedIn })) return;
    // >>> THE SIGNED-IN BRANCH IS ASKED BEFORE THE DOOR, AND THE ORDER IS THE WHOLE OF IT. <<< The
    // door is a PAGE now rather than a modal, so *she is standing at it* is a page she has to be
    // carried OFF once she is in. Guarding the whole effect on that page — which is what a
    // modal-shaped guard becomes when the modal becomes a route — leaves a member who has just
    // signed up sitting on the signup page forever, with nothing red anywhere.
    if (isLoggedIn) {
      const next = pendingPage;
      setPendingPage(null);
      goTo(next);
      return;
    }
    // She is already standing at the door: opening it again would be a navigation on top of itself.
    if (page === "signup") return;
    // TRIGGER 4 of 8 -- the tap taken while the session was still loading, finished now the answer
    // has landed. It keeps the page she was going to, which is the whole point of `pendingPage`.
    openSignup("join", pendingPage);
  }, [pendingPage, page, sessionKnown, isLoggedIn]);

  // >>> **CHECKOUT-SIGNIN-Q1: YES (hers, 2026-10-02).** <<< A stranger who pressed a tier's Choose was sent
  // to sign in; the moment she is in, she goes STRAIGHT ON to that tier's checkout, by whichever door she
  // signed in (Google comes back as a full page load, so the tier rides sessionStorage, taken once).
  // A refusal or a failure walks her to the membership page, never to nothing.
  // THE WIN-BACK'S BUTTON (her word, 2026-10-18: "go directly to pay"): `/membership?pay=vet` stashes that plan exactly as a
  // signed-out Choose does, before the effect below reads it, so a signed-in member goes straight on to checkout and a
  // signed-out one is sent through sign-in first. Website only: the phone app never starts a purchase.
  const [payLink, setPayLink] = useState(() => !APP_MODE && typeof window !== "undefined" && stashPayLink(window, safeSessionStorage(), Date.now()));
  useEffect(() => {
    if (payLink && sessionKnown && !isLoggedIn) { setPayLink(false); openSignup("login", "membership"); }
  }, [payLink, sessionKnown, isLoggedIn]);
  useEffect(() => {
    if (!sessionKnown || !isLoggedIn) return;
    const body = takePendingCheckout(safeSessionStorage(), Date.now());
    if (!body) return;
    void (async () => {
      try {
        const res = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, returnTo: "/my-room" }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
        if (data?.portalUrl) { window.location.href = data.portalUrl; return; }
      } catch { /* she lands on the tiers below */ }
      goTo("membership");
    })();
  }, [sessionKnown, isLoggedIn]);

  function completeMembership(nextMember: MemberSession) {
    setMember(nextMember);

    if (nextMember.subscribed && pendingPage) {
      goTo(pendingPage);
      setPendingPage(null);
    }
    // Return-path: once membership is active, resume the gated action the member
    // was trying to run when the gate stopped them.
    if (nextMember.subscribed && pendingGateAction.current) {
      const action = pendingGateAction.current;
      pendingGateAction.current = null;
      action();
    }
  }

  // Gate an action behind membership. Free for members; otherwise open the purple membership gate
  // and remember the action to resume after they join.
  // T8-3: a caller that already holds the server's 402 payload hands it in, so the box tells her which
  // door she touched and what opens it instead of the generic version. Optional, so every existing
  // one-argument caller is unchanged.
  // >>> PERF-1 — STABLE IDENTITY, MEMBER-FACING: a plain `function` churned every App render, which
  // recreated LivingRoom's gate -> loadMe -> its mount effect (`/me` EIGHT times per feed load). Its
  // dep is EXACT: `isMember` is the only reactive value read here, and the ref and setters are stable
  // by React's own guarantee — wider restores the churn, narrower resumes against a stale tier. <<<
  // >>> **THE ONE ANSWER TO WHAT A REFUSED DOOR DOES, AND IT IS ONE FUNCTION BECAUSE IT WAS FIVE.**
  // <<< `requireMembership`, `requireFeature` (twice), `openLockedDoor` and T8-3's interceptor each
  // set the same two pieces of state and opened the same box -- five spellings of one act, which is
  // exactly the shape that lets ONE of them keep raising a popup after the other four stop
  // (R28/R29). Her live tester found precisely that class. There is one now, and the census asserts
  // no second one exists.
  //
  // IT IS A PLAIN FUNCTION, LIKE `goTo`, `enter` AND `requireFeature` BESIDE IT, and everything it
  // touches is stable by React's own guarantee (a ref, two setters, and `enter`'s `setPage`), so a
  // `useCallback` capturing it from an earlier render behaves identically -- which is what keeps
  // PERF-1's stable identity on `requireMembership` below true rather than merely claimed.
  // A LOCKED DOOR MOVES HER ONLY WHEN SHE REACHED; the lock is recorded either way (#122/#125/#132). Why + all four callers: scripts/onboard-reach.mjs.
  function sendToMembership(lock?: LockInfo, resume?: () => void, reached = askedByHer()) {
    // ENTITLEMENT-UNREADABLE-Q1 (her YES, 2026-10-12): a membership the house could not read is not one she lacks, so she is
    // told so where she stands rather than sent to the sign-up page. Nothing opens.
    // ...and the page's own re-read is rung, so one failed read never outlives the outage that caused it (second reviewer, 53de7d13).
    if (lock?.unread) { if (reached) setMembershipUnreadNote(true); window.dispatchEvent(new Event(ENTITLEMENTS_CHANGED)); return; }
    pendingGateAction.current = resume || null;
    setGateLock(lock || {});
    if (!reached) return;
    // Her defect 4: the stamp is written HERE because `page` is the only honest answer to where the
    // back button goes — the whole argument is in `return-path.ts`, where a gate RUNS it (R8's ledger).
    const cameFrom = page;
    goTo("membership");
    stampMembershipFrom(typeof window === "undefined" ? null : window, cameFrom);
  }

  // >>> PERF-1 -- STABLE IDENTITY, MEMBER-FACING: a plain `function` churned every App render, which
  // recreated LivingRoom's gate -> loadMe -> its mount effect (`/me` EIGHT times per feed load). Its
  // dep is EXACT: `isMember` is the only reactive value read here. <<<
  // ENTITLEMENT-UNREADABLE-Q1: "not a member" read off a membership the house could not read is not that fact (a ref, so no new dep).
  const requireMembership = useCallback((action: () => void, lock?: LockInfo) => {
    if (isMember && !lock) { action(); return; }
    sendToMembership(lock || (membershipUnreadRef.current ? { unread: true } : undefined), action, askedByHer());
  }, [isMember]);

  // W1-1 — LOCKED MEANS CLOSED. THE FEATURE QUESTION, ASKED BEFORE THE SURFACE OPENS. Her law: below
  // tier, the tap raises the box and the surface NEVER MOUNTS; close the box and she is exactly where
  // she was, with nothing half-open. `requireMembership` could not do that (it only knew whether she
  // pays at all); this asks which door she touched.
  // THE THIRD STATE MATTERS. `featureEnt === null` means the answer has not come back yet, and treating
  // that as "no" would flash the box at a paying Vet on a slow network, so an unknown answer WAITS.
  // R20: an answer that ARRIVED without this key is NOT that fact and is closed, as on the server.
  function requireFeature(feature: string, action: () => void) {
    const reached = askedByHer(); // at ENTRY: a slow read would turn her tap into a mount read
    const decide = (ent: ReturnType<typeof featureEntitlements>) => {
      const lock = featureLockAfterAnswer(ent, feature);
      if (!lock) { action(); return; }
      // The action is remembered so that joining resumes exactly what she reached for.
      sendToMembership(lock, action, reached);
    };
    // >>> **A WALL IS RAISED ON A FRESH ANSWER, NEVER ON A CACHED ONE (2026-09-05).** <<<
    //
    // **THE MEASUREMENT THAT FORCED IT, OFF THE REAL DOOR.** A funnel walk cast its chart, was
    // greeted by name, watched its meter read *"5 more with me, love"*, typed its first question —
    // and was handed the MEMBERSHIP PICKER. That seat's `lala_messages` holds ONE row, the greeting:
    // her question was never sent. A second walk minutes later went straight through, and the only
    // difference between them was FOUR SECONDS against TWENTY-NINE.
    //
    // **BECAUSE `featureEnt` IS A CACHE, AND ON `/talk` ITS FIRST VALUE IS THE SIGNED-OUT ANSWER.**
    // A stranger arrives signed out, so the read at load says `lala:chat: false` — quite correctly,
    // she is nobody yet. She then submits her details, a seat is minted, the chart casts and her
    // allotment is granted. **Her meter draws off the cast's own fresh payload while this door reads
    // the cache**: two sources for one question (R28/R29), and the window between them is the width
    // of one network read. Inside it the meter says five and the door says no.
    //
    // **AN UNLOCKED CACHE STILL OPENS WITH NO EXTRA READ** — the repair may not cost every member a
    // round trip — and a LOCKED one falls through to the fresh read below rather than walling her.
    // If that answer also refuses she sees precisely the same box, so **this cannot open a door that
    // is genuinely shut**; the only member it moves is the one holding turns she was told she had.
    // It falls THROUGH rather than calling a second re-ask, so the read below stays the one and only
    // spelling of it (R28/R29) — and so the mutation batteries anchored on it keep measuring.
    if (featureEnt && !featureLockAfterAnswer(featureEnt, feature)) { action(); return; }
    // R91: one transport for all three sites. The RULING here is unchanged — a network that cannot
    // answer is no free pass to a paid room — and is reached only once the re-ask has been spent.
    void readEntitlements().then((answer) => {
      if (answer.kind !== "payload") {
        // ENTITLEMENT-UNREADABLE-Q1: the house could not be reached at all, which is the same fact as an unread membership.
        sendToMembership({ feature, unread: true }, action, reached);
        return;
      }
      membershipUnreadRef.current = (answer.payload as { membershipUnread?: unknown })?.membershipUnread === true;
      const ent = featureEntitlements(answer.payload);
      setFeatureEnt(ent);
      decide(ent);
    });
  }

  // T1: open the purple box FROM A SERVER REFUSAL. Every 402 carries the feature, the tier that
  // opens it, the whole ladder and the free layer, so the box tells her the truth about the door
  // she actually touched instead of a generic "members only".
  function openLockedDoor(lock: LockInfo, retry?: () => void) {
    sendToMembership(lock, retry, askedByHer());
  }

  // T8-3: the interceptor at the top of this file fires this for EVERY 402 the app receives, from
  // any surface, whether or not that surface remembered to handle one. A caller that DOES handle it
  // (the report generator, which also passes a retry) opens the same box with the same payload, so
  // the two paths agree rather than fighting; there is no retry to resume here, because the hook
  // sees the response and not the action behind it.
  useEffect(() => {
    const onLocked = (event: Event) => {
      const lock = (event as CustomEvent).detail as (LockInfo & { answered?: boolean }) | null;
      if (!lock) return;
      // >>> **IT #174 — AN ANSWER IS NOT A WALL.** <<< A 402 reached her with nothing to read, so
      // navigating is the whole remedy. A 200 that carries a lock means Lala ALREADY REPLIED, in her
      // own voice, and that reply is sitting in the thread this line used to walk her out of. The box
      // names the door either way; only the wall moves her.
      // No retry to resume here: the hook sees the RESPONSE and not the action behind it.
      sendToMembership(lock, undefined, !lock.answered); // THE EVENT IS THE REACH (asked at SEND time)
    };
    window.addEventListener(LOCKED_DOOR_EVENT, onLocked);
    return () => window.removeEventListener(LOCKED_DOOR_EVENT, onLocked);
  }, []);

  async function logOut() {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      setMember(null);
      setPendingPage(null);
      // CHECKOUT-SIGNIN-Q1: a tier pressed before sign-in never carries the next person on this tab to it.
      clearPendingCheckout(safeSessionStorage());
      // >>> BREACH-1 — SIGNING OUT CLEARED THE SESSION AND LEFT HER ROOM ON THE SCREEN. <<< It
      // cleared `member` and `pendingPage` and nothing else, so on a shared device the next person
      // to sign up in the same tab inherited the last one's name, birth date and life path.
      clearMemberState();
      goTo("home");
    }
  }

  function updateBirthChart<K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) {
    setBirthChart((current) => ({ ...current, [field]: value }));
  }

  // HER CONTINUITY LAW — **THE BRIDGE LANDS PRE-FILLED, AND THE SERVER DECIDES WHOSE DETAILS THOSE
  // ARE.** The funnel's bridge carries `?lead=<signed token>`; the token names a ROW and nothing
  // else, so this asks the server what that row holds rather than reading anything out of the
  // address. A funnel that takes a stranger's details and then asks for them again is a worse
  // promise than one that never opened, which is why the capture did not ship until this existed.
  //
  // It runs ONCE on mount and never on `page`: a member who navigates back to the home page an hour
  // later must not have a stale lead re-poured over a form she has been typing into.
  // >>> **L10 — AND THE BRIDGE LANDED ON NOTHING, MEASURED AT COORDINATES BEFORE A LINE WAS
  // WRITTEN.** A stranger tapping *Finish this reading* arrived on `/?join=1&lead=<token>` signed
  // out and met a home page **byte-identical to `/`**: no signup box, no sign-in door, no form of any
  // kind. Three separate things were missing and each one alone is enough to break her clause.
  //
  //   1. **`join=1` WAS READ BY NOBODY.** It was composed in two places and consumed in none.
  //   2. **THE SIGNUP SURFACE IS BEHIND A SESSION.** `showOnboarding`'s first clause is `isLoggedIn`,
  //      so the box a stranger is being sent to could not open for a stranger.
  //   3. **AND THE PRE-FILL DIED AT THE ONE DOOR SHE WAS TOLD TO USE.** Google is a full page
  //      navigation back to `/?auth=google-success`; React remounts, the form is empty again and the
  //      token has already been spent. Her details were lost BY doing the thing the bridge asked.
  //
  // It runs ONCE on mount and never on `page`: a member who navigates back to the home page an hour
  // later must not have a stale lead re-poured over a form she has been typing into.
  useEffect(() => {
    const search = window.location.search;
    const token = new URLSearchParams(search).get(FUNNEL_LEAD_PARAM);
    const asked = joinRequested(search);
    const store = safeSessionStorage();

    // **THE SWEEP HAPPENS FIRST AND DOES NOT WAIT ON THE FETCH.** It used to sit in the request's
    // `.finally()`, so a mailed bridge — `?join=1` with no token, which is what
    // `server/funnel-email.js` composes — was never cleaned at all and a refresh re-opened the door
    // she had just closed. The token leaves the address before anything else is attempted.
    const clean = addressWithoutFunnelHandoff(search, window.location.pathname);
    if (clean) window.history.replaceState({}, "", clean);

    if (asked) {
      // **SHE JUST ASKED FOR THIS SCREEN, SO A DISMISSAL SHE MADE EARLIER NO LONGER SPEAKS FOR HER.**
      // L2.6's dismissal means *not now*; tapping her bridge is *now*. Left standing, a member who
      // waved the box away while browsing would tap Finish this reading and get nothing back.
      setOnboardDismissed(false);
      writeDismissed(store, false);
      // **THE WAIT IS L5.1's OWN LAW AND NOT A SECOND ONE** (R28/R29). `pendingPage` is already the
      // thing that holds a tap until the session has answered, and then either navigates (signed in)
      // or opens the sign-in door (signed out) — which is exactly this decision. Its signed-in branch
      // is a genuine no-op here: `enter` only pushes history when the path really changes, and the
      // bridge lands on the home path already. Opening the door on mount instead would raise a
      // sign-in box over a member who was signed in the whole time (L5.1's own reported defect).
      setPendingPage("home");
    }

    if (!token) {
      // **THE RETURN LEG FROM GOOGLE.** No token, because it was spent on the way in — so the details
      // come back off the stash, which is the only thing that crosses a full page navigation.
      const stashed = readStashedPrefill(store);
      if (stashed) {
        setBirthChart((current) => ({ ...current, ...prefillPatch(stashed) }));
        setLeadEmail(String(stashed.email || ""));
        setPrefilled(true);
      }
      return;
    }

    let active = true;
    void fetch(`/api/funnel/prefill?lead=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        // R39/R47: READ THE PAYLOAD. This door answers 200 with `ok: true, prefill: null` for a token
        // whose row has gone, and a client reading the status code would pour `{}` over the form.
        if (!active || !data?.ok || !prefillHasSomething(data.prefill)) return;
        setBirthChart((current) => ({ ...current, ...prefillPatch(data.prefill) }));
        setLeadEmail(String(data.prefill.email || ""));
        setPrefilled(true);
        // Kept for the round trip to Google, and for nothing else.
        writeStashedPrefill(store, data.prefill);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // L14 — **HER CLAUSE: THE TAP OPENS CARD UPDATE.** The billing screen is Stripe's and the only way
  // to it is a signed-in POST (`/api/membership/portal`, which reads the customer off her own row and
  // never takes one from the client), while a notification carries a URL. So the reminder lands on
  // `/membership?billing=1` and this is what turns the flag into the request.
  //
  // **IT WAITS ON THE SESSION RATHER THAN FIRING ON MOUNT**, which is L5.1's own reported defect one
  // door over: `isLoggedIn` is false until `restoreSession` answers, so a mount-run would ask the
  // portal for a member the app has not read yet and land her on the refusal instead of her card.
  //
  // **AND THE FLAG IS SWEPT**, `addressWithoutFunnelHandoff`'s discipline one address over: left in
  // place, a refresh or a back button re-opens Stripe's billing screen under a member who has already
  // finished with it. `asked` is captured BEFORE the sweep, because the sweep is what makes it false.
  const billingOpened = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || billingOpened.current) return;
    const asked = billingRequested(window.location.search);
    if (!asked) return;
    billingOpened.current = true;
    const clean = addressWithoutBillingFlag(window.location.search, window.location.pathname);
    if (clean) window.history.replaceState({}, "", clean);
    void openBilling();
  }, [isLoggedIn]);

  async function createMemberRoom(event: FormEvent) {
    event.preventDefault();

    // Creating a room is free once logged in; only require a login (not membership).
    // The preparing/ready flow drives navigation via "Enter Your Room".
    if (!isLoggedIn) {
      // TRIGGER 5 of 5 — creating a room. Creating one is FREE once signed in, so this door was never
      // a paywall and does not become one: it asks for the session the room will be attached to.
      // TRIGGER 5 of 7 -- creating a room. Creating one is FREE once signed in, so this door was
      // never a paywall and does not become one: it asks for the session the room attaches to.
      openSignup("join", "my-room");
      return;
    }

    // Combine the name parts (including middle) into the full name numerology uses.
    const fullName = combineName(birthChart.firstName, birthChart.middleName, birthChart.lastName) || birthChart.name;
    const profile = {
      ...birthChart,
      name: fullName,
      createdAt: new Date().toISOString(),
    };
    setRoomProfile(profile);
    setSubmittedChart(profile);
    // SU1: match what the server actually generates and saves (birth chart, numerology, human
    // design). The old "compatibility" here (and "personality" on the server) named reports that
    // are never produced, so the checklist advertised a phantom report and the poll never finished.
    // N18-1 (owner ruling, 2026-07-28): the SIGNUP SET is birth chart + numerology, generated in
    // parallel and COMPLETE before she can enter her room. Human design is not in the set: it waits
    // in My Room as a free, ready-to-generate card written on her first tap. So the door stays shut
    // until those two land, and the checklist names exactly the two that are being written.
    const expected = ["birth-chart", "numerology"];
    setRoomBuildReports(expected.map((type) => ({ type, ok: false })));
    setRoomBuildStatus("preparing");

    try {
      const response = await fetch("/api/room/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // T2-2: the agreement travels WITH the request that acts on it, naming the version she was
        // shown, and the server records that exact version on her row.
        // C8b: her number travels as she TYPED it and the server stores the normalised form — one
        // law, `phoneSubmission`, validates it on both sides, so the field and the door can never be
        // two answers about what is dialable (R15/R28/R29).
        // HER CORRECTION (2026-08-19): the card she clicked to arrive, so her room draws THAT door
        // and nothing else. The SERVER decides whether the value names a reading this house really
        // delivers (`resolveOrigin` inside the store), so what travels here is a claim rather than a
        // fact — which is what makes it safe to take off a request body at all. The FUNNEL half is
        // recorded server-side at account birth and never rides this request.
        body: JSON.stringify({
          name: fullName, chart: profile, termsAccepted: termsAgreed, termsVersion: TERMS_VERSION,
          phone: signupPhone, smsConsent: signupSmsConsent,
          originCard: readStashedOrigin(safeSessionStorage()),
          // LANDING v3: the door she arrived through -- a CLAIM, like the card above it. The ROSTER
          // in the store decides whether it names one of hers (R41).
          landingTag: readLandingArrival(safeSessionStorage()).tag,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!data.ok) {
        // A consent refusal is not a build failure: put her back on the form with the reason, rather
        // than showing a "ready" room that was never made.
        // C8b — a number this house cannot dial puts her back on the form with the reason beside
        // the field, exactly as a refused consent does. Refusing at the door and saying nothing is
        // the shape that loses a signup with no explanation on any screen.
        if (response.status === 400 && data.code === "phone-shape") {
          setRoomProfile(null);
          setRoomBuildStatus("idle");
          setSignupPhoneNote(data.message || "That phone number does not look right. Leave it blank if you would rather not.");
          return;
        }
        if (response.status === 403 && data.code === "terms-consent-required") {
          setRoomProfile(null);
          setRoomBuildStatus("idle");
          setTermsAgreed(false);
          setConsentNote(data.message || "Please agree to the Terms of Service and Privacy Policy to create your room.");
          return;
        }
        setRoomBuildStatus("ready");
        return;
      }
      setConsentNote("");
      // L10 — **THE STASH HAS DONE ITS ONE JOB AND IS STRUCK HERE.** It exists to carry her details
      // across the round trip to Google and no further; a room now exists, so anything still holding
      // her birth date is a copy nobody needs. It is cleared on the SUCCESS branch only, because a
      // refused consent or a failed build puts her back on the form that stash is the backing for.
      clearStashedPrefill(safeSessionStorage());
      // HER CORRECTION (2026-08-19): the arrival is spent — the room exists and the origin is on her
      // row. Struck on the SUCCESS branch only, for the same reason the prefill above is: a refused
      // consent or a failed build puts her back on the form, and the card she clicked is part of
      // what that retry is carrying. Write-once on the column means a stale one could never overwrite
      // anything anyway; this is hygiene rather than the guard.
      clearStashedOrigin(safeSessionStorage());
      // >>> **AND THE LANDING ARRIVAL IS NOT STRUCK HERE ANY MORE (2026-09-02), BECAUSE THE ASK LALA
      // PAGE STILL NEEDS IT ONE NAVIGATION LATER.** <<<
      //
      // Walked on production: the window arrived, Lala greeted her BY NAME — and the page was NOT
      // dressed (`.ask-lala-main` with no `is-talk`), no meter, and the membership ladder after one
      // turn. `free.payload` was null, because the page's arrival read is guarded and its funnel
      // exception asks THIS STASH whether she is a funnel arrival. It was struck HERE, on the
      // room-create success branch, milliseconds before that page mounted — so the exception read an
      // empty stash and the guard closed on the one cohort it was written to let through.
      //
      // **THE NOTE ABOVE ALREADY SAYS THIS CLEAR IS HYGIENE RATHER THAN THE GUARD** — write-once on
      // the column means a stale arrival could never overwrite anything — and `sessionStorage` dies
      // with the tab, so keeping it costs nothing and cannot reach another session. The stash is what
      // tells her own conversation she came through the funnel, so it lives as long as the visit does.
      // `clearLandingArrival` is kept and still exported for the doors that genuinely spend it.
      // The server names the set it is actually writing, so the checklist and the door follow the
      // server rather than a guess made here. A deploy that defers everything (`deferred: true`)
      // still resolves: nothing to wait for, room ready now.
      if (data.deferred === false || data.generating === true) {
        await pollRoomReports(Array.isArray(data.expected) ? data.expected : expected);
        return;
      }
      setRoomBuildReports((Array.isArray(data.expected) ? data.expected : expected).map((type: string) => ({ type, ok: false })));
      setRoomBuildStatus("ready");
    } catch {
      setRoomBuildStatus("ready");
    }
  }

  // The reports generate server-side in the background; poll until they appear so
  // the "Preparing your room…" overlay reflects real progress and never hangs.
  async function pollRoomReports(expected: string[]) {
    const startedAt = Date.now();
    // Stall safety cap: all three normally finish within ~90s. If something truly
    // hangs, this cap is the only path that reveals the room before everything is
    // ready, so a stalled report can never trap the member at the door forever.
    const maxMs = 4 * 60 * 1000;
    while (Date.now() - startedAt < maxMs) {
      // R53 — **A FAILED READ IS NOT AN EMPTY SHELF, AND THIS IS THE SCREEN WHERE THAT COST MOST.**
      // This used to fall back to `{ reports: [] }`, so one lost request PAINTED every slot unwritten
      // on the one screen a brand-new member is watching. `null` means nothing was learned: nothing
      // is painted, and the poll comes round again with the cap below as the safety valve.
      const list = await listSavedReports<{ type: string }>();
      if (list) {
        const have = new Set(list.map((r) => r.type));
        setRoomBuildReports(expected.map((type) => ({ type, ok: have.has(type) })));
        // The single ready moment: only when the whole signup set is written. No early entry.
        if (expected.every((type) => have.has(type))) {
          setRoomBuildStatus("ready");
          return;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 4000));
    }
    // Cap reached with something still generating (a stall) — reveal anyway; the
    // room shows any missing report as "still being prepared".
    setRoomBuildStatus("ready");
  }

  function enterRoom() {
    setRoomBuildStatus("idle");
    // F4-3 — **NEVER THE ROOM TOUR FOR A TALK ARRIVAL**, and everybody else lands exactly where she
    // always has. The stash is what survives the Google round trip, which is a FULL PAGE NAVIGATION
    // and takes every value in memory with it; the whole of the reasoning is in src/talkDoor.js.
    goTo(landingAfterRoom(readTalkLanding(safeSessionStorage())));
  }

  const [talkCapturing, setTalkCapturing] = useState(false);
  // ITEM 2 - her ONE action, decided by the law (`talkStartPlan`): her ITEM 2 names no signup step,
  // so a signed-in member goes straight to the conversation and a stranger stays on the capture,
  // which takes her details once and mints the account itself. The stranger arm is UNREACHABLE from
  // here (`TalkPage` draws the capture for her), so the openSignup branch this replaced was dead.
  function startTalk() {
    const plan = talkStartPlan({ isLoggedIn });
    if (plan.stash) writeTalkLanding(safeSessionStorage());
    goTo(plan.go as Page);
  }

  // X6: click a recent entry to load it INTO the pad, as written, for editing; Save then updates it.
  function selectJournalEntry(entry: JournalEntry) {
    setJournalEditingId(String(entry.id));
    setJournalTitle(entry.title === "Untitled reflection" ? "" : entry.title || "");
    setJournalDraft(entry.body || "");
  }

  // X6: clear the pad and drop back to writing a brand-new entry (Save will create, not update).
  function newJournalEntry() {
    setJournalEditingId(null);
    setJournalTitle("");
    setJournalDraft("");
  }

  async function saveJournalEntry(event: FormEvent) {
    event.preventDefault();

    const body = journalDraft.trim();
    if (!body) {
      return;
    }
    const title = journalTitle.trim() || "Untitled reflection";
    const editingId = journalEditingId;

    // R57: the transport decides PATCH vs POST from the ONE id law (src/lib/journal.ts). This call
    // site used to decide it with `!/^\d+$/.test(editingId)`, and a saved entry's id is a Postgres
    // serial, so every real entry read as session-only and Update entry POSTed a duplicate.
    // No member at all is a sign-in answer without a request: nothing about the words is decided.
    const answer: JournalAnswer<JournalEntry> = member
      ? await writeJournalEntry<JournalEntry>({ title, body }, editingId)
      : { ok: false, kind: "sign-in" };
    setJournalNoteText(journalNote(answer, {
      signIn: t(language, "journal.signIn"),
      failed: t(language, "journal.saveFailed"),
    }));

    // THE SESSION-ONLY FALLBACK IS KEPT — her words are never thrown away over a refusal — but it is
    // no longer drawn as a saved row. `unsaved` is what the list reads to say so.
    setJournalEntries((current) => {
      if (editingId != null) {
        return current.map((e) => (String(e.id) === editingId
          ? (answer.ok ? answer.entry : { ...e, title, body, unsaved: true })
          : e));
      }
      const fresh: JournalEntry = answer.ok
        ? answer.entry
        : { id: sessionOnlyId(), title, body, createdAt: new Date().toISOString(), unsaved: true };
      return [fresh, ...current];
    });
    newJournalEntry();
  }

  // HP1: the home search bar. The gate has already been passed by the time this runs (the search
  // form submits through requireMembership), so this just carries the question to Ask Lala.
  function askLalaFromHome(question: string) {
    setPendingAsk(question);
    goTo("ask-lala");
  }

  // N4-3: the ONE listener for the reading handoff. Every reading surface dispatches; this is the only
  // thing that hears it, and it reuses the SAME pendingAsk pipe the home search bar has always used, so
  // the reading reaches her IN MEMORY and never touches the URL. Membership is gated here rather than in
  // twenty-odd buttons, so a signed-out tap opens the gate and resumes into the handoff afterwards.
  useEffect(() => {
    const onReading = (event: Event) => {
      const d = (event as CustomEvent).detail || {};
      const text = readingToAskText(d.report, d.kind, d.subject);
      if (!text) return;
      // N17-2: the reading rides with its own label, so the server can tell a delivered reading from
      // the member's own words. N16-1: the same label says which KIND arrived.
      const type = String((d.report as { type?: string })?.type || "");
      requireMembership(() => {
        // T5-2: if this reading was born with suggested questions, the tap raises the intercept box
        // first. A reading that predates the feature has none, so the tap behaves exactly as it
        // always did and goes straight through.
        const suggestions = suggestedQuestionsOf(d.report as { suggestedQuestions?: unknown });
        if (suggestions.length) {
          setHandoffIntercept({ report: d.report, kind: String(d.kind || ""), subject: String(d.subject || ""), type, questions: suggestions });
          return;
        }
        setPendingHandoff({ type, kind: handoffKindFor(type), subject: String(d.subject || "") });
        setPendingAsk(text);
        goTo("ask-lala");
      });
    };
    window.addEventListener(ASK_LALA_READING_EVENT, onReading);
    return () => window.removeEventListener(ASK_LALA_READING_EVENT, onReading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember]);

  // JD: delete one journal entry. A session-only entry (written signed out) has a numeric id and
  // never reached the server, so it is dropped locally; a saved entry is deleted server-side and
  // only leaves the list once the server confirms, so the list never lies about what still exists.
  async function deleteJournalEntry(id: string): Promise<boolean> {
    // R57: the same id law the save asks, declared once rather than spelled a second way here.
    if (!isSessionOnlyId(id) && !(await removeJournalEntry(id))) return false;
    setJournalEntries((current) => current.filter((e) => String(e.id) !== String(id)));
    // X6: if the pad was editing the entry just deleted, drop back to a fresh new entry.
    if (journalEditingId === String(id)) newJournalEntry();
    return true;
  }

  async function calculateChart(event: FormEvent) {
    event.preventDefault();

    if (!requestMemberAccess("astrology")) {
      return;
    }

    setSubmittedChart(birthChart);
    setChartError("");
    setPlanetPositions([]);
    goTo("astrology");

    const [year, month, day] = birthChart.birthDate.split("-").map(Number);
    const convertedTime = birthChart.birthTimeUnknown ? { hour: 12, min: 0 } : convertToTwentyFourHour(birthChart.birthTime, birthChart.birthPeriod);
    const lat = Number(birthChart.latitude);
    const lon = Number(birthChart.longitude);
    const tzone = Number(birthChart.timezone);

    if (!year || !month || !day || !birthChart.birthplace || !birthChart.birthCountry) {
      setChartStatus("error");
      setChartError("Enter birth date, birthplace, and country to start the compatibility reading.");
      return;
    }

    if (!birthChart.birthTimeUnknown && !convertedTime) {
      setChartStatus("error");
      setChartError("Enter birth time as hour and minute, then choose AM or PM. If you do not know it, select Unknown.");
      return;
    }

    if (birthChart.birthTimeUnknown) {
      setChartStatus("success");
      return;
    }

    setChartStatus("loading");

    if (Number.isNaN(lat) || Number.isNaN(lon) || Number.isNaN(tzone)) {
      setChartStatus("error");
      setChartError("Choose a birthplace from the suggestions so the timezone can calculate automatically.");
      return;
    }

    try {
      const response = await fetch("/api/astrology/planets-tropical", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          day,
          month,
          year,
          hour: convertedTime.hour,
          min: convertedTime.min,
          lat,
          lon,
          tzone,
          house_type: "placidus",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "The chart engine could not return a reading yet.");
      }

      setPlanetPositions(Array.isArray(data) ? data : []);
      setChartStatus("success");
    } catch (error) {
      setChartStatus("error");
      setChartError(error instanceof Error ? error.message : "The chart engine could not return a reading yet.");
    }
  }

  // >>> **L1 (her live-test order, 2026-08-13) — THE STANDALONE PAGE WEARS NO HOUSE CHROME.** <<<
  //
  // Her report, off her daughter's real visit: on DESKTOP the funnel rendered INSIDE the site with
  // the full side menu. Her ruling: `/funnel` is its own landing page at EVERY viewport — no side
  // menu, no top bar, no bottom nav, no site navigation at all; the page is the hook, the form, the
  // reading, the bridge, nothing else.
  //
  // **IT IS ASKED ONCE HERE, IN THE SHELL, RATHER THAN INSIDE TWELVE CONTROLS** — twelve controls
  // each remembering is eleven chances for the next one to forget, and the one that forgets is the
  // one somebody adds next (MG1/A3's ruling: climb the rung at the choke point).
  //
  // **AND THE CHROME IS NOT RENDERED, RATHER THAN HIDDEN BY A RULE**, which is `siteFooterHidden`'s
  // own reasoning one component over: a hidden bar still occupies the document, still takes the tab
  // order and still answers a hit test — and a stranger who tabs into a side menu that is painted out
  // is a stranger this page just lost.
  // =================================================================================================
  // >>> **HER FUNNEL CONTAINMENT LAW (2026-08-27), ENFORCED STRUCTURALLY AT THE ONE SWITCH THAT
  //     ALREADY DECIDES WHETHER THE HOUSE IS DRAWN.** <<<
  //
  // Her incognito walk: a `/talk` arrival landed in the REAL Ask Lala room — full navigation around
  // it, and a "your birth chart is ready" toast popping mid-conversation that routed her to the
  // membership page. Her law, verbatim in intent: *a /talk arrival lives in the FUNNEL EXPERIENCE
  // ONLY ... NO navigation into the house ... the conversation is the whole world ... LALA is the
  // only door out.*
  //
  // **THE CONVERSATION IS NOT FORKED TO ACHIEVE THAT, AND THAT IS DELIBERATE.** CLAUDE.md's standing
  // law is that Lala is ONE MIND BEHIND TWO DOORS — the drawer and this page run the single
  // `handleLalaChat` with the same prompt, retrieval and gate stack — and *"Never fork it."* A second
  // funnel-only chat surface would be exactly that fork. So the CHAT stays where it is and the SHELL
  // stands down around it, which is the same thing her standalone pages already do.
  //
  // **IT REUSES `standalone` RATHER THAN ADDING A SECOND SWITCH (R28/R29).** That flag already kills
  // the site menu AND `houseRails` — the drawer rail and **the generation follower**, which is the
  // chart-ready toast she watched interrupt her conversation. One flag, one answer to *is the house
  // drawn*, so a surface added next year cannot be drawn for a funnel arrival by forgetting.
  //
  // **THE COHORT TEST IS THE ALLOTMENT ITSELF, NEVER THE PAGE OR A ROUTE.** `freeTurnView` is the
  // same law the counter and the wall are drawn from, so the chrome cannot disagree with the meter
  // about whether she is mid-trial. It holds through the WALL as well as the counter, because her
  // clause is that Lala's wall is the door out — a member who has spent five turns and has not yet
  // tapped through is still inside the funnel, and giving her the menu back at that exact moment is
  // handing her every exit except the one being offered.
  //
  // **AND IT IS BOUNDED BY THE FREE TIER, WHICH IS WHAT STOPS IT REACHING A MEMBER.** A paying
  // member can still hold a spent allotment on her row forever; asking the tier as well means the
  // house is never taken away from somebody who has bought it (R19: the direction that costs a
  // member her navigation is the expensive one).
  const funnelTrial = serverTier === "free" && freeTurnView(featureEnt?.freeTurns) !== null;
  const standalone = pageIsStandalone(page) || (funnelTrial && page === "ask-lala");
  // The right-hand drawer rail and the generation follower are house navigation too — they are the
  // Shelves, Ask Lala, the Journal, the craft drawers. They have always been signed-in-only, so a
  // stranger off an ad never met them; an OWNER opening her own funnel link did, which is exactly the
  // visit her report came from. `isLoggedIn` is kept as the other half rather than replaced, because
  // *signed out* and *standalone* are two different reasons for the same absence (R18).
  const houseRails = isLoggedIn && !standalone;

  // ITEM G3 — R2, HER WORDS: "When they download it, they have to have a sign in, to sign in." In the
  // store app nothing of the house shows until the session has answered and she is in; the website
  // build folds this away (APP_MODE is a literal false there).
  if (APP_MODE && !isLoggedIn) {
    return sessionKnown ? <AppSignIn onMemberChange={completeMembership} /> : <main className="app-signin app-signin--waiting" aria-busy="true" />;
  }

  return (
    <RequireFeature.Provider value={requireFeature}><FeatureAccess.Provider value={featureEnt}><main>
      {standalone ? null : (
      <SiteMenu
        currentPage={page}
        goHome={() => goTo("home")}
        goTo={(nextPage) => requestMemberAccess(nextPage)}
        isOwner={isOwner}
        language={language}
      />
      )}

      {standalone ? null : (
      <nav className="nav" aria-label={t(language, "nav.primary")}>
        <button className="wordmark" type="button" onClick={() => goTo("home")}>
          12<span>th</span> House
        </button>
        <div className="nav__links">
          {/* ITEM 3 — SITE INSTRUCTIONS LEFT THIS BAR (her order). On a phone the top right was
              carrying four things and she wanted three: Notifications, Inbox, Members. The guide is
              one entry inside the Members dropdown now, opened through the same state as before. */}
          {/* LR1-12 ROUND FOUR + THE HEADER-PAIR AMENDMENT — THE BELL AND THE MAIL LIVE HERE, between
              Site Instructions and Member, on every page and both viewports. This is THE bell and THE
              inbox; the Living Room's local placement is retired and the room keeps only its own
              content. Both are Apprentice surfaces, so a below-tier tap raises the warm box rather
              than opening an empty panel. */}
          {isLoggedIn ? <HouseInbox isMember={isMember} onRequireMembership={requireMembership} language={language} /> : null}
          {/* AU1-2: signed in, this is the ACCOUNT MENU, and it is where Log out lives. It used to
              navigate straight to My Room, which left the house with no way out of a session on
              either viewport: the only Log out was inside the membership modal, and a signed-in
              member could no longer open that. Signed out it is unchanged: a clear Log in. */}
          {/* W1-4: the Member pill and the Upgrade button are ONE non-wrapping group. Her spec is "to
              the RIGHT of the Member button", and at 390px the nav wraps: measured, a third pill in a
              flat row dropped Upgrade onto its own line DIRECTLY BENEATH Member, which reads as under
              it, not beside it. Grouped, the pair stays shoulder to shoulder on every width and Site
              Instructions is what wraps instead. Desktop is unchanged: the group's gap is the row's. */}
          <div className="nav__member">
          {isLoggedIn ? (
            <div className="account-menu" ref={accountMenuRef}>
              <button
                type="button"
                className="login-pill"
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
                onClick={() => setAccountMenuOpen((v) => !v)}
              >
                <ShieldCheck size={16} />
                {isMember ? t(language, "account.pill.member") : t(language, "account.pill.account")}
              </button>
              {/* ITEM 14(c) / roadmap 7 — the whole panel is in src/components/AccountMenuPanel.tsx,
                  where a gate CALLS it once per language instead of slicing this file between two
                  class names to find the owner's door. The comments that explain each entry
                  travelled with the code. */}
              {/* MR-MOB: PORTALLED to <body> on the `--z-header-panel` token — a room cannot
                  outrank something that is not in its tree. The measurement is in the component. */}
              {accountMenuOpen ? (
                <AccountMenuPortal anchorEl={accountMenuRef.current}>
                  <AccountMenuPanel
                    email={member?.email || ""} isMember={isMember} isOwner={isOwner} serverTier={serverTier}
                    subStatus={subStatus} billingBusy={billingBusy} billingNote={billingNote} subNote={subNote} language={language}
                    onGoTo={(page) => { setAccountMenuOpen(false); goTo(page); }}
                    onOpenBilling={() => { void openBilling(); }}
                    onLogOut={() => { setAccountMenuOpen(false); void logOut(); }}
                    onChooseLanguage={chooseLanguage}
                    onOpenInstructions={() => { setAccountMenuOpen(false); setInstructionsOpen(true); }}
                    onOpenMemory={() => { setAccountMenuOpen(false); setMemoryOpen(true); }}
                  />
                </AccountMenuPortal>
              ) : null}
            </div>
          ) : (
            <button type="button" className="login-pill login-pill--in" onClick={() => requestMemberAccess()}>
              <LogIn size={16} />
              {t(language, "account.pill.logIn")}
            </button>
          )}

          {/* W1-4: the button every refusal on this site now points at. The whole of it — including the
              condition that decides who sees it, which is the part that matters — is in
              src/components/NavUpgrade.tsx, where a gate can CALL it once per tier instead of reading
              a regex over this file. */}
          <NavUpgrade isLoggedIn={isLoggedIn} tier={serverTier} onUpgrade={() => goTo("membership")} />
          {/* Q-DASH (1), her order: "a 'Dashboard' button next to her member button", owners only (src/components/NavDashboard.tsx). */}
          {isLoggedIn ? <NavDashboard isOwner={isOwner} onOpen={() => goTo("dashboard")} /> : null}
          </div>
        </div>
      </nav>
      )}

      {instructionsOpen ? <SiteInstructions onClose={() => setInstructionsOpen(false)} /> : null}

      {/* MEM-1 — what Lala remembers, and both halves of her clause 2's control. The panel is a leaf:
          it renders what it is handed and reports taps, so the page owns the reads and the writes. */}
      {memoryOpen ? <LalaMemoryPanel onClose={() => setMemoryOpen(false)} /> : null}

      {page === "home" ? (
        <HomePage
          showcase={(
            <HomeShowcase
              memberChart={submittedChart}
              isMember={isMember}
              onRequireMembership={requireMembership}
              onAsk={askLalaFromHome}
              onOpenRoom={(page) => requestMemberAccess(page as Page)}
              language={language}
            />
          )}
          goTo={requestMemberAccess} signedIn={!showsStrangerHome({ sessionKnown, isLoggedIn })} onMenu={openSiteMenu} onLogin={() => openSignup("login")}
          // LANDING v3: a TAG where HOME-1's map handed a reading key, refused by its own roster at
          // the choke point. `onTalk` is her 2026-08-23 Ask Lala door -- `startTalk`'s own decision.
          onSignup={(tag?: string) => openSignup("join", undefined, "", tag)}
          onFunnel={goToFunnel} onTalk={startTalk}
        />
      ) : page === "my-room" ? (
        <MyRoomPage
          member={member}
          profile={roomProfile}
          roomUnreachable={roomReadFailed}
          onRetryRoom={() => setRoomReadTry((n) => n + 1)}
          journalEntries={journalEntries}
          journalNoteText={journalNoteText}
          onDeleteEntry={deleteJournalEntry}
          books={books}
          activeBook={activeBook}
          isMember={isMember}
          // TRAP-1a: the ribbon needs to know whether this app has LOOKED yet, not merely whether
          // she is a member -- L5.1's own distinction, on a strip that would otherwise flash at a
          // paying member on every load of her own room. Computed by the ONE law that answers it.
          membershipKnown={membershipKnown({ sessionKnown, tierKnown, isLoggedIn })}
          // HER CORRECTION (2026-08-19): which reading has a claim on this member. Off the SAME
          // payload `membershipKnown` is derived from, so the room's two facts can never be out of
          // step -- a door drawn for a beat, or held back after the answer landed (R28/R29).
          roomOrigin={subStatus?.originReading || ""}
          isLoggedIn={isLoggedIn}
          onRequireMembership={requireMembership}
          onLockedDoor={openLockedDoor}
          // ITEM 6: the theme is owned at the root (it paints the whole document and every page has
          // to be right, not just this one); My Room holds the CONTROL, which is where her order
          // puts it.
          theme={theme}
          onChooseTheme={chooseTheme}
          // 14-B4: her language, from the same root that owns the theme and for the same reason.
          language={language}
          // OV2-2: the saved details go back into the room profile AND the birth form, so the
          // identity strip and every room that reads the chart show the new details immediately.
          onProfileSaved={(saved) => {
            setRoomProfile((current) => ({
              ...initialBirthChart,
              ...(current || {}),
              ...saved,
              createdAt: saved.createdAt || current?.createdAt || new Date().toISOString(),
            }));
            setBirthChart((current) => ({ ...current, ...saved, intention: current.intention }));
          }}
          goHome={() => goTo("home")}
          goTo={requestMemberAccess}
        />
      ) : page === "funnel" ? (
        // F1/F2 — the query string is read ONCE and handed to both, so the page and the form can never
        // disagree about which door this visitor came through (R28/R29).
        // F1 — the one router arm here that reads a query string (see src/components/FunnelPage.tsx).
        // F2 — **THE CAPTURE IS COMPOSED IN AS A NODE**, `HomeShowcase`'s pattern: the form holds
        // state, the page stays hookless, and a gate keeps its reach into all three doors.
        <FunnelPage funnel={funnelParam()} capture={<FunnelCapture funnel={funnelParam()} />} />
      ) : page === "talk" ? (
        // F4-1 — her ad door. Hookless, so a gate CALLS it; the reasoning is at its declaration in
        // src/components/TalkPage.tsx and the one decision behind the tap is in src/talkDoor.js.
        // >>> HER ITEM 2: A STRANGER MEETS THE CAPTURE, A MEMBER MEETS F4-1's DOOR. <<< The form
        // is composed in as a NODE (FunnelPage's own pattern) so TalkPage stays hookless and a gate
        // goes on CALLING it. `isLoggedIn` is L5.1's own flag and is FALSE for a beat before the
        // session answers -- which is the right direction here (R19): a member sees the capture for
        // one frame and then her door, where the other way round a stranger would be shown a button
        // that does nothing for her.
        <TalkPage
          onStart={startTalk}
          signedIn={!talkShowsCapture({ signedIn: isLoggedIn, capturing: talkCapturing })}
          capture={<TalkCapture onMember={setMember} onArrive={(chart) => { if (chart) setRoomProfile({ ...initialBirthChart, ...chart, timezone: "", createdAt: new Date().toISOString() }); setPendingAsk(readAskText(safeSessionStorage())); goTo("ask-lala"); }} onCapturing={setTalkCapturing} />}
        />
      ) : page === "claim" ? (
        // AUTH-BREACH-1 — the box her claim link opens. Standalone: she came from her inbox to do
        // ONE thing, and a side menu here is eleven ways to abandon it half-done.
        <ClaimPage onMemberChange={completeMembership} />
      ) : page === "signup" ? (
        // >>> HER CONVERSION SURFACE 2: THE SIGNUP PAGE HER ARTWORK IS. <<< It is the ONE door now --
        // every signup and login trigger in the house lands here through openSignup, and the modal
        // it replaces is gone. leadEmail is L10's continuity token surviving the move: her funnel
        // bridge lands on this page and her address is already in the box.
        <SignupPage
          intent={signupIntent}
          leadEmail={leadEmail}
          notice={signInNotice}
          onMemberChange={completeMembership}
          onOpenDoc={(doc) => setLegalDoc(doc)}
        />
      ) : page === "membership" ? (
        <MembershipPage
          goBack={() => runMembershipBack(window.history.state, Object.keys(PAGE_PATHS), { back: () => window.history.back(), go: goTo })}
          language={language} tier={serverTier} known={membershipKnown({ sessionKnown, tierKnown, isLoggedIn })} lock={gateLock}
          /* F: her real rate rides the entitlements payload this shell already holds. */
          rate={(featureEnt as { rate?: { tier: string; rate: number; credit: number; heldLabel?: string } | null } | null)?.rate ?? null}
          /* HER RULING 2026-09-18 (#271) — the cancel she could not find, handed the SAME portal door
             the account menu already opens. `paying` is the SERVER’s own subscription status rather
             than a guess from the tier: a member between a lapse and a renewal must never be offered
             a control that would refuse her (R18). */
          onManageMembership={() => { void openBilling(); }}
          manageBusy={billingBusy}
          paying={subStatus?.canManageBilling === true} onSignIn={() => openSignup("join", "membership")} />
      ) : page === "library" ? (
        <LibraryPage
          books={books}
          setActiveBook={(book) => {
            setActiveBook(book);
            setActivePage(0);
          }}
          isMember={isMember}
          onRequireMembership={requireMembership}
          goHome={() => goTo("home")} language={language}
        />
      ) : page === "astrology" ? (
        <AstrologyPage
          chart={submittedChart}
          chartStatus={chartStatus}
          chartError={chartError}
          planetPositions={planetPositions}
          isMember={isMember}
          onRequireMembership={requireMembership}
          onOpenPeople={() => requestMemberAccess("my-room")}
          goHome={() => goTo("home")}
          language={language}
        />
      ) : page === "numerology" ? (
        <NumerologyPage chart={submittedChart || birthChart} isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "tarot" ? (
        <TarotPage chart={submittedChart || birthChart} isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "human-design" ? (
        <HumanDesignPage chart={submittedChart || birthChart} isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "astrocartography" ? (
        <AstrocartographyPage chart={submittedChart || birthChart} isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "akashic" ? (
        <AkashicPage
          onRequireMembership={requireMembership}
          goHome={() => goTo("home")}
          backLabel={t(language, "common.backHome")}
          onAskLala={(rep) => askLalaAboutReading({ report: rep, kind: rep.type })}
          renderPicker={({ label, allowNew = true, onPick }) => (
            <SubjectPicker
              language={language}
              memberChart={submittedChart || birthChart}
              label={label}
              allowNew={allowNew}
              // R139: the house shape — `c` spread LAST over the blank, never cherry-picked. The room
              // itself reads only the META (which seat she chose), but the picker's contract is that a
              // mount hands on a WHOLE chart, and a third shape here is how the subject stamp falls off.
              onPick={(c, m) => onPick({ ...initialBirthChart, ...c } as unknown as Record<string, unknown>, m)}
            />
          )}
        />
      ) : page === "spell-book" ? (
        <SpellBookPage isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "seals" ? (
        <SealCreatorPage isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "ask-lala" ? (
        <AskLalaPage
          firstName={memberFirstName(member, roomProfile)}
          isMember={isMember}
          onRequireMembership={requireMembership}
          pendingQuestion={pendingAsk}
          pendingHandoff={pendingHandoff}
          onPendingConsumed={() => { setPendingAsk(""); setPendingHandoff(null); }}
          goHome={() => goTo("home")} language={language}
        />
      ) : page === "living-room" ? (
        <LivingRoomPage isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} isLoggedIn={isLoggedIn} sessionKnown={sessionKnown} onSignIn={() => openSignup("login", "living-room")}
          canGoLive={isOwner || serverTier === "vet"} isOwner={isOwner} /* LIVE-1: drawn only; the server gates the act */ />
      ) : page === "inbox" ? (
        <InboxScreen onRequireMembership={requireMembership} goBack={closeInbox} />
      ) : page === "dreams" ? (
        <DreamPage isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "palmistry" ? (
        <PalmistryPage isMember={isMember} onRequireMembership={requireMembership} goHome={() => goTo("home")} language={language} />
      ) : page === "terms" ? (
        <LegalPage title={LEGAL_DOC_TITLES.terms} source={LEGAL_DOC_SOURCES.terms} goHome={() => goTo("home")} />
      ) : page === "privacy" ? (
        <LegalPage title={LEGAL_DOC_TITLES.privacy} source={LEGAL_DOC_SOURCES.privacy} goHome={() => goTo("home")} />
      ) : page === "faq" ? (
        <FaqPage source={faqMarkdown} goHome={() => goTo("home")} />
      ) : page === "contact" ? (
        <ContactPage goHome={() => goTo("home")} />
      ) : page === "dashboard" ? (
        <OwnerDashboardPage goHome={() => goTo("home")} />
      ) : activeGenerator ? (
        <GeneratorPage generator={activeGenerator} goHome={() => goTo("home")} />
      ) : null}

      {signedInAs ? (
        <SignInConfirmation
          room={signedInAs.room}
          email={signedInAs.email}
          onClose={() => setSignedInAs(null)}
        />
      ) : null}

      {membershipUnreadNote ? (
        <p className="membership-unread-note" role="status" onClick={() => setMembershipUnreadNote(false)}>{t(language, "membership.unread")}</p>
      ) : null}
      {/* >>> THE LOCKED DOOR IS HER MEMBERSHIP PAGE. THERE IS NO POPUP IN THIS TREE. <<< T1-3/T1-6's
          box rendered here from whatever the server refused with; her 2026-08-18 ruling retires it
          from every member-facing surface, so the refusal travels to the page as `gateLock` and the
          component is gone from the bundle rather than merely unrendered. */}
      {/* New-member onboarding. L2.6: DISMISSIBLE by her ruling, and the whole condition — including
          why a dismissal lapses at a gated action — lives in src/lib/onboarding.ts so a gate RUNS it. */}
      {showOnboarding({
        isLoggedIn, profileChecked, hasRoom: Boolean(roomProfile), buildStatus: roomBuildStatus,
        // ITEM 0: `OnboardState`'s key is `membershipOpen` and it is NOT renamed here. What that
        // field means to L2.6 is *a surface is already claiming the screen, so do not open
        // onboarding over it* — which was true of the sign-in modal and is true of her signup PAGE,
        // the one door that replaced it. Renaming a field across a module boundary that three
        // fixtures read, in a round about the door itself, would be a second change wearing this
        // one's face (R25/R126).
        membershipOpen: page === "signup", dismissed: onboardDismissed, page,
        // The /talk capture owns the screen while it is in flight; see FUNNEL_PAGES.
        funnelCapturing: talkCapturing,
      }) ? (
        <ModalBackdrop label="Create your room" className="onboard-backdrop" onClose={dismissOnboarding}>
          <div className="onboard-modal">
            {/* Her X — the house's own `.modal-close`, one markup for BOTH viewports. */}
            <button type="button" className="modal-close onboard-modal__close" onClick={dismissOnboarding} aria-label={t(language, "common.close")}>
              &times;
            </button>
            <p className="eyebrow">{t(language, "signup.eyebrow")}</p>
            <h2>{t(language, "signup.heading")}</h2>
            {/* 14-B2 — THE SWITCHER GOES WHERE THE JOURNEY STARTS. ITEM 14(c) deliberately kept it
                off this screen because signup's copy was not in the catalog and a switcher over
                untranslated copy is a control that visibly does nothing. That reason expired the
                moment the rows above existed, and it belongs FIRST: a member who reads no English
                has to find this before she can read anything else on the screen. */}
            <LanguageChoice language={language} onChoose={chooseLanguage} />
            {/* N19-3 — THE HOW-THIS-WORKS MOMENT. Placeholder copy in her register, awaiting the
                owner's word. It replaced an intro that had gone out of date the moment N18-1
                landed: it promised "compatibility readings" at signup, which is not what signup
                makes. Say what she actually gets. */}
            <HowThisWorks language={language} />
            {/* HER CONTINUITY LAW, SAID OUT LOUD. She gave these details to a landing page minutes
                ago and needs to know why they are already here — continuity that is true and
                invisible reads as a form that filled itself in. */}
            {prefilled ? <p className="onboard-prefill">{PREFILL_NOTE}</p> : null}
            <CreateRoomForm birthChart={birthChart} updateBirthChart={updateBirthChart} onSubmit={createMemberRoom} submitLabel={t(language, "signup.submit")} requireMiddleName collectCurrentLocation language={language}
              consent={{
                agreed: termsAgreed,
                onChange: (agreed) => { setTermsAgreed(agreed); if (agreed) setConsentNote(""); },
                onOpenDoc: (doc) => setLegalDoc(doc),
                note: consentNote,
              }}
              phone={{
                value: signupPhone,
                onChange: (value) => { setSignupPhone(value); if (signupPhoneNote) setSignupPhoneNote(""); },
                smsConsent: signupSmsConsent,
                onSmsConsent: setSignupSmsConsent,
                label: t(language, "signup.phone"),
                placeholder: t(language, "signup.phonePlaceholder"),
                note: signupPhoneNote,
              }} />
          </div>
        </ModalBackdrop>
      ) : null}

      {/* T5-2: the intercept box, raised by the shared Ask Lala button on any reading that was born
          with suggested questions. One send, one turn. */}
      {handoffIntercept ? (
        <AskLalaIntercept
          questions={handoffIntercept.questions}
          onClose={() => setHandoffIntercept(null)}
          onSend={(picked, typed) => {
            const h = handoffIntercept;
            const message = composeHandoffMessage({ report: h.report, kind: h.kind, subject: h.subject, questions: picked, typed });
            setHandoffIntercept(null);
            if (!message) return;
            setPendingHandoff({ type: h.type, kind: handoffKindFor(h.type), subject: h.subject });
            setPendingAsk(message);
            goTo("ask-lala");
          }}
        />
      ) : null}

      {/* T2-2: the documents open OVER signup (and over the re-consent box), so she never loses
          what she typed to go and read them. */}
      {legalDoc ? <LegalModal doc={legalDoc} source={LEGAL_DOC_SOURCES[legalDoc]} onClose={() => setLegalDoc(null)} /> : null}

      {/* R2: server-driven, and false for everyone until the owner declares the attorney-final Terms. */}
      {isLoggedIn && reconsentOpen ? (
        <ReconsentModal onOpenDoc={(doc) => setLegalDoc(doc)} onDone={() => setReconsentOpen(false)} />
      ) : null}

      {roomBuildStatus !== "idle" ? (
        <RoomBuildOverlay
          status={roomBuildStatus}
          name={(roomProfile?.name || birthChart.name || "").trim()}
          reports={roomBuildReports}
          onEnter={enterRoom}
          language={language}
        />
      ) : null}

      {houseRails ? (
        <FloatingJournal
          isOpen={journalOpen}
          title={journalTitle}
          draft={journalDraft}
          entries={journalEntries}
          editingId={journalEditingId}
          note={journalNoteText}
          setOpen={setJournalOpen}
          setTitle={setJournalTitle}
          setDraft={setJournalDraft}
          onSelectEntry={selectJournalEntry}
          onNewEntry={newJournalEntry}
          onDeleteEntry={deleteJournalEntry}
          onSave={saveJournalEntry}
          language={language}
        />
      ) : null}

      {/* X3: the side "Being prepared" tray was REMOVED (it overlapped the menu). The generation
          signal now lives ON each control: every generate button pulses (is-generating) while its
          own work runs, so the member sees it in place instead of in a floating rail. */}

      {/* M1: the one button that pops the drawer rail out on a phone. Mounted once, beside the
          drawers it governs; it hides itself on desktop, where nothing changed. */}
      {/* MR-MOB: publishes the bar's REAL height; --nav-h is a static 88px and the nav wraps. */}
      <NavHeightVar />
      {houseRails ? <MobileDrawerToggle /> : null}

      {/* SH2: the Shelves drawer is the FIRST (topmost) tab on the right rail. */}
      {houseRails ? <FloatingShelves isMember={isMember} onRequireMembership={requireMembership} language={language} /> : null}

      {houseRails ? <AskLala isMember={isMember} onRequireMembership={requireMembership} language={language} /> : null}

      {houseRails ? <FloatingVerseSearch isMember={isMember} onRequireMembership={requireMembership} language={language} /> : null}

      {houseRails ? <FloatingCraft isMember={isMember} onRequireMembership={requireMembership} language={language} /> : null}
      {/* N1-2: what is generating follows the member around the house, on every page. */}
      {houseRails ? <GenerationFollower currentRoom={ROOM_FOR_PAGE[page] || null} language={language} /> : null}

      {/* S6-2: the footer, and the one page that does not get one. The law lives in the component. */}
      <SiteFooter page={page} strangerHome={showsStrangerHome({ sessionKnown, isLoggedIn })} note="12thhouse.ai, spiritual guidance and metaphysical intelligence." />

      {/* M4-3 (owner approval granted, 2026-07-25): the thumb-first bottom tab bar, for EVERY member.
          Phone widths only, which the CSS decides. The owner-only flag it shipped behind is gone. */}
      {/* L1: and NOT on a standalone landing page. Her clause names the bottom nav by name. */}
      {/* ==========================================================================================
          >>> **L4 (her live-test order, 2026-08-13) — THE BAR IS PORTALLED TO `<body>`.** <<<

          Her report: on phones the bottom bar loses its fixed position DURING a visit — it starts
          stuck, unsticks mid-session, swiping away and back temporarily rights it, and a route change
          such as landing on the home page can unstick it. **It has been fixed before and keeps coming
          back**, so her order is to fix the CLASS rather than the instance: the nav lives in a layer
          no route, popup, drawer or scroll-lock can re-parent or transform.

          **THE CLASS IS THE CONTAINING BLOCK, AND `<main>` IS THE ANCESTOR THAT KEEPS BEING ONE.**
          A `transform`, `filter`, `backdrop-filter`, `perspective`, `contain` or `will-change` on ANY
          ancestor makes that ancestor the containing block for a `position: fixed` descendant, and
          the bar then scrolls with the page. Every page in this house renders inside that one
          `<main>`, so any rule, any animation and any future page wrapper that touches one of those
          six properties captures the bar — from a file that has nothing to do with navigation, with
          no symptom but the bar drifting.

          **PORTALLED, THE ANCESTOR CHAIN IS `body` AND NOTHING ELSE**, so that entire class is
          removed by construction rather than by a rule somebody has to keep true. It is MR-MOB's own
          repair one control over (*"a room cannot outrank something that is not in its tree"*), and
          it is her own prescribed pattern in as many words.

          **THE PORTAL IS AT THIS CALL SITE RATHER THAN INSIDE `MobileNav.tsx`**, deliberately: R22's
          gate CALLS `MobileTabBar` and reads the tree back, which is the only reason N9-1's highlight
          law is RUN rather than grepped. A `createPortal` in the component returns a portal object
          that walk cannot read, so the reach would be traded for the layer. The DOM parent is `body`
          either way — which is the whole of what this fixes.
          ========================================================================================== */}
      {standalone ? null : <NavPinWatchdog />}
      {standalone ? null : createPortal((
      <MobileTabBar
        page={page}
        openSheetTab={openSheetTab}
        onHome={() => goTo("home")}
        onRooms={() => { setRoomsSheetOpen(true); announceOverlayTab("rooms", true); }}
        onAskLala={() => requestMemberAccess("ask-lala")}
        onLivingRoom={() => requestMemberAccess("living-room")}
        onMyRoom={() => (isLoggedIn ? goTo("my-room") : requestMemberAccess("my-room"))}
        language={language}
      />
      ), document.body)}
      {roomsSheetOpen && !standalone ? (
        <MobileRoomsSheet
          currentPage={page}
          onClose={() => { setRoomsSheetOpen(false); announceOverlayTab("rooms", false); }}
          onHome={() => { setRoomsSheetOpen(false); announceOverlayTab("rooms", false); goTo("home"); }}
          onPick={(nextPage) => { setRoomsSheetOpen(false); announceOverlayTab("rooms", false); requestMemberAccess(nextPage); }}
          language={language}
        />
      ) : null}
      {/* PN1 / DC-4 — the update notice, last in the tree and fixed above the bottom bar, so it can
          never sit under her thumb bar or push a room's layout around. */}
      {/* W13-1b: the permission moment. Two lines here; the law, the sheet and the driver are their own files. */}
      {/* EV3 — her countdown. It renders NOTHING on an ordinary day, and when the window ends it
          takes itself away and tells the readers above to ask again what is open. */}
      <EventCountdownBanner
        signedIn={isLoggedIn}
        onWindowChange={() => window.dispatchEvent(new Event(ENTITLEMENTS_CHANGED))}
      />
      <PushOptIn signedIn={isLoggedIn} hasRoom={Boolean(roomProfile)} />
      {/* P2-INSTALL: her spec is HER ROOM, which is also what keeps this modal off every other page. */}
      <InstallPrompt signedIn={isLoggedIn && page === "my-room"} hasRoom={Boolean(roomProfile)} language={language} />
      {/* P2 — her phone popup; the DECISION is phonePromptState in src/memberPhone.js, which a gate RUNS. */}
      <PhonePrompt signedIn={isLoggedIn} hasRoom={Boolean(roomProfile)} page={page} />
      {/* P2-7 — pull to refresh: ONE mount, one registry; the law is in src/pullToRefresh.js. */}
      <PullToRefresh waitingUpdate={updateWaiting} />
      <UpdateBanner
        open={updateWaiting && !updateDismissed}
        onRefresh={applyUpdate}
        onDismiss={() => setUpdateDismissed(true)}
      />
    </main></FeatureAccess.Provider></RequireFeature.Provider>
  );
}

// R7-6: the sidebar line icons (HP3) — `MENU_ICONS` and `MenuIcon` — moved VERBATIM to
// `src/components/MenuIcon.tsx`, where the gate can finally hold the law the comment states: a room
// the menu lists cannot render iconless. See the import at the top of this file.

function SiteMenu({
  currentPage,
  goHome,
  goTo,
  isOwner,
  language = DEFAULT_LANGUAGE,
}: {
  currentPage: Page;
  goHome: () => void;
  goTo: (nextPage: Page) => void;
  isOwner: boolean;
  // 14-B5: the menu's own words turn; the ROOM NAMES it lists are the roster's and stay hers.
  language?: string;
}) {
  // M2 (mobile): on phones the sidebar stacks inline ABOVE the room content, so a full menu
  // list buried every room's real action ~900px down the page. The room list now lives behind
  // a tap-to-open drawer (collapsed by DEFAULT on mobile) so room content leads; each choice
  // closes it. Desktop is untouched — the toggle is hidden and `.site-sidebar__drawer` is
  // `display: contents`, so the sidebar's layout is byte-identical to before.
  const [navOpen, setNavOpen] = useState(false);
  // HOME-1 FINAL: her artwork paints its own MENU, and that zone asks THIS menu to open rather than
  // a second drawer built under her picture (R28/R29). The listener lives here because the state does.
  useEffect(() => {
    const open = () => setNavOpen(true);
    window.addEventListener(OPEN_SITE_MENU_EVENT, open);
    return () => window.removeEventListener(OPEN_SITE_MENU_EVENT, open);
  }, []);
  const goToClose = (nextPage: Page) => { setNavOpen(false); goTo(nextPage); };
  return (
    <aside className={navOpen ? "site-sidebar is-open" : "site-sidebar"} aria-label={t(language, "nav.menu")}>
      {/* MN1: the round 12th House logo replaces the wordmark, sized to fill the corner (smaller
          than the homepage hero), visible in every room. The tagline stays beneath, gold chrome. */}
      <div className="site-sidebar__brand">
        <img className="site-sidebar__logo" src={menuLogoUrl} alt="12th House" />
        {/* N20-4: the tagline is her ruling and it is ONE CONSTANT, so it can never be two things
            on two screens again. */}
        <small className="site-sidebar__tagline">{SITE_TAGLINE}</small>
      </div>

      <button
        type="button"
        className="site-sidebar__toggle"
        aria-expanded={navOpen}
        aria-controls="site-menu-drawer"
        onClick={() => setNavOpen((o) => !o)}
      >
        {navOpen ? <X size={18} /> : <Menu size={18} />}
        {t(language, navOpen ? "nav.closeMenu" : "nav.menu")}
      </button>

      <div className="site-sidebar__drawer" id="site-menu-drawer">
      <div className="site-sidebar__primary">
        <button className={currentPage === "home" ? "site-sidebar__link site-sidebar__link--active" : "site-sidebar__link"} type="button" onClick={() => { setNavOpen(false); goHome(); }}>
          <MenuIcon page="home" />{t(language, "nav.home")}
        </button>
        <button className={currentPage === "my-room" ? "site-sidebar__link site-sidebar__link--active" : "site-sidebar__link"} type="button" onClick={() => goToClose("my-room")}>
          <MenuIcon page="my-room" />{t(language, "account.myRoom")}
        </button>
      </div>

      <div className="site-menu" aria-label={t(language, "nav.rooms")}>
        {/* Every top-level item is a direct click into its room, no submenus. */}
        {homeMenuItems.map((room) => (
          <button
            key={room.page}
            type="button"
            className={currentPage === room.page ? "site-sidebar__link site-sidebar__link--active" : "site-sidebar__link"}
            onClick={() => goToClose(room.page)}
          >
            <MenuIcon page={room.page} />{room.menuTitle || room.title}
          </button>
        ))}
      </div>

      {/* HER SUPPORT-DESK RULING, CLAUSE 1: Technical Support is a REAL menu entry, prominent, in
          the room list area — never small print. It vanished twice because THERE WERE TWO MENUS AND
          ONLY ONE WAS GUARDED; the label is the SHARED declaration (R28/R29). See r22-mobile-nav. */}
      <div className="site-menu site-menu--support" aria-label="Support">
        <button type="button" onClick={() => goToClose("contact")}
          className={`site-sidebar__link site-sidebar__link--support${currentPage === "contact" ? " site-sidebar__link--active" : ""}`}>
          <MenuIcon page="contact" />{mobileHelpLabel("contact")}
        </button>
      </div>

      {/* B3: the owner's door to her dashboard, pinned at the BOTTOM near the settings/
          footer area (owner's placement), owner-only. This is convenience, not security —
          every dashboard endpoint checks the role server-side regardless of this button.
          Direct URL for bookmarking: /dashboard. */}
      {isOwner ? (
        <div className="site-sidebar__owner">
          <button
            className={currentPage === "dashboard" ? "site-sidebar__owner-link is-active" : "site-sidebar__owner-link"}
            type="button"
            onClick={() => goToClose("dashboard")}
          >
            <MenuIcon page="dashboard" />{t(language, "account.dashboard")}
          </button>
        </div>
      ) : null}

      {/* LG3: the quiet footer. Deliberately a different, lighter weight than a room link
          and pinned to the bottom, so new rooms always sit above it. */}
      <SidebarFooterNav currentPage={currentPage} onGo={goToClose} />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------- LEGAL PAGES (LG1)
// The Terms, the Privacy Policy and the FAQ are owner-authored markdown at the repo root,
// bundled at build time (?raw) and rendered as designed pages. The content is HERS: this
// renders it faithfully and never rewrites, summarises, or fills in anything.
//
// R7-5/R36/R85: `Markdown` and then all three legal surfaces moved out, `LegalModal` last — R36's
// reason for holding it back (`ModalBackdrop` lived here) was retired by R81 and nobody had gone
// back for the note. The ONE page -> source map stays here with the `?raw` imports it is built from,
// and the popup reads it AT THE CALL SITE exactly as both router arms already do.

// T2-2/R2 — the one-time re-consent moved to `src/components/ReconsentModal.tsx` (R87). It holds
// three hooks and could not be CALLED either here or there; what the move buys is a named file for
// the one write that records what she agreed to.

// ---------------------------------------------------------------- OWNER DASHBOARD (OD)
// Owner-only. The nav entry renders only for her, and every endpoint behind it re-checks
// the role server-side, so hiding the button is convenience, not the security.

// GL: one rejection as the owner reads it. `detail` is whatever the gate recorded, verbatim —
// deliberately not typed into a shape, because each gate records what its own rejection means and
// flattening that into a lowest common denominator is how a review surface stops being useful.
type GateEntry = {
  id: string; gate: string; outcome: string; delivered: boolean | null;
  userId: string | null; at: string; detail: Record<string, unknown>;
};

function OwnerDashboardPage({ goHome }: { goHome: () => void }) {
  // D6-1, HER ORDER: **the dashboard opens on THE BUSINESS, never a member list.**
  // ITEM A — **A SECTION IS A DOOR AND A VIEW IS A SURFACE, and they are two pieces of state because
  // they are two facts** (D6-3 had one, which is why three surfaces ended up typed as literals at the
  // render site). `goToView` is the ONE place that answers where a tap lands: every tile, every
  // needs-you line and every menu button routes through it, so a destination inside the collapsed
  // Advanced area opens Advanced instead of silently doing nothing.
  const [section, setSection] = useState<string>(DEFAULT_SECTION);
  const [tab, setTab] = useState<string>(defaultViewOf(DEFAULT_SECTION));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // M1 — the door's SELECTION, cleared on every navigation so one door's filter cannot follow her
  // onto the next surface (R86's `tabNote` finding, one piece of state over). Why a door is a view
  // AND a filter is in `src/alertDoors.js`, where the law lives.
  const [viewQuery, setViewQuery] = useState("");
  const goToView = useCallback((next: string, query = "") => {
    const to = goToViewLaw(next);
    setSection(to.section);
    setTab(to.view);
    setViewQuery(typeof query === "string" ? query : "");
    if (to.openAdvanced) setAdvancedOpen(true);
    closeMemberDetail();
    setFilter("");
  }, []);
  // D6-2: ONE range, owned here and read by every ranged tab, so two tabs cannot disagree about it.
  const [range, setRange] = useState<{ key: string; from: string; to: string }>({ key: DEFAULT_RANGE, from: "", to: "" });
  const rangeQuery = new URLSearchParams({
    range: range.key, ...(range.key === "custom" ? { from: range.from, to: range.to } : {}),
  }).toString();
  // A4 — the catalogs view is GONE from her dashboard, so its state went with it. Its numbers were
  // never stored here: they were counted on the server at open time, and every catalog they counted
  // is still read by the surfaces that serve members.
  // R86 — ONE NOTE FOR EVERY TAB THAT LOADS ITSELF HERE. Four of the six said nothing when their read
  // was refused; the fifth (`catalogs`, since retired by A4) had the page's only honest sentence, a
  // fifth copy of the idea.
  const [tabNote, setTabNote] = useState("");
  const [probes, setProbes] = useState<Array<{ userId: string; email: string; name: string; events: number; spice: number; termsWarnings: number; cooldowns: number; blockedAttempts: number; maxStrike: number | null; topics: string[]; recentAsks: string[]; lastAt: string; clearedAt?: string }>>([]);
  // HER DASHBOARD ITEM, SURFACE 1: the Archive link, the server's account of a clear, and the stamp.
  const [probeView, setProbeView] = useState<"new" | "archive">("new");
  const [probeNote, setProbeNote] = useState("");
  const [probeStamp, setProbeStamp] = useState(0);
  const [gateEntries, setGateEntries] = useState<GateEntry[]>([]);
  const [gateSummary, setGateSummary] = useState<Array<{ gate: string; outcome: string; count: number; lastAt: string }>>([]);
  const [gateSince, setGateSince] = useState<string | null>(null);
  const [gateFilter, setGateFilter] = useState("");
  const [openEntry, setOpenEntry] = useState<string>("");
  const [members, setMembers] = useState<OwnerMember[]>([]);
  // W11-1: backstage by default, revealed by one owner-only toggle. Never a delete.
  const [showHarness, setShowHarness] = useState(false);
  const [hiddenHarness, setHiddenHarness] = useState(0);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<MemberDetail | null>(null);
  const [messages, setMessages] = useState<Array<{ id: string; userId: string | null; reason: string; name: string; email: string; subject: string; message: string; createdAt: string }>>([]);
  // D6-4 — SEARCH ON MESSAGES AND PROBES, one box, cleared when she changes tab. Members searches on
  // the SERVER (the list is paged there); these two lists arrive whole, so filtering them in the
  // page is the honest cheap answer rather than a route that re-asks for what is already here.
  const [filter, setFilter] = useState("");
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  // ITEM 4b — the `Wrote in` view declared a `reply` act since ITEM A and had no control for it.
  const [reply, setReply] = useState<SupportReplyState>(EMPTY_SUPPORT_REPLY);
  const [mailArmed, setMailArmed] = useState(false);   // T114-1 — may that reply go by EMAIL at all?
  const [messagesStamp, setMessagesStamp] = useState(0);
  // ITEM 4b clause 3 — which of her four filters is on, and clause 2 — the conversation a ticket row
  // sent her to. `""` is none of either; both are read by ONE law (`deskView`), which ranks them.
  const [deskState, setDeskState] = useState("");
  const [deskFocus, setDeskFocus] = useState("");
  // ITEM 4b — her reply, as the house. The decision is `src/lib/support-reply.ts`'s, so a gate RUNS
  // it; the send goes through the SHARED owner door (R86) which reads the PAYLOAD, not the status.
  const sendSupportReply = useCallback(async (m: OwnerContactMessage) => {
    setReply((r) => ({ ...r, sending: true, note: "" }));
    const a = await postOwnerAction<{ threadId?: string; via?: string; sentence?: string }>("/api/owner/support-desk/reply", { contactId: m.id, body: reply.draft });   // T114-1: the CONVERSATION and nothing else — an address a caller can type is one a caller can choose.
    if (!a.ok && a.kind === "denied") setDenied(true);
    setReply(supportReplyNext(a, { openId: m.id, draft: reply.draft, who: supportReplyWho(m), refused: ownerActionNote(a), via: a.ok ? String(a.data?.via || "") : "" }));
    if (a.ok) setMessagesStamp((n) => n + 1);
  }, [reply.draft]);

  // L6 — violation evidence: per-member flagged exchanges (with surrounding thread + owner ruling).
  const [evidence, setEvidence] = useState<Record<string, EvidenceEvent[]>>({});
  const [evidenceLoading, setEvidenceLoading] = useState<string>("");

  // R86: cleared on a tab change (a refusal following her onto another tab is R65's freshness lie in
  // another spelling). ABOVE the loaders: a reset below them would wipe its own loader's sentence.
  useEffect(() => { setTabNote(""); }, [tab]);
  // R216 — **AND THAT RESET IS DEFEATED BY ORDERING, WHICH IS WHY IT IS NOT ENOUGH.** These six
  // reads all write the SAME `tabNote`, `loading` and `denied`, and a read is not cancelled by her
  // leaving the tab that started it: she opens Members, switches to Catalogs, and the members read
  // lands afterwards — CLEARING the spinner over a tab that is still loading, and writing R86's own
  // sentence about the members list under a heading that has nothing to do with it. The reset runs
  // BEFORE the stale answer, so it cannot see it. ONE counter, because these reads share their
  // output state: whichever read is newest owns all three, and every older one is dropped WHOLE.
  const tabSeq = useRef(0);
  // R221 — **AND R86 NAMED SIX READS ON THIS PAGE WHILE R216 GUARDED FIVE.** R86's own comment on
  // `openMember` below calls it *"THE SIXTH READ HERE AND THE WORST"*; R216 found its reads by the
  // `if (tab !== "x") return;` line the five EFFECTS carry, and a handler has none — so the census
  // could not see the one door D6-4 put on eight surfaces across six tabs.
  //
  // **IT GETS ITS OWN COUNTER AND MAY NEVER SHARE `tabSeq` (R18: two questions).** *Which member's
  // detail is newest* and *which tab's list is newest* are different facts, and one counter
  // answering both is worse than none here: she is standing on Members with the list read in
  // flight, she taps a name from the rows already drawn, and the list read is superseded — but the
  // list read is the only thing that ever calls `setLoading(false)`, so **the spinner is stuck
  // forever on a tab whose read really did answer.**
  const memberSeq = useRef(0);
  // **THE CLOSE RETIRES THE READ, OR IT IS NOT A CLOSE.** Both doors that shut this modal — her own
  // Close, and a navigation, which `goToView` closes it on — write `setDetail(null)`, and a pending
  // answer is a pending `setDetail(...)` that lands afterwards and re-opens it over wherever she has
  // gone. ONE declaration, because two spellings of *shut this modal* is one of them keeping the
  // hole (R28/R29).
  const closeMemberDetail = useCallback(() => { closeRead(memberSeq); setDetail(null); }, []);

  // R76 — THE EIGHTH OWNER SURFACE, AND R65's ROSTER COULD NOT CONTAIN IT (R69's lesson): that round
  // declared its biconditional over the seven `src/components/Owner*.tsx` tabs, and this drawer lives
  // in App.tsx inside the Members tab. Both halves were the shape R65 closed — a read with no `else`
  // and an act with an empty `.catch` — and here the read leaves `evidence[userId]` UNDEFINED, so the
  // drawer she opened under "Review the evidence" is COMPLETELY BLANK: no rows, no "Loading", not
  // even the honest "No preserved exchanges yet".
  // TWO FACTS, TWO STATES (R66, and the round would have broken its own rule without it): a ruling
  // RE-READS the evidence when it lands, so one shared note would be overwritten by the read's silence
  // in the same breath — her act's own account gone, on an append-only ledger.
  const [evidenceNote, setEvidenceNote] = useState<Record<string, string>>({});
  const [rulingNote, setRulingNote] = useState<Record<string, string>>({});
  const loadEvidence = async (userId: string) => {
    setEvidenceLoading(userId);
    const answer = await loadOwnerData<{ events: EvidenceEvent[] }>(`/api/owner/probe-evidence?userId=${encodeURIComponent(userId)}`);
    if (!answer.ok && answer.kind === "denied") setDenied(true);
    if (answer.ok) setEvidence((prev) => ({ ...prev, [userId]: answer.data.events }));
    setEvidenceNote((prev) => ({ ...prev, [userId]: ownerLoadNote(answer, "The evidence could not be read just now.") }));
    setEvidenceLoading("");
  };
  const ruleOnEvidence = async (userId: string, id: string, ruling: "confirmed" | "innocent" | "watch") => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/probe-ruling", { id, ruling });
    if (!answer.ok && answer.kind === "denied") setDenied(true);
    // R66: an act that LANDED is not silent either — the SERVER composes the account of what it did,
    // strike step included, because lifting a false-positive strike is the whole point of Innocent.
    setRulingNote((prev) => ({ ...prev, [userId]: ownerActionNote(answer) }));
    if (answer.ok) await loadEvidence(userId);
  };

  useEffect(() => {
    if (tab !== "members") return;
    const ticket = beginRead(tabSeq);
    setLoading(true);
    // W11-1: humans only unless she asks. The server withholds the harness rows and says how many,
    // so the toggle can name the number instead of hiding a silent filter.
    const parts = [search.trim() ? `q=${encodeURIComponent(search.trim())}` : "", showHarness ? "harness=1" : ""].filter(Boolean);
    const params = parts.length ? `?${parts.join("&")}` : "";
    // R86: the list of everybody paying for this site drew itself EMPTY on a refused or dropped read.
    void loadOwnerData<{ members: OwnerMember[]; hiddenHarness?: number }>(`/api/owner/members${params}`).then((a) => {
      // R216: this one re-reads on every keystroke in her search box, so it is the read most often
      // superseded on this dashboard. Dropped WHOLE — the rows, the note and the spinner together.
      if (!readMayPaint(tabSeq, ticket)) return;
      if (!a.ok && a.kind === "denied") setDenied(true);
      else if (a.ok) { setMembers(a.data.members); setHiddenHarness(Number(a.data.hiddenHarness) || 0); }
      setTabNote(ownerLoadNote(a)); setLoading(false);
    });
  }, [tab, search, showHarness]);

  // A4: D1's catalogs loader is DELETED here with the view it fed. `/api/owner/catalogs` went with
  // it, because a route no client calls is one R202 reads as unreached and R203 reads as a payload
  // of dead fields.

  // GL: the gate history. THE GATE LAW's roster clause says every gate logs its rejections FOR
  // OWNER REVIEW — this is the review.
  useEffect(() => {
    if (tab !== "gates") return;
    const ticket = beginRead(tabSeq);
    setLoading(true);
    const q = gateFilter ? `&gate=${encodeURIComponent(gateFilter)}` : "";
    // R86 — THE SHARPEST: THE GATE LAW's roster clause points here. Every gate logs its rejections FOR
    // OWNER REVIEW and this IS the review, so a refused read drew a site where none ever has.
    void loadOwnerData<{ entries: GateEntry[]; summary: typeof gateSummary; since: string | null }>(`/api/owner/gate-log?days=30${q}`).then((a) => {
      if (!readMayPaint(tabSeq, ticket)) return;
      if (!a.ok && a.kind === "denied") setDenied(true);
      else if (a.ok) { setGateEntries(a.data.entries); setGateSummary(a.data.summary); setGateSince(a.data.since); }
      setTabNote(ownerLoadNote(a)); setLoading(false);
    });
  }, [tab, gateFilter]);

  useEffect(() => {   // L5: who tripped the probe ladder, so she can decide on removal herself.
    if (tab !== "probes") return;
    const ticket = beginRead(tabSeq);
    setLoading(true);
    // R86: an empty probe list reads as a site with no repeat offenders on it.
    // HER DASHBOARD ITEM, SURFACE 1: one route, both faces, and `probeStamp` moves when she clears —
    // without it the row she just cleared sits there until she changes tabs (ITEM 4b's own lesson).
    void loadOwnerData<{ probes: typeof probes }>(`/api/owner/probes?view=${encodeURIComponent(probeView)}`).then((a) => {
      if (!readMayPaint(tabSeq, ticket)) return;
      if (!a.ok && a.kind === "denied") setDenied(true);
      else if (a.ok) setProbes(a.data.probes);
      setTabNote(ownerLoadNote(a)); setLoading(false);
    });
  }, [tab, probeView, probeStamp]);

  // SURFACE 1's clear. The SERVER composes the account (D2/R66), counted off the rows that moved.
  const clearProbeRows = async (userId: string | null) => {
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/probe-clear", userId ? { userId } : { all: true });
    if (!answer.ok && answer.kind === "denied") setDenied(true);
    setProbeNote(ownerActionNote(answer));
    if (answer.ok) setProbeStamp((n) => n + 1);
  };

  // D6-3 RENAMED THIS TAB AND THE FETCH DID NOT FOLLOW: the key became `messages` and this effect
  // still tested `contact`, so the tab loaded NOTHING and read as an empty inbox.
  // ITEM 4b: LIFTED OUT OF THE EFFECT so her reply can re-read the desk after answering somebody —
  // without it the row she just answered keeps saying NEEDS ME until she changes tabs, which is the
  // desk lying about who is still waiting. R216's guard is kept: only the newest read may paint.
  useEffect(() => {
    if (tab !== "messages") return;
    const ticket = beginRead(tabSeq);
    setLoading(true);
    // R86: D6-3's defect one layer deeper — it fixed the tab key, and the read still drew an empty inbox.
    void loadOwnerData<{ messages: typeof messages; mail?: { armed?: boolean } }>("/api/owner/contact").then((a) => {
      if (!readMayPaint(tabSeq, ticket)) return;
      if (!a.ok && a.kind === "denied") setDenied(true);
      else if (a.ok) { setMessages(a.data.messages); setMailArmed(a.data.mail?.armed === true); }
      setTabNote(ownerLoadNote(a)); setLoading(false);
    });
    // ITEM 4b: `messagesStamp` moves when she answers somebody, so the desk re-reads.
  }, [tab, messagesStamp]);

  // ITEM 4b clause 2/3 — THE DESK, NARROWED. The whole decision (her four filters, the search, and a
  // focus arriving from a ticket row) is `src/supportDesk.js`'s `deskView`, so a FAST gate RUNS it;
  // the search moved there with it and had never been reachable by any instrument.
  const desk = deskView(messages, { search: filter, filter: deskState, focusId: deskFocus });
  // SURFACE 1: her search AND her "newest first by date" are one declaration in `src/probeClears.js`
  // now, so a gate RUNS both. The retired inline filter here could be reached by nothing.
  const shownProbes = visibleProbes(probes, filter);

  async function openMember(id: string) {
    // R221: the ticket is taken FIRST, so this open is the newest thing that has happened to the
    // modal from this instant — and taking it is also what supersedes a read she started a moment
    // ago on a different member. Without it, two taps race and the OLDER answer lands last: she is
    // reading member B's payments and her support door, and **the panel swaps to member A under her
    // eyes** with no navigation and no tap of hers behind it.
    const ticket = beginRead(memberSeq);
    setDetail(null);
    // D5-2: this panel was measured 13,233px below the fold on her phone. It is a MODAL now, so the
    // answer cannot land off-screen however tall the members table under it grows.
    // R86 — THE SIXTH READ HERE AND THE WORST, BECAUSE ITS FAILURE IS AN ABSENCE (R25). D6's law is
    // that every member name on five tabs is a DOOR, and a refused read left `detail` null, so the
    // door opened NOTHING and said nothing. The `fetch` was not wrapped either (only its `.json()`),
    // so a thrown request rejected this handler. The sentence lands on the tab she tapped from.
    const a = await loadOwnerData<MemberDetail>(`/api/owner/members/${id}`);
    // R221 — DROPPED WHOLE, and `tabNote` is why the note goes with the panel rather than being
    // kept "in case it is useful" (ITEM 10's own words: a stale note under fresh numbers is the
    // same lie one line down). That slot is SHARED with the five tab reads, so a superseded
    // member refusal writes *"That member could not be opened just now."* under a heading that has
    // nothing to do with it — and a superseded SUCCESS is worse in the other direction, because
    // `ownerLoadNote` answers "" on ok and it wipes a fresh tab read's own sentence.
    if (!readMayPaint(memberSeq, ticket)) return;
    if (!a.ok && a.kind === "denied") setDenied(true);
    else if (a.ok) setDetail(a.data);
    setTabNote(ownerLoadNote(a, "That member could not be opened just now."));
  }

  if (denied) {
    return (
      <section className="page-shell owner-page">
        <button className="back-button" type="button" onClick={goHome}><ArrowLeft size={18} />Back</button>
        <p className="owner-page__denied">This page is not available.</p>
      </section>
    );
  }

  return (
    <section className="page-shell owner-page">
      <button className="back-button" type="button" onClick={goHome}><ArrowLeft size={18} />Back</button>
      {/* ITEM X2: in the store app, Manage Amara opens Truth signed in (src/storeApp/truthDoor.ts); the website follows the link. */}
      <div className="owner-page__head" onClickCapture={APP_MODE ? captureTruthLinks : undefined}>
        <p className="eyebrow">Owner</p>
        <h1>Your dashboard</h1>
        {/* ITEM B7a (MANAGE-AMARA-BUTTON, her YES): this page is drawn for owners only and the
            console route refuses every other seat. */}
        <a className="owner-page__amara" href="/api/truth-door?to=%2Fconsole%2Famara">Manage Amara</a>
        {/* ITEM H6: in the store app, the door to Truth itself, beside Manage Amara (src/storeApp/OpenTruth.tsx). */}
        {APP_MODE ? <OpenTruth /> : null}
      </div>

      {/* >>> **EV3 + EV6 — THE LIVE EVENT BAR, ABOVE EVERYTHING, BEHIND NO TAB.** <<< On the night
          she is holding a phone in one hand and talking to a camera: the one tap that opens the
          house, the clock that raises it again by itself, and the number climbing beside them are
          the whole of what she needs, and they are above the fold. **Nothing on it can refuse a
          member anything** — her ruling, 2026-09-09: the ticker notifies, it never brakes.
          It is a sibling of `LiveKillSwitch` below by design: both are night-of controls. */}
      <OwnerEventBar />

      {/* ITEM A — HER SIX SECTIONS AND ADVANCED, in src/components/OwnerNav.tsx. The three surfaces
          D6-3 had to type as LITERALS here (triage, catalogs, review) are on the roster now, so this
          page no longer carries a second answer to what the dashboard can show. */}
      <OwnerNav section={section} view={tab} advancedOpen={advancedOpen}
        onSection={(key) => goToView(defaultViewOf(key))}
        onView={goToView}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)} />

      {/* D6-2 — one control, and only where a range means anything. The moderation and grant seats
          are not time series and must not grow a control that does nothing. */}
      {RANGED_TABS.has(tab) ? (
        <RangePicker value={range.key} from={range.from} to={range.to}
          onChange={(next) => setRange({ key: next.key, from: next.from ?? range.from, to: next.to ?? range.to })} />
      ) : null}

      {/* R86 — ABOVE THE TAB, WHERE THE EMPTINESS IT EXPLAINS IS (R65's ruling one file over), and
          rendered ONCE for all five loaders rather than per tab. */}
      {/* LIVE-1 -- her kill switch. ITEM 6b: it draws only while somebody is live. */}
      <LiveKillSwitch />
      {tabNote ? <p className="owner-page__note owner-page__note--failed" role="alert">{tabNote}</p> : null}

      {tab === "overview" ? (
        <OwnerOverview rangeQuery={rangeQuery} rangeKey={range.key}
          onDenied={() => setDenied(true)} onGo={goToView} />
      ) : tab === "members" ? (
        /* R221 — MOVED VERBATIM to src/components/OwnerMembersTab.tsx. Hookless, so the gate CALLS
           it: W11-1's toggle, its count sentence and D6-4's two doors were only ever reachable by a
           regex over this file. The extraction is what pays this round's R8 ceiling. */
        <OwnerMembersTab members={members} search={search} showHarness={showHarness}
          hiddenHarness={hiddenHarness} loading={loading} shortDate={shortDate}
          onSearch={setSearch} onToggleHarness={() => setShowHarness((v) => !v)} onOpenMember={openMember} />
      ) : tab === "tickets" ? ( /* ITEM 9 — the IT sheet; its laws are src/itTickets.js */
        /* ITEM 4b clause 2 — the row's door. `goToView` is the ONE law for where a tap lands, and the
           focus is set BEFORE it so the desk paints already showing what she asked for. */
        <OwnerTicketsTab onDenied={() => setDenied(true)}
          onOpenConversation={(id) => { setDeskFocus(id); goToView("messages"); }} />
      ) : tab === "truth" ? (
        /* Q-DASH (2), her order: the TRUTH tab. src/components/OwnerTruth.tsx; every number from an owner-only door. */
        <OwnerTruth rangeQuery={rangeQuery} onDenied={() => setDenied(true)} />
      ) : tab === "alerts" ? (
        /* SURFACE 3 / A2 — every alert is a door. src/components/OwnerAlerts.tsx, on src/standingAlerts.js, which the PATROL reads too. */
        <OwnerAlerts onDenied={() => setDenied(true)} />
      ) : tab === "grants" ? (
        /* D2 — owner admin powers. The whole tab is src/components/OwnerGrants.tsx; the rules it
           runs on are src/accessGrants.js, shared with the server so there is one answer key. */
        <OwnerGrants onDenied={() => setDenied(true)} />
      ) : tab === "broadcasts" ? (
        /* HER BROADCAST TOOL (queue item 4): src/components/OwnerBroadcasts.tsx, on the rules in
           src/broadcasts.js, which the SERVER reads too. */
        <OwnerBroadcasts />
      ) : tab === "teaching" ? (
        /* TEACH-1 — her teaching room. src/components/OwnerTeaching.tsx, on the law in
           src/teachingRoom.js, which the SERVER reads too. The door is the EXISTING support
           passcode (her clause 1) and every teaching is written to the action log. */
        <OwnerTeaching />
      ) : tab === "moderation" ? (
        /* ITEM A — **HER MERGED QUEUE.** One home for everything held or flagged, opened on the
           `needs` lane: the same seat, the same content-in-context and the same APPROVE/DECLINE
           LR19 wired, with the two lanes she used to have to check separately joined by ONE SQL
           predicate — the same one the Overview's count is taken from. Held, Reported, Photos and
           her DMs are all still there beside it; the merge is a better default, not a removal.
           **The LANE is what makes this a different view from the room seat below** — the two arms
           were byte-identical for an hour, which is two doors onto one surface. */
        <OwnerLivingRoom lane="needs" onDenied={() => setDenied(true)} onOpenMember={(id) => openMember(id)} />
      ) : tab === "checks" ? (
        /* ITEM 7c — her old stack, off Overview entirely and reachable here under Advanced. */
        <OwnerChecks rangeQuery={rangeQuery} onDenied={() => setDenied(true)} onGo={goToView} />
      ) : tab === "delivery" ? (
        /* ITEM A — the three beacon screens, moved off the Overview into Advanced where their own
           language is allowed. Same payload, same components, nothing deleted. */
        <OwnerDelivery rangeQuery={rangeQuery} onDenied={() => setDenied(true)} />
      ) : tab === "living" ? (
        /* LR1-5 — her moderation seat, shipping WITH the room. src/components/OwnerLivingRoom.tsx;
           the rules and the action sentences come from src/livingRoom.js, shared with the server.
           ITEM A: it opens on EVERYTHING, because this is the seat she comes to when she is reading
           the room rather than clearing what is waiting. */
        <OwnerLivingRoom lane="all" onDenied={() => setDenied(true)} onOpenMember={(id) => openMember(id)} />
      ) : tab === "veil" ? (
        /* VEIL-1 — her veiled group oversight, beside the room seat because it is the same job.
           src/components/OwnerVeil.tsx; the door is the EXISTING support passcode and every opening
           is written to the action log before the words are read. Members are never told. */
        <OwnerVeil onDenied={() => setDenied(true)} />
      ) : tab === "reviews" ? (
        /* REV-1 — her approval desk. Her approvals are the gate before any display renders. */
        <OwnerReviews />
      ) : tab === "triage" ? (
        /* A1 — the support triage queue. Drafts land HERE, not in RUNBOOK.md: nothing is an order
           until she taps approve. src/components/OwnerTriage.tsx; the rules are src/triage.js,
           shared with the pass so the classifier and the queue read one law. */
        /* M1 — her money line opens HERE now, on exactly the rows it counted. */
        <OwnerTriage openQuery={viewQuery} onDenied={() => setDenied(true)} onOpenMember={(id) => openMember(id)} />
      ) : tab === "review" ? (
        /* D3-9 — the monitoring queue. Owner-facing views over what the gates ALREADY log; the flag
           families and the gates under each live in src/usagePage.js, shared with the server. */
        <OwnerReview onDenied={() => setDenied(true)} />
      ) : tab === "wordmap" ? (
        /* B5 — her Moses word-map approval list. src/components/OwnerWordMapPanel.tsx. */
        <OwnerWordMapPanel onDenied={() => setDenied(true)} />
      ) : tab === "analytics" ? (
        /* D6-3 — rooms ranked, generations by type, member growth, Lala volume, retention, ranged
           and CSV-exportable. src/components/OwnerAnalytics.tsx. It reads the ONE range above rather
           than the 1/7/30/90 buttons this replaced: two range controls is two answers to "this month". */
        <OwnerAnalytics rangeQuery={rangeQuery} onDenied={() => setDenied(true)} />
      ) : tab === "financials" ? (
        /* D6-3 — revenue by tier, purchases, refunds, vendor cost by day, margin. Test mode and
           every estimate say so on their own faces. src/components/OwnerFinancials.tsx. */
        <OwnerFinancials rangeQuery={rangeQuery} onDenied={() => setDenied(true)}
          onOpenMember={(id) => openMember(id)} />
      /* A4 — D1's catalogs arm is GONE from this ternary with the view it rendered. */
      ) : tab === "gates" ? (
        /* GL: the gate history. Every gate on the roster logs its rejections here, and this is
           where they are read. Until now they went to a file on the container and died with each
           redeploy, so this page could not have existed. */
        <>
          <div className="owner-bar">
            <p className="owner-page__note">
              Every time a gate stopped something before it reached a member, and why. Kept here so it survives.
            </p>
            <select className="owner-search" value={gateFilter} onChange={(e) => setGateFilter(e.target.value)} aria-label="Filter by gate">
              <option value="">Every gate</option>
              {[...new Set(gateSummary.map((s) => s.gate))].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          {loading ? <p className="owner-page__note">Loading...</p> : null}

          {gateSummary.length ? (
            <ul className="owner-bars owner-bars--gates">
              {gateSummary.map((s) => (
                <li key={`${s.gate}-${s.outcome}`}>
                  <span className="owner-bars__label">{s.gate}{s.outcome ? ` · ${s.outcome}` : ""}</span>
                  <span className="owner-bars__count">{s.count}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="owner-messages">
            {gateEntries.map((e) => (
              <article className="owner-message" key={e.id}>
                <header>
                  <span className="owner-message__reason">{e.gate}{e.outcome ? ` · ${e.outcome}` : ""}</span>
                  <span className="owner-message__date">{shortDate(e.at)}</span>
                </header>
                <h3>{e.delivered === false ? "Stopped before delivery" : e.delivered === true ? "Delivered" : "Recorded"}</h3>
                <button type="button" className="owner-link" onClick={() => setOpenEntry(openEntry === e.id ? "" : e.id)}>
                  {openEntry === e.id ? "Hide the detail" : "Read the detail"}
                </button>
                {openEntry === e.id ? (
                  /* Verbatim, because a rejection you cannot read is not a rejection you can
                     review, and each gate records what its own rejection means. */
                  <pre className="owner-gate__detail">{JSON.stringify(e.detail, null, 2)}</pre>
                ) : null}
              </article>
            ))}
            {!loading && !gateEntries.length ? (
              <p className="owner-page__note">
                Nothing has been stopped{gateSince ? ` since ${shortDate(gateSince)}, when this started being kept` : " yet"}.
              </p>
            ) : null}
          </div>
        </>
      ) : tab === "probes" ? (
        /* L5: members who kept pushing on a protected topic (machinery, family, or money) past the
           warm decline. Strikes, warnings, and cooldowns are the evidence file; the owner decides on
           removal herself. The whole surface is src/components/OwnerProbes.tsx — hookless, so a gate
           CALLS it (extracted by ITEM 4b to pay this file's ceiling as that round went). */
        <OwnerProbes probes={shownProbes} filter={filter} loading={loading} openEntry={openEntry}
          evidence={evidence} evidenceLoading={evidenceLoading} evidenceNote={evidenceNote}
          view={probeView} clearNote={probeNote}
          onView={(next) => { setProbeView(next === "archive" ? "archive" : "new"); setProbeNote(""); }}
          onClear={clearProbeRows}
          rulingNote={rulingNote} shortDate={shortDate} onFilter={setFilter} onOpenMember={openMember}
          onToggle={(userId) => {
            const opening = openEntry !== userId;
            setOpenEntry(opening ? userId : "");
            if (opening && !evidence[userId]) loadEvidence(userId);
          }}
          onRule={ruleOnEvidence} />
      ) : (
        /* ITEM A — the "Wrote in" view, now src/components/OwnerMessages.tsx. It was the router's
           fall-through arm, which is the same defect the flat tab roster had: the surface that
           catches everything is the one nobody declared. It is a named view under IT Tickets now,
           because a member writing in and a ticket are one support world (her order). */
        <OwnerMessages messages={desk.rows} filter={filter} loading={loading} shortDate={shortDate}
          onFilter={setFilter} onOpenMember={openMember}
          state={deskState} onState={setDeskState}
          focused={desk.focused} focusMissing={desk.missing} onClearFocus={() => setDeskFocus("")}
          reply={reply}
          onReplyOpen={(id) => setReply({ openId: id, draft: "", sending: false, note: "" })}
          onReplyDraft={(next) => setReply((r) => ({ ...r, draft: next }))}
          onReplySend={sendSupportReply} mailArmed={mailArmed} />
      )}


      {/* D6-4 — THE MEMBER MODAL BELONGS TO THE DASHBOARD, NOT TO THE MEMBERS TABLE. Her ruling is
          "tap a name anywhere and the modal opens right there, without leaving the tab", so it is
          rendered here, outside every tab branch, and a door on Messages or Probes opens the same
          one the Members table does.
          R221 — BOTH of her doors out go through the ONE closer. The backdrop tap and the Close
          button are two controls and one act, and a spelling that only cleared `detail` would let a
          read still in flight put the panel straight back up. */}
      {detail ? (
        <ModalBackdrop label="Member detail" className="owner-detail-backdrop" onClose={closeMemberDetail}>
          <OwnerMemberDetail detail={detail} onClose={closeMemberDetail} onDenied={() => setDenied(true)}
            renderReport={(d) => <ReportBody data={d as AnyReport} />} />
        </ModalBackdrop>
      ) : null}
    </section>
  );
}

// R? / ITEM 7 (2026-09-03) — **`ContactPage` MOVED VERBATIM to `src/components/ContactPage.tsx`**,
// see the import at the top of this file. **It is the door every member ACK-NOACCOUNT-1 dropped came
// through** — a contact row with no `user_id` IS this form with nobody signed in — and no fixture
// could reach that surface while it lived in here. The move buys the IMPORT, and the extraction is
// EARNED rather than argued for, which is roadmap 7's own method.

// Sign-in identity confirmation. Non-blocking on purpose: it floats, it never takes the screen,
// and it leaves on its own. Tapping it shows which account this is, because the email is the
// thing that answers "why is my room empty" and it should not be on screen until asked for.
// R5-1: `nameNumber` (a client-side Pythagorean name reduction) was removed here — zero call sites.
// The numerology room's numbers come from the server engine, which is the one answer key for them.

function MyRoomPage({
  member,
  profile,
  roomUnreachable: roomReadFailed = false,
  onRetryRoom,
  membershipKnown: knownAnswer = false,
  roomOrigin = "",
  isLoggedIn = false,
  journalEntries,
  journalNoteText = "",
  onDeleteEntry,
  books,
  activeBook,
  isMember,
  onRequireMembership,
  onLockedDoor,
  onProfileSaved,
  theme,
  onChooseTheme,
  goHome,
  goTo,
  language = DEFAULT_LANGUAGE,
}: {
  member: MemberSession | null;
  profile: RoomProfile | null;
  /** IT #326: her room read did not land. Only meaningful while profile is null. */
  roomUnreachable?: boolean;
  onRetryRoom?: () => void;
  membershipKnown?: boolean;
  /** HER CORRECTION (2026-08-19): the reading she clicked to arrive, "" for an organic signup. */
  roomOrigin?: string;
  isLoggedIn?: boolean;
  onProfileSaved: (profile: Partial<BirthChartForm> & { createdAt?: string }) => void;
  journalEntries: JournalEntry[];
  // R57: the one sentence her journal can say back to her, owned at the root and read here too.
  journalNoteText?: string;
  onDeleteEntry: (id: string) => Promise<boolean>;
  books: Book[];
  activeBook: Book;
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  // T1: hand a SERVER REFUSAL to the purple box, with the action to retry once she has joined.
  onLockedDoor: (lock: LockInfo, retry?: () => void) => void;
  // ITEM 6: her chosen theme, and the one writer of it. Both come from the root.
  theme: string;
  onChooseTheme: (theme: string) => void;
  goHome: () => void;
  goTo: (page: Page) => void;
  // ITEM 14(b) — her language, handed down from the root the way the theme is. Optional and English
  // by default, so nothing that renders this page without one moves.
  language?: string;
}) {
  const requireFeature = useRequireFeature();
  const firstName = memberFirstName(member, profile);
  const [savedBooks, setSavedBooks] = useState<ShelfBook[]>([]);
  const [selected, setSelected] = useState<ShelfBook | null>(null);
  const [readerPage, setReaderPage] = useState(0);
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [openReport, setOpenReport] = useState<AnyReport | null>(null);
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const [openNote, setOpenNote] = useState("");
  const [backgrounds, setBackgrounds] = useState<RoomArtRow[]>([]);
  // HER ORIENTATION LAW — her CURRENT art, which may not be in the viewport-scoped list above. The
  // room paints from THIS, and QC-8's version is read off it too (see `src/components/RoomArt.tsx`).
  const [chosenArt, setChosenArt] = useState<RoomArtRow | null>(null);
  const bgUrl = useCallback((slug: string) => {
    const base = String(slug).replace(/-v$/, "");
    const v = backgrounds.find((b) => b.slug === base)?.version
      ?? (chosenArt?.slug === base ? chosenArt.version : undefined);
    return v ? `/api/assets/room/${slug}?v=${v}` : `/api/assets/room/${slug}`;
  }, [backgrounds, chosenArt]);
  const [watching, setWatching] = useState<WatchedVideo[]>([]);
  const [playingVideo, setPlayingVideo] = useState<{ slug: string; title: string; startAt: number } | null>(null);
  // R55: T8-9's three-state, one panel over. "It arrived empty" and "it did not arrive" are not the
  // same sentence, and `videos.empty` states as fact something a failed read cannot know.
  const [watchLoad, setWatchLoad] = useState<"loading" | "ok" | "error">("loading");
  const [watchNote, setWatchNote] = useState("");
  // >>> **MYROOM-1: THREE COUNTERS, ONE PER PANEL, AND THE SPLIT IS THE ROUND'S ONE DECISION
  // (R221's OWN RULING). <<<** *Which watch-list is newest*, *which verse list is newest* and *which
  // bookmark list is newest* are three questions with three independent output states. Shared, a
  // bookmark removal supersedes an in-flight watching read and **Continue watching is stuck on
  // "loading" forever**, because that read is the only thing that ever calls `setWatchLoad("ok")` —
  // R221's M4 defect, one page over.
  const watchSeq = useRef(0);
  const loadWatching = async () => {
    // >>> **R216, AND `loadWatching` HAS THREE CALLERS, WHICH IS WHY NO CLEANUP FLAG COULD EVER HAVE
    // GUARDED IT.** <<< A `let active` flag lives in an effect's closure and is flipped by React's
    // own cleanup, so it can only ever guard reads THAT effect started. The other two are
    // `removeWatching`'s put-back and the player's `onClose` — and the ordinary sequence is: she
    // closes the player (a re-read goes out carrying video X), taps remove on X, the delete
    // SUCCEEDS, and **the older read lands last and puts X straight back on her screen**, deleted on
    // the server, with the note silent because nothing failed.
    const ticket = beginRead(watchSeq);
    const list = await listWatching();
    // **THE GUARD COMES BEFORE THE PAINT** (R53's one vacuous mutation, closed as arithmetic on
    // POSITION): a failed read is null and leaves Continue watching exactly as it was.
    // A superseded read is dropped WHOLE — the list, the face and the note together (ITEM 10's own
    // ruling: a stale note under fresh rows is the same lie one line down).
    if (!readMayPaint(watchSeq, ticket)) return;
    if (!list) { setWatchLoad("error"); return; }
    setWatchLoad("ok"); setWatching(list);
  };
  useEffect(() => { loadWatching(); }, []);
  // MR2: remove a video from Continue Watching (both-halves persistence: a member can undo it).
  // R55: R35 ruled this delete cheap enough not to ask, so the optimistic paint STAYS — what was
  // missing is the PUT-BACK. The put-back is a RE-READ rather than a splice, because the server is
  // what disagreed and it is what orders this list.
  async function removeWatching(slug: string) {
    setWatching((cur) => cur.filter((v) => v.slug !== slug));
    setWatchNote("");
    const answer = await deleteWatching(slug);
    if (answer.ok) return;
    setWatchNote(progressNote(answer, { signIn: t(language, "videos.signIn"), failed: t(language, "videos.removeFailed") }));
    loadWatching();
  }
  // R92 — **NULL UNTIL THE ACCOUNT ANSWERS** (the argument is in src/lib/room-state.ts). What the
  // room DRAWS is unchanged to the byte: `roomStateFrom(null)` IS the old initial value. What is new
  // is that a WRITE can tell the account's answer apart from the house's defaults.
  const [roomState, setRoomState] = useState<MyRoomState | null>(null);
  const room = roomState || roomStateFrom(null);
  const selectedBg = room.background;

  // P2-7: MY ROOM JOINS THE REFRESH REGISTRY. Her shelves and her room state are what goes stale
  // here (a reading finishing while she is looking at the room is the whole case), and both are
  // `no-store` reads already, so a pull genuinely re-fetches rather than re-reading a cache.
  useRefresh("my-room", async () => {
    const [list, state] = await Promise.all([listSavedReports<SavedReport>(), getRoomState()]);
    if (list) setReports(list);
    // A pull deliberately moves only the background (her open panels must not jump mid-session) —
    // but if nothing has landed yet, THIS is the answer landing, so it is taken whole.
    if (state) setRoomState((prev) => (prev ? { ...prev, background: roomStateFrom(state).background || prev.background } : roomStateFrom(state)));
  });

  // R60 — **THE GUARD IS BEFORE THE PAINT AND IT IS HELD BY POSITION** (R52/R53/R55). The picker is
  // only ever filled from an answer that arrived; a failed read leaves it alone and raises the one
  // sentence below, instead of opening the customize modal with nothing in it to choose.
  // The FAILURE is the state; the SENTENCE is derived at render, so switching language repaints it
  // rather than leaving the line she is looking at in the language she just left (R58's shape).
  const [bgFailed, setBgFailed] = useState(false);
  const bgNote = roomBackgroundsNote(bgFailed ? null : [], { failed: t(language, "customize.backgroundsFailed") });
  // HER ORIENTATION LAW — the picker asks for ITS viewport, the SERVER decides, re-asked on rotation.
  // >>> **MYROOM-1 FOUND THIS ONE, AND IT IS R216's CENTRAL ARGUMENT IN ITS PUREST FORM: A CLEANUP
  // FLAG GUARDS MOUNT AND UNMOUNT, NEVER ORDER.** <<< `load` has TWO callers — the immediate one and
  // the `matchMedia` change listener — so **a member rotating her phone and rotating back has two
  // reads in flight, `active` is true for both, and the older one lands last and paints the WRONG
  // ORIENTATION'S list.** That is her own reported room-art defect (*"vertical images offered on
  // DESKTOP, stretching blurry across the screen"*) reachable through the picker that was built to
  // end it. `active` STAYS: unmount and ordering are two questions (R18) and only the ref answers
  // the second.
  const artSeq = useRef(0);
  useEffect(() => {
    let active = true;
    const load = () => {
      const ticket = beginRead(artSeq);
      const vp = viewportFor(typeof window === "undefined" ? 1440 : window.innerWidth);
      void listRoomBackgrounds<RoomArtRow>(fetch, vp).then((answer) => {
        if (!active) return;
        if (!readMayPaint(artSeq, ticket)) return;
        if (answer) { setBackgrounds(answer.backgrounds); setChosenArt(answer.chosen); }
        setBgFailed(answer === null);
      });
    };
    load();
    const media = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(ROOM_ART_MOBILE_MEDIA) : null;
    media?.addEventListener?.("change", load);
    return () => { active = false; media?.removeEventListener?.("change", load); };
  }, []);

  // Room state (background, open/minimized panels, first-visit flag) loads from
  // the member's profile on every visit, so the room is identical on any device.
  useEffect(() => {
    let active = true;
    void getRoomState().then((state) => {
      if (active && state) setRoomState(roomStateFrom(state));
    });
    return () => { active = false; };
  }, []);

  // R51 — **THE ANSWER IS READ, AND THE SERVER'S OWN SENTENCE FINALLY REACHES HER.** Five surfaces
  // write through here (her background, every panel she opens or collapses, both one-time pointers
  // and her room name) and this route can refuse FOUR ways — 401, 400, 500, and HTTP 200 with
  // `{ ok: false }` — none of which was read at all: the whole body was `.catch(() => {})`. The
  // screen has already changed by the time this runs, so a refusal she cannot see leaves the room
  // and the account disagreeing until the next reload, if ever.
  //
  // A SUCCESS SAYS NOTHING. `roomStateNote` answers "" for an ok, which clears the line — "Saved"
  // under every panel she collapses is noise, and this is the one route that fires on an ordinary tap.
  const [roomSaveNote, setRoomSaveNote] = useState("");
  // R92: `landed` is the account's own answer (null when it has not arrived) and `key` is the ONE
  // field this surface owns — the partial patch this transport's header has always required.
  function persistRoomState<K extends RoomStateKey>(landed: MyRoomState | null, key: K, value: MyRoomState[K]) {
    const write = roomStateWrite(landed, key, value);
    if (!write.write) {
      setRoomSaveNote(roomStateWriteNote(write, { notLanded: t(language, "room.saveNotLoaded") }));
      return;
    }
    void postRoomState(write.patch).then((answer) => {
      setRoomSaveNote(roomStateNote(answer, {
        signIn: t(language, "room.saveSignIn"),
        failed: t(language, "common.saveFailed"),
      }));
    });
  }

  function chooseBackground(slug: string) {
    setRoomState((current) => {
      const now = current || roomStateFrom(null);
      const next = { ...now, background: now.background === slug ? "" : slug };
      persistRoomState(current, "background", next.background);
      return next;
    });
  }

  const panelCollapsed = (key: string) => panelCollapsedIn(room, key);
  // `panels` is the one key a partial patch alone could not have saved — the jsonb `||` is a
  // TOP-LEVEL merge, so this object is replaced wholesale either way. It is the sharpest case for
  // refusing to write from a payload that never arrived.
  function togglePanel(key: string) {
    setRoomState((current) => {
      const now = current || roomStateFrom(null);
      const next = { ...now, panels: { ...now.panels, [key]: !panelCollapsedIn(now, key) } };
      persistRoomState(current, "panels", next.panels);
      return next;
    });
  }

  function dismissWelcome() {
    setRoomState((current) => {
      persistRoomState(current, "welcomed", true);
      return { ...(current || roomStateFrom(null)), welcomed: true };
    });
  }

  // R6: the one-time Reports pointer, dismissed the same way (profile-persisted).
  function dismissReportsPointer() {
    setRoomState((current) => {
      persistRoomState(current, "reportsSeen", true);
      return { ...(current || roomStateFrom(null)), reportsSeen: true };
    });
  }

  // SH2: a one-time first-visit pointer to the Shelves drawer (client-remembered, like a tip).
  const [shelvesSeen, setShelvesSeen] = useState(() => {
    // SAFE-STORAGE-1: through the ONE door, like every other store in this house. A `try` around
    // a bare global is the RIGHT position and still the wrong shape — the next call site written
    // from this one as a pattern is one argument away from the blank page (R52/R138).
    return safeLocalStorage()?.getItem("12h_shelves_seen") === "1";
  });
  function dismissShelvesPointer() {
    setShelvesSeen(true);
    safeLocalStorage()?.setItem("12h_shelves_seen", "1");
  }

  // S1: the member renames their room; the name persists to their profile (room_state) and
  // shows everywhere the room name appears. Empty reverts to the default "<First>'s room".
  const [editingRoomName, setEditingRoomName] = useState(false);
  const [roomNameDraft, setRoomNameDraft] = useState("");
  function saveRoomName() {
    const trimmed = roomNameDraft.trim();
    setRoomState((current) => {
      persistRoomState(current, "roomName", trimmed);
      return { ...(current || roomStateFrom(null)), roomName: trimmed };
    });
    setEditingRoomName(false);
  }

  // N1-4 — THE MEMBER EDITS THE DETAILS THEY SIGNED UP WITH, from the identity strip, the same way the
  // room name is editable. It reuses CreateRoomForm whole, so the middle-name law, the unknown-birth-time
  // rule and the real geocoded birthplace picker are the SAME ones they signed up with rather than a
  // second form that could drift from them.
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState<BirthChartForm>(initialBirthChart);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [detailsNote, setDetailsNote] = useState("");
  // N19-1 REMAINDER — THE ONE-TIME ASK. Shown only when the SERVER says she has never been asked and
  // has no current location on record, so her transits are running on her birthplace. Either answer
  // records the ask, so the room never raises it twice; "Not now" is a real answer, not a snooze.
  const [locationAskAnswered, setLocationAskAnswered] = useState(false);
  const showLocationAsk = Boolean(profile?.needsCurrentLocation) && !locationAskAnswered && !editingDetails;
  function answerLocationAsk(openEditor: boolean) {
    setLocationAskAnswered(true);
    fetch("/api/profile/location-prompted", { method: "POST" }).catch(() => {});
    if (openEditor) openDetailsEdit();
  }

  function openDetailsEdit() {
    setDetailsDraft({
      ...initialBirthChart,
      ...(profile || {}),
      // A saved row with a middle name never wants the "I don't have one" box ticked.
      noMiddleName: !((profile?.middleName || "").trim()),
      intention: "",
    } as BirthChartForm);
    setDetailsNote("");
    setEditingDetails(true);
  }
  const updateDetailsDraft = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) =>
    setDetailsDraft((current) => ({ ...current, [field]: value }));
  async function saveDetails(event: FormEvent) {
    event.preventDefault();
    setDetailsBusy(true);
    setDetailsNote(t(language, "details.recasting"));
    try {
      const r = await fetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chart: detailsDraft }),
      });
      const d = await r.json().catch(() => ({}));
      if (!d?.ok) { setDetailsNote(d?.message || t(language, "details.failed")); return; }
      // OV2-2 ROOT CAUSE: this used to call `setProfile`, which does not exist in this scope. The
      // save SUCCEEDED server-side and then the handler threw a ReferenceError before the success
      // note ran, so she was told her details could not be saved on a save that had worked. The
      // profile lives in App, so the update goes back up through the prop.
      if (d.profile) onProfileSaved(d.profile);
      setDetailsNote(d.message || t(language, "details.saved"));
      setEditingDetails(false);
    } catch {
      setDetailsNote(t(language, "details.failed"));
    } finally {
      setDetailsBusy(false);
    }
  }

  // N18-1's three rosters — the signup set, the free first tap, the on-demand pair — are DATA and
  // live in src/lib/room-reports.ts, where a gate can load them (R37/R42).

  // N18-2's law now lives in src/lib/room-reports.ts, beside the roster it reads, where a gate runs it.
  const arrivalPointerOn = arrivalPointerShows(!!profile, room.reportsSeen, reports, isPersonSubject);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    // N18-1: only the SIGNUP SET is being written for her, so only those two are worth refreshing
    // for. Polling for human design would never resolve — nothing is generating it until she taps.
    const expected = ["birth-chart", "numerology"];
    async function load() {
      // X1-REOPEN follow-up: the identity strip + Sun/Moon/Rising chips are derived from this list,
      // so it must never be served from a stale HTTP cache — a member could otherwise sit on an old
      // strip after a fix or a birth-detail edit. The transport reads `no-store` for that reason.
      // R53: this used to fall back to `[]` and `setReports` it, so ONE lost request emptied her
      // whole room. A failed read leaves what is on screen standing, and still schedules the retry,
      // because nothing was learned either way.
      const list = await listSavedReports<SavedReport>();
      if (!active) return;
      if (list) setReports(list);
      // Keep refreshing briefly while a freshly-created room's reports land.
      const have = new Set((list || []).map((x) => x.type));
      if (profile && !expected.every((t) => have.has(t)) && Date.now() - startedAt < 3 * 60 * 1000) {
        timer = setTimeout(load, 8000);
      }
    }
    load();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [profile]);

  // R53 — **A TAP THAT OPENS NOTHING NOW SAYS SO.** This was `if (!response.ok) return;` with
  // `catch { /* leave closed */ }` after it: she taps a reading she paid a generation for and the
  // screen simply does not change, with no way to tell a broken tap from a slow one. That is R25's
  // argument (an absent control has no error state) applied to an absent RESULT. The sentence lands
  // ABOVE the panels and never inside a 190px report chip — R35's capture photographed a confirm
  // clipped by one of those, and a line she cannot read is worse than no line.
  async function openSavedReport(id: string) {
    const answer = await fetchSavedReport<AnyReport>(id);
    setOpenNote(openReportNote(answer, { signIn: t(language, "common.openSignIn"), failed: t(language, "common.openFailed") }));
    if (!answer.ok) return;
    setOpenReport(answer.data);
    setOpenReportId(id);
  }

  // R35: THE ROW COMES OFF HER SCREEN ONLY IF THE SERVER REALLY DELETED IT. This used to catch the
  // fetch, ignore it, and filter the row out regardless — so a signed-out session or a database
  // outage looked byte-for-byte like a successful delete, and the reading came back on reload with
  // nothing ever having said no. The boolean is what raises DeleteControl's "That did not delete."
  async function deleteSavedReport(id: string): Promise<boolean> {
    if (!(await deleteSavedItem("report", id))) return false;
    setReports((current) => current.filter((entry) => entry.id !== id));
    if (openReportId === id) {
      setOpenReport(null);
      setOpenReportId(null);
    }
    return true;
  }

  // R38: the same law as the shelf drawer's rename, on the surface she actually lands on. The STORED
  // title is what the row takes, and null is what raises the control's failure sentence.
  async function renameSaved(id: string, title: string): Promise<string | null> {
    const stored = await renameSavedReport(id, title);
    if (stored) setReports((current) => current.map((entry) => (entry.id === id ? { ...entry, title: stored } : entry)));
    return stored;
  }

  useEffect(() => {
    let active = true;
    // R59: the guard is BEFORE the paint (R52's position law) and null is NOT an empty shelf.
    void listSavedBooks().then((rows) => {
      if (active && rows) setSavedBooks(shelfBooksFrom(rows));
    });
    return () => { active = false; };
  }, []);

  // Identity-bar data: pull the saved birth-chart report for Sun/Moon/Rising and
  // a rotating set of chart facts.
  const [chartSummary, setChartSummary] = useState<{ sun?: string; moon?: string; rising?: string; facts: string[] }>({ facts: [] });
  const [factIndex, setFactIndex] = useState(0);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [dailyCardOpen, setDailyCardOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState<SavedBookmark[]>([]);
  const [featuredMap, setFeaturedMap] = useState<Record<string, FeaturedBook>>({});
  const [bmReader, setBmReader] = useState<{ featured: boolean; book: FeaturedBook | ShelfBook; page: number } | null>(null);
  const [bmTextPage, setBmTextPage] = useState(0);

  // R59: a failed read is not an empty shelf of bookmarks — what a bookmark holds is the page she
  // stopped on, and the list it would have replaced was correct a second earlier.
  // >>> **MYROOM-1 — THE SHARPEST OF THE THREE, BECAUSE `removeBookmark` RE-READS UNCONDITIONALLY.
  // <<<** Every remove fires one, so a member tidying her shelf — which is the ordinary way anybody
  // uses this panel — has two in flight at once, and the older one landing last **puts the mark she
  // removed second back on her screen**, gone from the database, with no note because nothing
  // failed. R59's put-back defeated by ordering, which is R216's finding on the Spellbook exactly.
  const bookmarkSeq = useRef(0);
  const reloadBookmarks = () => {
    const ticket = beginRead(bookmarkSeq);
    return listBookmarks<SavedBookmark>()
      .then((rows) => { if (readMayPaint(bookmarkSeq, ticket) && rows) setBookmarks(rows); });
  };
  // R61: the one line the bookmarks panel says when a mark would not come off.
  const [bookmarkNoteText, setBookmarkNoteText] = useState("");

  // Verses saved from the right-edge Verse Search drawer collect here in My Room.
  const [savedVerses, setSavedVerses] = useState<SavedVerse[]>([]);
  // R56: the chip carries the ANSWER, not a boolean — a boolean cannot say "it did not go".
  const [verseCopied, setVerseCopied] = useState<{ id: number; state: CopyState } | null>(null);
  // R54: the one line this panel says when an unpin did not take.
  const [verseNoteText, setVerseNoteText] = useState("");
  // R54: a failed read is NOT an empty collection (R18) — this used to `setSavedVerses([])` on any
  // bad answer, so one lost request blanked her whole saved-verse panel.
  // MYROOM-1: two callers (the mount read and `removeSavedVerse`'s put-back), same class — the mount
  // read landing after an optimistic unpin puts her verse back on a list the server no longer has it
  // on. R54's put-back is untouched; what is added is that only the NEWEST read may paint.
  const verseSeq = useRef(0);
  const reloadSavedVerses = () => {
    const ticket = beginRead(verseSeq);
    return listSavedVerses().then((list) => {
      if (readMayPaint(verseSeq, ticket) && list) setSavedVerses(list);
    });
  };
  useEffect(() => { void reloadSavedVerses(); }, []);
  // R54 — **THE OPTIMISTIC PAINT IS KEPT AND THE PUT-BACK IS WHAT WAS MISSING.** R35 ruled this
  // delete right not to ask (the verse itself is untouched; the pin is one tap to restore), and a
  // screen that runs slightly ahead of the database is a nice feeling. A screen that STAYS wrong is
  // a lie — and the server already answered `{ ok: <did a row go> }` here while the client threw it
  // away with `.catch(() => {})`.
  // THE PUT-BACK IS A RE-READ, not a splice: the server is what disagreed, so the server is what
  // says where the row goes. Re-inserting from memory would put her verse back in the wrong place
  // on a list that is served newest-first — and if the re-read itself fails it answers null and the
  // panel simply keeps what it is showing (R18), which is the right thing twice over.
  const removeSavedVerse = async (id: number) => {
    setVerseNoteText("");
    setSavedVerses((current) => current.filter((v) => v.id !== id));
    const answer = await deleteSavedVerse(id);
    if (answer.ok) return;
    setVerseNoteText(verseNote(answer, { signIn: t(language, "verses.signIn"), failed: t(language, "verses.removeFailed") }));
    void reloadSavedVerses();
  };
  // R56: the write is AWAITED and the chip is derived from its answer. It used to be
  // `.catch(() => {})` with `setVerseCopied` on the next line, so a browser with no Clipboard API
  // short-circuited to `undefined` and the chip said Copied over nothing at all.
  const copySavedVerse = async (v: { id: number; reference: string; verseText: string; source: string }) => {
    const answer = await copyText(`"${v.verseText}" (${v.reference}, ${v.source})`);
    setVerseCopied({ id: v.id, state: copyState(answer) });
    window.setTimeout(() => setVerseCopied(null), COPY_FLASH_MS);
  };

  useEffect(() => {
    reloadBookmarks();
    // R59: null keeps the map she already had — it is readerKindFor's own input, so blanking it
    // turns every saved featured book back into a text-reader dead end (W5-2's whole point).
    void listFeaturedBooks<FeaturedBook>().then((rows) => {
      if (!rows) return;
      const m: Record<string, FeaturedBook> = {};
      rows.forEach((b) => { m[b.slug] = b; });
      setFeaturedMap(m);
    });
  }, []);

  // W1-6: her tap lands on her page. The two readers count differently; both conversions are the
  // shared law's now, not an inline offset here.
  const openBookmark = (bm: SavedBookmark) => {
    const feat = featuredMap[bm.bookSlug];
    if (feat) {
      setBmReader({ featured: true, book: feat, page: toImagePage(bm.page, feat.pageCount || bm.page) });
    } else {
      const shelf: ShelfBook = { slug: bm.bookSlug, title: bm.bookTitle, author: null, mediaType: "book", categories: [], pageCount: 0 };
      // The text reader learns its page count on load, so the clamp here is against the mark itself.
      setBmTextPage(toReaderIndex(bm.page, bm.page));
      setBmReader({ featured: false, book: shelf, page: bm.page });
    }
  };

  // R61 — R35 ruled this delete cheap enough not to ASK (`NO_ASK_BY_DESIGN`: a mark is re-made by
  // turning to the page again). **THAT RULING IS ABOUT THE CONFIRM AND NEVER ABOUT THE ANSWER**, and
  // the server has always sent one — `{ ok: <did a row go> }`, off a real `rowCount` — which this
  // threw away with `.catch(() => {})`. R54's finding on the verse unpin, one list over. The re-read
  // stays (the server is what disagreed, so the server says what the list is); the sentence is what
  // was missing.
  const removeBookmark = async (id: number) => {
    setBookmarkNoteText("");
    const answer = await deleteBookmark(id);
    if (!answer.ok) setBookmarkNoteText(bookmarkNote(answer, { signIn: t(language, "bookmark.signIn"), failed: t(language, "bookmark.removeFailed") }));
    reloadBookmarks();
  };
  const [genState, setGenState] = useState<"idle" | "generating" | "error">("idle");

  useEffect(() => {
    // X1: the member's OWN birth chart, selected by SUBJECT (their profile row), never by type
    // alone. A saved person-of-interest's chart is the same `type: "birth-chart"` and (being newer)
    // sorted first, so the unguarded find put THEIR name + placements on the member's own room. The
    // room identity strip and the header Sun/Moon/Rising both read this, so both were wrong.
    const chartMeta = reports.find((entry) => entry.type === "birth-chart" && entry.subject === "profile");
    if (!chartMeta) {
      setChartSummary({ facts: [] });
      return;
    }
    let active = true;
    // X1-REOPEN follow-up: revalidate the chart data itself on room load (no stale cache), so the
    // chips/facts always reflect the current stored chart after a fix or a birth-detail edit.
    // R53: this read joins the transport for its answer-reading and deliberately says NOTHING on a
    // failure — she did not ask for it, so the strip stays exactly as it is (R51's rule: a sentence
    // belongs to an answer she is waiting on).
    void fetchSavedReport<{ planets?: Array<{ name: string; sign: string; house?: number }>; houses?: Array<{ house: number; sign: string }>; overview?: string }>(chartMeta.id)
      .then((answer) => {
        if (!active || !answer.ok) return;
        const data = answer.data;
        const planets = data.planets || [];
        const sun = planets.find((p) => p.name === "Sun")?.sign;
        const moon = planets.find((p) => p.name === "Moon")?.sign;
        const rising = (data.houses || []).find((h) => h.house === 1)?.sign;
        const facts: string[] = [];
        if (data.overview) {
          // IPAD-BLANK-1: the first sentence, WITHOUT a lookbehind. Safari could not parse one
          // before 16.4, and esbuild ships an unsupported literal as `new RegExp(...)`, which turns
          // it into a runtime throw. This one sits inside a handler rather than at module level, so
          // it blanked a reading rather than the whole app — the same defect, one blast radius down.
          // `match` keeps the terminator; `split` on a lookbehind dropped only the space after it.
          const first = (String(data.overview).match(/^[\s\S]*?[.!?](?=\s|$)/) || [String(data.overview)])[0].trim();
          if (first) facts.push(first);
        }
        for (const p of planets.slice(0, 6)) {
          facts.push(`Your ${p.name} is in ${p.sign}${p.house ? ` in the ${ordinalLabel(p.house)} house` : ""}.`);
        }
        setChartSummary({ sun, moon, rising, facts });
      });
    return () => { active = false; };
  }, [reports]);

  useEffect(() => {
    if (chartSummary.facts.length < 2) return;
    const timer = setInterval(() => setFactIndex((value) => value + 1), 6500);
    return () => clearInterval(timer);
  }, [chartSummary.facts]);

  const currentFact = chartSummary.facts.length ? chartSummary.facts[factIndex % chartSummary.facts.length] : "";
  // SH1: My Room shows only what the member chose to keep here. Shelf-only readings (auto-saved
  // on-demand, inMyRoom === false) live on their ROOM shelf, not in the My Room aggregate.
  const tarotReadings = reports.filter((entry) => entry.type === "tarot" && entry.inMyRoom !== false);
  const [genType, setGenType] = useState("");

  // Distinguish a room build actively in progress (show "still being prepared")
  // from an established room missing a report (offer to generate it).
  const [buildingActive, setBuildingActive] = useState(false);
  useEffect(() => {
    function check() {
      // SAFE-STORAGE-1: no store at all reads as "no build in progress", which is what the catch
      // below already said — the door just says it without a throw to swallow first.
      const until = Number(safeSessionStorage()?.getItem("12h_room_building_until") || "0");
      setBuildingActive(Date.now() < until);
    }
    check();
    const timer = setInterval(check, 5000);
    return () => clearInterval(timer);
  }, []);

  // On-demand reports (beyond the free three) require membership. The three
  // foundational reports are generated free at room creation, not here.
  // G1 PART A, the owner's live complaint. `genType` is React state, so it is gone the moment she
  // leaves My Room or refreshes, and the button drops back to "Generate" while the reading is
  // still being made. The server knows what is actually running (slice 1), so the button asks it.
  // Local state still drives the instant feedback on the press; server truth keeps it honest
  // after that, and outlives the page.
  // The id is the server's generation key, which is built as `${kind}:${hash}` (genKeyFor), and
  // this handler registers each report under its own type as the kind. So the prefix IS the type.
  const { active: activeGens } = useActiveGenerations(isMember);
  // T1-FIX: the server's answer on what is free at any tier. See useEntitlements.
  const entitlements = useEntitlements();
  const isGenerating = (type: string) => genType === type || activeGens.some((g) => g.id.startsWith(`${type}:`));

  // T1-FIX — THE FREE LAYER NEVER RAISES A BOX.
  //
  // The bug the owner hit live: this routed EVERY report type through the membership check, which
  // had never heard of T1-5. Her OWN signup Birth Chart and Numerology, and Human Design on its
  // free first tap, are hers at any tier. Writing them, re-opening them and downloading them are
  // the same entitlement, and not one of them is a purchase.
  //
  // WHICH TYPES ARE FREE IS THE SERVER'S ANSWER, never a list kept here: `freeReportTypes` comes
  // from /api/membership/entitlements, which reads the one tier config. A second copy of the rule
  // on the client is a copy that drifts, and this bug is exactly what that costs.
  //
  // R91 — **A READ THAT NEVER LANDED IS NOT "NOTHING IS FREE"**, and the forged `[]` put the bug
  // above back on any dropped packet. The free layer is free at ANY tier and a second copy of which
  // types those are is forbidden above, so an unknown answer goes to the SERVER, the answer key.
  function generateReport(type: string) {
    if (!entitlements || entitlements.freeReportTypes.includes(type)) {
      void runGenerateReport(type);
      return;
    }
    onRequireMembership(() => runGenerateReport(type));
  }

  async function runGenerateReport(type: string) {
    setGenType(type);
    setGenState("generating");
    try {
      const response = await fetch("/api/room/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
      });
      const data = await response.json().catch(() => ({}));
      // T1: a 402 is a LOCKED DOOR, not a failure. It carries the feature, the tier that opens it,
      // the ladder and the free layer, so the purple box renders exactly the door she touched — and
      // the retry is remembered, so joining takes her straight through it.
      if (response.status === 402 && data?.locked) {
        setGenState("idle");
        setGenType("");
        onLockedDoor(data, () => { void runGenerateReport(type); });
        return;
      }
      if (!response.ok || !data.ok) throw new Error();
      const list = await listSavedReports<SavedReport>();
      if (list) setReports(list);
      setGenState("idle");
      // SD1: the slot said "Open", so it OPENS. A member who taps Open and is handed a chip that
      // merely changed colour has been made to tap twice for one thing she already asked for.
      const made = (list || []).find((r) => r.type === type && r.inMyRoom !== false && !isPersonSubject(r.subject));
      if (made?.id) await openSavedReport(String(made.id));
    } catch {
      setGenState("error");
    } finally {
      setGenType("");
    }
  }

  const [daily, setDaily] = useState<DailyGuidance | null>(null);
  const [dailyStatus, setDailyStatus] = useState<"idle" | "loading">("idle");
  // R75: what the LAST attempt said. A read that landed empty is the normal morning and says nothing.
  const [dailyAnswer, setDailyAnswer] = useState<DailyAnswer<DailyGuidance> | null>(null);

  // >>> **IT #171/#172 — "THE EXACT SAME WORDS EVERY SINGLE DAY", REPORTED FOUR TIMES, AND EVERY
  // SERVER SURFACE WAS INNOCENT.** <<<
  //
  // This said *"loads fresh every visit"* and the dependency array said `[]`, which is **once per
  // MOUNT** — not once per visit. On the web those are nearly the same thing. **In an installed PWA
  // on an iPhone or iPad they are not:** the app is resumed from the background for days without the
  // React tree ever unmounting, so this ran on the day she installed it and never again. She saw one
  // morning's guidance for a week.
  //
  // **AND IT SURVIVED EVERY REPAIR SHE WAS ASKED TO TRY**, which is why it was reported four times
  // and closed three: a pull-to-refresh inside a standalone PWA does not reload the document, and
  // signing out and back in does not remount a component this high in the tree. Only force-quitting
  // the app fixed it, and nobody thinks to ask that.
  //
  // Measured on her account the day this was written: the server held a DIFFERENT, correctly dated
  // guidance for every one of the last ten days, `/api/room/daily` returned today's row (id 5856,
  // 2026-08-31) on demand, `/api/tarot/daily` returned today's card, the service worker never caches
  // the API, and nothing is kept in localStorage. Every instrument said fresh. She was still right.
  //
  // **SO IT RE-READS WHEN SHE COMES BACK TO THE APP**, which is the moment the day can have rolled
  // over underneath a process that never stopped running. Throttled, because a resume is common and
  // this is a GET that would otherwise fire on every glance; the row is already cached server-side,
  // so the cost of the read itself is a lookup.
  useEffect(() => {
    let active = true;
    let lastRead = 0;
    const RE_READ_AFTER_MS = 5 * 60 * 1000;

    const load = () => {
      lastRead = Date.now();
      void readDailyGuidance(fetch, dailyIdFromSearch(window.location.search)).then((a) => {
        if (!active) return;
        setDailyAnswer(a);
        if (a.status === "ok") setDaily(a.value);
      });
    };
    load();

    // **VISIBILITY AND FOCUS BOTH, BECAUSE THE TWO PLATFORMS DISAGREE ABOUT WHICH ONE A RESUME
    // FIRES.** iOS standalone reliably fires `visibilitychange`; a desktop tab returning from
    // another window fires `focus`. Listening for one of them is how this would go on being broken
    // for half the members who reported it.
    const again = () => {
      if (!active) return;
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRead < RE_READ_AFTER_MS) return;
      load();
    };
    document.addEventListener("visibilitychange", again);
    window.addEventListener("focus", again);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", again);
      window.removeEventListener("focus", again);
    };
  }, []);

  // R75: this is the one control on this site whose every tap costs two vendor calls and an engine
  // call, so a refusal it swallows is a refusal she pays to repeat. The answer is read, and it is
  // read BEFORE the button goes live again.
  async function getDaily() {
    setDailyStatus("loading");
    const answer = await runDailyGuidance();
    setDailyAnswer(answer);
    if (answer.status === "ok") setDaily(answer.value);
    setDailyStatus("idle");
  }

  // People of interest.
  // I5: hasChart / chartReportId / chartPossible are computed SERVER-side from the one DB truth,
  // so the button reflects reality rather than anything the client tracks.
  type RoomPerson = PersonForm & { id: string; hasChart?: boolean; chartReportId?: string | null; chartPossible?: boolean; hasCompat?: boolean; compatReportId?: string | null };
  const [people, setPeople] = useState<RoomPerson[]>([]);
  const [chartBusy, setChartBusy] = useState("");
  const [chartNote, setChartNote] = useState("");
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [personForm, setPersonForm] = useState<PersonForm>(EMPTY_PERSON);
  const [personSaving, setPersonSaving] = useState(false);
  const [relBusy, setRelBusy] = useState("");
  const [editingPerson, setEditingPerson] = useState<(PersonForm & { id: string }) | null>(null);

  // People load fresh from the DB on every room visit (not from stale state), and
  // the endpoint returns [] when there is no member, so no profile gate is needed.
  useEffect(() => {
    let active = true;
    void listPeople<RoomPerson>().then((list) => { if (active && list) setPeople(list); });
    return () => { active = false; };
  }, []);

  // R52 — **THE FRONT DOOR OF THIS FEATURE READ NOTHING BUT SUCCESS.** `if (data.ok && data.person)`
  // had no else, so I6 Rule 3's duplicate — the answer this route exists to give — did nothing at
  // all here: no prompt, no sentence, the form still open with her details in it. Rule 3's prompt
  // was built and lived on the OTHER surface that posts this route (R26/R47's shape). The answer is
  // read now, all five ways, and the question is asked rather than swallowed.
  const [dupePerson, setDupePerson] = useState<SubjectPickerPerson | null>(null);
  const [personNoteText, setPersonNoteText] = useState("");
  function savePerson(event: FormEvent) { event.preventDefault(); void submitPerson(); }
  async function submitPerson(onDuplicate?: DuplicateChoice) {
    const fullName = combineName(personForm.firstName, personForm.middleName, personForm.lastName) || personForm.name;
    if (!fullName.trim()) return;
    setPersonSaving(true);
    const answer = await addPerson<RoomPerson>({ ...personForm, name: fullName, ...(onDuplicate ? { onDuplicate } : {}) });
    setPersonNoteText(personNote(answer, { signIn: t(language, "people.saveSignIn"), failed: t(language, "common.saveFailed") }));
    if (answer.ok) {
      setPeople((current) => [answer.person, ...current]);
      setPersonForm(EMPTY_PERSON);
      setShowAddPerson(false);
      setDupePerson(null);
    } else if (answer.kind === "duplicate") {
      setDupePerson(answer.existing as unknown as SubjectPickerPerson);
    }
    setPersonSaving(false);
  }

  // R35: removing someone is the most expensive delete on this site — the server invalidates their
  // rendered chart and deletes the compatibility reading keyed to their row as well. It asks first
  // now, and the card comes off her screen only if the server really took it.
  async function removePerson(id: string): Promise<boolean> {
    if (!(await deletePerson(id))) return false;
    setPeople((current) => current.filter((p) => p.id !== id));
    return true;
  }

  // X3: RUN / READ, one control. If a compat report already exists for this person, open the
  // stored one (no re-pull, no money); otherwise run it, then open the fresh one. Either way the
  // button lands on "Read Report" because the People list re-reads the DB truth.
  async function runOrViewCompat(person: { id: string; hasCompat?: boolean; compatReportId?: string | null }) {
    if (person.hasCompat && person.compatReportId) {
      await openSavedReport(person.compatReportId);
      return;
    }
    setRelBusy(person.id);
    try {
      const response = await fetch("/api/room/relationship", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ personId: person.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (data.ok) {
        const list = await listSavedReports<SavedReport>();
        if (list) setReports(list);
        // I5: that compatibility just cast this person, so their button has become "View
        // Birth Chart". Re-read the list so it flips here and now, with no refresh trick.
        refreshPeople();
        // X3: open the fresh reading straight away, and the compat button becomes "Read Report".
        if (data.reportId) await openSavedReport(data.reportId);
      }
    } catch {
      /* ignore */
    }
    setRelBusy("");
  }

  function refreshPeople() {
    void listPeople<RoomPerson>().then((list) => { if (list) setPeople(list); });
  }

  // I5: RUN / VIEW, NEVER RE-PULL. One control: it runs the chart the first time and opens the
  // stored one every time after. The server decides which, because the server holds the truth.
  async function runOrViewChart(person: { id: string; name: string; hasChart?: boolean }) {
    setChartBusy(person.id);
    setChartNote("");
    try {
      // R52: the CHART door already read every answer this route gives (401, `!data.ok`, the
      // server's message), so only its PATH joins the transport — it is a paid generation with its
      // own payload, not a fifth copy of the CRUD answer.
      const response = await fetch(personChartPath(person.id), { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setChartNote(t(language, "people.signIn")); return; }
      if (!data.ok || !data.report) {
        setChartNote(data.message || t(language, "people.chartFailed", { name: person.name }));
        return;
      }
      setOpenReport(data.report as AnyReport);
      setOpenReportId(data.reportId || null);
      // The chart now exists, so the button becomes "View" and it joins Your Reports.
      refreshPeople();
      const list = await listSavedReports<SavedReport>();
      if (list) setReports(list);
    } catch {
      setChartNote(t(language, "people.chartFailed", { name: person.name }));
    } finally {
      setChartBusy("");
    }
  }

  return (
    <>
      {/* HER ORIENTATION LAW: backdrop and thumbnails ask ONE question, so both live in RoomArt.tsx. */}
      <RoomBackdrop slug={selectedBg} art={chosenArt} url={bgUrl} />
      <section className="page-shell my-room">
      <div className="my-room__top">
        <button className="back-button" type="button" onClick={goHome}>
          <ArrowLeft size={18} />
          {t(language, "common.backHome")}
        </button>
        <div className="room-customize-wrap">
          <button
            className={profile && !room.welcomed ? "room-customize-btn room-customize-btn--pulse" : "room-customize-btn"}
            type="button"
            onClick={() => setCustomizeOpen(true)}
          >
            ✦ {t(language, "room.customize")}
          </button>
          {profile && !room.welcomed ? (
            <div className="room-pointer" role="note">
              <span className="room-pointer__arrow" aria-hidden="true">▲</span>
              <span className="room-pointer__text">{t(language, "room.pointer.customize")}</span>
              <button type="button" className="room-pointer__dismiss" onClick={dismissWelcome} aria-label={t(language, "common.dismiss")}>{t(language, "common.gotIt")}</button>
            </div>
          ) : null}
        </div>
      </div>

      {/* SH2: first-visit pointer to the Shelves drawer, alongside the report + customize pointers.
          N21-3: it now sits BESIDE the Shelves rail and points at it. The text and the Got it share a
          column so the arrow can hold the right edge on its own, level with the tab it means.
          IT QUEUES BEHIND THE CUSTOMIZE POINTER: measured, the two want the same top-right corner and
          overlapped by 56px there. Both are one-time pointers, so they take turns rather than fight
          for the space, and three glowing boxes never land on a member at once. */}
      {profile && room.welcomed && !shelvesSeen ? (
        <div className="room-pointer room-pointer--shelves" role="note">
          <span className="room-pointer__body">
            <span className="room-pointer__text">{t(language, "room.pointer.shelves")}</span>
            <button type="button" className="room-pointer__dismiss" onClick={dismissShelvesPointer} aria-label={t(language, "common.dismiss")}>{t(language, "common.gotIt")}</button>
          </span>
          <span className="room-pointer__arrow room-pointer__arrow--right" aria-hidden="true">→</span>
        </div>
      ) : null}

      {editingRoomName ? (
        <div className="my-room__name-edit">
          <input
            className="my-room__name-input"
            value={roomNameDraft}
            onChange={(e) => setRoomNameDraft(e.target.value)}
            placeholder={t(language, "room.defaultName", { name: firstName })}
            maxLength={60}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") saveRoomName(); if (e.key === "Escape") setEditingRoomName(false); }}
          />
          <button type="button" className="button button--primary" onClick={saveRoomName}>{t(language, "common.save")}</button>
          <button type="button" className="button button--ghost" onClick={() => setEditingRoomName(false)}>{t(language, "common.cancel")}</button>
        </div>
      ) : (
        <h1 className="my-room__name">
          {room.roomName || t(language, "room.defaultName", { name: firstName })}
          <button
            type="button"
            className="my-room__name-editbtn"
            onClick={() => { setRoomNameDraft(room.roomName || t(language, "room.defaultName", { name: firstName })); setEditingRoomName(true); }}
            aria-label={t(language, "room.rename")}
            title={t(language, "room.rename")}
          >
            ✎
          </button>
        </h1>
      )}

      {/* R51 — THE ONE LINE THIS ROOM SAYS WHEN THE ACCOUNT DID NOT TAKE THE CHANGE. It sits under
          the name because the name is the loudest of the five things written through this route, and
          it carries the SERVER'S OWN sentence for a refusal (the only one that can say why). A
          success clears it, so it is never on screen when there is nothing to say. */}
      {roomSaveNote ? <p className="room-identity__saved" role="status">{roomSaveNote}</p> : null}

      {/* >>> HER CORRECTION (2026-08-19) — ONE DOOR, HERS, OR NO DOOR AT ALL. <<< The ribbon that
          stood above the room title is DELETED (her verdict: overcrowding, and the taps did nothing)
          and the nine-card grid that stood here is replaced by the reading she actually clicked to
          arrive. It sits BELOW her room name on purpose: her report names the TOP of the page as
          what was crowded, and the door is a thing she came for rather than the first thing the room
          says. It draws for a paying member and for one who has not paid alike — the difference is
          FREE-1's gate at the generation moment, never the door. */}
      <MyRoomReadingDoor known={knownAnswer} origin={roomOrigin} goTo={goTo}
        heading={t(language, "roomDoor.heading")} />

      {/* ITEM 4(c) — HER PHOTO, under the name editor: the corner above belongs to the pointers. */}
      <ProfilePhoto pointerReady={Boolean(profile) && room.welcomed && shelvesSeen} />

      <div className="room-identity">
        <span className="room-identity__name">{profile?.name || firstName}</span>
        {profile ? (
          <button
            type="button"
            className="room-identity__editbtn"
            onClick={openDetailsEdit}
            aria-label={t(language, "room.editDetails")}
            title={t(language, "room.editDetails")}
          >
            ✎
          </button>
        ) : null}
        {chartSummary.sun || chartSummary.moon || chartSummary.rising ? (
          <span className="room-identity__chip">
            {[
              chartSummary.sun && `${chartSummary.sun} Sun`,
              chartSummary.moon && `${chartSummary.moon} Moon`,
              chartSummary.rising && `${chartSummary.rising} Rising`,
            ].filter(Boolean).join(", ")}
          </span>
        ) : null}
        {profile?.birthDate ? <span className="room-identity__chip">{formatBirthday(profile.birthDate)}</span> : null}
        {profile ? <span className="room-identity__chip">{t(language, "room.lifePath", { number: lifePathFromDate(profile.birthDate) })}</span> : null}
        {currentFact ? (
          <span className="room-identity__fact">{currentFact}</span>
        ) : !profile && roomReadFailed ? (
          // IT #326: a read that did not land is not a member with no room. The words she read as
          // everything being gone are kept for a room that really is not built yet.
          <span className="room-identity__fact room-identity__fact--unreachable" role="status">
            {t(language, "room.unreachable")}{" "}
            {onRetryRoom ? (
              <button type="button" className="room-identity__retry" onClick={onRetryRoom}>{t(language, "room.tryAgain")}</button>
            ) : null}
          </span>
        ) : !profile ? (
          <span className="room-identity__fact">{t(language, "room.noChartYet")}</span>
        ) : null}
      </div>

      {/* N19-1: the one-time ask for members who signed up before the current-location step existed.
          Gentle, honest about what it changes, and it takes either answer without asking again. */}
      {showLocationAsk ? (
        <div className="location-ask">
          <p className="location-ask__text">{t(language, "room.location.ask")}</p>
          <div className="location-ask__actions">
            <button type="button" className="location-ask__btn location-ask__btn--go" onClick={() => answerLocationAsk(true)}>
              {t(language, "room.location.go")}
            </button>
            <button type="button" className="location-ask__btn" onClick={() => answerLocationAsk(false)}>
              {t(language, "room.location.not")}
            </button>
          </div>
        </div>
      ) : null}

      {/* N1-4: the edit sits in a modal over the room, using the SHARED signup form. Escape closes it;
          a background tap does not (the N1-6/M4-2 rule: nobody loses half-typed details to a stray tap). */}
      {editingDetails ? (
        <ModalBackdrop label={t(language, "details.label")} className="details-edit-backdrop" onClose={() => setEditingDetails(false)}>
          <div className="details-edit">
            <button className="modal-close" type="button" onClick={() => setEditingDetails(false)} aria-label={t(language, "common.close")}>x</button>
            <p className="eyebrow">{t(language, "details.eyebrow")}</p>
            <h2>{t(language, "details.title")}</h2>
            <p className="details-edit__lede">{t(language, "details.lede")}</p>
            <div className="details-edit__body">
              {/* N19-1: where she lives now is editable HERE, in the same editor and through the
                  same shared form as her birth details. It is the one fact on this screen that is
                  expected to change over a member's life, so it had better not need support. */}
              <CreateRoomForm
                birthChart={detailsDraft}
                updateBirthChart={updateDetailsDraft}
                onSubmit={saveDetails}
                submitLabel={detailsBusy ? t(language, "details.saving") : t(language, "details.submit")}
                language={language}
                requireMiddleName
                generating={detailsBusy}
                disabled={detailsBusy}
                collectCurrentLocation
              />
              {detailsNote ? <p className="find-your-type__status">{detailsNote}</p> : null}
            </div>
          </div>
        </ModalBackdrop>
      ) : null}
      {!editingDetails && detailsNote ? <p className="room-identity__saved">{detailsNote}</p> : null}
      {/* R53: room width, outside every chip, and a live region so it is announced as well as seen. */}
      {openNote ? <p className="room-identity__saved" role="status">{openNote}</p> : null}

      {/* S-3: her two counts and the list behind each. Every state, act and sentence is in
          `src/components/FollowCircle.tsx`, where a gate CALLS them; this file gains a mount. */}
      <FollowCircle isMember={isMember} />

      <div className="room-panels">
        <RoomPanel
          eyebrow={t(language, "panel.reports.eyebrow")}
          title={t(language, "panel.reports.title")}
          language={language}
          wide
          collapsed={panelCollapsed("reports")}
          onToggle={() => togglePanel("reports")}
          pointer={arrivalPointerOn ? (
            <div className="room-pointer room-pointer--reports" role="note">
              <span className="room-pointer__arrow" aria-hidden="true">▼</span>
              <span className="room-pointer__text">{t(language, "room.pointer.reports")}</span>
              <button type="button" className="room-pointer__dismiss" onClick={dismissReportsPointer} aria-label={t(language, "common.dismiss")}>{t(language, "common.gotIt")}</button>
            </div>
          ) : null}
        >
          {/* CE-6's sweep, 2026-08-10: this asked `!profile`, and `rowToProfile` hands back an OBJECT
              for an EMPTY row — so 214 members who signed in and never created a room were shown
              report chips for readings nothing was ever going to write. The question is the BIRTH
              DATE now, which is CE-1's own law (readiness comes from the fact, never from a row
              being present) asked one question earlier. */}
          {!profile && roomReadFailed ? (
            <p className="room-panel__empty">{t(language, "room.unreachable")}</p>
          ) : !memberHasRoom(profile?.birthDate) ? (
            <p className="room-panel__empty">{t(language, "reports.noProfile")}</p>
          ) : (
            <div className="report-strip">
              {expectedReports.map((slot) => {
                // X1: the FOUNDATIONAL slots are the member's OWN reports. Exclude any person-of-
                // interest chart that was promoted to My Room (same type) so a circle row can never
                // occupy "Your Birth Chart". Legacy un-stamped rows (subject null) read as own.
                const found = reports.find((entry) => entry.type === slot.type && entry.inMyRoom !== false && !isPersonSubject(entry.subject));
                const firstTap = firstTapReports.some((r) => r.type === slot.type);
                // CE-1: BOTH branches resolve through the one law, so the biconditional she asked for
                // is structural rather than only asserted. `isGenerating` joins `buildingActive`
                // because it reads the SERVER's in-flight registry: the sessionStorage deadline dies
                // on a reload and a real build does not.
                // NIKA: the fourth input is the row's own `interpreted`, so a chart whose engine
                // never answered stops claiming to be a reading. The chip and the shelf card read
                // ONE predicate (`readingIsWritten`), which is what keeps the two surfaces from
                // disagreeing about what she owns.
                const slotState = reportSlotState(Boolean(found), buildingActive || isGenerating(slot.type), firstTap, readingIsWritten(found?.interpreted));
                return (
                  <FoundationalReportChip
                    key={slot.type}
                    slot={slot}
                    found={found || null}
                    slotState={slotState}
                    firstTap={firstTap}
                    generating={isGenerating(slot.type)}
                    arrival={arrivalPointerOn && signupSetReports.some((r) => r.type === slot.type)}
                    language={language}
                    onOpen={(id) => { if (arrivalPointerOn) dismissReportsPointer(); openSavedReport(id); }}
                    onGenerate={(type) => generateReport(type)}
                    onRename={(id, title) => renameSaved(id, title)}
                    onDelete={(id) => deleteSavedReport(id)}
                  />
                );
              })}
              {/* On-demand cards: Personality & Purpose and Compatibility Profile. Found =
                  open; missing = a one-click Generate (never "still being prepared"). */}
              {onDemandReports.map((slot) => {
                const found = reports.find((entry) => entry.type === slot.type && entry.inMyRoom !== false && !isPersonSubject(entry.subject));
                if (found) {
                  return (
                    <div key={slot.type} className="report-chip">
                      <button type="button" className="report-chip__open" onClick={() => openSavedReport(found.id)}>
                        <span className="report-chip__kind">{slot.label}</span>
                        <span className="report-chip__title">{found.title}</span>
                        <PreviousDetailsBadge when={found.fromPreviousDetails} />
                        <span className="report-chip__date">{new Date(shelfActivityAt(found)).toLocaleDateString()}</span>
                      </button>
                      <RenameControl kind="reading" value={found.title} onRename={(t) => renameSaved(found.id, t)} className="report-chip__rename-ask" />
                      <DeleteControl kind="reading" what={found.title} className="report-chip__delete-ask" onDelete={() => deleteSavedReport(found.id)} />
                    </div>
                  );
                }
                return (
                  <div key={slot.type} className="report-chip report-chip--pending">
                    <span className="report-chip__kind">{slot.label}</span>
                    <button
                      type="button"
                      className={isGenerating(slot.type) ? "report-chip__gen is-generating" : "report-chip__gen"}
                      onClick={() => generateReport(slot.type)}
                      disabled={isGenerating(slot.type)}
                    >
                      {isGenerating(slot.type) ? t(language, "reports.working") : t(language, "reports.generate")}
                    </button>
                  </div>
                );
              })}
              {newestFirst(reports
                // Everything that is not one of the foundational or on-demand slots above
                // lists here: find-your-type, relationship, tarot, dream, tarot-spread,
                // human-design-compat, and any future type. SH1: only the ones the member kept
                // in My Room (inMyRoom); shelf-only readings live on their own room's shelf.
                .filter((entry) => entry.inMyRoom !== false
                  && !["birth-chart", "numerology", "human-design", "personality", "compatibility"].includes(entry.type)))
                .map((entry) => (
                  <div key={entry.id} className="report-chip">
                    <button type="button" className="report-chip__open" onClick={() => openSavedReport(entry.id)}>
                      <span className="report-chip__kind">{reportTypeLabel(entry.type)}</span>
                      <span className="report-chip__title">{entry.title}</span>
                      <PreviousDetailsBadge when={entry.fromPreviousDetails} />
                      <span className="report-chip__date">{new Date(shelfActivityAt(entry)).toLocaleDateString()}</span>
                    </button>
                    <RenameControl kind="reading" value={entry.title} onRename={(t) => renameSaved(entry.id, t)} className="report-chip__rename-ask" />
                    <DeleteControl kind="reading" what={entry.title} className="report-chip__delete-ask" onDelete={() => deleteSavedReport(entry.id)} />
                  </div>
                ))}
            </div>
          )}
          {genState === "error" ? <p className="room-panel__note">{t(language, "reports.failed")}</p> : null}
        </RoomPanel>

        <MasonryPanels>
        <RoomPanel eyebrow={t(language, "panel.daily.eyebrow")} title={t(language, "panel.daily.title")} language={language} collapsed={panelCollapsed("daily")} onToggle={() => togglePanel("daily")}>
          <p className="room-daily__date">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          {daily ? (
            <>
              <p className="room-daily__text">{daily.text}</p>
              {/* N16-3: daily guidance is a delivered reading and the owner named it. It has no report
                  row of its own, so it is shaped into one here rather than left silently uncovered. */}
              <div className="room-daily__actions">
                <AskLalaAboutButton
                  report={{ type: "prediction", kicker: "Today's guidance", title: "Daily guidance", overview: daily.text }}
                  kind="reading"
                />
              </div>
            </>
          ) : profile ? (
            <>
              <p className="room-panel__empty">{t(language, "daily.empty")}</p>
              <button className={dailyStatus === "loading" ? "button button--ghost is-generating" : "button button--ghost"} type="button" onClick={() => onRequireMembership(getDaily)} disabled={dailyStatus === "loading"}>
                {dailyStatus === "loading" ? t(language, "daily.reading") : t(language, "daily.get")}
              </button>
              {/* R75: at panel width, under the button that spends, never inside a chip (R35). */}
              {dailyNote(dailyAnswer, t(language, "daily.failed"), t(language, "daily.signIn"))
                ? <p className="room-daily__note" role="status">{dailyNote(dailyAnswer, t(language, "daily.failed"), t(language, "daily.signIn"))}</p>
                : null}
            </>
          ) : (
            <p className="room-panel__empty">{t(language, !profile && roomReadFailed ? "room.unreachable" : "daily.noProfile")}</p>
          )}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.tarot.eyebrow")} title={t(language, "panel.tarot.title")} language={language} collapsed={panelCollapsed("tarot")} onToggle={() => togglePanel("tarot")}>
          {tarotReadings.length ? (
            /* MR2: show all (the panel scroll-caps at 10 rows) with a per-item delete. */
            <ul className="report-list room-scroll room-scroll--10">
              {tarotReadings.map((entry) => (
                <li key={entry.id} className="report-list__item">
                  <button type="button" className="report-list__open" onClick={() => openSavedReport(entry.id)}>
                    <span className="report-list__title">{entry.title}</span>
                    <PreviousDetailsBadge when={entry.fromPreviousDetails} />
                    <span className="report-list__date">{new Date(shelfActivityAt(entry)).toLocaleDateString()}</span>
                  </button>
                  <RenameControl kind="reading" value={entry.title} onRename={(t) => renameSaved(entry.id, t)} className="report-list__rename-ask" />
                  <DeleteControl kind="reading" what={entry.title} className="report-list__delete-ask" onDelete={() => deleteSavedReport(entry.id)} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="room-panel__empty">{t(language, "tarot.empty")}</p>
          )}
          <div className="room-tarot__actions">
            <button className="button button--primary" type="button" onClick={() => onRequireMembership(() => setDailyCardOpen(true))}>{t(language, "tarot.drawDaily")}</button>
            <button className="button button--ghost" type="button" onClick={() => goTo("tarot")}>{t(language, "tarot.openRoom")}</button>
          </div>
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.shelf.eyebrow")} title={t(language, "panel.shelf.title")} language={language} collapsed={panelCollapsed("shelf")} onToggle={() => togglePanel("shelf")}>
          {savedBooks.length === 0 ? (
            <>
              <p className="room-panel__empty">{t(language, "shelf.empty")}</p>
              <button className="button button--ghost" type="button" onClick={() => goTo("library")}>{t(language, "shelf.openLibrary")}</button>
            </>
          ) : (
            <div className="bookcase saved-shelf">
              <Shelf
                // The id and the data key stay put; the PLATE is the house's own word and turns.
                category="Saved Shelf"
                label={t(language, "savedshelf.plate")}
                language={language}
                books={savedBooks}
                activeSlug={selected?.slug ?? null}
                // W5-2 — THE FAR END OF THE SAVE LOOP, AND IT WAS A DEAD END. Every saved book opened
                // in the TEXT reader, so one of her three books (page IMAGES, no ingested text) landed
                // on "being prepared" after she saved it. Which reader a slug opens is the shared law's
                // answer now, taken from the server's own featured list.
                onOpen={(book) => {
                  if (readerKindFor(book.slug, Object.keys(featuredMap)) === "page-image") {
                    setBmReader({ featured: true, book: featuredMap[book.slug], page: 1 });
                    return;
                  }
                  setSelected(book);
                  setReaderPage(0);
                }}
              />
            </div>
          )}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.bookmarks.eyebrow")} title={t(language, "panel.bookmarks.title")} language={language} collapsed={panelCollapsed("bookmarks")} onToggle={() => togglePanel("bookmarks")}>
          {/* R61: at PANEL width, never inside a list row (R35's clipped-confirm capture). */}
          {bookmarkNoteText ? <p className="reading-menu-note" role="status">{bookmarkNoteText}</p> : null}
          {bookmarks.length ? (
            <ul className="bookmark-list">
              {newestFirst(bookmarks).map((bm) => (
                <li key={bm.id} className="bookmark-list__item">
                  {bm.gone ? (
                    // M3 (her order, 2026-10-20): the book left the library; the mark stays and says so, never a dead link.
                    <div className="bookmark-list__open bookmark-list__open--gone">
                      <span className="bookmark-list__name">{bm.name}</span>
                      <span className="bookmark-list__book">{bookmarkWhere(bm)}</span>
                      <span className="bookmark-list__gone">{t(language, "bookmarks.gone")}</span>
                    </div>
                  ) : (
                    <button type="button" className="bookmark-list__open" onClick={() => openBookmark(bm)}>
                      <span className="bookmark-list__name">{bm.name}</span>
                      <span className="bookmark-list__book">{bookmarkWhere(bm)}</span>
                    </button>
                  )}
                  <button type="button" className="bookmark-list__remove" onClick={() => removeBookmark(bm.id)} aria-label={t(language, "bookmarks.remove", { name: bm.name })}>×</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="room-panel__empty">{t(language, "bookmarks.empty")}</p>
          )}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.verses.eyebrow")} title={t(language, "panel.verses.title")} language={language} collapsed={panelCollapsed("verses")} onToggle={() => togglePanel("verses")}>
          {savedVerses.length ? (
            <ul className="saved-verse-list">
              {savedVerses.map((v) => (
                <li key={v.id} className="saved-verse">
                  <div className="saved-verse__head">
                    <span className="saved-verse__ref">{v.reference}</span>
                    <span className={`saved-verse__src saved-verse__src--${(v.source || "").toLowerCase()}`}>{v.source}</span>
                  </div>
                  <p className="saved-verse__text">{v.verseText}</p>
                  <div className="saved-verse__actions">
                    <button type="button" onClick={() => copySavedVerse(v)}>{copyLabel(verseCopied?.id === v.id ? verseCopied.state : "idle", copyWords(language, t))}</button>
                    <button type="button" onClick={() => removeSavedVerse(v.id)} aria-label={t(language, "verses.removeAria", { reference: v.reference })}>{t(language, "verses.remove")}</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="room-panel__empty">{t(language, "verses.empty")}</p>
          )}
          {/* R54: panel width, outside every row, and announced — the put-back has to say why. */}
          {verseNoteText ? <p className="room-panel__empty" role="status">{verseNoteText}</p> : null}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.journal.eyebrow")} title={t(language, "panel.journal.title")} language={language} collapsed={panelCollapsed("journal")} onToggle={() => togglePanel("journal")}>
          {journalEntries.length ? (
            <ul className="journal-list">
              {newestFirst(journalEntries).map((entry) => (
                <JournalPanelItem key={entry.id} entry={entry} onDelete={onDeleteEntry} unsavedLabel={t(language, "journal.unsaved")} />
              ))}
            </ul>
          ) : (
            <p className="room-panel__empty">{t(language, "journal.empty")}</p>
          )}
          {/* R57: panel width, outside every row, and announced — R35's capture lesson (a sentence
              she cannot read is worse than none) plus R54's placement one panel up. */}
          {journalNoteText ? <p className="room-panel__empty" role="status">{journalNoteText}</p> : null}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.people.eyebrow")} title={t(language, "panel.people.title")} language={language} collapsed={panelCollapsed("people")} onToggle={() => togglePanel("people")}>
          {people.length ? (
            <ul className="people-list">
              {people.map((p) => (
                <li key={p.id} className="people-list__item">
                  <div className="people-list__who">
                    <strong>{p.name}</strong>
                    {p.relationship ? <span className="people-list__rel">{p.relationship}</span> : null}
                    <span className="people-list__date">{p.birthDate || t(language, "people.noBirthDate")}</span>
                  </div>
                  <div className="people-list__actions">
                    {/* I5: RUN / VIEW, NEVER RE-PULL. The label is not a mode we track: it reads
                        the server's one truth about whether this person's chart exists yet, so it
                        flips to View the moment ANY surface casts them. */}
                    {p.chartPossible === false ? (
                      <button type="button" className="button button--ghost" onClick={() => setEditingPerson(p)}
                        title={t(language, "people.needsDetails", { name: p.name })}>
                        {t(language, "people.addDetails")}
                      </button>
                    ) : (
                      <button type="button" className={chartBusy === p.id ? "button button--ghost is-generating" : "button button--ghost"} onClick={() => runOrViewChart(p)} disabled={chartBusy === p.id}>
                        {chartBusy === p.id
                          ? t(language, p.hasChart ? "people.opening" : "people.running")
                          : t(language, p.hasChart ? "people.viewChart" : "people.runChart")}
                      </button>
                    )}
                    {/* X3: glow while the reading runs, then flip to the ready label. A compat that
                        already exists reads "Read Report" and opens the stored one, no re-pull. */}
                    <button type="button" className={relBusy === p.id ? "button button--ghost is-generating" : "button button--ghost"} onClick={() => runOrViewCompat(p)} disabled={relBusy === p.id}>
                      {relBusy === p.id ? t(language, "people.reading") : t(language, p.hasCompat ? "people.readReport" : "people.compatibility")}
                    </button>
                    {/* SP5: edit this person's card in place (spelling, birth time, city). */}
                    <button type="button" className="button button--ghost" onClick={() => setEditingPerson(p)}>{t(language, "people.edit")}</button>
                    <DeleteControl kind="person" what={p.name} className="people-list__delete-ask" onDelete={() => removePerson(p.id)} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="room-panel__empty">{t(language, "people.empty")}</p>
          )}
          {chartNote ? <p className="room-panel__empty">{chartNote}</p> : null}
          {showAddPerson ? (
            <form className="reading-form people-form" onSubmit={savePerson}>
              <NameFields
                first={personForm.firstName}
                middle={personForm.middleName}
                last={personForm.lastName}
                onChange={(field, value) => setPersonForm((f) => ({ ...f, [field]: value }))}
                language={language}
              />
              <label>
                <span>{t(language, "people.relationship")}</span>
                <input value={personForm.relationship} onChange={(e) => setPersonForm((f) => ({ ...f, relationship: e.target.value }))} placeholder={t(language, "people.relationshipPlaceholder")} />
              </label>
              <label>
                <span>{t(language, "form.birthDate")}</span>
                <input type="date" value={personForm.birthDate} onChange={(e) => setPersonForm((f) => ({ ...f, birthDate: e.target.value }))} />
              </label>
              <label>
                <span>{t(language, "form.birthTime")}</span>
                <BirthTimeFields
                  birthTime={personForm.birthTime}
                  birthPeriod={personForm.birthPeriod}
                  disabled={personForm.birthTimeUnknown}
                  onChange={(patch) => setPersonForm((f) => ({ ...f, ...patch }))}
                  language={language}
                />
                <UnknownTimeCheck checked={personForm.birthTimeUnknown}
                  onChange={(next) => setPersonForm((f) => ({ ...f, birthTimeUnknown: next }))}
                  label={t(language, "form.birthTimeUnknown")} />
              </label>
              <BirthPlaceFields
                language={language}
                country={personForm.birthCountry}
                birthplace={personForm.birthplace}
                latitude={personForm.latitude}
                longitude={personForm.longitude}
                timezone={personForm.timezone}
                birthDate={personForm.birthDate}
                onChange={(patch) => setPersonForm((f) => ({ ...f, ...patch }))}
              />
              <div className="people-form__actions">
                <button className="button button--primary" type="submit" disabled={personSaving}>{t(language, personSaving ? "people.saving" : "people.savePerson")}</button>
                <button className="button button--ghost" type="button" onClick={() => setShowAddPerson(false)}>{t(language, "common.cancel")}</button>
              </div>
              {personNoteText ? <p className="person-edit__err">{personNoteText}</p> : null}
            </form>
          ) : (
            <button className="button button--ghost" type="button" onClick={() => setShowAddPerson(true)}>{t(language, "people.add")}</button>
          )}
          {/* R52 — I6 RULE 3's PROMPT, on the surface that adds most of her circle. The server has
              always handed the duplicate back so this could be asked warmly; this form was the one
              that dropped it on the floor. `RESOLUTION_CANCEL` is a real answer and closes it. */}
          {dupePerson ? (
            <DuplicatePersonPrompt
              existing={dupePerson}
              onChoose={(choice) => { if (choice === RESOLUTION_CANCEL) { setDupePerson(null); return; } void submitPerson(choice); }}
            />
          ) : null}
          {editingPerson ? (
            <PersonEditModal
              person={editingPerson}
              onClose={() => setEditingPerson(null)}
              onSaved={() => { setEditingPerson(null); refreshPeople(); }}
            />
          ) : null}
        </RoomPanel>

        <RoomPanel eyebrow={t(language, "panel.videos.eyebrow")} title={t(language, "panel.videos.title")} language={language} collapsed={panelCollapsed("videos")} onToggle={() => togglePanel("videos")}>
          {/* R55: the panel is `src/components/WatchList.tsx` now — props in, tree out, no hook, so
              a gate CALLS it. Three laws lived in this JSX and nothing ran any of them: T8-9's
              arrived-empty vs did-not-arrive sentence, the percentage over a nullable duration, and
              the resume point that restarts a finished video. */}
          <WatchList
            videos={watching}
            language={language}
            load={watchLoad}
            note={watchNote}
            onResume={(v) => requireFeature("library", () => setPlayingVideo({ slug: v.slug, title: v.title, startAt: resumeAt(v) }))}
            onRemove={removeWatching}
          />
        </RoomPanel>
        </MasonryPanels>
      </div>

      {playingVideo ? (
        <VideoPlayerModal
          slug={playingVideo.slug}
          title={playingVideo.title}
          language={language}
          startAt={playingVideo.startAt}
          onClose={() => { setPlayingVideo(null); loadWatching(); }}
        />
      ) : null}

      {dailyCardOpen ? <DailyCardModal onClose={() => setDailyCardOpen(false)} /> : null}

      {bmReader && bmReader.featured ? (
        <PageImageReader book={bmReader.book as FeaturedBook} startPage={bmReader.page} onClose={() => setBmReader(null)} language={language} />
      ) : bmReader ? (
        <BookReaderModal book={bmReader.book as ShelfBook} page={bmTextPage} setPage={setBmTextPage} onClose={() => setBmReader(null)} language={language} />
      ) : null}

      {customizeOpen ? (
        <ModalBackdrop label={t(language, "room.customize")}>
          <div className="customize-modal">
            <button className="modal-close" type="button" onClick={() => setCustomizeOpen(false)} aria-label={t(language, "common.close")}>x</button>
            <p className="eyebrow">{t(language, "customize.eyebrow")}</p>
            <h2>{t(language, "room.customize")}</h2>
            {/* ITEM 6 — HER THEME, IN MY ROOM'S SETTINGS, which is where her order puts it. It sits
                above the backgrounds because it is the one choice that follows her out of this room. */}
            <ThemeChoice theme={theme} onChoose={onChooseTheme} />
            <p>{t(language, "customize.backgrounds")}</p>
            {bgNote ? <p className="reading-menu-note" role="status">{bgNote}</p> : null}
            <RoomArtPicker
              backgrounds={backgrounds} selected={selectedBg} url={bgUrl}
              onChoose={chooseBackground}
              label={(name) => t(language, "customize.choose", { name })}
            />
          </div>
        </ModalBackdrop>
      ) : null}

      {selected ? (
        <BookReaderModal book={selected} page={readerPage} setPage={setReaderPage} onClose={() => setSelected(null)} language={language} />
      ) : null}

      {/* OV2-9: onClose is what makes a tab supersede this modal (and Escape close it).
          W12-2: the noun is asked for, never assumed. A spell saved to My Room opened in a modal
          called "Saved report" and its toolbar said "Your reading" — of a working she MADE. */}
      {openReport ? (
        <ModalBackdrop label={outputNoun((openReport as any)?.type, "Saved report")} onClose={() => { setOpenReport(null); setOpenReportId(null); }}>
          <div className="reading-modal">
            <button
              className="modal-close"
              type="button"
              onClick={() => { setOpenReport(null); setOpenReportId(null); }}
              aria-label={t(language, "reports.closeReport")}
            >
              x
            </button>
            <div className="chart-report__wrap">
              <div className="reader__toolbar">
                <span><Eye size={16} /> {outputNoun((openReport as any)?.type, "Saved report")}</span>
                <div className="chart-report__actions">
                  {pdfAllowed(openReport) ? <button type="button" onClick={() => downloadReportPdf(openReport)}>{t(language, "reports.download")}</button> : null}
                  {/* N4-3: the shared handoff. Renders only for READING types (src/askLalaHandoff.js). */}
                  <AskLalaAboutButton report={openReport} kind="reading" />
                </div>
              </div>
              <ReportWithPrint data={openReport} />
            </div>
          </div>
        </ModalBackdrop>
      ) : null}
      </section>
    </>
  );
}

// W4-7: the dictation hook and MicButton moved to src/components/MicButton.tsx, beside their two
// pure laws (src/lib/dictation.ts, src/lib/live-view.ts). The move PAID for the lines W4-7 needed in
// this file, which is the R8 ratchet working rather than merely being obeyed, and every check that read
// them as source text reads them there now.

// Shared base for the edge drawers (Ask Lala, Journal). Rendered through a portal
// to <body> so it escapes the transformed edge containers that otherwise trapped
// its fixed position and pushed it off-screen. Always fits the viewport (top below
// the bar, capped height with the content scrolling inside), and always has a
// visible close X, plus click-outside and Escape to close.
// R25: DrawerTab + DRAWER_ICONS moved to src/components/DrawerTab.tsx, where a gate can CALL them.
// The label is a literal at six call sites and the icon is a Record lookup with a SILENT Sparkles
// fallback, so an unrostered label wears the wrong face rather than showing a missing one.

// M1 (owner's ruling, 2026-07-17): on a phone the drawer tabs are HIDDEN by default. Measured:
// all seven sit pinned to right:0 and eat 84px of a 390px screen (21% of it), permanently, on
// every page — they were covering the Ask Lala chips, the tarot Card Library subtitle and the "+"
// in the home hero line.
//
// The tabs belong to five separate drawer components, so the open/closed state lives on <body>
// rather than being threaded through all of them: one attribute, one CSS rule, every tab obeys,
// and no drawer needs to know this exists. Desktop is untouched (the CSS is inside a media query).
// M4-1's mobile rail toggle moved to src/components/MobileDrawerToggle.tsx (R8).

// R26: the whole Cauldron door — OV2-7/OV2-8's event, the request flag, `goToCauldron` and
// `GoToCauldronButton` — moved to src/components/CauldronDoor.tsx, where a gate can RUN the door
// against a fake window and read back what it pushed, dispatched and flagged. It also stopped
// hand-typing both halves of the route map there, and R26-2 put the way back into the SHARED sender:
// ten surfaces send to the pot and only one was offering the trip home.

// ---------------------------------------------------------------------------
// N4-3 — THE ASK LALA HANDOFF. One mechanism, every reading surface.
//
// Tapping "Ask Lala" on any delivered reading takes the member straight to the Ask Lala room with the
// reading already handed to her, and she begins answering on arrival with nothing retyped.
//
// *** THE READING TRAVELS IN MEMORY, NEVER IN THE URL. *** That is the standing privacy decision, and it
// is why this is an event carrying an object rather than a query string or a route param: a member's
// dream, chart or palm reading must never sit in a browser history entry, a server log or a shared link.
//
// ONE SHARED COMPONENT per the consolidation law. Every surface renders <AskLalaAboutButton>, which
// dispatches this event; App is the only listener, and it reuses the SAME pendingAsk pipe the home-page
// search bar has always used. No per-room copies, and a reading surface added tomorrow gets the
// behaviour by rendering the button.
// ---------------------------------------------------------------------------
// R25: the event, `askLalaAboutReading` and the button moved to
// src/components/AskLalaAboutButton.tsx, where a gate can CALL the button once per type the SERVER
// can write. Its `ASK_LALA_READING_TYPES.has(t)` guard is a roster crossing whose default is that the
// door DISAPPEARS, and a regex can only ever see that the guard is written.

// T5-2 — THE INTERCEPT BOX. ONE SHARED COMPONENT: every handoff surface inherits it by rendering
// <AskLalaAboutButton>, because the button dispatches the event and App raises this. There is no
// per-room copy to keep in step.
//
// The questions were born WITH the reading (T5-1), so opening this box costs nothing. She taps every
// one she wants answered, may type her own words, and ONE send carries all of it as ONE turn against
// her daily allowance. "Just send it" is the old behaviour, unchanged, for a member who does not want
// the box in her way.
function AskLalaIntercept({ questions, onSend, onClose }: {
  questions: string[];
  onSend: (picked: string[], typed: string) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [typed, setTyped] = useState("");
  const toggle = (q: string) =>
    setPicked((current) => (current.includes(q) ? current.filter((x) => x !== q) : [...current, q]));
  return (
    <ModalBackdrop label="Ask Lala about this reading" className="handoff-backdrop" onClose={onClose}>
      <div className="handoff-modal">
        <button type="button" className="handoff-modal__close" aria-label="Close" onClick={onClose}>
          <X size={18} />
        </button>
        <p className="eyebrow">Ask Lala</p>
        <h2>What do you want to know?</h2>
        <p className="handoff-modal__line">Tap anything you want her to answer. Add your own words if you like. It all goes to her together.</p>
        <div className="handoff-modal__questions">
          {questions.map((q) => (
            <button
              key={q}
              type="button"
              className={`handoff-bubble${picked.includes(q) ? " is-picked" : ""}`}
              aria-pressed={picked.includes(q)}
              onClick={() => toggle(q)}
            >
              {q}
            </button>
          ))}
        </div>
        <textarea
          className="handoff-modal__own"
          placeholder="Or ask her something else, in your own words"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          rows={3}
        />
        <div className="handoff-modal__actions">
          <button type="button" className="button button--primary" onClick={() => onSend(picked, typed)}>
            {picked.length ? `Send ${picked.length} to Lala` : "Send to Lala"}
          </button>
          <button type="button" className="button button--ghost" onClick={() => onSend([], "")}>Just send it</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}


type VerseResult = { reference: string; text: string; source: "Bible" | "Quran" };

// Verse Search: the third right-edge tab (with Ask Lala and Journal). A member enters a
// topic and gets exact verses from the Bible (KJV) and the Quran (Yusuf Ali), each with a
// copy button and save-to-room. Feeds the seal and spell-book workflows, so copy is one tap.
function FloatingVerseSearch({ isMember, onRequireMembership, language }: { isMember: boolean; language: string; onRequireMembership: (action: () => void, lock?: LockInfo) => void }) {
  const requireFeature = useRequireFeature();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [verses, setVerses] = useState<VerseResult[]>([]);
  const [filter, setFilter] = useState<"all" | "Bible" | "Quran">("all");
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<{ reference: string; state: CopyState } | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!topic.trim()) { setStatus("Enter a topic to find verses for."); return; }
    onRequireMembership(() => { void run(); });
  }
  async function run() {
    setBusy(true);
    setVerses([]);
    setStatus("Searching the Bible and the Quran...");
    try {
      const response = await fetch("/api/verses/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: topic.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus("Sign in to search the scriptures."); return; }
      if (!response.ok || data?.ok === false || !Array.isArray(data?.verses)) {
        setStatus(data?.message || "The verse search could not run right now. Please try again."); return;
      }
      setVerses(data.verses);
      setStatus(data.verses.length ? "" : "No verses found for that topic.");
    } catch {
      setStatus("The verse search could not run right now. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  // R56: the second of the two that flashed over a swallowed write, byte for byte the same shape.
  const copy = async (v: VerseResult) => {
    const answer = await copyText(`"${v.text}" (${v.reference}, ${v.source})`);
    setCopied({ reference: v.reference, state: copyState(answer) });
    window.setTimeout(() => setCopied(null), COPY_FLASH_MS);
  };
  // R54 — **A `.catch` FIRES ON A THROWN REQUEST AND ON NOTHING ELSE.** This route refuses four ways
  // and every one of them RESOLVES (401; HTTP 200 `{ ok: false }` when the database is unreachable;
  // 200 "Nothing to save."; 500), so the chip read "✓ Saved", went disabled, and the verse was
  // nowhere. She found out by opening My Room and not finding it. The paint still leads — it is what
  // makes the chip feel instant — and now it is TAKEN BACK on any answer that is not a save.
  const save = async (v: VerseResult) => {
    setSaved((s) => ({ ...s, [v.reference]: true }));
    const answer = await saveVerse({ reference: v.reference, verseText: v.text, source: v.source, topic: topic.trim() });
    if (answer.ok) return;
    setSaved((s) => ({ ...s, [v.reference]: false }));
    setStatus(verseNote(answer, { signIn: t(language, "verses.signIn"), failed: t(language, "verses.saveFailed") }));
  };

  const shown = verses.filter((v) => filter === "all" || v.source === filter);

  return (
    <aside className="verse-search rail-anchor" aria-label="Verse Search">
      {/* RAIL RESTORE: her ruling put Verses back on the rail with its own tab, fourth from the top. */}
      {/* FREE-1: opens. Nothing is fetched until she submits, and the submit is the box's moment. */}
      <DrawerTab label="Verses" onClick={() => setOpen(!open)} />
      <EdgeDrawer open={open} onClose={() => setOpen(false)} label="Verse Search" className="verse-drawer">
        <div className="verse-search__panel">
          <p className="eyebrow">Verse Search</p>
          <h2>Find verses by topic</h2>
          <p className="verse-search__intro">Exact verses from the Bible (King James Version) and the Quran (Yusuf Ali). Enter a topic like protection, abundance, or forgiveness.</p>
          <form className="verse-search__form" onSubmit={submit}>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="protection, abundance, forgiveness..." />
            <button className={`button button--primary${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
              {busy ? "Searching..." : "Search"}
            </button>
          </form>
          {status ? <p className="verse-search__status">{status}</p> : null}
          {verses.length ? (
            <>
              <div className="verse-search__filter" role="tablist">
                {(["all", "Bible", "Quran"] as const).map((f) => (
                  <button key={f} type="button" className={filter === f ? "verse-chip verse-chip--on" : "verse-chip"} onClick={() => setFilter(f)}>
                    {f === "all" ? "All" : f}
                  </button>
                ))}
              </div>
              <ul className="verse-list">
                {shown.map((v, i) => (
                  <li key={`${v.reference}-${i}`} className="verse-item">
                    <div className="verse-item__head">
                      <span className="verse-item__ref">{v.reference}</span>
                      <span className={`verse-item__src verse-item__src--${v.source.toLowerCase()}`}>{v.source}</span>
                    </div>
                    <p className="verse-item__text">{v.text}</p>
                    <div className="verse-item__actions">
                      <button type="button" onClick={() => copy(v)}>{copyLabel(copied?.reference === v.reference ? copied.state : "idle", copyWords(language, t))}</button>
                      <button type="button" onClick={() => save(v)} disabled={saved[v.reference]}>
                        {saved[v.reference] ? "✓ Saved" : "Save to My Room"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </EdgeDrawer>
    </aside>
  );
}

// X6 — JOURNAL REDESIGN. The recent entries live in a list to the LEFT of the writing pad, in the
// same panel and background (no bottom strip, no tiny inner scrollbox until the list outgrows the
// panel). Clicking an entry loads it into the pad as written for editing; saving updates that entry;
// starting fresh (New) saves a new one. Deletes stay.
function FloatingJournal({
  isOpen,
  title,
  draft,
  entries,
  editingId,
  note = "",
  setOpen,
  setTitle,
  setDraft,
  onSelectEntry,
  onNewEntry,
  onSave,
  onDeleteEntry,
  language,
}: {
  isOpen: boolean;
  title: string;
  draft: string;
  entries: JournalEntry[];
  editingId: string | null;
  // R57: what the pad says back when a save did not land. Empty on a success — the entry appearing
  // in the list beside it IS the answer.
  note?: string;
  setOpen: (open: boolean) => void;
  setTitle: (value: string) => void;
  setDraft: (value: string | ((current: string) => string)) => void; // W1-13: the mic needs the updater form
  onSelectEntry: (entry: JournalEntry) => void;
  onNewEntry: () => void;
  onSave: (event: FormEvent) => void;
  onDeleteEntry: (id: string) => Promise<boolean>;
  language: string;
}) {
  const requireFeature = useRequireFeature();
  // W4-7: the pad the mic writes into, so a long dictation stays visible in it.
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const editing = editingId != null;
  return (
    <aside className={isOpen ? "floating-journal rail-anchor floating-journal--open" : "floating-journal rail-anchor"} aria-label="Floating journal">
      {/* FREE-1: opens. The pad holds her own words; the SAVE is what the server prices. */}
      <DrawerTab label="Journal" onClick={() => setOpen(!isOpen)} />
      <EdgeDrawer open={isOpen} onClose={() => setOpen(false)} label="Journal" className="journal-drawer">
        <div className="floating-journal__layout">
          {/* LEFT: the member's entries, click one to edit it in the pad. */}
          <div className="journal-entries">
            <div className="journal-entries__head">
              <p className="eyebrow">Your entries</p>
              <button type="button" className="button button--ghost journal-entries__new" onClick={onNewEntry}>New</button>
            </div>
            {entries.length > 0 ? (
              <ul className="journal-entries__list">
                {entries.map((entry) => (
                  <JournalEntryRow
                    key={entry.id}
                    entry={entry}
                    active={editingId === String(entry.id)}
                    unsavedLabel={t(language, "journal.unsaved")}
                    onSelect={onSelectEntry}
                    onDelete={onDeleteEntry}
                  />
                ))}
              </ul>
            ) : (
              <p className="journal-entries__empty">No entries yet. Write your first note in the pad.</p>
            )}
          </div>

          {/* RIGHT: the writing pad. Editing an entry updates it; a fresh pad saves a new one. */}
          <form className="floating-journal__panel" onSubmit={onSave}>
            <h2>{editing ? "Edit this entry" : "Keep a note"}</h2>
            <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Entry title" />
            <div className="journal-draft">
              {/* W4-7: the mic keeps the newest words in view. No limits passed, so this keeps its own designed height and only scrolls. */}
              <textarea ref={draftRef} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Write what you want to remember..." />
              <MicButton setValue={setDraft} field={draftRef} />
            </div>
            {/* OV2-8 (owner): Send to Cauldron sits beside Save entry, and because opening a saved
                entry loads it straight into THIS pad, the same control serves both paths she named
                (a fresh note and a saved one re-opened) rather than two buttons that could drift.
                THE SACRED-WORDS LAW: it rides as kind "note", the same shape the Notepaper uses, so
                her words go into the working exactly as she typed them and are never rewritten.
                Disabled until there is something to send, so it can never post an empty entry. */}
            <div className="journal-actions">
              <button className="button button--primary" type="submit">{editing ? "Update entry" : "Save entry"}</button>
              <SendToCauldronButton
                language={language}
                kind="note"
                title={title.trim() || "Journal entry"}
                data={{
                  type: "note", kicker: "Your Journal", title: title.trim() || "Journal entry",
                  header: { name: title.trim() || "Journal entry" },
                  noteText: draft, overview: "Your own words, from your journal.",
                  sections: [{ heading: "Your words, as you wrote them", body: draft }],
                }}
                disabled={draft.trim().length < 2}
              />
            </div>
            {/* R57: pad width, under the controls, announced. It is the only thing on this site that
                can tell her the words she just wrote are not in her room. */}
            {note ? <p className="journal-note" role="status">{note}</p> : null}
          </form>
        </div>
      </EdgeDrawer>
    </aside>
  );
}

// Lala replies in plain prose; strip any stray markdown so bubbles never show
// literal ** or * or # symbols.
// Ask Lala: a floating chat on every screen, backed by claude-sonnet-4-6 with the
// member's chart and readings as context. Conversations persist to their room.
// SEL-1 COMPLETE — one option in a purpose choice-set Lala offers. The member ticks the ones they
// want and sends them to the Cauldron as one layered working (a candle per seal, activated in turn).
type SealOption = {
  slug: string;
  name: string;
  planet?: string;
  ordinal?: number;
  purpose: string;
  verseRef?: string;
  verseText?: string;
  imageUrl: string;
};
function AskLala({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const requireFeature = useRequireFeature();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{ id?: string; role: string; content: string; seal?: DeliveredSeal; sealOptions?: SealOption[]; cards?: ChatCard[]; manual?: ManualHandoff; compilation?: Compilation; attachments?: LalaAttachment[] }>>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false); const inFlightRef = useRef(false);
  const [turnNotice, setTurnNotice] = useState<TurnNotice>(null);
  const free = useFreeTurns();   // FUNNEL-4: the server's count, never a tally this browser keeps
  const composer = composerLock(turnNotice, "", free.atWall);   // W6-2 at the cap, FUNNEL-4 at the wall
  const logRef = useRef<HTMLDivElement | null>(null);
  // W4-7: the composer itself, so the mic can keep her latest words in view while she talks.
  const composeRef = useRef<HTMLTextAreaElement | null>(null);
  const composeLimits = limitsForWidth(typeof window === "undefined" ? 0 : window.innerWidth);
  const files = useAttachments();

  // U4: load the thread AND its attachments, and put each file back beside the message it
  // was sent with, so reopening the drawer shows the conversation exactly as it happened.
  // R60 — **THE GUARD IS BEFORE THE PAINT** (R52). W10-3: `cards` comes back on the stored message,
  // so a reopened drawer shows the pictures again rather than a reading with nothing under it.
  const [threadNote, setThreadNote] = useState("");
  // FREE-1 — **A SURFACE THAT ARRIVES LOCKED DOES NOT ASK THE SERVER FOR WHAT IT MAY NOT HAVE.**
  // This drawer reads the thread the moment it OPENS, both reads are `/api/lala/`, and a 402 there
  // would raise the box ON ARRIVAL: her clause 1 broken by a FETCH rather than a wall, which is the
  // failure that looks like the item working. `opensSurface` holds only a DEFINITE refusal, so
  // `pending` re-runs when the entitlements land and a paid member keeps her history (her clause 4).
  const lalaOpens = opensSurface(useFeatureOutcome("lala:chat"));
  useEffect(() => {
    if (!open || !lalaOpens) return;
    void Promise.all([
      readLalaThread<{ id?: string; role: string; content: string; cards?: ChatCard[]; manual?: ManualHandoff; compilation?: Compilation }>(),
      listLalaAttachments<LalaAttachment>(),
    ]).then(([read, att]) => {
      const hist = read ? read.messages : null;
      if (hist) setMessages(attachToMessages(hist, att));
      free.take(read?.freeTurns);   // FUNNEL-4: her counter is on screen the moment she lands
      setThreadNote(lalaThreadNote(hist, att, LALA_THREAD_COPY));
    });
  }, [open, lalaOpens]);

  // Open at the BOTTOM (newest), like every chat: scroll the log to the end whenever it
  // opens, loads history, or a new message arrives. Deferred a tick so the messages have
  // laid out before we measure scrollHeight.
  useEffect(() => {
    if (!open) return;
    const el = logRef.current;
    if (!el) return;
    const toBottom = () => { el.scrollTop = el.scrollHeight; };
    const id = window.setTimeout(toBottom, 0);
    // W10-3, FOUND IN THE WALKTHROUGH CAPTURE: an image that has not loaded yet has NO HEIGHT, so the
    // pass above measures a log that is three cards shorter than the one she ends up looking at, and
    // her reading lands with the cards half off the bottom of the screen. Re-scroll as each one
    // arrives. This is not card-specific: an attachment thumbnail had the same defect, unnoticed.
    const pending = [...el.querySelectorAll("img")].filter((img) => !img.complete);
    pending.forEach((img) => img.addEventListener("load", toBottom, { once: true }));
    return () => {
      window.clearTimeout(id);
      pending.forEach((img) => img.removeEventListener("load", toBottom));
    };
  }, [open, messages, sending]);

  async function send(event: FormEvent) {
    event.preventDefault();
    const text = input.trim();
    if (lalaSendState({ text, attachments: files.pending.length, inFlight: inFlightRef.current }) !== "send") return;
    inFlightRef.current = true;   // E5: the REF decides, never `sending`.
    const afterId = newestStoredId(messages);   // IT #300: the rows she already had, before her turn is drawn
    const sent = files.pending;
    const ids = files.ids;
    setMessages((m) => [...m, { role: "user", content: text, attachments: sent }]);
    setInput("");
    files.clear();
    setSending(true);
    // R98: the ANSWER, never `data.reply || "..."`. Three dots in the thread is not a placeholder,
    // it is Lala with nothing to say to her, drawn over a refusal that composed a real sentence.
    const answer = await sendToLala<DeliveredSeal, SealOption[], ChatCard[], ManualHandoff, Compilation>({ message: text, attachmentIds: ids, surface: "lala-drawer" });
    // IT #300: a DROPPED request is not a refusal. Her reply may already be being written, so the thread
    // is read again for it before the failure sentence is ever said.
    const dropped = answer.kind === "offline"
      ? await awaitDroppedReply(async () => (await readLalaThread<{ id?: string; role: string; content: string; cards?: ChatCard[]; manual?: ManualHandoff; compilation?: Compilation }>())?.messages ?? null, text, afterId, { normalise: stripAttachNote })
      : null;
    const turn = dropped ? dropped.content : lalaSendTurn(answer);
    if (turn) setMessages((m) => [...m, dropped
      ? { role: "assistant", content: dropped.content, cards: dropped.cards, manual: dropped.manual, compilation: dropped.compilation }
      : { role: "assistant", content: turn, seal: answer.seal, sealOptions: answer.sealOptions, cards: answer.cards, manual: answer.manual, compilation: answer.compilation }]);
    // T7-5: she was answered warmly AND the door she touched is named, through the shared box. Both
    // ride ONLY on an answer that landed: a refusal carries no lock and no turn count.
    if (answer.kind === "ok") { setTurnNotice(applyTurnPayload(answer.turns)); free.take(answer.freeTurns); raiseLockedDoor(answer.lock, undefined, { answered: true }); }
    setSending(false); inFlightRef.current = false;   // E5: a stuck flag is a dead Send.
  }

  return (
    <aside className="ask-lala rail-anchor" aria-label="Ask Lala">
      {/* FREE-1, HER CLAUSE 2 — *"asking Lala"* is a GENERATION moment: the drawer opens, the SEND
          meets the box, and the on-open history read is held above. */}
      <DrawerTab label="Ask Lala" onClick={() => setOpen(!open)} />
      <EdgeDrawer open={open} onClose={() => setOpen(false)} label="Ask Lala" className="ask-lala-drawer">
        <div className="ask-lala__panel">
          <div className="ask-lala__head">
            <p className="eyebrow">Ask Lala</p>
            <h2>Your guide</h2>
          </div>
          <div className="ask-lala__log" ref={logRef}>
            {messages.length === 0 ? (
              <div className="ask-lala__row ask-lala__row--lala">
                <div className="ask-lala__msg ask-lala__msg--lala">
                  Hello, I am Lala. Ask me anything about your chart, your readings, or your path.
                </div>
              </div>
            ) : null}
            {messages.map((m, index) => (
              <div key={index} className={m.role === "user" ? "ask-lala__row ask-lala__row--user" : "ask-lala__row ask-lala__row--lala"}>
                {m.attachments?.length ? <MessageAttachments items={m.attachments} /> : null}
                {m.role === "user" ? (
                  stripAttachNote(m.content) ? (
                    <div className="ask-lala__msg ask-lala__msg--user">{stripAttachNote(m.content)}</div>
                  ) : null
                ) : (
                  <div className="ask-lala__msg ask-lala__msg--lala">{stripMarkdown(m.content)}</div>
                )}
                {m.cards?.length ? <ChatCards cards={m.cards} /> : null}
                {m.manual ? <ManualHandoffCard manual={m.manual} /> : null}{m.compilation ? <CompilationCard compilation={m.compilation} /> : null}
                {sealsOf(m.seal).map((s) => <SealCard key={s.slug} seal={s} onDownload={(url) => { void downloadFromUrl(url); }} />)}
                {m.sealOptions?.length ? <SealChoiceList options={m.sealOptions} onRequireMembership={onRequireMembership} language={language} /> : null}
              </div>
            ))}
            {sending ? (
              <div className="ask-lala__row ask-lala__row--lala">
                <div className="ask-lala__msg ask-lala__msg--lala ask-lala__msg--typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            ) : null}
          </div>
          {/* R60: BELOW the log, because the log scrolls itself to the newest message and a line at
              the top of it scrolls straight out of sight. */}
          {threadNote ? <p className="reading-menu-note" role="status">{threadNote}</p> : null}
          <AttachmentTray items={files.pending} onRemove={files.remove} uploading={files.uploading} error={files.error} />
          <LalaTurnNotice notice={turnNotice} freeTurns={free.payload} language={language} onUpgrade={APP_MODE ? undefined : () => onRequireMembership(() => {}, { feature: "lala:chat" })} />
          <form className="ask-lala__form" onSubmit={send}>
            {/* W4-7: a growing textarea, not a one-line input. A single line can be scrolled perfectly
                and still cannot let her read back a paragraph before she sends it. Enter sends,
                Shift+Enter takes a new line, and the ref callback re-applies the live view on every
                render, which covers typing as well as speaking. */}
            <textarea
              ref={(el) => { composeRef.current = el; applyLiveView(el, composeLimits); }}
              value={input}
              rows={1}
              disabled={composer.disabled}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(e as unknown as FormEvent); } }}
              placeholder={composer.placeholder || "Ask Lala..."}
            />
            <AttachButton onFiles={(f) => onRequireMembership(() => { void files.attach(f); })} disabled={files.uploading} />
            <MicButton setValue={setInput} field={composeRef} limits={composeLimits} />
            <button className={sending ? "button button--primary is-generating" : "button button--primary"} type="submit" disabled={sending || composer.disabled}>Send</button>
          </form>
        </div>
      </EdgeDrawer>
    </aside>
  );
}

// SEL-1 COMPLETE — the choice-list Lala shows for a PURPOSE ask. Every genuinely-fitting seal, each
// with its plate thumbnail and its one-line source purpose, multi-select. The member ticks one or
// several and sends them to the Cauldron as ONE layered working (a candle per seal, activated in turn
// per her method). The set is already ban + defense filtered server-side; this only renders and rides.
// R50: `language` is REQUIRED (14-B6a), because the two sentences this list shows on a refusal are
// the SAME rows the shared sender reads, and an optional prop nobody passes is R27's live defect.
function SealChoiceList({ options, onRequireMembership, language }: { options: SealOption[]; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [note, setNote] = useState("");
  const toggle = (slug: string) => {
    setState("idle"); setNote("");
    setPicked((p) => { const n = new Set(p); if (n.has(slug)) n.delete(slug); else n.add(slug); return n; });
  };
  const chosen = options.filter((o) => picked.has(o.slug));
  // R50: named `send`, as the shared sender's is. `sendToCauldron` would SHADOW the imported
  // transport it calls — 14-B3's `TierCards` lesson, and a rename back is silent at the type level.
  async function send() {
    if (!chosen.length) return;
    setState("sending"); setNote("");
    const [primary, ...rest] = chosen;
    const seal = (o: SealOption) => ({ slug: o.slug, name: o.name, verseText: o.verseText || "", verseRef: o.verseRef || "" });
    const title = chosen.length > 1 ? `Layered seals: ${chosen.map((c) => c.name).join(", ")}` : primary.name;
    const data = {
      type: "keys-working",
      seal: seal(primary),
      additionalSeals: rest.map(seal),
      overview: chosen.map((c) => `${c.name}: ${c.purpose}`).join(" "),
    };
    // R50: the same transport the shared sender uses, reading the same two catalog rows. This
    // surface hand-typed both the POST and three English sentences until now.
    const answer = await sendToCauldron({ kind: "keys-working", title, data });
    setState(cauldronSendState(answer));
    setNote(cauldronSendNote(answer, { signIn: t(language, "cauldron.sendSignIn"), failed: t(language, "cauldron.sendFailed") }));
  }
  return (
    <div className="seal-choice">
      <p className="seal-choice__eyebrow">{t(language, "cauldron.seals.eyebrow")}</p>
      <ul className="seal-choice__list">
        {options.map((o) => (
          <li key={o.slug} className={picked.has(o.slug) ? "seal-choice__item is-picked" : "seal-choice__item"}>
            <label className="seal-choice__pick">
              <input type="checkbox" checked={picked.has(o.slug)} onChange={() => toggle(o.slug)} />
              <img className="seal-choice__thumb" src={o.imageUrl} alt={o.name} loading="lazy" />
              <span className="seal-choice__text">
                <span className="seal-choice__name">{o.name}</span>
                <span className="seal-choice__purpose">{o.purpose}</span>
              </span>
            </label>
            {/* IT #198/#199: a bare `<a download>` on a phone is a handoff to the OS, which gives the
                file to whatever app claims the type -- an astrology app, for her -- and the seal is
                gone. The ONE transport instead (R28/R29; `SealCard` learned this at #127). */}
            <button type="button" className="seal-choice__download" onClick={() => { void downloadFromUrl(o.imageUrl); }} title={t(language, "cauldron.seals.download", { name: o.name })}>
              <Download size={14} />
            </button>
          </li>
        ))}
      </ul>
      <div className="seal-choice__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={!chosen.length || state === "sending" || state === "sent"}
          onClick={() => onRequireMembership(() => { void send(); })}
        >
          {state === "sent"
            ? t(language, "cauldron.sent")
            : state === "sending"
              ? t(language, "cauldron.sending")
              : chosen.length > 1
                ? t(language, "cauldron.seals.layer", { count: chosen.length })
                : chosen.length === 1
                  ? t(language, "cauldron.send")
                  : t(language, "cauldron.seals.none")}
        </button>
        {/* OV2-7: once the send lands, the door to where it landed. R26 generalised the reasoning onto the shared sender; it is not restated here. */}
        {state === "sent" ? <GoToCauldronButton className="button button--ghost seal-choice__go" language={language} /> : null}
        {note ? <span className="find-your-type__status">{note}</span> : null}
      </div>
    </div>
  );
}

// A conjure drawer (C4): Chants, Affirmations, Incantations. One shared component, three
// tabs. Each is Lala, one shot, in her full voice through every gate, grounded in the
// owner's teachings. Situation box + mic (shared dictation) + generate; the result carries
// Copy and Save to My Room.
// W1-1: the three conjure drawers sit at THREE different tiers, so the tab asks about its own kind.
// The key is DERIVED from the kind in src/magicRoom.js now (`CRAFT_FEATURES`), beside the roster the
// drawer itself comes from — it was a hand-typed map here, 300 lines from the tab that reads it.
function CraftDrawer({ kind, tab, title, blurb, placeholder, cta, isMember, onRequireMembership, language = DEFAULT_LANGUAGE }: {
  kind: "chant" | "affirmation" | "incantation";
  tab: string;
  title: string;
  blurb: string;
  placeholder: string;
  cta: string;
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void; language?: string;
}) {
  const requireFeature = useRequireFeature();
  // W4-7: the field the mic writes into, so a long dictation stays visible in it.
  const situationRef = useRef<HTMLTextAreaElement | null>(null);
  const [open, setOpen] = useState(false);
  const [situation, setSituation] = useState("");
  const [candle, setCandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState<{ text: string; label: string; genKey?: string } | null>(null);
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [copied, setCopied] = useState<CopyState>("idle");

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!situation.trim()) { setStatus(t(language, "craft.needSituation")); return; }
    onRequireMembership(() => { void run(); });
  }
  async function run() {
    setBusy(true); setResult(null); setSaved("idle"); setStatus(t(language, "craft.status"));
    try {
      const res = await fetch("/api/lala/craft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, situation: situation.trim(), candle: candle.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "craft.signIn")); return; }
      if (!res.ok || data?.ok === false || !data?.text) { setStatus(data?.message || t(language, "craft.failed")); return; }
      setResult({ text: data.text, label: data.label || tab, genKey: data.genKey }); setStatus(data.autosaved ? t(language, "craft.autosaved") : "");
      if (data.autosaved) setSaved("saved");
    } catch { setStatus(t(language, "craft.failed")); }
    finally { setBusy(false); }
  }
  // R56: this one was already honest about the write and SILENT about the failure — an empty
  // catch, so a refused copy was a tap that did nothing at all (R25's argument, applied to a
  // control rather than to an absent one).
  async function copy() {
    if (!result) return;
    const answer = await copyText(result.text);
    setCopied(copyState(answer));
    window.setTimeout(() => setCopied("idle"), COPY_FLASH_MS);
  }
  async function save() {
    if (!result) return;
    setSaved("saving");
    // This drawer shows no sentence, only the chip, so it needs the STATE and no copy at all.
    const answer = await postReadingToRoom({ type: kind, title: `${result.label}: ${situation.trim().slice(0, 48)}`, genKey: result.genKey, data: { header: { name: "You" }, kicker: result.label, overview: result.text, situation: situation.trim() } });
    setSaved(saveReadingState(answer));
  }

  return (
    <aside className={`craft-drawer craft-drawer--${kind}`} aria-label={tab}>
      {/* FREE-1: all three conjure drawers open; each submit already asks `onRequireMembership`. */}
      <DrawerTab label={tab} onClick={() => setOpen(!open)} />
      <EdgeDrawer open={open} onClose={() => setOpen(false)} label={tab} className="craft-edge-drawer">
        <div className="craft-panel">
          <div className="craft-panel__head">
            <p className="eyebrow">{tab}</p>
            <h2>{title}</h2>
            <p className="craft-panel__blurb">{blurb}</p>
          </div>
          <form className="craft-panel__form" onSubmit={submit}>
            <label className="craft-panel__field">
              <span>{t(language, "craft.field.situation")}</span>
              <textarea ref={situationRef} value={situation} onChange={(e) => setSituation(e.target.value)} placeholder={placeholder} rows={4} />
            </label>
            {kind === "chant" ? (
              <label className="craft-panel__field">
                <span>{t(language, "craft.field.candle")}</span>
                <input value={candle} onChange={(e) => setCandle(e.target.value)} placeholder={t(language, "craft.field.candlePlaceholder")} />
              </label>
            ) : null}
            <div className="craft-panel__controls">
              <MicButton setValue={setSituation} field={situationRef} />
              <button className={busy ? "button button--primary is-generating" : "button button--primary"} type="submit" disabled={busy}>
                {busy ? t(language, "craft.going") : cta}
              </button>
            </div>
          </form>
          {status ? <p className="craft-panel__status">{status}</p> : null}
          {result ? (
            <div className="craft-result">
              {/* CD2: chants and affirmations are individual, one-line pieces — show them as a
                  visually-separated list. An incantation is one flowing working — keep it whole. */}
              {kind === "incantation" ? (
                <pre className="craft-result__text">{result.text}</pre>
              ) : (
                <ul className="craft-result__list">
                  {result.text.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => (
                    <li key={i} className="craft-result__item">{line}</li>
                  ))}
                </ul>
              )}
              <div className="craft-result__actions">
                <button type="button" className="button button--ghost" onClick={copy}>{copyLabel(copied, copyWords(language, t))}</button>
                <button type="button" className="button button--ghost" onClick={save} disabled={saved === "saving" || saved === "saved"}>
                  {t(language, saved === "saved" ? "result.saved" : saved === "saving" ? "result.saving" : "result.save")}
                </button>
              </div>
              {saved === "error" ? <p className="craft-panel__status">{t(language, "common.saveFailed")}</p> : null}
            </div>
          ) : null}
        </div>
      </EdgeDrawer>
    </aside>
  );
}

function FloatingCraft({ isMember, onRequireMembership, language = DEFAULT_LANGUAGE }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language?: string }) {
  const requireFeature = useRequireFeature();
  return (
    /* RAIL RESTORE (her ruling, 2026-08-10) — **THE BOTTOM FIVE OF HER NINE, ALL WITH THEIR OWN
       TABS: Chants, Affirmations, Incantations, Angels, and My Closet LAST.** RAIL-9's `.rail-tucked`
       box and its `More` tab are gone: every drawer is a member of this stack again, so it inherits
       the rail's right-edge placement, its stacking order and its rhythm by BEING in it rather than
       by a second set of coordinates that rot the first time the rail moves (ITEM 1's original
       correction, which still stands). The order in this file IS the order on her screen — the grid
       has no other opinion — and `src/rail.js` holds it. */
    <div className="craft-stack rail-anchor" aria-label="Conjure drawers">
      {/* R37: the three drawers are the ONE roster in src/magicRoom.js, mapped. They used to be
          hand-typed here, and the kind typed here is the kind the server registers — so a fourth
          drawer added at this spot and forgotten in that file is R28's defect exactly: her name and
          her date of birth printed on the working. */}
      {CRAFT_DRAWERS.map((d) => (
        <CraftDrawer key={d.kind} kind={d.kind} tab={d.tab} title={t(language, d.copy.title)}
          blurb={t(language, d.copy.blurb)} placeholder={t(language, d.copy.placeholder)} cta={t(language, d.copy.cta)}
          isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
      ))}
      <AngelsDrawer isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
      <FloatingCloset requireFeature={requireFeature} language={language} />
    </div>
  );
}

// The daily card, drawn and revealed in a popup inside My Room. One per day: if
// today's card was already drawn, it shows that same card again (cached).
// OV2-4 / R11-1: TarotFlipCard (she turns her own cards) and CardZoomOverlay moved to
// src/components/TarotViews.tsx with the reading view that is built from them.


// Purple-glow gate shown when a non-member triggers report generation beyond the
// free three. The purchase button is wired to Stripe at launch; the return-path
// (resume the gated action after joining) is already built in completeMembership.
// T1-FIX — WHAT THIS MEMBER MAY OPEN, ASKED OF THE SERVER.
//
// The tier map lives in `server/tiers.js` and the server resolves it; this hook only carries the
// answer across. Nothing here re-implements a rule, which is the whole point: the free layer was
// missed in the first place because the check that mattered lived on the client with no idea the
// rule existed.
//
// It fails CLOSED on the free list (an empty list until the answer arrives) so a network hiccup can
// never accidentally hand out something paid, and it settles within one round trip.
// W1-1 — THE FEATURE GATE, REACHABLE FROM ANY SURFACE WITHOUT THREADING A PROP THROUGH SEVEN
// COMPONENTS. Every floating drawer, every player and every reader asks the same question through
// this: "may she open THIS?" The default is deliberately a NO-OP that never opens anything — a
// surface rendered outside the provider stays shut rather than quietly opening ungated.
const RequireFeature = createContext<(feature: string, action: () => void) => void>(() => {});
const useRequireFeature = () => useContext(RequireFeature);

// FREE-1 — **THE SAME QUESTION ASKED WITHOUT A TAP BEHIND IT.** `requireFeature` answers *she
// pressed this* and answers a refusal with the box; a surface that merely ARRIVED needs *may I ask
// the server for this at all*, because with the browse walls gone a locked surface fetching on mount
// turns T8-3's interceptor into the popup-on-arrival her clause 1 forbids — and no call site can be
// blamed for that (~50 of them know nothing about 402s). It carries the ENTITLEMENTS, not a boolean,
// so the caller can tell R20's `pending` from `locked`, and the default `null` IS `pending`.
// >>> **EV3 — THE ONE SIGNAL THAT SAYS *ASK AGAIN WHAT SHE MAY OPEN*.** <<<
//
// A gate drop changes what every door answers, and this client drew its locks from a read taken
// BEFORE the window existed. The server already honours the drop on every request — this is what
// makes the SCREEN agree with it, without a reload in the middle of her event.
//
// **ONE NAME, DECLARED HERE**, because two listeners spelling it differently is a page half-open
// (R28/R29). Both entitlement readers in this file answer to it.
export const ENTITLEMENTS_CHANGED = "12h:entitlements-changed";

const FeatureAccess = createContext<ReturnType<typeof featureEntitlements>>(null);
const useFeatureOutcome = (feature: string) => featureOutcome(useContext(FeatureAccess), feature);

// R91 — **`null` IS A STATE, NOT A DEFAULT, AND THE OLD INITIAL VALUE WAS A FORGERY.** It seeded
// `{ tier: "free", ... }`: a COMPLETE ANSWER saying she pays nothing and owns nothing, which a failed
// read left standing forever. `src/lib/entitlements.ts` holds what each consumer paid for it.
function useEntitlements(): MemberEntitlements | null {
  const [ent, setEnt] = useState<MemberEntitlements | null>(null);
  useEffect(() => {
    let active = true;
    const read = () => {
      void readEntitlements().then((answer) => {
        if (!active || answer.kind !== "payload") return;
        setEnt(memberEntitlements(answer.payload));
      });
    };
    read();
    // EV3 — the same signal the lock reader answers to, so one drop cannot leave two readers
    // disagreeing about what is open (R28/R29).
    window.addEventListener(ENTITLEMENTS_CHANGED, read);
    return () => { active = false; window.removeEventListener(ENTITLEMENTS_CHANGED, read); };
  }, []);
  return ent;
}
import { MembershipPage } from "./components/MembershipPage";

// W1-5 / her 2026-08-18 ruling — the image box, the coded box and `PurpleBox` are all retired; her
// artwork IS the membership page (`src/components/MembershipPage.tsx`), and the two W1-5 PNGs stay
// in `design/membership-art/`, out of `public/` and off the wire. The whole retirement record is in
// CLAUDE.md's membership build note, where it is read rather than carried by the file it left (R8).

// =============================================================================================
// T8-7 — THE MEMBERSHIP PAGE KNOWS WHO SHE IS. ONE ANSWER, AND SINCE W1-5 ONE RENDERER.
//
// The owner's walk: she was on NEWBIE, and the page told her she was "already on Apprentice", her
// own tier's card did nothing when she pressed it, and a Vet click refreshed into nothing. The page
// was selling a ladder it had no idea she was standing on.
//
// So what a card IS to this member is decided once, in `src/lib/tier-state.ts`, and the coded box
// reads it. Four states, and every one of them is a different button: the tier she is on is HERS and
// inert, everything below it is already INCLUDED and inert, everything above it is a live UPGRADE
// through real checkout (which under T8-6 grants nothing until the webhook lands), and a member on no
// tier at all simply JOINS. There were three renderers of that answer and W1-5 left one, which is the
// quiet win of the rebuild: they can no longer disagree.
// =============================================================================================


// W1-2: `MembershipGate` was DELETED here, and it is worth one line on why rather than a silent
// removal. It was a second locked-door modal, defined and never rendered since the purple box
// replaced it: a members-only eyebrow, a DISABLED join button advertising that membership was not
// open yet, and a promise that she would pick up where she left off once it was. Membership has been
// open for weeks. A dead refusal surface is not harmless: it is the thing the next agent copies for
// its shape, and this one named no tier and pointed at no button. (The banned strings it carried are
// not quoted here, because `scripts/w1-refusal-copy.mjs` scans this file for them.)
// >>> MIGRATED AT ITS DECLARATION (DL-2/DT-2b/R181), BECAUSE IT NAMED A SURFACE HER 2026-08-18
// RULING RETIRED. <<< It read *"There is ONE locked door on this site and it is `PurpleBox`."* --
// true the day it was written, and left standing it would have ENFORCED the popup her live tester
// found. **THERE IS ONE LOCKED DOOR ON THIS SITE AND IT IS THE MEMBERSHIP PAGE.** What the line was
// ever about survives whole and is stronger: a dead refusal surface is the thing the next agent
// copies for its shape, so there is exactly one, and it is a page rather than a modal.

// Combine name parts into the full name numerology and display use.
// combineName / splitName / withNameParts now live in ./nameParts.js (shared with the fast gate
// so the Q2 "wrong numbers" fix is locked by a deterministic test).

// R12-1: NameFields + NAME_AT_BIRTH_NOTE moved to src/components/BirthFields.tsx with the other two
// field blocks, so CreateRoomForm could follow them out and be CALLED by a gate.

// Map a saved Person of Interest (or the member's own chart) onto a full BirthChartForm so
// any generator's fields fill in one tap. first/middle/last stay empty for a saved person
// (generators fall back to the full `name`).
// R75: personToChart moved to src/components/BirthFields.tsx, beside initialBirthChart (the blank
// it patches) and the three field blocks that emit these exact keys — where a gate already CALLS it.

// R220: `SaveSubjectOffer` and `savePersonFromChart` moved VERBATIM to
// `src/components/SaveSubjectOffer.tsx` — see the import at the top of this file. Nine call sites
// render that offer and nothing could reach either half of it from in here: the transport is a PURE
// three-way law (saved / duplicate / failed) no `.mjs` gate could load, and the component that
// speaks its three sentences could not be imported by one. It paid this round's ceiling.

// THE SUBJECT PICKER (site-wide). No member ever retypes what the site already holds. Every
// generator that needs birth details opens with this: "For me" (the member's own card, one
// tap), each saved Person of Interest (one tap), or "Someone new" (blank fields + an offer to
// save them after). Build every new generator WITH this, never bare fields (PROJECT_DIRECTIVES).
// 14-B6c: `label` and `meLabel` DEFAULT from the catalog, never from an English literal, so a
// caller's own question still wins (R24's trap: ten of the fifteen call sites were re-typing the
// default in English, and translating over a call site would have renamed the four that mean
// something else).
function SubjectPicker({ memberChart, onPick, language = DEFAULT_LANGUAGE, label, meLabel, allowNew = true }: {
  memberChart: BirthChartForm | null;
  onPick: (chart: BirthChartForm, meta: { kind: "me" | "person" | "new"; personId?: string }) => void;
  language?: string;
  label?: string;
  meLabel?: string;
  allowNew?: boolean;
}) {
  const [people, setPeople] = useState<SubjectPickerPerson[]>([]);
  const [ownCard, setOwnCard] = useState<BirthChartForm | null>(null);
  // R85: whether her own card could be READ, which is a different question from whether she has one.
  // `null` on this state means "nothing was learned", exactly as `known: false` does in the law.
  const [ownCardNote, setOwnCardNote] = useState("");
  const [active, setActive] = useState<string>("");
  const [editing, setEditing] = useState<SubjectPickerPerson | null>(null);
  const loadPeople = useCallback(() => {
    void listPeople<SubjectPickerPerson>().then((list) => { if (list) setPeople(list); });
  }, []);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  // SP1: if the parent did not supply a populated member chart, fetch the member's own card so
  // "For me" ALWAYS appears (the astrology page was passing an empty chart, hiding it). The
  // picker owns its "For me" source; parents no longer have to thread the profile through.
  const parentHasChart = Boolean(memberChart && (memberChart.birthDate || memberChart.name || memberChart.firstName));
  useEffect(() => {
    if (parentHasChart) return;
    let on = true;
    // R85 — **R71's LAW ALREADY ANSWERS FOUR WAYS AND THIS SITE READ ONE FIELD OF IT.** `known` is
    // "we learned whether she has a room", and it is the whole difference between *she has no chart*
    // and *we could not ask*. Without it a dropped read took "For me" off the first control in every
    // reading room — an absent control, with no error state (R25), on the one subject she is most
    // likely to have come to read.
    void readProfile<Partial<BirthChartForm>>().then(({ profile: p, known }) => {
      if (!on) return;
      setOwnCardNote(known ? "" : t(language, "subject.meFailed"));
      if (p && (p.birthDate || p.name)) setOwnCard({ ...initialBirthChart, ...p });
    });
    return () => { on = false; };
  }, [parentHasChart, language]);
  const effectiveMember = parentHasChart ? memberChart : ownCard;
  const memberHasChart = Boolean(effectiveMember && (effectiveMember.birthDate || effectiveMember.name || effectiveMember.firstName));
  // SP4: People of Interest as a "My circle" dropdown (scattered chips do not scale).
  const [circleOpen, setCircleOpen] = useState(false);
  const circleRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!circleOpen) return;
    const onDown = (e: MouseEvent) => { if (circleRef.current && !circleRef.current.contains(e.target as Node)) setCircleOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [circleOpen]);
  const activePerson = active.startsWith("person:") ? people.find((p) => `person:${p.id}` === active) : null;
  // IT #318: while the circle is open the picker says so, so it can lift itself above the form that
  // follows it. The rooms lift every child to z-index 1, and the later sibling painted over the menu.
  return (
    <div className={circleOpen ? "subject-picker is-circle-open" : "subject-picker"}>
      <p className="subject-picker__label">{label ?? t(language, "subject.label")}</p>
      <div className="subject-picker__choices">
        {memberHasChart ? (
          <button type="button" className={active === "me" ? "subject-chip is-active" : "subject-chip"}
            onClick={() => { setActive("me"); onPick(withNameParts({ ...initialBirthChart, ...(effectiveMember || {}), subjectKind: "profile", subjectId: undefined }), { kind: "me" }); }}>
            {meLabel ?? t(language, "subject.me")}
          </button>
        ) : null}
        {/* R85: and where the chip would have been, when the read that feeds it did not land. It is
            drawn ONLY in that case — a member who genuinely has no chart yet is told nothing here,
            because the create-your-room flow is the one that owns that sentence. */}
        {!memberHasChart && ownCardNote ? (
          <p className="subject-picker__note">{ownCardNote}</p>
        ) : null}
        {people.length > 0 ? (
          <div className="subject-circle" ref={circleRef}>
            <button type="button" className={activePerson ? "subject-chip is-active" : "subject-chip"}
              onClick={() => setCircleOpen((o) => !o)} aria-expanded={circleOpen} aria-haspopup="listbox">
              <span className="subject-chip__name">{activePerson ? activePerson.name : t(language, "subject.circle")}</span>
              <span className="subject-circle__chev" aria-hidden="true">▾</span>
            </button>
            {circleOpen ? (
              <ul className="subject-circle__menu" role="listbox">
                {people.map((p) => (
                  <li key={p.id} className="subject-circle__row">
                    <button type="button" className="subject-circle__item" role="option" aria-selected={active === `person:${p.id}`}
                      onClick={() => { setActive(`person:${p.id}`); setCircleOpen(false); onPick(personToChart(p), { kind: "person", personId: p.id }); }}>
                      <span className="subject-circle__name">{p.name}</span>
                      {p.relationship ? <span className="subject-circle__rel">{p.relationship}</span> : null}
                    </button>
                    {/* SP5: edit a saved person in place (spelling, birth time, city). */}
                    <button type="button" className="subject-circle__edit" title={t(language, "subject.edit", { name: p.name })} aria-label={t(language, "subject.edit", { name: p.name })}
                      onClick={() => { setCircleOpen(false); setEditing(p); }}>✎</button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {allowNew ? (
          <button type="button" className={active === "new" ? "subject-chip subject-chip--new is-active" : "subject-chip subject-chip--new"}
            onClick={() => { setActive("new"); onPick({ ...initialBirthChart }, { kind: "new" }); }}>
            {t(language, "subject.new")}
          </button>
        ) : null}
      </div>
      {editing ? (
        <PersonEditModal person={editing} language={language} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadPeople(); }} />
      ) : null}
    </div>
  );
}

// SP5: edit a saved person in place. Corrects their card (name spelling, birth time, city);
// the person keeps the same id, so existing readings stay attached. PATCHes /api/people/:id.
// R12-2: PersonEditModal moved to src/components/PersonEditModal.tsx, where a gate can IMPORT it.
// It carries `personToChart`, whose 12/24-hour trap this house has already paid for. Moved verbatim.

// The birth-details form that creates a member's room, used on the home page and in
// the new-member onboarding modal.
// R12: CreateRoomForm — THE FRONT DOOR — moved to src/components/CreateRoomForm.tsx, where a gate
// CALLS it: the consent checkbox that holds the button shut, the middle-name requirement, the two
// locations, and every disabled state. It holds no hook, which is why it could go.

// ---------------------------------------------------------------- FEATURED READINGS (HP1/HP2)
// The home page's showcase, built to `homepage example.png`. The bubbles are gone; the questions
// and the engine behind them are unchanged, which is why this was a presentation change and not a
// rebuild. Config-driven from server/data/featured-readings.json: the owner tunes the roster by
// reordering `featured`, and every other question stays in the file for rotation.
// R74: both payload types moved to src/lib/home-showcase.ts, beside the ONE transport that reads
// them — and the read they described drew no hero at all when it failed.

// SH2 — THE SHELVES DRAWER (supersedes SH1's in-room shelf display). Shelves are the FIRST tab on
// the right rail. Opening it lists the rooms that have shelves; picking one shows that room's
// shelved generations in ASCENDING date order, scrollable inside the drawer. Picking an item opens
// the full report (Save to My Room + PDF + delete; no shelf option, it is already shelved). The SH1
// storage/auto-shelf plumbing is unchanged; this is only where they LIVE now — one home, the drawer.
// R17: SHELF_ROOMS moved to src/rooms.js — it is the receiving end of the `room` the server writes
// onto a generation, so a gate has to be able to import it.

function FloatingShelves({ isMember, onRequireMembership, language }: { isMember: boolean; language: string; onRequireMembership: (action: () => void, lock?: LockInfo) => void }) {
  const requireFeature = useRequireFeature();
  const [open, setOpen] = useState(false);
  const [room, setRoom] = useState<string>(""); // "" = the room list
  // R81: null is not []. W5-2/R39's third state — an untaken count is not a room with nothing in it.
  const [items, setItems] = useState<ShelfItem[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [openReport, setOpenReport] = useState<AnyReport | null>(null);
  const [viewing, setViewing] = useState<ShelfItem | null>(null); // the item whose report is open
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  // R81: one transport for both reads (src/lib/room-shelf.ts); the guard is above the paint (R52).
  const loadRoom = useCallback(async (r: string) => {
    const answer = await fetchRoomShelf<ShelfItem>(r);
    const list = shelfItemsOr(answer);
    if (!list) { setNote(shelfNote(answer, SHELF_READ_COPY)); return; }
    setNote("");
    // OV2-6: NEWEST FIRST, the same law as every other shelf (this reverses I3's oldest-first).
    list.sort(byNewestActivity); // W8-2: on ACTIVITY, so a re-run rises to where she just put it
    setItems(list);
    setCounts((c) => ({ ...c, [r]: list.length }));
  }, []);

  // When the drawer opens, tally each room's shelf so the list shows counts and empties read clearly.
  useEffect(() => {
    if (!open || !isMember) return;
    let on = true;
    (async () => {
      const answers = await Promise.all(SHELF_ROOMS.map(async (r: string) => [r, shelfCountOr(await fetchRoomShelf<ShelfItem>(r))] as const));
      if (!on) return;
      setCounts(Object.fromEntries(answers));
      if (answers.every(([, n]: readonly [string, number | null]) => n === null)) setNote(SHELF_READ_COPY.failed);
    })();
    return () => { on = false; };
  }, [open, isMember]);

  // N9-1: this drawer belongs to the Shelves TAB, so it tells the bar when it is up and when it has gone.
  // The bar cannot infer it (a drawer does not change the page), and the member closing it with its own X
  // has to clear the highlight just as surely as tapping another tab does.
  useEffect(() => { announceOverlayTab("shelves", open); }, [open]);

  // M3: the mobile bottom tab bar's "Shelves" tab opens this drawer via a global event, so the
  // tab bar does not need to reach into this component's local state.
  // N1-2: the same event now optionally carries a room (and an item), so the site-wide generation
  // follower can hand a finished reading straight to the member wherever they are standing, without a
  // second report viewer existing anywhere. One door.
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);
  useEffect(() => {
    // R49: read through the door's own law, and `canOpenItem` is what stops an item with no room
    // latching a pending id against a shelf this drawer never loads — R17's silence, closed here.
    const onOpenShelves = (e: Event) => {
      const request = shelvesRequestFrom(e);
      const go = () => {
        setOpen(true);
        if (request.room) { setRoom(request.room); setNote(""); void loadRoom(request.room); }
        if (canOpenItem(request)) setPendingOpenId(request.itemId);
      };
      if (isMember) go(); else onRequireMembership(go);
    };
    window.addEventListener(OPEN_SHELVES_EVENT, onOpenShelves);
    return () => window.removeEventListener(OPEN_SHELVES_EVENT, onOpenShelves);
  }, [isMember, onRequireMembership, loadRoom]);

  function pickRoom(r: string) { setRoom(r); setNote(""); loadRoom(r); }

  // R53 — **THE DRAWER'S OWN SILENT TAP.** `fetchReport` was `catch { return null; }` and this was
  // `if (data) setOpenReport(data)` with no else: `setViewing(item)` had already run, so the drawer
  // showed the item selected and the reading never arrived. The note this drawer already renders is
  // where it says so, at drawer width, outside every card.
  async function openItem(item: ShelfItem) {
    setBusy(item.id);
    setNote("");
    try {
      setViewing(item);
      if (item.kind === "seal") { setOpenReport({ __seal: item.id, header: { name: item.title } } as unknown as AnyReport); return; }
      const answer = await fetchSavedReport<AnyReport>(item.id);
      setNote(openReportNote(answer, { signIn: t(language, "common.openSignIn"), failed: t(language, "common.openFailed") }));
      if (answer.ok) setOpenReport(answer.data);
    } finally { setBusy(""); }
  }
  // N1-2: a finished reading handed over by the follower opens as soon as its room's shelf has loaded.
  useEffect(() => {
    if (!pendingOpenId || !items?.length) return;
    const hit = items.find((i) => String(i.id) === pendingOpenId);
    setPendingOpenId(null);
    if (hit) void openItem(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenId, items]);

  // R39 — THE SAVE READS THE ANSWER, not the HTTP status: this route refuses with 200 + `ok:false`,
  // so a refused save used to mark the row "In My Room". The whole story is in src/lib/saved-readings.ts.
  async function saveMyRoom(item: ShelfItem) {
    setBusy(item.id);
    try {
      if (!(await saveReadingToMyRoom(item.id))) { setNote(`${shelfTitle(item)} could not be saved to My Room right now.`); return; }
      setItems((cur) => (cur ? cur.map((i) => (i.id === item.id ? { ...i, inMyRoom: true } : i)) : cur));
      setViewing((v) => (v && v.id === item.id ? { ...v, inMyRoom: true } : v));
      setNote(`${shelfTitle(item)} is in My Room.`);
    } finally { setBusy(""); }
  }
  // N7-4 — RENAME ON THE SHELF. A member can call anything she made whatever she wants, whenever she
  // wants, and it sticks (the title is replaced in the database in place, nothing regenerated). Seals
  // live in their own table and are not renameable here, so the pencil does not offer it for them.
  // R38: the box, the blank-name law and the failure sentence are the house's one control now — this
  // keeps only what is this drawer's own business, which is the two lists that hold the row.
  async function renameItem(item: ShelfItem, title: string): Promise<string | null> {
    const stored = await renameSavedReport(item.id, title);
    if (!stored) return null;
    setItems((cur) => (cur ? cur.map((i) => (i.id === item.id ? { ...i, title: stored } : i)) : cur));
    setViewing((v) => (v && v.id === item.id ? { ...v, title: stored } : v));
    setNote(`Renamed to ${stored}.`);
    return stored;
  }

  // R35: same law as deleteSavedReport. This one also decremented the shelf COUNT on a delete that
  // never happened, so the drawer's own tally disagreed with the shelf it was counting.
  // R53: the transport takes BOTH kinds, because this one control destroys two of them and the two
  // routes answer in the same shape (R35's "one house, one answer").
  async function remove(item: ShelfItem): Promise<boolean> {
    if (!(await deleteSavedItem(item.kind === "seal" ? "seal" : "report", item.id))) return false;
    setItems((cur) => (cur ? cur.filter((i) => i.id !== item.id) : cur));
    // R81: decrementing `null` would invent a tally out of a read that failed.
    setCounts((c) => (typeof c[room] === "number" ? { ...c, [room]: Math.max(0, (c[room] as number) - 1) } : c));
    return true;
  }

  const openReadModal = openReport ? (openReport as unknown as { __seal?: string }).__seal : undefined;
  const activeItem = viewing;

  return (
    <div className="floating-shelves rail-anchor" aria-label="Shelves drawer">
      {/* FREE-1, HER CLAUSE 3 — THE SHELVES ARE FREE: the wall is gone AND `shelves` is declared free in `server/tiers.js`. */}
      <DrawerTab label="Shelves" onClick={() => setOpen(!open)} />
      <EdgeDrawer open={open} onClose={() => setOpen(false)} label="Shelves" className="shelves-drawer">
        <div className="shelves-drawer__panel">
          {!room ? (
            <>
              <p className="eyebrow">Your shelves</p>
              <h2 className="shelves-drawer__title">Everything you've made</h2>
              <p className="shelves-drawer__intro">Every reading you run is saved to its room's shelf, present or not. Open a shelf to read, save, or download any of them.</p>
              <ul className="shelves-drawer__rooms">
                {SHELF_ROOMS.map((r) => (
                  <li key={r}>
                    <button type="button" className="shelves-drawer__room" onClick={() => pickRoom(r)}>
                      <span className="shelves-drawer__room-name">{roomLabel(r)}</span>
                      {/* R81: a count that could not be read draws a dash, never a zero. */}
                      <span className="shelves-drawer__room-count">{typeof counts[r] === "number" ? counts[r] : "—"}</span>
                      <ArrowRight size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <button type="button" className="shelves-drawer__back" onClick={() => setRoom("")}><ArrowLeft size={16} /> All shelves</button>
              <h2 className="shelves-drawer__title">{roomLabel(room)}</h2>
              {/* R81 — three states where there were two: the empty-shelf invitation over a shelf
                  that never arrived is R60's finding, a wrong sentence rather than a missing one. */}
              {items === null ? (
                <p className="shelves-drawer__empty">{SHELF_READ_COPY.failed}</p>
              ) : items.length === 0 ? (
                <p className="shelves-drawer__empty">Nothing on this shelf yet. Readings you run in this room land here and stay until you take them down.</p>
              ) : (
                <ul className="shelves-drawer__items">
                  {items.map((item) => (
                    <li key={`${item.kind}-${item.id}`} className="shelf-card">
                      <button type="button" className="shelf-card__open" onClick={() => openItem(item)} disabled={busy === item.id}>
                        <span className="shelf-card__title">{shelfTitle(item)}</span>
                        <PreviousDetailsBadge when={item.fromPreviousDetails} />
                        {/* NIKA: the SAME mark My Room's chip draws, off the SAME predicate. The
                            owner's report was that these two surfaces read her differently; they
                            now cannot, because there is one law and one component. */}
                        <UnwrittenBadge interpreted={item.interpreted} />
                        <span className="shelf-card__date">{new Date(shelfActivityAt(item)).toLocaleDateString()}</span>
                      </button>
                      {/* A seal lives in its own table and has neither a rename route nor a My Room
                          row, so neither control is offered for one — the reason is declared in the
                          R38 and R39 fixtures, not implied. */}
                      {item.kind === "seal" ? null : (<>
                        <SaveReadingButton id={item.id} title={shelfTitle(item)} saved={item.inMyRoom === true} variant="row" onSave={() => saveMyRoom(item)} className="shelf-card__myroom" />
                        <RenameControl kind="reading" value={item.title} what={shelfTitle(item)} onRename={(t) => renameItem(item, t)} className="shelf-card__rename-ask" />
                      </>)}
                      <DeleteControl kind={item.kind === "seal" ? "seal" : "reading"} what={shelfTitle(item)} className="shelf-card__delete-ask" onDelete={() => remove(item)} />
                    </li>
                  ))}
                </ul>
              )}
              {note ? <p className="shelves-drawer__note" role="status">{note}</p> : null}
            </>
          )}
        </div>
      </EdgeDrawer>

      {/* OV2-9: the shelf reading she named by name. onClose is the tab's authority over it.
          W12-2: what it is CALLED comes from src/magicRoom.js — the Magic Room's shelf items open
          in this same modal, and a spell is not a reading. */}
      {openReport ? (
        <ModalBackdrop label={outputNoun((openReport as any)?.type, "Shelf reading")} onClose={() => { setOpenReport(null); setViewing(null); }}>
          <div className="reading-modal">
            <button className="modal-close" type="button" onClick={() => { setOpenReport(null); setViewing(null); }} aria-label="Close">x</button>
            {openReadModal ? (
              // #127: this shipped with ONE download, wired to the description, which draws no seal
              // at all. Extracted so a gate CALLS it — why: scripts/seal-download.mjs.
              <SealShelfView sealId={String(openReadModal)} title={activeItem ? shelfTitle(activeItem) : "Your seal"} language={language} />
            ) : (
              <div className="chart-report__wrap">
                <div className="reader__toolbar">
                  <span><Eye size={16} /> {outputNoun((openReport as any)?.type)}</span>
                  <div className="chart-report__actions">
                    {/* SH2: already shelved, so no shelf option here. Save to My Room + PDF only.
                        R39: the same control the shelf CARD wears, so the two cannot drift apart on
                        what the state is called or on what a refused save does. */}
                    {activeItem ? (
                      <SaveReadingButton id={activeItem.id} title={shelfTitle(activeItem)} saved={activeItem.inMyRoom === true} onSave={() => saveMyRoom(activeItem)} />
                    ) : null}
                    {pdfAllowed(openReport) ? <button type="button" onClick={() => downloadReportPdf(openReport)}>Download PDF</button> : null}
                    {/* N4-3: the shared handoff. Renders only for READING types (src/askLalaHandoff.js). */}
                    <AskLalaAboutButton report={openReport} kind="reading" />
                  </div>
                </div>
                <ReportWithPrint data={openReport} />
              </div>
            )}
          </div>
        </ModalBackdrop>
      ) : null}
    </div>
  );
}

// SH1 — the three destinations, on every delivered result. The room SHELF is automatic (the
// no-hostage law: the server persisted it the moment it finished, present member or not), so it
// shows as already-done — a true, reassuring confirmation, not a button that could fail. My Room
// and PDF are the member's active choices. This is the ONE result-actions block for every room.
// R24: ResultDestinations and NoHostageNote moved to src/components/ResultDestinations.tsx — the
// only two sentences on this site that tell her WHICH SHELF her reading landed on, and a gate now
// calls them and holds every room they can name against the real roster.

// 14-B6c: `language` is threaded through here for the SUBJECT PICKER inside the bubble modal, which
// is the same control the reading rooms open with. The showcase's own copy is SERVER-composed
// (`/api/featured-readings`), so nothing else on this strip turns — that is 14-B3's seam, not a gap.
function HomeShowcase({ memberChart, isMember, onRequireMembership, onAsk, onOpenRoom, language = DEFAULT_LANGUAGE }: {
  memberChart: BirthChartForm | null;
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  onAsk: (question: string) => void;
  // AKASHIC-1: a room-linked card is a door, and the door needs somewhere to go.
  onOpenRoom: (page: string) => void;
  language?: string;
}) {
  // R74: `undefined` is in flight, `null` is a read that did not land, and a showcase is a showcase.
  const [config, setConfig] = useState<ShowcaseConfig | null | undefined>(undefined);
  const [active, setActive] = useState<ShowcaseBubble | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => { void readShowcase().then(setConfig); }, []);

  // R74: her logo is a committed asset and always arrives, so the front door keeps its anchor and
  // says one quiet sentence where the welcome would have been — rather than the whole hero going.
  const failed = showcaseNote(config, t(language, "home.showcaseFailed"));
  if (failed) return (
    <section className="home-hero" aria-label="Welcome">
      <img className="home-hero__logo" src={homeLogoUrl} alt="12th House" />
      <p className="home-hero__note" role="status">{failed}</p>
    </section>
  );
  if (!config?.featured?.length) return null;

  // HP1: the question is the point. Submitting hands it to the Ask Lala page ALREADY ASKED, so
  // the answer is generating on arrival and nobody retypes anything. The gate fires here, on
  // submit, so a non-member meets it before they are moved anywhere.
  const ask = (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    onRequireMembership(() => { onAsk(q); setQuery(""); });
  };

  return (
    <>
      {/* HP8: the banner is GONE (the owner's call: it no longer fit the aesthetic). Her logo is
          the front door's anchor now, centred at the top of the hero. Nothing is left behind
          where the banner sat: the element and its spacing went with it. */}
      <section className="home-hero" aria-label="Welcome">
        <img className="home-hero__logo" src={homeLogoUrl} alt="12th House" />
        <p className="home-hero__eyebrow">{config.eyebrow}</p>
        <h1 className="home-hero__title">{config.title}</h1>
        <div className="home-hero__rule" aria-hidden="true"><span /><Sparkles size={13} /><span /></div>
        {/* HP7.3 removed the subtext line. The config still carries the field, so the owner can
            put a line back by filling it in; empty means no line, not an empty gap. */}
        {config.subtext.trim() ? (
          <p className="home-hero__sub">
            {config.subtext.split("\n").map((line, i) => <span key={i}>{line}</span>)}
          </p>
        ) : null}

        {/* HP7.4: this is the Ask Lala section. */}
        <h2 className="home-ask__heading">{config.askHeading || "Ask Lala"}</h2>

        {/* HP9: the line sits directly ABOVE the box now (it used to sit under the chips), so it
            reads as the invitation to the box rather than a footnote after it. */}
        {config.askLine ? <p className="home-ask__line">{config.askLine}</p> : null}

        <form className="home-search" onSubmit={ask} role="search">
          <Search className="home-search__icon" size={19} aria-hidden="true" />
          <input
            className="home-search__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={config.searchPlaceholder}
            aria-label={config.searchPlaceholder}
          />
          <button className="home-search__go" type="submit">Search</button>
        </form>

        {/* The chip carries the question itself, so it travels in memory exactly like the bar
            above it. Nothing is put in the URL: a question is the member's business. */}
        {config.askChips?.length ? (
          <div className="home-ask__chips">
            {config.askChips.map((chip) => (
              <button
                key={chip}
                className="home-ask__chip"
                type="button"
                onClick={() => onRequireMembership(() => onAsk(chip))}
              >
                {chip}
              </button>
            ))}
          </div>
        ) : null}

      </section>

      <section className="section featured-readings" aria-label={config.featuredHeading}>
        <div className="featured-readings__head">
          <span aria-hidden="true" />
          <h2>{config.featuredHeading}</h2>
          <span aria-hidden="true" />
        </div>

        <div className="featured-grid">
          {config.featured.map((r) => (
            <article key={r.key} className={`reading-card reading-card--${r.tint}`}>
              <span className="reading-card__ring" aria-hidden="true">
                {READING_ICONS[r.icon] || READING_ICONS.star}
              </span>
              <div className="reading-card__body">
                <p className="reading-card__eyebrow">{r.categoryLabel}</p>
                <h3 className="reading-card__q">{r.question}</h3>
                {/*
                  AKASHIC-1: **A CARD THAT NAMES A ROOM IS A DOOR, NOT A QUESTION.** These five are in
                  the rotation config for her later pick, and the day she features one it must walk a
                  member INTO the room rather than hand a soul-record reading to the bubble engine.
                  The server refuses the same key outright, so the two halves agree.
                */}
                <button
                  className="reading-card__cta"
                  type="button"
                  onClick={() => (r.room
                    ? onRequireMembership(() => onOpenRoom(String(r.room)))
                    : (isMember ? setActive(r) : onRequireMembership(() => setActive(r))))}
                >
                  {r.room ? "Open the room" : "Get My Answer"} <ArrowRight size={15} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {active ? <ReadingBubbleModal bubble={active} memberChart={memberChart} language={language} onClose={() => setActive(null)} /> : null}
    </>
  );
}


function ReadingBubbleModal({ bubble, memberChart, onClose, language = DEFAULT_LANGUAGE }: {
  bubble: ShowcaseBubble;
  memberChart: BirthChartForm | null;
  onClose: () => void;
  language?: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(memberChart || {}) }));
  const [pick, setPick] = useState<"me" | "person" | "new">("me");
  const [localBusy, setBusy] = useState(false); const busy = useModalBusy("bubble", localBusy); // R224
  const [status, setStatus] = useState("");
  const [needTime, setNeedTime] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  // The second person, for a "they" reading. "unnamed" is a real, first-class choice: the member
  // may not want to name them, and the reading still stands on their own chart + transits + cards.
  // SP2: for a reading that REQUIRES a second person, force an explicit choice (no default), so
  // generate stays gated until they pick. Optional second-person readings default to "unnamed".
  const needSecond = !!bubble.secondPerson && !bubble.secondPersonOptional;
  const [secondMode, setSecondMode] = useState<"" | "unnamed" | "person" | "new">(needSecond ? "" : "unnamed");
  const [secondForm, setSecondForm] = useState<BirthChartForm>({ ...initialBirthChart });
  const [secondPersonId, setSecondPersonId] = useState<string>("");
  // SP2: generate is only possible once the required second person is chosen.
  const secondChosen = !needSecond
    || (secondMode === "person" && !!secondPersonId)
    || (secondMode === "new" && !!secondForm.firstName);

  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const run = async (event: FormEvent) => {
    event.preventDefault();
    // BIRTH-TIME LAW, up front: no time = no houses, so refuse before the engine at zero cost. R113:
    // this was a hand-typed THIRD copy, drifted to "below" — right by accident. The NOTICE moved.
    if (form.birthTimeUnknown || !String(form.birthTime || "").trim()) {
      setNeedTime(t(language, "reading.needTimeYours"));
      return;
    }
    setBusy(true); setStatus(t(language, "bubble.casting")); setNeedTime(""); setReport(null);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const body: Record<string, unknown> = { key: bubble.key, name: fullName, chart: { ...form, name: fullName } };
      if (bubble.secondPerson) {
        if (secondMode === "unnamed") body.secondUnnamed = true;
        else if (secondMode === "person" && secondPersonId) body.secondPersonId = secondPersonId;
        else if (secondMode === "new") {
          body.secondChart = secondForm;
          body.secondName = combineName(secondForm.firstName, secondForm.middleName, secondForm.lastName) || secondForm.name;
        }
      }
      const res = await fetch("/api/reading-bubble", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.status === 401) setStatus(t(language, "reading.signIn"));
      else if (data.requiresBirthTime) setNeedTime(data.message);
      else if (!res.ok || data.ok === false || !data.report) setStatus(data.message || t(language, "reading.failed"));
      else { setReport(data.report); setStatus(""); }
    } catch {
      setStatus(t(language, "reading.failed"));
    } finally {
      setBusy(false);
    }
  };

  const saveToRoom = async () => {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    // R47 — ONE OF THE TWO LIVE ONES. This tested `res.ok`, which is the HTTP status and nothing
    // else, so the route's DELIBERATE refusal (200 + `{ ok: false }`, "Report storage is not
    // connected yet.") was announced to her as "Saved to your room." Its two sentences were also
    // hand-typed English, so a Spanish member read them in English.
    const answer = await postReadingToRoom({ type: "bubble-reading", title: subjectReadingTitle((report as { header?: { name?: string } }).header?.name, bubble.question), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  };

  return (
    // ModalBackdrop, matching every other generator's popup: it portals to document.body so the
    // popup actually OVERLAYS the page. (ClosableReport renders inline, which would have put this
    // reading below the fold, reading to the member as a dead click.) Per C2 it has no
    // click-outside, so a generated reading cannot be dismissed by a stray background click.
    <ModalBackdrop label={t(language, "bubble.modalLabel", { question: bubble.question })} onClose={onClose}>
      <div className="reading-modal mini-report-modal bubble-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.close")}>x</button>
        <p className="eyebrow">{t(language, "bubble.eyebrow")}</p>
        <h2>{bubble.question}</h2>

        {!report ? (
          // I4: this wrapper is a DIV, never a <form>. CreateRoomForm IS a <form>, so wrapping
          // it in another one nested the forms, which is invalid HTML: the browser submitted
          // NATIVELY, the page reloaded, React remounted, and the popup vanished with nothing
          // delivered. That is exactly the shell the owner reported ("the box closes"), and it
          // was silent: no request, no error. The same lesson is already written at the
          // deep-report partner form; this modal had missed it.
          <div className="bubble-form">
            <SubjectPicker
              language={language}
              memberChart={memberChart}
              onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPick(m.kind); setNeedTime(""); }}
            />

            {/* SP2: the second-person selector renders ABOVE the generate button, always visible
                before a reading can run, so a two-person reading is never cast on one person. */}
            {bubble.secondPerson ? (
              <div className="bubble-second">
                <p className="bubble-second__label">{t(language, "bubble.secondLabel")}</p>
                <div className="bubble-second__choices">
                  <button type="button" className={secondMode === "person" ? "is-on" : ""} onClick={() => setSecondMode("person")}>{t(language, "bubble.secondCircle")}</button>
                  <button type="button" className={secondMode === "new" ? "is-on" : ""} onClick={() => setSecondMode("new")}>{t(language, "bubble.secondNew")}</button>
                  {bubble.secondPersonOptional ? (
                    <button type="button" className={secondMode === "unnamed" ? "is-on" : ""} onClick={() => setSecondMode("unnamed")}>{t(language, "bubble.secondUnnamed")}</button>
                  ) : null}
                </div>
                {secondMode === "person" ? (
                  <SubjectPicker
                    language={language}
                    memberChart={null}
                    label={t(language, "subject.labelCircle")}
                    meLabel={t(language, "subject.meShort")}
                    allowNew={false}
                    onPick={(c, m) => { setSecondForm({ ...initialBirthChart, ...c }); setSecondPersonId(m.personId || ""); }}
                  />
                ) : null}
                {secondMode === "new" ? (
                  <CreateRoomForm
                    birthChart={secondForm}
                    updateBirthChart={(f, v) => setSecondForm((s) => ({ ...s, [f]: v }))}
                    onSubmit={run}
                    submitLabel={busy ? t(language, "reading.going") : t(language, "bubble.go")}
                    requireMiddleName={false} generating={busy}
                  />
                ) : null}
                {secondMode === "unnamed" ? (
                  <p className="bubble-second__note">{t(language, "bubble.secondNote")}</p>
                ) : null}
              </div>
            ) : null}

            <CreateRoomForm
              birthChart={form}
              updateBirthChart={update}
              onSubmit={run}
              submitLabel={busy ? t(language, "reading.going") : t(language, "bubble.go")}
              requireMiddleName={false}
              disabled={!secondChosen}
              disabledHint={secondChosen ? "" : t(language, "bubble.chooseSecond")} generating={busy}
            />
            {/* R113: every birth-time notice on this site renders BELOW its form, because the refusal says "add your birth time ABOVE" — held BY POSITION, all five surfaces, in scripts/r113-birth-time-notice.mjs. */}
            {needTime ? <p className="reading-modal__needtime">{needTime}</p> : null}
            {status ? <p className="reading-modal__status">{status}</p> : null}
          </div>
        ) : (
          <>
            <ReportWithPrint data={report} />
            <div className="reading-modal__actions">
              {pdfAllowed(report) ? <button type="button" onClick={() => downloadReportPdf(report)}>{t(language, "reports.download")}</button> : null}
              {/* N4-3: the shared handoff. Renders only for READING types (src/askLalaHandoff.js). */}
              <AskLalaAboutButton report={report} kind="reading" />
              <button type="button" onClick={saveToRoom} disabled={save === "saving" || save === "saved"}>
                {t(language, save === "saved" ? "result.saved" : save === "saving" ? "result.saving" : "result.save")}
              </button>
            </div>
            {saveNote ? <p className="reading-modal__status">{saveNote}</p> : null}
            <SaveSubjectOffer pickKind={pick} chart={form} language={language} />
          </>
        )}
      </div>
    </ModalBackdrop>
  );
}

// R23: HomePage moved to src/components/RoomPages.tsx. `HomeShowcase` holds state and stays here,
// so it is composed IN as a node — a component out there cannot import back out of this file.

function AstrologyPage({
  chart,
  chartStatus,
  chartError,
  planetPositions,
  isMember,
  onRequireMembership,
  onOpenPeople,
  goHome,
  language,
}: {
  chart: BirthChartForm | null;
  chartStatus: ChartStatus;
  chartError: string;
  planetPositions: PlanetPosition[];
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  onOpenPeople: () => void;
  goHome: () => void;
  language: string;
}) {
  const [activeTool, setActiveTool] = useState<AstrologyTool>(chartCapabilities[0]);
  const [readingModalOpen, setReadingModalOpen] = useState(false);
  const [readingForm, setReadingForm] = useState<AstrologyReadingForm>(() => readingFormFromChart(chart));
  // Find Your Type: the flagship birth-details calculator at the top of the room.
  const [typeForm, setTypeForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(chart || {}) }));
  const [typeStatus, setTypeStatus] = useState("");
  const [typeBusy, setTypeBusy] = useState(false);
  const [typeReport, setTypeReport] = useState<AnyReport | null>(null);
  const [typeSave, setTypeSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [typeSaveNote, setTypeSaveNote] = useState("");
  const [typePick, setTypePick] = useState<"me" | "person" | "new">("me");
  const updateType = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) =>
    setTypeForm((f) => ({ ...f, [field]: value }));

  // R58: one counter for the four menu reads below, and ONE sentence (src/lib/reading-menus.ts).
  const [menusFailed, setMenusFailed] = useState(0);
  const menuFailed = () => setMenusFailed((n) => n + 1);
  const menuNote = readingMenuNote(menusFailed, { failed: t(language, "menus.loadFailed") });

  // A2: config-driven mini-reports (buttons + prompts come from the server config).
  const [miniConfigs, setMiniConfigs] = useState<Array<{ key: string; label: string; blurb: string; requiresBirthTime?: boolean }>>([]);
  const [activeMini, setActiveMini] = useState<{ key: string; label: string; blurb: string } | null>(null);
  useEffect(() => {
    void listReadingMenu<{ key: string; label: string; blurb: string; requiresBirthTime?: boolean }>("astrology-minis")
      .then((rows) => (rows ? setMiniConfigs(rows) : menuFailed()));
  }, []);

  // >>> TRAP-1a's RECEIVING END: THE READING THE RIBBON ASKED FOR. <<< A mini reading is a MODAL in
  // this page rather than a route, so a tap from My Room cannot be a link (`CauldronDoor`'s own
  // situation). Two cases and BOTH are covered on purpose:
  //   · she was ALREADY here -- the listener exists, so the event finds it;
  //   · she came from her room -- the navigation REMOUNTED this page, so the event fired before this
  //     listener was attached and the request is read as a FLAG instead.
  // It resolves against `miniConfigs`, the room's own roster, so a key the room does not offer opens
  // NOTHING rather than a modal with no config behind it -- and the flag is CONSUMED either way, so a
  // stale request cannot open a paid door on some later visit.
  useEffect(() => {
    const open = (key: string) => {
      const row = miniConfigs.find((m) => m.key === key);
      if (row) setActiveMini(row);
      return Boolean(row);
    };
    // >>> THE FLAG IS ONLY CONSUMED ONCE THIS ROOM CAN ANSWER IT, AND THAT ORDER IS THE WHOLE
    // WIRING. <<< Measured at coordinates: the navigation remounts this page and `miniConfigs` is
    // EMPTY on the first pass, so consuming there threw the request away and the reading never
    // opened -- the tap landing her in the room with nothing on it, which is exactly the half-done
    // tap this door exists to prevent. The effect re-runs when the roster arrives (its own dep), and
    // THAT is the pass that may consume.
    if (miniConfigs.length) {
      const pending = miniRequestPending();
      // Consumed either way once the roster is here: a key this room does not offer must DROP rather
      // than sit waiting to open something on a later visit.
      if (pending) { consumeMiniRequest(); open(pending); }
    }
    const onEvent = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (typeof key !== "string" || !key) return;
      // Only consume if this pass can really open it; otherwise the mount pass above still will.
      if (open(key)) consumeMiniRequest();
    };
    window.addEventListener(OPEN_MINI_READING_EVENT, onEvent);
    return () => window.removeEventListener(OPEN_MINI_READING_EVENT, onEvent);
  }, [miniConfigs]);

  // A2: the astrology TRADITIONS (Vedic, Nakshatra, Chinese, natal Moon phase) — each a real delivering
  // reading through the same subject-picker + report pipeline (no more "coming soon"). Config-driven.
  const [traditionConfigs, setTraditionConfigs] = useState<Array<{ key: string; label: string; blurb: string; requiresBirthTime?: boolean }>>([]);
  const [activeTradition, setActiveTradition] = useState<{ key: string; label: string; blurb: string; requiresBirthTime?: boolean } | null>(null);
  useEffect(() => {
    void listReadingMenu<{ key: string; label: string; blurb: string; requiresBirthTime?: boolean }>("astrology-traditions")
      .then((rows) => (rows ? setTraditionConfigs(rows) : menuFailed()));
  }, []);

  // Item 7: config-driven predictions (three cast on the member's chart, two sun-sign).
  const [predictionConfigs, setPredictionConfigs] = useState<PredictionConfig[]>([]);
  const [activePrediction, setActivePrediction] = useState<PredictionConfig | null>(null);
  useEffect(() => {
    void listReadingMenu<PredictionConfig>("astrology-predictions")
      .then((rows) => (rows ? setPredictionConfigs(rows) : menuFailed()));
  }, []);

  // Items 9-11: the deep readings (synastry, relationship weight, returns, karmic,
  // spiritual, transit). Config-driven like the rest.
  const [deepConfigs, setDeepConfigs] = useState<DeepReportConfig[]>([]);
  const [activeDeep, setActiveDeep] = useState<DeepReportConfig | null>(null);
  // A1-TILES: a tile with no designed engine opens an HONEST coming-soon (never a raw dump
  // or a silent bounce). Holds the tile whose message is showing.
  const [comingSoonTool, setComingSoonTool] = useState<AstrologyTool | null>(null);
  useEffect(() => {
    void listReadingMenu<DeepReportConfig>("astrology-deep")
      .then((rows) => (rows ? setDeepConfigs(rows) : menuFailed()));
  }, []);

  // A3: "Want to know more?" — chart + live transits + one tarot card, synthesized.
  const [askForm, setAskForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(chart || {}) }));
  const [askQuestion, setAskQuestion] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askStatus, setAskStatus] = useState("");
  const [askReport, setAskReport] = useState<AnyReport | null>(null);
  const [askSave, setAskSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [askSaveNote, setAskSaveNote] = useState("");
  const askMic = useDictation(setAskQuestion);
  const askSpeechOk = askMic.supported;

  // R220: local `busy` dies on navigation, so both doors ask the SERVER what is already running —
  // per DOOR and by exact KIND, because this room's work is many different readings and a member
  // waiting on a prediction may not be told her type is being found (AC3).
  const { active: astroActive } = useActiveGenerations(isMember);
  const { running: astroRunning } = astroRunState({ busy: { type: typeBusy, ask: askBusy }, active: astroActive });
  const askListening = askMic.listening;
  const toggleAskMic = askMic.toggle;

  async function runAsk() {
    setAskBusy(true); setAskReport(null); setAskSave("idle"); setAskSaveNote("");
    const stages = ["cast.chart", "ask.stage2", "ask.stage3", "ask.stage4"].map((k) => t(language, k));
    let i = 0; setAskStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setAskStatus(stages[i]); }, 2500);
    try {
      const fullName = combineName(askForm.firstName, askForm.middleName, askForm.lastName) || askForm.name;
      const res = await fetch("/api/astrology/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: askQuestion.trim(), name: fullName, chart: { ...askForm, name: fullName } }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setAskStatus("Sign in to ask."); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setAskStatus(data?.message || "Your answer could not be prepared right now. Please try again."); return; }
      setAskReport(data.report as AnyReport); setAskStatus("");
    } catch { setAskStatus("Your answer could not be prepared right now. Please try again."); }
    finally { window.clearInterval(timer); setAskBusy(false); }
  }

  async function saveAskToRoom() {
    if (!askReport) return;
    setAskSave("saving"); setAskSaveNote("");
    const name = (askReport as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "question-reading", title: subjectReadingTitle(name, (askReport as { question?: string }).question?.slice(0, 60) || "Your question"), data: askReport });
    setAskSave(saveReadingState(answer)); setAskSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  function submitFindYourType(event: FormEvent) {
    event.preventDefault();
    // Gated action: only members run the reading; everyone can see the form.
    onRequireMembership(() => {
      void runFindYourType();
    });
  }

  async function runFindYourType() {
    const fullName = combineName(typeForm.firstName, typeForm.middleName, typeForm.lastName) || typeForm.name;
    setTypeBusy(true);
    setTypeReport(null);
    setTypeSave("idle");
    setTypeSaveNote("");
    // Live status that cycles through the real build stages while it works. The four phases are
    // NAMED keys rather than built from a counter (14-B2's lesson: a key assembled from a template
    // is a key no grep, no gate and nobody renaming a row can find).
    const stages = ["type.stage1", "type.stage2", "type.stage3", "type.stage4"].map((k) => t(language, k));
    let stageIndex = 0;
    setTypeStatus(stages[0]);
    const timer = window.setInterval(() => {
      stageIndex = (stageIndex + 1) % stages.length;
      setTypeStatus(stages[stageIndex]);
    }, 2600);
    try {
      const response = await fetch("/api/astrology/find-your-type", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          chart: {
            // I6 R1: the subject stamp travels with the chart so the server keys the canonical
            // stored chart to the row the picker selected.
            subjectKind: typeForm.subjectKind,
            subjectId: typeForm.subjectId,
            name: fullName,
            birthDate: typeForm.birthDate,
            birthTime: typeForm.birthTime,
            birthPeriod: typeForm.birthPeriod,
            birthTimeUnknown: typeForm.birthTimeUnknown,
            birthplace: typeForm.birthplace,
            birthRegion: typeForm.birthRegion,
            birthCountry: typeForm.birthCountry,
            latitude: typeForm.latitude,
            longitude: typeForm.longitude,
            timezone: typeForm.timezone,
            houseType: "placidus",
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) {
        setTypeStatus("Sign in to run your Find Your Type reading.");
        return;
      }
      if (!response.ok || data?.ok === false || !data?.report) {
        setTypeStatus(data?.message || t(language, "reading.failed"));
        return;
      }
      setTypeReport(data.report as AnyReport);
      setTypeStatus("");
    } catch {
      setTypeStatus(t(language, "reading.failed"));
    } finally {
      window.clearInterval(timer);
      setTypeBusy(false);
    }
  }

  async function saveTypeToRoom() {
    if (!typeReport) return;
    setTypeSave("saving");
    setTypeSaveNote("");
    const name = (typeReport as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "find-your-type", title: subjectReadingTitle(name, "Find Your Type"), data: typeReport });
    setTypeSave(saveReadingState(answer)); setTypeSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  function openAstrologyTool(tool: AstrologyTool) {
    setActiveTool(tool);
    setReadingForm(readingFormFromChart(chart));
    setReadingModalOpen(true);
  }

  function openOffering(tool: AstrologyTool) {
    // A1-TILES (no-shells): every tile delivers a REAL designed reading or an honest
    // coming-soon. One source of truth (astrologyTileRoutes) so the fast gate can prove
    // the roster. No raw-data dump, no silent bounce to My Room.
    const route = astrologyTileRoute(tool.id);
    if (route.kind === "chart") {
      openAstrologyTool(tool); // AstrologyReadingModal in report mode = real birth-chart report
      return;
    }
    if (route.kind === "prediction") {
      const config = predictionConfigs.find((p) => p.key === route.key);
      if (config) { onRequireMembership(() => setActivePrediction(config)); return; }
    }
    if (route.kind === "deep") {
      const config = deepConfigs.find((d) => d.key === route.key);
      if (config) { onRequireMembership(() => setActiveDeep(config)); return; }
    }
    // coming-soon, or a real-engine route whose config has not loaded yet: be honest.
    setComingSoonTool(tool);
  }

  // R112: `prepareAstrologyTool` posted to `/api/astrology/room-tool` — the raw-text path
  // `astrologyTileRoutes.js` line 2 forbids — and NOTHING could reach it, because every tool this
  // modal is ever opened with comes from that file's own `kind: "chart"` arm. It is gone with the
  // arm that rendered its output.

  return (
    <section className="page-shell astrology-page">
      <PageOutlineTitle page="astrology" />
      <div className="astro-stage" aria-hidden="true">
        <div className="astro-disc">
          {ZODIAC_SIGNS.map((s) => <span key={s} className={`zodiac-mark zodiac-mark--${s}`}>{ZODIAC_MARKS[s]}</span>)}
        </div>
        {ASTRO_STAGE_THREADS.map((n) => <div key={n} className={`astro-thread astro-thread--${n}`} />)}
        {ASTRO_STAGE_PLANETS.map((n) => <div key={n} className={`astro-planet astro-planet--${n}`} />)}
      </div>

      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className="astrology-room-head">
        <p className="eyebrow">Astrology room</p>
      </div>

      <section className="find-your-type">
        <div className="section__header section__header--narrow">
          <h2>{t(language, "type.title")}</h2>
        </div>
        <SubjectPicker
          language={language}
          memberChart={chart}
          onPick={(c, m) => { setTypeForm({ ...initialBirthChart, ...c }); setTypePick(m.kind); }}
        />
        <form className="birth-chart-form find-your-type__form" onSubmit={submitFindYourType}>
          <div className="form-row form-row--name">
            <NameFields first={typeForm.firstName} middle={typeForm.middleName} last={typeForm.lastName} onChange={updateType} language={language} />
          </div>
          <label className="form-field--date">
            <span>{t(language, "form.birthDate")}</span>
            <input type="date" value={typeForm.birthDate} onChange={(e) => updateType("birthDate", e.target.value)} required />
          </label>
          <div className="form-field form-field--time">
            <span>{t(language, "form.birthTime")}</span>
            <BirthTimeFields
              birthTime={typeForm.birthTime} language={language}
              birthPeriod={typeForm.birthPeriod}
              disabled={typeForm.birthTimeUnknown}
              onChange={(patch) => {
                if (patch.birthTime !== undefined) updateType("birthTime", patch.birthTime);
                if (patch.birthPeriod !== undefined) updateType("birthPeriod", patch.birthPeriod);
              }}
            />
            <label className="unknown-time-check">
              <input type="checkbox" checked={typeForm.birthTimeUnknown} onChange={(e) => updateType("birthTimeUnknown", e.target.checked)} />
              {t(language, "form.birthTimeUnknown")}
            </label>
          </div>
          <BirthPlaceFields
            country={typeForm.birthCountry} language={language}
            birthplace={typeForm.birthplace}
            latitude={typeForm.latitude}
            longitude={typeForm.longitude}
            timezone={typeForm.timezone}
            birthDate={typeForm.birthDate}
            onChange={(patch) => {
              if (patch.birthCountry !== undefined) updateType("birthCountry", patch.birthCountry);
              if (patch.birthplace !== undefined) updateType("birthplace", patch.birthplace);
              if (patch.birthRegion !== undefined) updateType("birthRegion", patch.birthRegion);
              if (patch.latitude !== undefined) updateType("latitude", patch.latitude);
              if (patch.longitude !== undefined) updateType("longitude", patch.longitude);
              if (patch.timezone !== undefined) updateType("timezone", patch.timezone);
            }}
          />
          <button
            className={`button button--primary find-your-type__go${astroRunning.type ? " is-generating" : ""}`}
            type="submit"
            disabled={astroRunning.type}
          >
            {t(language, astroRunning.type ? "reading.goingChart" : "type.go")}
          </button>
          {typeStatus ? (
            <p className={`find-your-type__status${astroRunning.type ? " is-live" : ""}`}>{typeStatus}</p>
          ) : null}
        </form>

        {typeReport ? (
          <DeliveredReading
            report={typeReport} room="astrology" language={language} save={typeSave} saveNote={typeSaveNote}
            onClose={() => { setTypeReport(null); setTypeSave("idle"); setTypeSaveNote(""); }}
            onSaveMyRoom={saveTypeToRoom}
            extras={<>
              <AskLalaAboutButton report={typeReport} kind="reading" />
              <SaveSubjectOffer pickKind={typePick} chart={typeForm} language={language} />
            </>}
          />
        ) : null}
      </section>

      {/* R58: at ROOM width, above the grids that are missing (R35's capture lesson: a sentence she
          cannot read is worse than none), and announced. Nothing at all when every menu arrived. */}
      {menuNote ? <p className="reading-menu-note" role="status">{menuNote}</p> : null}

      {/* A2: mini-reports row — config-driven glass buttons, each a designed 2-3 paragraph
          reading through the report engine (save + PDF), opened via the Subject Picker. */}
      <ReadingMenuSection
        rows={miniConfigs}
        ariaLabel={t(language, "astro.minis.aria")}
        onPick={(m) => onRequireMembership(() => setActiveMini(m))}
      />

      {/* N10-2 (owner 2026-07-27) SECTION ORDER: "Want to know more?" sits directly under the quick
          readings row (where "Why are people drawn to you" lives), and "What's coming?" sits directly
          under it. The deeper readings and traditions follow. */}
      {/* A3: Want to know more — the member's own question answered from chart + live
          transits + one drawn tarot card, synthesized in Lala's voice with the card inline. */}
      <section className="want-more" aria-label={t(language, "astro.ask.aria")}>
        <div className="want-more__head">
          <h2>{t(language, "astro.ask.title")}</h2>
          <p>{t(language, "astro.ask.lede")}</p>
        </div>
        <SubjectPicker
          language={language}
          memberChart={chart}
          onPick={(c) => setAskForm({ ...initialBirthChart, ...c })}
        />
        <div className="want-more__ask">
          <textarea
            value={askQuestion}
            onChange={(e) => setAskQuestion(e.target.value)}
            placeholder={t(language, "astro.ask.placeholder")}
            rows={3}
          />
          {askSpeechOk ? (
            <button type="button" className={`want-more__mic${askListening ? " is-live" : ""}`} onClick={toggleAskMic} aria-label={t(language, askListening ? "astro.ask.dictateStop" : "astro.ask.dictate")}>
              <Mic size={18} />
            </button>
          ) : null}
        </div>
        <button
          className={`button button--primary want-more__go${astroRunning.ask ? " is-generating" : ""}`}
          type="button"
          disabled={astroRunning.ask || !askQuestion.trim()}
          onClick={() => onRequireMembership(() => { void runAsk(); })}
        >
          {t(language, astroRunning.ask ? "astro.ask.going" : "astro.ask.go")}
        </button>
        {askStatus ? <p className={`find-your-type__status${astroRunning.ask ? " is-live" : ""}`}>{askStatus}</p> : null}
        {askReport ? (
          <DeliveredReading
            report={askReport} room="astrology" language={language} save={askSave} saveNote={askSaveNote}
            onClose={() => { setAskReport(null); setAskSave("idle"); setAskSaveNote(""); }}
            onSaveMyRoom={saveAskToRoom}
            extras={<AskLalaAboutButton report={askReport} kind="reading" />}
          />
        ) : null}
      </section>

      {/* Item 7: predictions — the three personal ones are cast on the member's own chart
          (Subject Picker + birth-time law), the two sun-sign ones need only a sign. The
          vendor supplies the real transit data; Lala writes every one of them. */}
      <ReadingMenuSection
        rows={predictionConfigs}
        className="predictions"
        ariaLabel={t(language, "astro.predictions.aria")}
        title={t(language, "astro.predictions.title")}
        onPick={(p) => onRequireMembership(() => setActivePrediction(p))}
      />

      {/* Items 9-11: the deep readings. Each is a full designed report in Lala's voice
          (save + PDF). Synastry and Relationship Weight replace the old compatibility. */}
      <ReadingMenuSection
        rows={deepConfigs}
        className="deep-reports"
        ariaLabel={t(language, "astro.deep.aria")}
        title={t(language, "astro.deep.title")}
        tag={t(language, "astro.deep.twoPeople")}
        onPick={(r) => onRequireMembership(() => setActiveDeep(r))}
      />

      {/* A2: traditions & timing — Vedic, Nakshatra, Chinese, and the natal Moon phase, each a real
          delivering reading (vendor DATA + Lala's voice), opened through the same Subject Picker. */}
      <ReadingMenuSection
        rows={traditionConfigs}
        className="traditions"
        ariaLabel={t(language, "astro.traditions.aria")}
        title={t(language, "astro.traditions.title")}
        onPick={(row) => onRequireMembership(() => setActiveTradition(row))}
      />

      <section className="astrology-offerings">
        <div className="section__header section__header--narrow">
          <h2>{t(language, "astro.offerings.title")}</h2>
          {/* N10-3: one short line. The paragraph that stood here restated the sections above it. */}
          <p>{t(language, "astro.offerings.lede")}</p>
        </div>
        <div className="offering-grid">
          {/* Item 3 (owner): the Reports block (subscriber archive, saved reading
              history, printable reports) and the AI Guidance block are OFF the
              astrology page. Filtered here so the data stays intact for any other
              consumer, but neither renders on this page. */}
          {astrologyOfferings.filter((offering) => !ASTROLOGY_OFFERINGS_HIDDEN.has(offering.id)).map((offering) => (
            <article className="offering-card" key={offering.title}>
              <h3>{offering.title}</h3>
              <ul>
                {offering.items.map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => openOffering(item)}>
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {readingModalOpen ? (
        <AstrologyReadingModal
          tool={activeTool}
          memberChart={chart}
          form={readingForm}
          setForm={setReadingForm}
          onClose={() => setReadingModalOpen(false)}
          onRequireMembership={onRequireMembership}
          language={language}
          roomName="Astrology reading"
        />
      ) : null}

      {activeMini ? (
        <MiniReportModal config={activeMini} memberChart={chart} onClose={() => setActiveMini(null)} language={language} />
      ) : null}

      {activeTradition ? (
        <MiniReportModal
          config={activeTradition}
          memberChart={chart}
          onClose={() => setActiveTradition(null)}
          language={language}
          endpoint="/api/astrology/tradition"
          saveType="astrology-tradition"
          eyebrow={t(language, "astro.traditions.title")}
        />
      ) : null}

      {activePrediction ? (
        <PredictionModal config={activePrediction} memberChart={chart} onClose={() => setActivePrediction(null)} language={language} />
      ) : null}

      {activeDeep ? (
        <DeepReportModal config={activeDeep} memberChart={chart} onClose={() => setActiveDeep(null)} language={language} />
      ) : null}

      {comingSoonTool ? (
        <ComingSoonModal tool={comingSoonTool} onClose={() => setComingSoonTool(null)} />
      ) : null}

    </section>
  );
}

// A1-TILES: an honest coming-soon for a tile with no designed reading behind it yet. No
// raw data, no fake success, no machinery talk, no em dashes. It also points the member at
// the real readings already on the page so the click is never a dead end.
// A mini-report: the member picks a subject (For me / a saved person / someone new), the astrologer
// reads the relevant placements, and a designed 2-3 paragraph reading comes back (save + PDF), all
// through the Subject Picker so no bare fields.
function MiniReportModal({ config, memberChart, onClose, language, endpoint = "/api/astrology/mini-report", saveType = "mini-report", eyebrow = t(language, "reading.miniEyebrow") }: {
  config: { key: string; label: string; blurb: string; requiresBirthTime?: boolean };
  memberChart: BirthChartForm | null;
  onClose: () => void;
  language: string;
  endpoint?: string;
  saveType?: string;
  eyebrow?: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(memberChart || {}) }));
  const [pick, setPick] = useState<"me" | "person" | "new">("me");
  const [localBusy, setBusy] = useState(false); const busy = useModalBusy("mini", localBusy); // R222
  const [status, setStatus] = useState("");
  const [needTime, setNeedTime] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => {
    if (field === "birthTime" || field === "birthTimeUnknown") setNeedTime("");
    setForm((f) => ({ ...f, [field]: value }));
  };

  useEscapeKey(onClose);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setNeedTime("");
    // Birth-time law: this is a house reading (requires_birth_time). With no birth time there
    // is no rising sign and no houses, so we do NOT call the engine (no guessed houses, no
    // cost). The fix is one tap: the birth-time field is right in this form.
    if (config.requiresBirthTime && (form.birthTimeUnknown || !String(form.birthTime || "").trim())) {
      setNeedTime(t(language, "reading.needTimeYours"));
      return;
    }
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = ["cast.chart", "mini.stage2", "type.stage4"].map((k) => t(language, k));
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2400);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const res = await fetch(endpoint, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: config.key, name: fullName, chart: { ...form, name: fullName } }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "reading.signIn")); return; }
      // Server-side birth-time guard (a saved person with no time): show the fix message. R113 took the `|| "I need this person's..."` fallback out — it was UNREACHABLE, because every send attaches one.
      if (data?.requiresBirthTime) { setStatus(""); setNeedTime(data.message); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setStatus(data?.message || t(language, "reading.failed")); return; }
      setReport(data.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "reading.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: saveType, title: subjectReadingTitle(name, config.label), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <ModalBackdrop label={t(language, "reading.modalLabel", { label: config.label })} onClose={onClose}>
      <div className="reading-modal mini-report-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.close")}>x</button>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{config.label}</h2>
        <p>{config.blurb}</p>
        {!report ? (
          <>
            <SubjectPicker
              language={language}
              memberChart={memberChart}
              onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPick(m.kind); setNeedTime(""); }}
            />
            <CreateRoomForm
              birthChart={form}
              updateBirthChart={update}
              onSubmit={submit}
              submitLabel={busy ? t(language, "reading.goingChart") : t(language, "reading.read", { label: config.label })}
              nameHint={t(language, "reading.nameHintSelf")} language={language} generating={busy}
            />
            {needTime ? <p className="mini-report-needtime" role="status">{needTime}</p> : null}
            {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
          </>
        ) : (
          <DeliveredReadingBody
            report={report} room="astrology" language={language} save={save} saveNote={saveNote}
            onSaveMyRoom={saveToRoom}
            extras={<>
              <AskLalaAboutButton report={report} kind="reading" />
              <SaveSubjectOffer pickKind={pick} chart={form} language={language} />
            </>}
          />
        )}
      </div>
    </ModalBackdrop>
  );
}

type PredictionConfig = {
  key: string;
  label: string;
  blurb: string;
  scope: "personal" | "sign";
  requiresBirthTime?: boolean;
};

type NumerologyMiniConfig = { key: string; label: string; blurb: string; needsPartner?: boolean };
type DeepReportConfig = { key: string; label: string; blurb: string; twoSubject?: boolean; requiresBirthTime?: boolean };

// Items 9-11: a deep astrology reading (synastry, relationship weight, the returns,
// karmic, spiritual, transit). Two-subject readings open TWO pickers; every one enforces
// the birth-time law up front (all of these read houses and angles).
function DeepReportModal({ config, memberChart, onClose, language }: {
  config: DeepReportConfig;
  memberChart: BirthChartForm | null;
  onClose: () => void;
  language: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(memberChart || {}) }));
  const [partnerForm, setPartnerForm] = useState<BirthChartForm>({ ...initialBirthChart });
  const [pick, setPick] = useState<"me" | "person" | "new">("me");
  const [localBusy, setBusy] = useState(false); const busy = useModalBusy("deep", localBusy); // R222
  const [status, setStatus] = useState("");
  const [needTime, setNeedTime] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => {
    if (field === "birthTime" || field === "birthTimeUnknown") setNeedTime("");
    setForm((f) => ({ ...f, [field]: value }));
  };
  const updatePartner = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => {
    if (field === "birthTime" || field === "birthTimeUnknown") setNeedTime("");
    setPartnerForm((f) => ({ ...f, [field]: value }));
  };

  useEscapeKey(onClose);

  const missingTime = (c: BirthChartForm) => c.birthTimeUnknown || !String(c.birthTime || "").trim();

  async function run(event: FormEvent) {
    event.preventDefault();
    setNeedTime("");
    // Birth-time law: every one of these reads houses and angles, so no time = no reading.
    // Check BOTH people before spending anything.
    if (config.requiresBirthTime && (missingTime(form) || (config.twoSubject && missingTime(partnerForm)))) {
      setNeedTime(t(language, "reading.needTimeThe"));
      return;
    }
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = config.twoSubject
      ? ["cast.bothCharts", "deep.overlay", "deep.between"].map((k) => t(language, k))
      : ["cast.chart", "deep.carries", "type.stage4"].map((k) => t(language, k));
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const partnerName = combineName(partnerForm.firstName, partnerForm.middleName, partnerForm.lastName) || partnerForm.name;
      const res = await fetch("/api/astrology/deep-report", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: config.key, name: fullName, chart: { ...form, name: fullName },
          partner: config.twoSubject ? { name: partnerName, chart: { ...partnerForm, name: partnerName } } : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "reading.signIn")); return; }
      if (data?.requiresBirthTime) { setStatus(""); setNeedTime(data.message); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setStatus(data?.message || t(language, "reading.failed")); return; }
      setReport(data.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "reading.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "astrology-report", title: subjectReadingTitle(name, config.label), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <ModalBackdrop label={t(language, "reading.modalLabel", { label: config.label })} onClose={onClose}>
      <div className="reading-modal mini-report-modal deep-report-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.close")}>x</button>
        <p className="eyebrow">Astrology reading</p>
        <h2>{config.label}</h2>
        <p>{config.blurb}</p>
        {!report ? (
          <>
            <SubjectPicker
              language={language}
              memberChart={memberChart}
              onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPick(m.kind); setNeedTime(""); }}
            />
            <CreateRoomForm
              birthChart={form}
              updateBirthChart={update}
              onSubmit={config.twoSubject ? (e) => e.preventDefault() : run}
              submitLabel={busy ? t(language, "reading.going") : t(language, "reading.read", { label: config.label })}
              nameHint={t(language, "reading.nameHintHouses")} language={language}
              className={config.twoSubject ? "deep-report-form" : ""} generating={busy}
            />
            {/* CreateRoomForm IS a <form>, so this wrapper stays a div: a nested form is
                invalid HTML and swallows the submit. The partner form owns the single
                submit for a two-subject reading; the first form's button is suppressed. */}
            {config.twoSubject ? (
              <div className="deep-report-partner">
                <SubjectPicker
                  language={language}
                  memberChart={null}
                  label={t(language, "subject.labelAlso")}
                  onPick={(c) => { setPartnerForm({ ...initialBirthChart, ...c }); setNeedTime(""); }}
                />
                <CreateRoomForm
                  birthChart={partnerForm}
                  updateBirthChart={updatePartner}
                  onSubmit={run}
                  submitLabel={busy ? t(language, "reading.goingBoth") : t(language, "reading.read", { label: config.label })}
                  nameHint={t(language, "reading.nameHintOther")} language={language}
                  className="deep-report-form deep-report-form--partner" generating={busy}
                />
              </div>
            ) : null}
            {needTime ? <p className="mini-report-needtime" role="status">{needTime}</p> : null}
            {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
          </>
        ) : (
          <DeliveredReadingBody
            report={report} room="astrology" language={language} save={save} saveNote={saveNote}
            onSaveMyRoom={saveToRoom}
            extras={<>
              <AskLalaAboutButton report={report} kind="reading" />
              {!config.twoSubject ? <SaveSubjectOffer pickKind={pick} chart={form} language={language} /> : null}
            </>}
          />
        )}
      </div>
    </ModalBackdrop>
  );
}

// OQ2: a numerology mini-reading. Each of the number buttons opens its own popup, picks a
// subject, and delivers ONLY that number as its own designed reading (save + PDF), exactly
// like the astrology mini-reports. Numerology needs the full name at birth and the birth
// DATE only (no birth time, no birthplace), so this form is deliberately just those fields.
// Compatibility is the two-slot shape: a picker for each person.
function NumerologyMiniModal({ config, memberChart, onClose, language }: {
  config: NumerologyMiniConfig;
  memberChart: BirthChartForm | null;
  onClose: () => void;
  language: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(memberChart || {}) }));
  const [partnerForm, setPartnerForm] = useState<BirthChartForm>({ ...initialBirthChart });
  const [pick, setPick] = useState<"me" | "person" | "new">("me");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) =>
    setForm((f) => ({ ...f, [field]: value }));
  const updatePartner = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) =>
    setPartnerForm((f) => ({ ...f, [field]: value }));

  useEscapeKey(onClose);

  const partnerName = combineName(partnerForm.firstName, partnerForm.middleName, partnerForm.lastName) || partnerForm.name;
  const ready = Boolean(
    (combineName(form.firstName, form.middleName, form.lastName) || form.name) && form.birthDate
    && (!config.needsPartner || (partnerName && partnerForm.birthDate)),
  );

  async function run(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = config.needsPartner
      ? ["num.bothNumbers", "num.harmonise", "num.writingCompat"].map((k) => t(language, k))
      : [t(language, "num.calculating", { label: config.label }), t(language, "num.meansForYou"), t(language, "type.stage4")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2400);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const res = await fetch("/api/numerology/report", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          chart: { name: fullName, birthDate: form.birthDate },
          focus: config.key,
          partner: config.needsPartner ? { name: partnerName, birthDate: partnerForm.birthDate } : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "reading.signIn")); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setStatus(data?.message || t(language, "reading.failed")); return; }
      setReport(data.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "reading.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "numerology", title: subjectReadingTitle(name, config.label), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <ModalBackdrop label={t(language, "reading.modalLabel", { label: config.label })} onClose={onClose}>
      <div className="reading-modal mini-report-modal numerology-mini-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.close")}>x</button>
        <p className="eyebrow">{t(language, "reading.miniReadingEyebrow")}</p>
        <h2>{config.label}</h2>
        <p>{config.blurb}</p>
        {!report ? (
          <form onSubmit={run}>
            <SubjectPicker
              language={language}
              memberChart={memberChart}
              onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPick(m.kind); }}
            />
            <NumberFields value={form} onUpdate={update} hint={t(language, "numerology.nameHint")} language={language} />
            {config.needsPartner ? (
              <div className="numerology-mini__partner">
                <SubjectPicker
                  language={language}
                  memberChart={null}
                  label={t(language, "subject.labelWith")}
                  onPick={(c) => setPartnerForm({ ...initialBirthChart, ...c })}
                />
                <NumberFields value={partnerForm} onUpdate={updatePartner} hint={t(language, "num.nameHintOther")} language={language} />
              </div>
            ) : null}
            <button className={`button button--primary find-your-type__go${busy ? " is-generating" : ""}`} type="submit" disabled={busy || !ready}>
              {busy ? t(language, "numerology.going") : t(language, "reading.read", { label: config.label })}
            </button>
            {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
          </form>
        ) : (
          <DeliveredReadingBody
            report={report} room="numerology" language={language} save={save} saveNote={saveNote}
            onSaveMyRoom={saveToRoom}
            extras={<>
              <AskLalaAboutButton report={report} kind="reading" />
              {!config.needsPartner ? <SaveSubjectOffer pickKind={pick} chart={form} language={language} /> : null}
            </>}
          />
        )}
      </div>
    </ModalBackdrop>
  );
}

// A prediction (item 7): AstroAPI supplies the real transit data for the window, Lala
// reads it. Two shapes from one config: "personal" is cast on the member's real chart
// (Subject Picker + birth-time law, because the vendor lands each aspect in a house), and
// "sign" is a sun-sign horoscope that needs only a sign (no birth data, so no picker).
function PredictionModal({ config, memberChart, onClose, language }: {
  config: PredictionConfig;
  memberChart: BirthChartForm | null;
  onClose: () => void;
  language: string;
}) {
  const isSign = config.scope === "sign";
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(memberChart || {}) }));
  const [sign, setSign] = useState(() => sunSignFromBirthDate(memberChart?.birthDate) || "aries");
  const [pick, setPick] = useState<"me" | "person" | "new">("me");
  const [localBusy, setBusy] = useState(false); const busy = useModalBusy("prediction", localBusy); // R222
  const [status, setStatus] = useState("");
  const [needTime, setNeedTime] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => {
    if (field === "birthTime" || field === "birthTimeUnknown") setNeedTime("");
    setForm((f) => ({ ...f, [field]: value }));
  };

  useEscapeKey(onClose);

  async function run(event?: FormEvent) {
    event?.preventDefault();
    setNeedTime("");
    // Birth-time law: a personal prediction is read against the member's houses (the
    // vendor lands every aspect in one). No time = no houses, so we never call the engine.
    if (!isSign && config.requiresBirthTime && (form.birthTimeUnknown || !String(form.birthTime || "").trim())) {
      setNeedTime(t(language, "reading.needTimeYours"));
      return;
    }
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = isSign
      ? ["pred.signTransits", "type.stage4"].map((k) => t(language, k))
      : ["cast.chart", "pred.moving", "type.stage4"].map((k) => t(language, k));
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2400);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const body = isSign
        ? { key: config.key, sign }
        : { key: config.key, name: fullName, chart: { ...form, name: fullName } };
      const res = await fetch("/api/astrology/prediction", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "reading.signIn")); return; }
      if (data?.requiresBirthTime) { setStatus(""); setNeedTime(data.message); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setStatus(data?.message || t(language, "reading.failed")); return; }
      setReport(data.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "reading.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "prediction", title: subjectReadingTitle(name, config.label), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <ModalBackdrop label={t(language, "reading.modalLabel", { label: config.label })} onClose={onClose}>
      <div className="reading-modal mini-report-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.close")}>x</button>
        <p className="eyebrow">{t(language, "prediction.eyebrow")}</p>
        <h2>{config.label}</h2>
        <p>{config.blurb}</p>
        {!report ? (
          <>
            {isSign ? (
              // A sun-sign horoscope needs only the sign, so no birth form and no picker.
              <form className="prediction-sign-form" onSubmit={run}>
                <label className="subject-picker__label" htmlFor="prediction-sign">{t(language, "prediction.whichSign")}</label>
                <select
                  id="prediction-sign"
                  className="prediction-sign-form__select"
                  value={sign}
                  onChange={(e) => setSign(e.target.value)}
                >
                  {ZODIAC_SIGNS.map((s) => (
                    <option key={s} value={s}>{signLabel(s)}</option>
                  ))}
                </select>
                <button type="submit" className={busy ? "button is-generating" : "button"} disabled={busy}>
                  {busy ? t(language, "reading.goingTransits") : t(language, "reading.read", { label: config.label })}
                </button>
              </form>
            ) : (
              <>
                <SubjectPicker
                  language={language}
                  memberChart={memberChart}
                  onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPick(m.kind); setNeedTime(""); }}
                />
                <CreateRoomForm
                  birthChart={form}
                  updateBirthChart={update}
                  onSubmit={run}
                  submitLabel={busy ? t(language, "reading.goingChart") : t(language, "reading.read", { label: config.label })}
                  nameHint={t(language, "reading.nameHintSelf")} language={language} generating={busy}
                />
                {needTime ? <p className="mini-report-needtime" role="status">{needTime}</p> : null}
              </>
            )}
            {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
          </>
        ) : (
          <DeliveredReadingBody
            report={report} room="astrology" language={language} save={save} saveNote={saveNote}
            onSaveMyRoom={saveToRoom}
            extras={<>
              <AskLalaAboutButton report={report} kind="reading" />
              {!isSign ? <SaveSubjectOffer pickKind={pick} chart={form} language={language} /> : null}
            </>}
          />
        )}
      </div>
    </ModalBackdrop>
  );
}

// --- Astrocartography room (item 8) ----------------------------------------
// Where on earth a member's chart works FOR them. Three experiences on one contract
// (POST /api/astrocartography with mode = map | location | relocation) plus the line
// reference. The vendor computes the geometry; Lala reads it.

// R74: AcgLineMeaningPlanet moved to src/lib/acg-lines.ts, beside the transport that reads it.
// R11-2: DeepReportData moved with DeepReportView (src/components/DeepReportView.tsx).
// AC5: the map reading carries the member's OWN geography now, computed server-side and travelling
// with the report so the page, the shelf copy and the PDF all render the same facts.
// R10-3: the seven astrocartography types moved WITH the three views that render them.

function AstrocartographyPage({ chart, isMember, onRequireMembership, goHome, language }: {
  chart: BirthChartForm | null;
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  goHome: () => void;
  language: string;
}) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(chart || {}) }));
  const [mode, setMode] = useState<"map" | "location" | "relocation" | "check-place">("map");
  const [place, setPlace] = useState<AcgPlace | null>(null);
  // HOUSE READINGS (e) — CHECK A PLACE. Offered only when the SERVER says its switch is on, and it
  // reads the same "Who is this reading for?" choice the map does (a circle pick travels by id).
  const [checkOn, setCheckOn] = useState(false);
  const [checkAt, setCheckAt] = useState<AcgPlace | null>(null);
  const [personId, setPersonId] = useState("");
  useEffect(() => {
    let on = true;
    void fetch("/api/astrocartography/modes").then((r) => (r.ok ? r.json() : null)).then((d) => { if (on) setCheckOn(d?.checkPlace === true); }).catch(() => {});
    return () => { on = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [needTime, setNeedTime] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const [meanings, setMeanings] = useState<AcgLineMeaningPlanet[] | null | undefined>(undefined);
  const [openPlanet, setOpenPlanet] = useState<string>("");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => {
    if (field === "birthTime" || field === "birthTimeUnknown") setNeedTime("");
    setForm((f) => ({ ...f, [field]: value }));
  };

  // AC3/R19: which of this room's three readings is running, asked of the SERVER as well as of local
  // state, and asked by KIND — local state dies on navigation, and the regexes over her own copy were
  // a paid-call hazard. R215: *may this door be pressed* is the OTHER question (R18) and it had never
  // asked the server at all; `roomBusy` answers it off the same walk of the same roster.
  const { active: activeGenerations } = useActiveGenerations(true);
  const { running, any: roomBusy } = acgRunState({ mode, busy, active: activeGenerations });

  // The line reference is the vendor's static table: free, no generation, so load it once.
  useEffect(() => { void readLineMeanings().then(setMeanings); }, []);

  async function run(nextMode: "map" | "location" | "relocation" | "check-place", event?: FormEvent) {
    event?.preventDefault();
    setNeedTime("");
    // Birth-time law at its hardest: every line is an angle line, and angles come from
    // the birth time. No time = no map at all, so never call the engine.
    if (form.birthTimeUnknown || !String(form.birthTime || "").trim()) {
      setNeedTime(t(language, "acg.needTime"));
      return;
    }
    // Check a Place reads its OWN place field; the other two place readings read theirs.
    const where = nextMode === "check-place" ? checkAt : place;
    if (nextMode !== "map" && !where) {
      setMode(nextMode);
      setStatus(t(language, "acg.tools.pickFirst"));
      return;
    }
    setMode(nextMode);
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = nextMode === "map"
      ? ["cast.chart", "acg.stage2", "acg.stage3"].map((k) => t(language, k))
      : nextMode === "location" || nextMode === "check-place"
        ? [t(language, "acg.stage.near", { place: where?.city }), t(language, "acg.stage.close"), t(language, "acg.stage.does")]
        : [t(language, "acg.stage.recast", { place: where?.city }), t(language, "acg.stage.moves"), t(language, "acg.relocation.going")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
      const res = await fetch("/api/astrocartography", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: nextMode, name: fullName, chart: { ...form, name: fullName },
          // HOUSE READINGS (e): on Check a Place a circle pick is read from her stored person, by id
          // (the person + place cache keys on it). The three live readings send exactly what they did.
          ...(nextMode === "check-place" && personId ? { personId } : {}),
          // AC2: the resolved place carries its full identity through — coordinates AND zone.
          location: where ? { city: where.city, label: where.label, countryCode: where.countryCode, latitude: where.latitude, longitude: where.longitude, timezone: where.timezone || "", resultType: where.resultType || "", state: where.state || "" } : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setStatus(t(language, "acg.signIn")); return; }
      if (data?.requiresBirthTime) { setStatus(""); setNeedTime(data.message); return; }
      if (!res.ok || data?.ok === false || !data?.report) { setStatus(data?.message || t(language, "acg.failed")); return; }
      setReport(data.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "acg.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }

  // AC3: open a reading that finished while she was elsewhere. It already auto-shelved, so this
  // reads the saved row back rather than regenerating anything (no second charge, ever).
  // R53: "Nothing to say: it is still on her shelf either way" was the old comment, and it was the
  // wrong reading — she TAPPED a finished reading in the queue strip and the page did not move. The
  // sentence lands in this room's own status line, directly above the strip she tapped. (`row.report`
  // went with it: `handleReports` returns the row, whose payload field is `data`, and a fallback onto
  // a field the route never sends is a branch nothing could ever take.)
  async function openShelved(savedId: number) {
    const answer = await fetchSavedReport<AnyReport>(String(savedId));
    setStatus(openReportNote(answer, { signIn: t(language, "common.openSignIn"), failed: t(language, "common.openFailed") }));
    if (!answer.ok) return;
    setReport(answer.data); setSave("idle"); setSaveNote("");
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    // R47: `type` was optional on this cast only, and the law's REQUIRED field is what said so out
    // loud. All three astrocartography reports are stamped by the server (`astrocartography-map`,
    // `-location`, `-relocation`), so the wire bytes are unchanged; what changes is that a report
    // arriving without one is now a compile error rather than a row shelved as a Birth Chart by
    // the route's own default.
    const meta = report as { header?: { name?: string }; type: string; kicker?: string };
    const answer = await postReadingToRoom({ type: meta.type, title: subjectReadingTitle(meta.header?.name, meta.kicker), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <section className="page-shell specialty-page astrocartography-page">
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>
      <div className="specialty-hero">
        <p className="eyebrow">Astrocartography room</p>
        <h1>{t(language, "acg.title")}</h1>
        {/* N10-4: summarized, meaning kept, bulk cut. */}
        <p>{t(language, "acg.lede")}</p>
      </div>

      {/* HOUSE READINGS (e) — CHECK A PLACE, FIRST ON THE PAGE (her order), only when the server's
          switch is on. It carries the room's one "Who is this reading for?" picker, so the map form
          below reads the same choice; "Someone new" is typed into that form. */}
      {checkOn ? (
        <section className="acg-tools acg-check" aria-label={t(language, "acg.check.title")}>
          <div className="section__header section__header--narrow">
            <h2>{t(language, "acg.check.title")}</h2>
            <p>{t(language, "acg.check.lede")}</p>
          </div>
          <SubjectPicker
            language={language}
            memberChart={chart}
            onPick={(c, meta) => { setForm({ ...initialBirthChart, ...c }); setNeedTime(""); setPersonId(meta?.personId || ""); }}
          />
          <PlaceSearch value={checkAt} onPick={setCheckAt} placeholder={t(language, "acg.check.placeholder")} language={language} />
          <div className="acg-tools__buttons">
            <button
              type="button"
              className={`button button--primary acg-check__go${running["check-place"] ? " is-generating" : ""}`}
              disabled={roomBusy}
              onClick={() => onRequireMembership(() => { void run("check-place"); })}
            >
              {t(language, running["check-place"] ? "acg.check.going" : "acg.check.go")}
            </button>
          </div>
          {mode === "check-place" && status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
        </section>
      ) : null}

      <div className="acg-form-wrap">
        {checkOn ? null : (
          <SubjectPicker
            language={language}
            memberChart={chart}
            onPick={(c) => { setForm({ ...initialBirthChart, ...c }); setNeedTime(""); }}
          />
        )}
        <CreateRoomForm
          birthChart={form}
          updateBirthChart={update}
          onSubmit={(e) => { e.preventDefault(); onRequireMembership(() => { void run("map"); }); }}
          // AC3: the lines reading gets the same pulsing generate treatment as every other
          // generator on the site, instead of a label change nobody notices. R215: it PULSES on its
          // own reading and it CLOSES on the room's — the form's own submit is `disabled || generating`.
          generating={running.map} disabled={roomBusy}
          submitLabel={t(language, running.map ? "acg.going" : "acg.go")}
          nameHint={t(language, "acg.nameHint")} language={language}
          className="acg-form"
        />
        {needTime ? <p className="mini-report-needtime" role="status">{needTime}</p> : null}
      </div>

      {/* The two place-based experiences. Both need exact coordinates, so both go
          through the same search: a typed name we never geocoded is never sent. */}
      <section className="acg-tools" aria-label={t(language, "acg.tools.aria")}>
        <div className="section__header section__header--narrow">
          <h2>{t(language, "acg.tools.title")}</h2>
          <p>{t(language, "acg.tools.lede")}</p>
        </div>
        <PlaceSearch value={place} onPick={setPlace} placeholder={t(language, "acg.tools.placeholder")} language={language} />
        {/* N12-1 (owner 2026-07-27) — WHY THESE ARE NO LONGER DISABLED ON A MISSING PLACE.
            THE ROOT CAUSE, in one sentence: the place field clears its picked place to null on every
            keystroke that is not an exact match for a geocoder label, and these buttons were disabled
            whenever there was no picked place, so the moment a member typed a second place the button
            went dead with nothing said, which is exactly the "I entered another place and the button
            does nothing, repeated clicks dead" she reported.
            The guidance for this case ALREADY EXISTED inside run() ("Pick a place from the list first,
            so I have its exact coordinates.") and was UNREACHABLE, because a disabled button never
            reaches its handler. Keeping the button live and letting run() speak turns a dead end into an
            instruction. A run in flight still disables it, and R215 is WHICH run: `roomBusy`, the
            room's answer, not this browser's `busy`, which is FALSE on the return R19 exists for. */}
        <div className="acg-tools__buttons">
          <button
            type="button"
            className={`button button--primary${running.location ? " is-generating" : ""}`}
            disabled={roomBusy}
            onClick={() => onRequireMembership(() => { void run("location"); })}
          >
            {t(language, running.location ? "acg.location.going" : "acg.location.go")}
          </button>
          <button
            type="button"
            className={`button button--ghost${running.relocation ? " is-generating" : ""}`}
            disabled={roomBusy}
            onClick={() => onRequireMembership(() => { void run("relocation"); })}
          >
            {t(language, running.relocation ? "acg.relocation.going" : "acg.relocation.go")}
          </button>
        </div>
        {status && mode !== "check-place" ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}

        {/* AC3: the in-room queue. The work runs server-side to completion and shelves itself, so
            clicking off or refreshing never cancels it. This is what SAYS so: return mid-run and
            it is here, pulsing, named, counting up, and it flips to a ready entry that opens the
            reading. R218: only this room and the Seal room mount it (`QUEUE_STRIP_ROOMS`). */}
        <GenerationQueueStrip
          room="astrocartography" language={language}
          onOpen={(savedId) => { if (savedId) void openShelved(savedId); }}
        />
      </section>

      {report ? (
        <DeliveredReading
          report={report} room="astrocartography" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<AskLalaAboutButton report={report} kind="reading" />}
        />
      ) : null}

      {/* The line reference: what each planet does on each angle. Free, static, browsable.
          R74: a read that did not land says so, exactly where the band would have been. */}
      {lineMeaningsNote(meanings, t(language, "acg.lines.failed")) ? (
        <p className="acg-reference__note" role="status">{t(language, "acg.lines.failed")}</p>
      ) : meanings?.length ? (
        <section className="acg-reference" aria-label={t(language, "acg.lines.title")}>
          <div className="section__header section__header--narrow">
            <h2>{t(language, "acg.lines.title")}</h2>
            <p>{t(language, "acg.lines.lede")}</p>
          </div>
          <div className="acg-reference__list">
            {meanings.map((p) => (
              <div key={p.planet} className="acg-reference__planet">
                <button
                  type="button"
                  className="acg-reference__toggle"
                  aria-expanded={openPlanet === p.planet}
                  onClick={() => setOpenPlanet((cur) => (cur === p.planet ? "" : p.planet))}
                >
                  <span>{p.planet}</span>
                  <span aria-hidden="true">{openPlanet === p.planet ? "−" : "+"}</span>
                </button>
                {openPlanet === p.planet ? (
                  <div className="acg-reference__lines">
                    {p.lines.map((l) => (
                      <div key={l.lineType} className="acg-reference__line">
                        <p className="acg-reference__line-head">{p.planet} {l.lineType} <span>{l.angle}</span></p>
                        <p>{l.meaning}</p>
                        {l.keywords.length ? <p className="acg-reference__keywords">{l.keywords.join(" · ")}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

    </section>
  );
}

// --- Designed birth-chart report (B2 web view) -----------------------------
// R9-5: ReportPlanet / ReportHouse / ReportData moved with the views that render them,
// src/components/ReportView.tsx. ReportData is imported back — the chart flows are typed by it.

// R6-2: SIGN_GLYPHS / PLANET_GLYPHS / ROMAN_HOUSES moved to `src/catalogs/glyphs.ts`.

// R31/R112: CHART_TOOL_IDS is DERIVED from ASTROLOGY_TILE_ROUTES in that file, and the DECISION now
// lives entirely at the door — "openOffering" opens this form only for a chart tile, so the form no
// longer asks a second time and no longer imports the Set. One roster, one decision, one place.


// R9-1: WorkingStep / NarrativeSection / NarrativeReportData moved with the component that renders
// them, src/components/NarrativeReportView.tsx. Imported back because eleven other views are typed by
// NarrativeReportData; App.tsx must never redefine what it imports.
// R10-1: HumanDesignReportData + HumanDesignCompatData moved WITH the two views that render them
// (`src/components/HumanDesignView.tsx`) and are imported back for the flows that are typed by them.
// R11-2: PetitionPaperData moved with the view that renders it (src/components/PetitionPaperView.tsx).
// R11-3: the AnyReport union moved with the dispatcher that switches on it.

// A compact, collapsible glass panel that floats over the room background. Its
// collapsed state is controlled by My Room so it can persist to the profile.
// J2: real column independence for the My Room panels. CSS grid shares row heights and
// CSS multicol re-balances globally, so in both a growing (expanded) panel shoved panels
// in OTHER columns. Here each panel is assigned to a column ROUND-ROBIN — a stable
// assignment that never changes with content — so a panel's height only ever grows its
// own column and can never move a sibling elsewhere. Column count follows the width.

// R27-3: RoomPanel moved to src/components/RoomPanel.tsx. Nine call sites in here still use it, and
// its decision — a collapsed panel renders NO BODY, so its children unmount — is arithmetic on a
// returned tree out there instead of a shape nothing could reach.

// R9-5: reportSectionCard moved to src/components/ReportView.tsx, where a gate can CALL it. Its
// law (no body, no box) was held by matching two strings in this file, which a function that draws
// the box anyway can satisfy. Imported back: seven call sites in here still use it.

// Renders numerology / personality reports (overview + titled sections + optional
// number tables) in the same black/gold shell as the chart report.
// `extras` is an optional slot rendered between the General Information block and the
// reading (the astrocartography views use it for the map SVG, the lines that reach a
// place, and the angle shifts on a move), so every narrative reading keeps ONE designed
// report shell instead of each new room re-drawing the letterhead.

// R11-1: TarotCard + TarotReadingData moved WITH the view that renders them; imported back here,
// because five flows in this file are typed by TarotReadingData.

// R11-1: TarotReadingView moved to src/components/TarotViews.tsx, so ReportBody could follow it out.

// R67: ClosableReport moved to src/components/DeliveredReading.tsx with the rest of the delivery
// block. It holds four hooks and came anyway, on R10-3's ruling — a hook is only out of reach for a
// component a gate CALLS, and the two blocks that render this one never invoke it.

// Picks the right renderer for a report by its type/shape.
// The Human Design bodygraph report: the vendor SVG (dark), the design's headline
// properties as chips, a nine-center map, channels, and the interpretation in the
// same designed language as every other room (letterhead, overview, cards).
// R10-1: HumanDesignView + HumanDesignCompatView moved to src/components/HumanDesignView.tsx —
// props in, tree out, no hook, so scripts/r10-report-views.mjs CALLS them instead of grepping them.
// R11-1: TarotSpreadView moved to src/components/TarotViews.tsx with TarotReadingView.

// R10-3: AcgPlanetGroups + the three astrocartography views moved to src/components/AcgViews.tsx.
// R11-2: DeepReportView moved to src/components/DeepReportView.tsx with DeepReportData.

// R11-2: PetitionPaperView moved to src/components/PetitionPaperView.tsx, where a gate CALLS it and
// W12-3(a) — the target name is written PLAIN, never struck — is arithmetic on the returned tree.

// R11-3: ReportBody — THE DISPATCHER — moved to src/components/ReportBody.tsx with the AnyReport
// union, once every view it names had left this file. A gate CALLS it now, once per report type
// string the SERVER can write. (The line that used to end this note — "ReportPrintPortal stays here:
// createPortal has no stub runtime" — was retired by R67, which measured the claim and moved it.)

// R67: ReportPrintPortal, ReportWithPrint, DeliveredReadingBody and DeliveredReading moved to
// src/components/DeliveredReading.tsx. R48 left them here because `createPortal` had "no stub
// runtime"; measured again, `loadTsx` stubs only `lucide-react` and `react/jsx-runtime`, so
// `react-dom` bundles like any other dependency and the two derived blocks are CALLED now.

// R112 — THE ASTROLOGY ROOM'S CHART FORM, AND THE SECOND STATUS MACHINE IT RAN WAS UNREACHABLE.
//
// This modal used to fork on `isReport = mode === "astrology" && CHART_TOOL_IDS.has(tool.id)` and
// carry a WHOLE SECOND ARM behind it: its own status, its own output, its own submit handler, its own
// on-screen reader, and a POST to `/api/astrology/room-tool` — **a PAID vendor call rendering the
// raw-data dump that `src/astrologyTileRoutes.js` line 2 forbids by name**, into a `<pre>`, with no
// Save to My Room anywhere on it.
//
// **IT HAD NOT BEEN REACHABLE SINCE R31, AND R31 IS WHAT MADE IT SO.** The modal has exactly ONE
// mount; `openAstrologyTool` is called from exactly ONE place — `openOffering`'s `route.kind ===
// "chart"` arm — and R31 DERIVED `CHART_TOOL_IDS` from that same route table rather than mirroring
// it, precisely so a chart tile could never miss the Set. So `isReport` was structurally always true,
// and the arm behind the ternary was a paid path nothing could take. **R31 made the fall-through
// unreachable by DERIVATION; this makes it unreachable by CONSTRUCTION, which is the difference
// between a law that is true and a law that cannot be broken.**
//
// The three `mode` conditionals went with it: at the one mount `mode` is "astrology", so all three
// were always true and the form always asks for a name, a birth date and full birth details.
//
// **THE SERVER ROUTE STAYS, DELIBERATELY, AND THE REASON IS HERS.** `handleRoomTool` is the only
// reader of `astrologyServiceCatalog` — **the record of ROADMAP 1's endpoint audit**, whose
// `comingSoon` flags are the worklist for the rooms she has not commissioned yet. Deleting it inside
// an unrelated round would destroy the artifact of a shipped roadmap item with open work on it.
// Nothing on this site calls it any more, and that is held below.
function AstrologyReadingModal({
  tool,
  memberChart = null,
  form,
  setForm,
  onClose,
  onRequireMembership,
  language,
  roomName = "Reading",
}: {
  tool: AstrologyTool;
  memberChart?: BirthChartForm | null;
  form: AstrologyReadingForm;
  setForm: Dispatch<SetStateAction<AstrologyReadingForm>>;
  onClose: () => void;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  language: string;
  roomName?: string;
}) {
  const [report, setReport] = useState<ReportData | null>(null);
  const [reportStatus, setReportStatus] = useState<ReadingStatus>("idle");
  const [reportError, setReportError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const [pickKind, setPickKind] = useState<"me" | "person" | "new">("me");
  // CHART-WAIT-Q1 (her YES, 2026-10-12): the chart now waits up to about two minutes for its words, so a long wait says so.
  const [chartWaitLong, setChartWaitLong] = useState(false);
  useEffect(() => {
    if (reportStatus !== "loading") { setChartWaitLong(false); return; }
    const timer = window.setTimeout(() => setChartWaitLong(true), 20_000);
    return () => window.clearTimeout(timer);
  }, [reportStatus]);

  async function saveReportToRoom() {
    if (!report) return;
    setSaveState("saving");
    setSaveNote("");
    const answer = await postReadingToRoom({ type: "birth-chart", title: subjectReadingTitle(report.header.name, "Birth Chart"), data: report });
    // R47: these two sentences were hand-typed ENGLISH at this call site and reached no catalog row,
    // so the birth chart — the first reading most members ever generate — told a Spanish member in
    // English that it could not be saved. `modal.saveFailed` already carried that exact sentence.
    setSaveState(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language, t(language, "modal.saveFailed"))));
  }

  async function handleReportSubmit(event: FormEvent) {
    event.preventDefault();
    setReportStatus("loading");
    setReportError("");
    setReport(null);

    const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    try {
      const response = await fetch("/api/astrology/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          chart: {
            // I6 R1: the subject stamp travels with the chart so the server keys the canonical
            // stored chart to the row the picker selected.
            subjectKind: form.subjectKind,
            subjectId: form.subjectId,
            name: fullName,
            birthDate: form.birthDate,
            birthTime: form.birthTime,
            birthPeriod: form.birthPeriod,
            birthTimeUnknown: form.birthTimeUnknown,
            birthplace: form.birthplace,
            birthRegion: form.birthRegion,
            birthCountry: form.birthCountry,
            latitude: form.latitude,
            longitude: form.longitude,
            timezone: form.timezone,
            houseType: "placidus",
          },
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.requiresInput) {
        throw new Error(data?.message || t(language, "modal.chartFailed"));
      }
      setReport(data as ReportData);
      setReportStatus("success");
      setSaveState("idle");
      setSaveNote("");
    } catch (error) {
      setReportStatus("error");
      setReportError(error instanceof Error ? error.message : t(language, "modal.chartFailed"));
    }
  }

  // R82 — **A TENTH COPY, AND THE ONE NO BYTE-FOR-BYTE ROSTER COULD EVER HAVE CONTAINED: it is
  // written as a named `function handleEscape(event: KeyboardEvent)` rather than the arrow the
  // other nine wore.** It is DELETED rather than folded, because this modal already passes
  // `onClose` to `ModalBackdrop`, which has kept the promise for it since R81 — so Escape was
  // calling `onClose()` TWICE here, which is what a law with ten copies looks like from the inside.

  return (
    <ModalBackdrop label={`${tool.title} reading form`} onClose={onClose}>
      <div className="reading-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "reading.closeForm")}>
          x
        </button>

        <p className="eyebrow">{roomName}</p>
        <h2>{tool.title}</h2>
        <p>{tool.text}</p>
        <span className="api-route-pill">Private 12th House service layer</span>

        <form className="reading-form" onSubmit={(e) => { e.preventDefault(); onRequireMembership(() => handleReportSubmit(e)); }}>
          <SubjectPicker
            language={language}
            memberChart={memberChart}
            onPick={(c, m) => { setForm((cur) => ({ ...readingFormFromChart(c), question: cur.question })); setPickKind(m.kind); }}
          />
          <NameFields
            first={form.firstName}
            middle={form.middleName}
            last={form.lastName}
            language={language}
            onChange={(field, value) => setForm((current) => {
              const next = { ...current, [field]: value };
              next.name = combineName(next.firstName, next.middleName, next.lastName);
              return next;
            })}
          />
          <label>
            <span>{t(language, "form.birthDate")}</span>
            <input
              type="date"
              value={form.birthDate}
              onChange={(event) => setForm((current) => ({ ...current, birthDate: event.target.value }))}
              required
            />
          </label>
          <label>
            <span>{t(language, "form.birthTime")}</span>
            <BirthTimeFields
              birthTime={form.birthTime}
              birthPeriod={form.birthPeriod}
              disabled={form.birthTimeUnknown}
              language={language}
              onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
            />
          </label>
          <label className="checkline">
            <input
              type="checkbox"
              checked={form.birthTimeUnknown}
              onChange={(event) => setForm((current) => ({ ...current, birthTimeUnknown: event.target.checked }))}
            />
            <span>{t(language, "form.birthTimeUnknown")}</span>
          </label>
          <BirthPlaceFields
            country={form.birthCountry}
            birthplace={form.birthplace}
            latitude={form.latitude}
            longitude={form.longitude}
            timezone={form.timezone}
            birthDate={form.birthDate}
            language={language}
            onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
          />
          <label className="wide">
            <span>{t(language, "modal.questionFocus")}</span>
            <textarea
              value={form.question}
              onChange={(event) => setForm((current) => ({ ...current, question: event.target.value }))}
              placeholder={t(language, "modal.placeholderFocus")}
            />
          </label>
          <button
            className={reportStatus === "loading" ? "button button--primary is-generating" : "button button--primary"}
            type="submit"
            disabled={reportStatus === "loading"}
          >
            {reportStatus === "loading" ? t(language, "reading.goingChart") : t(language, "modal.generateReport")}
          </button>
        </form>

        {reportStatus === "loading" ? (
          <p className="reading-status">{t(language, chartWaitLong ? "modal.chartStillWriting" : "modal.castingEvery")}</p>
        ) : null}
        {report && report.interpreted === false ? (
          <p className="reading-status">{t(language, "modal.chartWordsPending")}</p>
        ) : null}
        {reportStatus === "error" ? (
          <article className="reading-output reading-output--error">
            <pre>{reportError}</pre>
          </article>
        ) : null}
        {report ? (
          <div className="chart-report__wrap">
            <div className="reader__toolbar">
              <span><Eye size={16} /> {t(language, "modal.onScreen")}</span>
              <div className="chart-report__actions">
                {pdfAllowed(report) ? <button type="button" onClick={() => downloadReportPdf(report)}>{t(language, "reports.download")}</button> : null}
                {/* N4-3: the shared handoff. Renders only for READING types (src/askLalaHandoff.js). */}
                <AskLalaAboutButton report={report} kind="reading" />
                <button
                  type="button"
                  onClick={saveReportToRoom}
                  disabled={saveState === "saving" || saveState === "saved"}
                >
                  {saveState === "saved"
                    ? t(language, "result.saved")
                    : saveState === "saving"
                      ? t(language, "result.saving")
                      : t(language, "result.save")}
                </button>
              </div>
            </div>
            {saveNote ? <p className="chart-report__save-note">{saveNote}</p> : null}
            <div className="report-actions"><SaveSubjectOffer pickKind={pickKind} chart={form} language={language} /></div>
            <ReportView data={report} />
            <ReportPrintPortal data={report} />
          </div>
        ) : null}
      </div>
    </ModalBackdrop>
  );
}

// Dream Interpretation room: the member describes a dream (typed or spoken via the mic),
// Lala reads it through the report engine grounded in her dream/symbol teachings, and the
// designed reading saves to My Room and downloads as a PDF like every other room.
function DreamPage({ goHome, isMember, onRequireMembership, language }: { goHome: () => void; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [dream, setDream] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  // Browser dictation via the ONE shared engine (Web Speech, Chrome/Edge/Safari): keeps
  // listening until the member taps stop, auto-restarts on the browser's silence timeout,
  // and appends each phrase. Absent elsewhere, in which case the mic hides and typing works.
  const dreamMic = useDictation(setDream);
  const speechSupported = dreamMic.supported;
  const listening = dreamMic.listening;
  const toggleMic = dreamMic.toggle;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (dream.trim().length < 8) { setStatus(t(language, "dream.needMore")); return; }
    onRequireMembership(() => { void run(); });
  }

  const dreamCopy = { signIn: t(language, "dream.roomSignIn"), failed: t(language, "dream.failed") };

  async function run() {
    dreamMic.stop();
    setBusy(true);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = ["dream.stage1", "dream.stage2", "dream.stage3", "dream.stage4"].map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/dream/interpret", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dream: dream.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      const answer = readingAnswer(response.status, data, (d) => (d.report as AnyReport) ?? null);
      if (answer.kind !== "delivered") { setStatus(readingStatusLine(answer, dreamCopy)); return; }
      setReport(answer.body);
      setStatus("");
    } catch {
      setStatus(readingStatusLine(READING_UNREACHABLE, dreamCopy));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving");
    setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "dream", title: subjectReadingTitle(name, "Dream Reading"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <section className="page-shell specialty-page dream-page">
      <div className="dream-backdrop dream-backdrop--dream" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className="specialty-hero">
        <p className="eyebrow">Dream Interpretation</p>
        <h1 className="dream-title">{t(language, "dream.title")}</h1>
        <p>{t(language, "dream.lede")}</p>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <textarea
            value={dream}
            onChange={(e) => setDream(e.target.value)}
            placeholder={t(language, "dream.placeholder")}
            rows={6}
          />
          {speechSupported ? (
            <button
              type="button"
              className={`dream-mic${listening ? " dream-mic--live" : ""}`}
              onClick={toggleMic}
              aria-label={t(language, listening ? "dream.micStop" : "dream.micStart")}
              title={t(language, listening ? "dream.micStop" : "dream.micStart")}
            >
              <Mic size={20} />
            </button>
          ) : null}
        </div>
        {listening ? <p className="dream-listening">{t(language, "dream.listening")}</p> : dreamMic.trouble ? <p className="dream-listening dream-mic-trouble" role="status">{t(language, dreamMic.trouble === "refused" ? "dream.micRefused" : "dream.micUnavailable")}</p> : null}
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "dream.going" : "dream.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="dream" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<AskLalaAboutButton report={report} kind="reading" />}
        />
      ) : null}

    </section>
  );
}

// PR1 — THE PALMISTRY ROOM. The member uploads a photo of their open palm; Lala reads it (server-side
// vision) across the eight palmistry parts and returns a warm, concrete reading with the palm shown back
// beside it. Full delivery: auto-shelved, Save to My Room, Download PDF (SH1 pipeline).
function PalmistryPage({ goHome, isMember, onRequireMembership, language }: { goHome: () => void; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [preview, setPreview] = useState<string>(""); // data: URL for the on-screen thumbnail
  const [payload, setPayload] = useState<{ filename: string; data: string } | null>(null); // base64 (no prefix)
  // R223: this door reads the SERVER. A local flag dies on navigation, and a palm reading's dedupe
  // key is the freshly-inserted photo row, so a resubmit on her return is ALWAYS a second paid
  // vision call. `localBusy` is read EXACTLY twice — here and where it is handed to the law.
  const [localBusy, setBusy] = useState(false);
  const busy = usePhotoBusy("palm", localBusy);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  // OV2-14b: the same resize the tarot analyzer uses. A palm is photographed on the same phones, so it
  // carried the identical defect — the camera's original is a ~12.86MB request, over the server's cap,
  // and before the shared reader was fixed the refusal could not even be delivered. src/photoUpload.js
  // carries the numbers; a palm reading needs the lines legible, not the raw sensor resolution.
  async function pick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // A camera capture can arrive with an empty type on some phones, so trust the file when it says nothing.
    if (file.type && !/^image\//.test(file.type)) { setStatus(t(language, "palm.notImage")); return; }
    setStatus(t(language, "palm.preparing"));
    const shot = await preparePhotoForUpload(file);
    if (!shot.base64) { setStatus(t(language, "palm.photoFailed")); return; }
    setPreview(shot.dataUrl);
    setPayload({ filename: shot.filename, data: shot.base64 });
    setReport(null); setSave("idle"); setSaveNote("");
    setStatus("");
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!payload?.data) { setStatus(t(language, "palm.needPhoto")); return; }
    onRequireMembership(() => { void run(); });
  }

  const palmCopy = { signIn: t(language, "palm.roomSignIn"), failed: t(language, "palm.failed") };

  async function run() {
    setBusy(true);
    setReport(null); setSave("idle"); setSaveNote("");
    const stages = ["palm.stage1", "palm.stage2", "palm.stage3", "palm.stage4"].map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/palm/reading", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: payload }),
      });
      const data = await response.json().catch(() => ({}));
      // R210: this room used to invent a claim about her photograph whenever nobody explained.
      const answer = readingAnswer(response.status, data, (d) => (d.report as AnyReport) ?? null);
      if (answer.kind !== "delivered") { setStatus(readingStatusLine(answer, palmCopy)); return; }
      setReport(answer.body);
      setStatus("");
    } catch {
      setStatus(readingStatusLine(READING_UNREACHABLE, palmCopy));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "palm-reading", title: subjectReadingTitle(name, "Palm Reading"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <section className="page-shell specialty-page dream-page">
      <div className="dream-backdrop dream-backdrop--palm" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className="specialty-hero">
        <p className="eyebrow">Palmistry</p>
        <h1 className="dream-title">{t(language, "palm.title")}</h1>
        <p>{t(language, "palm.lede")}</p>
      </div>

      <form className="dream-form palm-form" onSubmit={submit}>
        <input ref={fileRef} type="file" accept={photoAccept()} className="palm-file" onChange={pick} hidden />
        <button type="button" className="palm-drop" onClick={() => fileRef.current?.click()}>
          {preview ? (
            <img src={preview} alt={t(language, "palm.alt")} className="palm-preview" />
          ) : (
            <span className="palm-drop__hint"><Camera size={26} /> {t(language, "palm.add")}</span>
          )}
        </button>
        {preview ? <button type="button" className="palm-retake" onClick={() => fileRef.current?.click()}>{t(language, "palm.retake")}</button> : null}
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy || !payload}>
          {t(language, busy ? "palm.going" : "palm.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="palmistry" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<AskLalaAboutButton report={report} kind="reading" />}
        />
      ) : null}
    </section>
  );
}

// OQ2: the numerology mini-readings menu now comes from the SERVER config
// (server/data/numerology-mini-reports.json, GET /api/numerology/mini-reports), so the
// buttons and the math that backs them can never drift apart. "full" stays the primary
// calculator button on the page itself.

function NumerologyPage({ chart, isMember, onRequireMembership, goHome, language }: { chart: BirthChartForm | null; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; goHome: () => void; language: string }) {
  // The numerology room is a full reading, to the same standard as every other room:
  // correct Western Pythagorean numbers plus a designed, per-number interpretation in
  // Lala's voice, with letterhead, Save to My Room, and PDF.
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(chart || {}) }));
  const [busy, setBusy] = useState(false);
  const [activeCat, setActiveCat] = useState<string>("full");
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const [pickKind, setPickKind] = useState<"me" | "person" | "new">("me");
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => setForm((f) => ({ ...f, [field]: value }));

  // OQ2: the mini-readings row is config-driven (same shape as the astrology mini-reports).
  // R58: and it loses its whole row the same way, so it says the same sentence.
  const [menusFailed, setMenusFailed] = useState(0);
  const menuNote = readingMenuNote(menusFailed, { failed: t(language, "menus.loadFailed") });
  const [miniConfigs, setMiniConfigs] = useState<NumerologyMiniConfig[]>([]);
  const [activeMini, setActiveMini] = useState<NumerologyMiniConfig | null>(null);
  useEffect(() => {
    void listReadingMenu<NumerologyMiniConfig>("numerology-minis")
      .then((rows) => (rows ? setMiniConfigs(rows) : setMenusFailed(1)));
  }, []);

  function submit(event: FormEvent) {
    event.preventDefault();
    onRequireMembership(() => { void run("full"); });
  }

  async function run(focus: string) {
    const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    setBusy(true);
    setActiveCat(focus);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = numerologyStages(focus).map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/numerology/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          chart: { name: fullName, birthDate: form.birthDate },
          focus,
          // R1: this used to read a `partner` that does not exist in this scope — a latent
          // ReferenceError, unreachable only because this page never runs the compatibility focus
          // (the mini-readings panel owns that, with its own partner form). The dead branch is gone
          // rather than left as a trap for whoever wires a compatibility button here next.
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "numerology.roomSignIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "reading.failed"));
        return;
      }
      setReport(data.report as AnyReport);
      setStatus("");
    } catch {
      setStatus(t(language, "reading.failed"));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving");
    setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "numerology", title: subjectReadingTitle(name, "Numerology"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <section className="page-shell specialty-page numerology-page">
      <div className="numerology-backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>
      <div className="specialty-hero">
        <p className="eyebrow">Numerology room</p>
        <h1 className="numerology-title">{t(language, "numerology.title")}</h1>
        <p>{t(language, "numerology.lede")}</p>
      </div>

      <SubjectPicker

        language={language}
        memberChart={chart}
        onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPickKind(m.kind); }}
      />
      <form className="birth-chart-form find-your-type__form" onSubmit={submit}>
        <div className="form-row form-row--name">
          <NameFields first={form.firstName} middle={form.middleName} last={form.lastName} onChange={update} language={language} />
          <p className="birth-chart-form__hint">{t(language, "numerology.nameHint")}</p>
        </div>
        <label className="form-field--date">
          <span>{t(language, "form.birthDate")}</span>
          <input type="date" value={form.birthDate} onChange={(e) => update("birthDate", e.target.value)} required />
        </label>
        <button className={`button button--primary find-your-type__go${busy && activeCat === "full" ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy && activeCat === "full" ? "numerology.going" : "numerology.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {/* OQ2: the mini-readings row. Each number is its own popup (picker + fields ->
          that number alone as a designed reading, save + PDF), exactly like the astrology
          mini-reports. Config-driven: GET /api/numerology/mini-reports drives the row, and
          the server only publishes rows its math actually backs, so no button can go dead. */}
      {/* R58: and when the row did not arrive at all, the room says so instead of losing it. */}
      {menuNote ? <p className="reading-menu-note" role="status">{menuNote}</p> : null}
      <ReadingMenuSection
        rows={miniConfigs}
        className="numerology-minis"
        ariaLabel={t(language, "numerology.minis")}
        lead={t(language, "numerology.oneNumber")}
        showBlurb
        onPick={(cat) => onRequireMembership(() => setActiveMini(cat))}
      />

      {report ? (
        <DeliveredReading
          report={report} room="numerology" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={activeCat !== "compatibility" ? <SaveSubjectOffer pickKind={pickKind} chart={form} language={language} /> : null}
        />
      ) : null}

      {busy ? <NoHostageNote room="numerology" language={language} /> : null}

      {activeMini ? (
        <NumerologyMiniModal config={activeMini} memberChart={chart} onClose={() => setActiveMini(null)} language={language} />
      ) : null}

    </section>
  );
}

// The tarot draw experience: ask a question, pick a spread, draw real cards
// (face-down), reveal them, and read the AI interpretation tied to the question.
// R6-3: TAROT_CATEGORIES and ANALYZE_SPREADS moved VERBATIM to `src/catalogs/tarot-spreads.ts`,
// where the gate diffs both of them against the SERVER'S own spread table.


// N3-1 — ANALYZE YOUR READING. The member pulls her own physical cards, photographs the spread, and Lala
// reads it. Mobile first: a coarse pointer gets Take a photo (the camera) and Choose from library side by
// side; a desktop gets the file picker. Her words in the context box ride to the server VERBATIM.
//
// Three answers can come back and each has its own face: a warm ask for a better photo (the free pre-gate,
// which costs nothing), a "here is what I can see, tell me the rest" when identification is partial, and
// the reading itself. She is never handed a confident reading of cards that were not there.
// R8 / roadmap 7: TarotAnalyzePanel moved VERBATIM to src/components/TarotAnalyze.tsx.

function TarotPage({ isMember, onRequireMembership, goHome, language }: { chart: BirthChartForm | null; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; goHome: () => void; language: string }) {
  const [cat, setCat] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [spread, setSpread] = useState("three-card");
  const [status, setStatus] = useState<"idle" | "drawing" | "drawn" | "error">("idle");
  const [reading, setReading] = useState<TarotReadingData | null>(null);
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");

  const [libraryOpen, setLibraryOpen] = useState(false); // T4: the rebuilt Card Library
  const questionRef = useRef<HTMLTextAreaElement | null>(null); // MIC-1: the mic's field, for the live view

  function reset() {
    setReading(null);
    setStatus("idle");
    setError("");
    setSaveState("idle");
    setSaveNote("");
  }

  function selectCategory(c: (typeof TAROT_CATEGORIES)[number]) {
    setCat(c.key);
    reset();
    setSpread(c.spread);
    // Pulling the daily card is a gated action.
    if (c.key === "daily") onRequireMembership(drawDaily);
  }

  // Daily card shares the one-per-day cache with My Room. R75: the THIRD hand-typed copy of this
  // route, and the count was the finding (R47). It read the GET's body with no status check and
  // then went STRAIGHT ON to the POST whenever no card came back — so a refusal was not an error,
  // it was the trigger for the request that draws. And it printed the server's own `message`, which
  // is R72's line (a message is the server's word only beside `ok: false`) and 14-B3's seam (that
  // sentence is English server copy in a room that turns).
  async function drawDaily() {
    setStatus("drawing");
    setError("");
    let answer = await readDailyCard();
    if (answer.status === "none") answer = await drawDailyCard();
    if (answer.status !== "ok") {
      setStatus("error");
      setError(dailyNote(answer, t(language, "tarot.dailyNotReady"), t(language, "daily.signIn")) || t(language, "tarot.dailyNotReady"));
      return;
    }
    const c = answer.value;
    setReading({
      type: "tarot",
      question: "Your card for today",
      spread: "single",
      spreadLabel: "Daily Card",
      cards: [{ slug: c.slug, name: c.name, position: "Today", orientation: c.orientation, interpretation: c.interpretation || "" }],
      overview: c.overview || "",
      evaluation: "",
    });
    setStatus("drawn");
  }

  function drawCards(event?: FormEvent) {
    event?.preventDefault();
    // Pulling the cards is a gated action.
    onRequireMembership(runDrawCards);
  }

  async function runDrawCards() {
    setStatus("drawing");
    setError("");
    setReading(null);
    setSaveState("idle");
    setSaveNote("");
    try {
      const response = await fetch("/api/tarot/draw", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, spread }),
      });
      const data = await response.json();
      if (!response.ok || !Array.isArray(data.cards)) throw new Error(data?.message || t(language, "tarot.drawFailed"));
      setReading(data as TarotReadingData);
      setStatus("drawn");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t(language, "tarot.drawFailed"));
    }
  }

  async function saveReading() {
    if (!reading) return;
    setSaveState("saving");
    setSaveNote("");
    // R47 — THE OTHER LIVE ONE, and the more expensive of the two: `if (!response.ok) throw` never
    // read the payload, so a refusal the server sent at 200 left this reader saying the saved word
    // for the rest of the session over a reading that was never stored.
    const answer = await postReadingToRoom({ type: "tarot", title: tarotShelfTitle(reading.spreadLabel), data: reading });
    setSaveState(saveReadingState(answer));
    setSaveNote(saveReadingNote(answer, { done: t(language, "save.done"), signIn: t(language, "tarot.saveSignIn"), failed: t(language, "tarot.saveFailed") }));
  }

  const showAsk = Boolean(cat) && cat !== "daily";

  return (
    <section className="page-shell specialty-page tarot-page">
      <div className="tarot-backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>
      <div className="tarot-hero">
        <h1>{t(language, "tarot.title")}</h1>
        <p className="tarot-hero__mantra">{t(language, "tarot.mantra")}</p>
      </div>

      {/* N8-1: SECTION ONE, the site pulls for you. The question box, then the spread choice. It is a
          closed section: everything that belongs to a site pull lives here and nothing else does. */}
      <section className="tarot-section tarot-section--pull">
      <p className="tarot-section__eyebrow">{t(language, "tarot.one.eyebrow")}</p>
      <h2 className="tarot-section__title">{t(language, "tarot.one.title")}</h2>
      <p className="tarot-section__blurb">{t(language, "tarot.one.blurb")}</p>

      <form className="tarot-ask reading-form" onSubmit={drawCards}>
        {/* The question box is always visible and spans the width of the five categories. */}
        {/* MIC-1: the shared mic, never a copy; `.mic-field` is the one declaration of the shape.
            The textarea is folded to two lines so this feature pays its own R8 ceiling (roadmap 7). */}
        <label className="wide tarot-ask__question">
          <span>{t(language, "tarot.ask")}</span>
          <div className="mic-field">
            <textarea ref={questionRef} value={question} placeholder={t(language, "tarot.askPlaceholder")}
              onChange={(event) => setQuestion(event.target.value)} />
            <MicButton setValue={setQuestion} field={questionRef} />
          </div>
        </label>

        <p className="tarot-ask__step">{t(language, "tarot.chooseSpread")}</p>
        <div className="tarot-categories">
          {TAROT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={cat === c.key ? "tarot-category is-active" : "tarot-category"}
              onClick={() => selectCategory(c)}
            >
              <span className="tarot-category__label">{c.label}</span>
              <span className="tarot-category__blurb">{c.blurb}</span>
            </button>
          ))}
        </div>

        {showAsk ? (
          <button className={status === "drawing" ? "button button--primary is-generating" : "button button--primary"} type="submit" disabled={status === "drawing"}>
            {t(language, status === "drawing" ? "tarot.pulling" : "tarot.pull")}
          </button>
        ) : null}
      </form>

      {status === "drawing" && cat === "daily" ? <p className="reading-status">{t(language, "tarot.pullingOne")}</p> : null}
      {status === "error" ? <p className="reading-status">{error}</p> : null}

      {/* OV2-4: same here. Face down on arrival, turned one at a time by the member. */}
      {reading ? (
        (
          <ClosableReport language={language} saved={saveState === "saved"} onClose={() => { setReading(null); setSaveState("idle"); setSaveNote(""); }}>
            <div className="chart-report__wrap">
              <p className="tarot-facedown__hint">{t(language, "tarot.facedown")}</p>
              <TarotReadingView data={reading} faceDown />
              <ReportPrintPortal data={reading} />
              <div className="reader__toolbar">
                <span><Eye size={16} /> {t(language, "tarot.yourReading")}</span>
                <div className="chart-report__actions">
                  {pdfAllowed(reading) ? <button type="button" onClick={() => downloadReportPdf(reading)}>{t(language, "reports.download")}</button> : null}
                  {/* N4-3: the shared handoff. Renders only for READING types (src/askLalaHandoff.js). */}
                  <AskLalaAboutButton report={reading} kind="reading" />
                  <button type="button" onClick={saveReading} disabled={saveState === "saving" || saveState === "saved"}>
                    {t(language, saveState === "saved" ? "result.saved" : saveState === "saving" ? "result.saving" : "result.save")}
                  </button>
                </div>
              </div>
              {saveNote ? <p className="chart-report__save-note">{saveNote}</p> : null}
            </div>
          </ClosableReport>
        )
      ) : null}
      </section>

      {/* N3-1 built it, N8-1 moved it out here: SECTION TWO, her own physical pull, photographed and
          read. It is a sibling of Section One, never a child of it. */}
      <TarotAnalyzePanel onRequireMembership={onRequireMembership} language={language} />

      {/* T2/T4: the Card Library is a long full-width button under the spreads. */}
      <button type="button" className="tarot-library-btn" onClick={() => onRequireMembership(() => setLibraryOpen(true))}>
        <span className="tarot-library-btn__label">{t(language, "tarot.library")}</span>
        <span className="tarot-library-btn__blurb">{t(language, "tarot.libraryBlurb")}</span>
      </button>

      {libraryOpen ? <TarotLibrary onClose={() => setLibraryOpen(false)} /> : null}

    </section>
  );
}

// The Ask Lala page, modeled on the Claude interface: it opens AIRY over her background
// with a first-name greeting, the input, and suggestion chips. Once the member asks, the
// input docks to the bottom and the conversation grows upward, iMessage bubbles floating
// directly over her background (no panel). Saved/named conversations live in a slim
// sidebar. Starting or sending is the gated action.
// R60 — THE TWO SENTENCES BOTH ASK LALA SURFACES SAY WHEN HER CONVERSATION DID NOT ARRIVE, declared
// ONCE for the drawer and the page (R47's count-the-copies, applied before there were two copies).
// **THEY ARE ENGLISH ON PURPOSE.** Every other word on both surfaces is bare English — Ask Lala is
// not in `strings.js` and is on the standing list for her word — and 14-B6's own lesson is that a
// lone translated line over an untranslated screen is the defect, not the fix.
// R62 adds the two the CREATE needs: her words still go out, and the thread they land in is the
// thing that did not get made.
type LalaConversation = { id: number; title: string; collection: string; messageCount: number; updatedAt: string };
type LalaPageMessage = { id?: string; role: string; content: string; seal?: DeliveredSeal; sealOptions?: SealOption[]; cards?: ChatCard[]; manual?: ManualHandoff; compilation?: Compilation; attachments?: LalaAttachment[] };

// AL1.1: the greeting's name comes from the SAME source as the My Room title
// (memberFirstName(member, profile), which prefers the member's saved profile over their Google
// account name). This page used to take the OAuth name and try to improve on it with its own
// /api/profile fetch that read `p.firstName` off a response shaped { profile: {...} } — always
// undefined, so it silently kept the sign-in name and the owner was greeted "Hi iVertical".
// One source, so when the room name changes the greeting follows for free.

// =================================================================================================
// >>> **N9-3's OVERLAP LAW, MEASURED RATHER THAN TUNED (her order, 2026-09-17).** <<<
//
// Her law: *the thread's scroll area ENDS ABOVE the dock, so the newest message is always fully
// visible and never renders behind the input.* It was held by a tuned constant, 190px, and the
// constant ROTTED. **Measured with a browser open at 390px before a line was changed:**
//
//     the dock ................. 166px tall, fixed, bottom at 760
//     the mobile tab bar ....... 81px, top at 763
//     together ................. 247px of an 844px viewport
//     the padding holding it ... 190px      <- 57px short, so the newest row sat UNDER the dock
//
// **AND THE CONSTANT WAS NOT THE WHOLE MISTAKE.** A first repair composed the dock height plus 28px
// and forgot the TAB BAR the dock is stacked on top of, so it still came out short and the check
// stayed red. The dock is positioned at the tab bar's height plus the safe-area inset, so the
// clearance the thread needs is the dock's own height PLUS that same offset — and the stylesheet now
// composes it from the very variables the dock is positioned with, which is what stops the two
// drifting apart again.
//
// **WHAT CSS CANNOT KNOW IS WHAT THIS SUPPLIES.** The dock's height moves with the Suggestions row,
// with the textarea as she types, with the member's font size and with her phone's safe-area inset.
// So the real height is published as `--ask-dock-h` and the stylesheet does the arithmetic. It is
// the same pattern `EventLive.tsx` already uses for the event banner, for the same reason: a
// hard-coded height tucks content under the bar on exactly the devices most of her members hold.
//
// **IT IS A REF CALLBACK RATHER THAN A HOOK**, so it adds no line inside the component body where
// this repo's mutation anchors live — R104 refused a first attempt that shifted one.
const askDockObservers = new WeakMap<HTMLElement, ResizeObserver>();
function measureAskDock(dock: HTMLElement | null) {
  if (!dock) return;
  const main = dock.closest(".ask-lala-main") as HTMLElement | null;
  if (!main) return;
  const apply = () => {
    const h = dock.offsetHeight || 0;
    // **A DOCK THAT MEASURES ZERO HAS NOT LAID OUT YET.** Publishing 0 would put the newest message
    // directly under the input, so an unreadable measurement changes nothing and the CSS fallback
    // stands: unreadable and zero are not the same answer (R18).
    if (h > 0) main.style.setProperty("--ask-dock-h", String(h) + "px");
  };
  apply();
  // Re-measured as the dock grows, because the textarea growing as she types is exactly the moment
  // the newest message would slide underneath it.
  if (typeof ResizeObserver === "function" && !askDockObservers.has(dock)) {
    const ro = new ResizeObserver(apply);
    ro.observe(dock);
    askDockObservers.set(dock, ro);
  }
}

function AskLalaPage({ firstName, isMember, onRequireMembership, pendingQuestion = "", pendingHandoff = null, onPendingConsumed = () => {}, goHome, language }: { firstName: string; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; pendingQuestion?: string; pendingHandoff?: { type: string; kind: string; subject?: string } | null; onPendingConsumed?: () => void; goHome: () => void; language: string }) {
  const [conversations, setConversations] = useState<LalaConversation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<LalaPageMessage[]>([]);
  const [threadNote, setThreadNote] = useState("");   // R60: the conversation that did not arrive
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false); // N2-3: the mobile Suggestions sheet
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // IT #322: a thread longer than one read offers the page before it. `earlierAtt` is the thread's
  // files, read once when it opened, so an earlier page gets its pictures back too; `keepPlace` is
  // where she was reading, so the page above her does not throw her to the bottom.
  const [earlier, setEarlier] = useState<"none" | "offer" | "loading" | "failed">("none");
  const earlierAtt = useRef<LalaAttachment[] | null>(null);
  const keepPlace = useRef<{ height: number; y: number } | null>(null);
  // R56: the icon carries the ANSWER. A boolean could not say "it did not go", so it did not.
  const [copiedIndex, setCopiedIndex] = useState<{ index: number; state: CopyState } | null>(null);
  const [turnNotice, setTurnNotice] = useState<TurnNotice>(null);
  const free = useFreeTurns();   // FUNNEL-4: one law, two surfaces (see the drawer)
  const composer = composerLock(turnNotice, "", free.atWall);
  const files = useAttachments();
  const abortRef = useRef<AbortController | null>(null); const sendInFlightRef = useRef(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  // W4-7: the composer, and the ceiling it may grow to (lower on a phone, where the keyboard has
  // already taken most of the screen).
  const composeRef = useRef<HTMLTextAreaElement | null>(null);
  const composeLimits = limitsForWidth(typeof window === "undefined" ? 0 : window.innerWidth);

  const [chips] = useState(() => pickSome(ASK_LALA_CHIP_POOL, 5));

  const showEmpty = messages.length === 0 && !sending;
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) { if (messages[i].role === "user") { lastUserIndex = i; break; } }


  // R62 — **A FAILED READ IS NOT AN EMPTY SIDEBAR.** `(data.conversations || [])` then
  // `setConversations` blanked every named conversation she has on a 200 refusal or an unparseable
  // body, while the `!res.ok` and `catch` paths (correctly) kept them — three paths, two behaviours,
  // and the wrong one is the path the server actually takes when it refuses. Guarded by POSITION.
  async function loadConversations(): Promise<LalaConversation[]> {
    const rows = await listLalaConversations<LalaConversation>();
    if (rows) setConversations(rows);
    return rows || [];
  }

  // FREE-1's guard, and it is the DRAWER'S OWN (R28/R29): a surface that arrives locked does not ask
  // the server for what it may not have, and `/api/lala/` is gated at `lala:chat`. It is also the
  // door this cohort came through, since a FREE tier only ever opens Ask Lala through the allotment.
  const askLala = useRequireFeature(); const lalaOpens = opensSurface(useFeatureOutcome("lala:chat"));
  useEffect(() => {
    // >>> D10 LALA-RESUME (her YES, 2026-10-27): SHE COMES BACK TO HER LAST CONVERSATION, NOT A BLANK PAGE. <<< #434/#435:
    // a member's whole day of work was spread over twelve threads because every visit opened a fresh one, and she believed it
    // was lost. Her newest thread opens on arrival, unless she came WITH a question or a handoff (those start fresh, by
    // design) or as a funnel arrival (that page has its own greeting).
    const arrivingWithAsk = Boolean(String(pendingQuestion || "").trim() || pendingHandoff || readLandingArrival(safeSessionStorage()).tag);
    if (isMember) void loadConversations().then((rows) => {
      const target = resumeTarget({ rows, arrivingWithAsk, alreadyResumed: resumedRef.current });
      if (target === null) return;
      resumedRef.current = true;
      void openConversation(target);
    });
    // >>> HER ITEM 2 CLAUSE -- *LALA ARRIVES SPEAKING FIRST* -- DID NOT HAPPEN. Measured on a staged
    // funnel seat: ZERO `/api/lala/` requests here, so the greeting never composed and the plum
    // window never painted (R38/R165). The read is free for everybody else and the ANSWER'S OWN
    // `freeTurns` is the cohort test -- `funnelArrivalMessages` is where the whole of it is written.
    // >>> **AND THE GUARD ABOVE DEADLOCKED THE VERY COHORT THE READ WAS ADDED FOR (2026-09-02).**
    // <<<
    //
    // `lalaOpens` is false for a brand-new FREE funnel arrival, because her allotment has not been
    // granted yet. **AND THE ONLY THING THAT GRANTS IT IS TOUCHING `/api/lala/`** — the server's
    // allotment door hangs off `requireFeature(FREE_TURN_FEATURE)`. So the guard refused the one
    // request that would have opened the surface, `lalaOpens` stayed false forever, and she was left
    // on the plain Ask Lala page with no greeting, no meter and no plum window — then thrown to the
    // membership ladder by T8-3's interceptor on the next refused read.
    //
    // The note directly above this one records the SAME symptom being fixed once already: *"ZERO
    // `/api/lala/` requests here, so the greeting never composed and the plum window never
    // painted"*. The read was added; the guard put in front of it restored the defect.
    //
    // **A FUNNEL ARRIVAL KNOCKS ANYWAY**, because knocking is what opens the door for her. Everyone
    // else keeps the guard exactly as it was: a locked surface still does not ask the server for what
    // it may not have. The server decides — this only stops the client deciding FOR it in the one
    // case where the client cannot know yet.
    const funnelArrival = Boolean(readLandingArrival(safeSessionStorage()).tag);
    if (!lalaOpens && !funnelArrival) return;
    void Promise.all([readLalaThread<LalaPageMessage>(), listLalaAttachments<LalaAttachment>()])
      .then(([read, att]) => {
        free.take(read?.freeTurns);
        const arrival = funnelArrivalMessages(read);
        if (arrival) setMessages(attachToMessages(arrival, att || []));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember, lalaOpens]);

  useEffect(() => {
    // One page-level scroll (item L7), and `dockClearance` is how far it must clear the sticky dock.
    const el = threadRef.current;
    if (!el) return;
    // IT #322: an earlier page landed ABOVE her. She stays on the message she was reading, and the
    // newest-message pin below is for a thread that grew at the bottom, which this did not.
    const kept = keepPlace.current;
    if (kept) {
      keepPlace.current = null;
      const hold = window.requestAnimationFrame(() => window.scrollTo(0, kept.y + (document.documentElement.scrollHeight - kept.height)));
      return () => window.cancelAnimationFrame(hold);
    }
    const toNewest = () => {
      const last = el.lastElementChild as HTMLElement | null;
      if (!last) return window.scrollTo(0, document.documentElement.scrollHeight);
      last.style.scrollMarginBottom = `${dockClearance(window, document.querySelector(".ask-lala-compose"))}px`;
      last.scrollIntoView({ block: "end", behavior: "auto" });
    };
    toNewest();
    // W7 — AND AGAIN AFTER THE BROWSER HAS LAID THE THREAD OUT. Measured while diagnosing the flaky
    // gate: opening a long conversation changes the document height by about 42px AFTER this effect
    // runs (fonts and wrapping settle), so a single pass can leave her a little short of the newest
    // message. One extra frame costs nothing and is the difference on a slow phone.
    const again = window.requestAnimationFrame(toNewest);
    // W10-3: and again as each image lands. One frame covers fonts and wrapping; it does not cover a
    // card picture that arrives over the network after the layout has already settled.
    const pending = [...el.querySelectorAll("img")].filter((img) => !img.complete);
    pending.forEach((img) => img.addEventListener("load", toNewest, { once: true }));

    // =============================================================================================
    // >>> **AND AGAIN WHILE THE THREAD IS STILL GROWING — HER ORDER, 2026-09-17.** <<<
    //
    // **MEASURED WITH A BROWSER OPEN AT 390px, opening a long conversation after a reload:**
    //
    //     when the scroll ran ..... document height 1,161px
    //     a moment later .......... document height 4,871px
    //     where she was left ...... 2,928px ABOVE her newest message
    //     one more scroll ......... 311px of clearance, newest message fully visible
    //
    // The three passes above — the effect, one animation frame, and each image as it lands — all run
    // BEFORE the thread's own height settles on this path, so every one of them scrolls to a bottom
    // that is about to move. **She opens a long conversation on her phone and lands thousands of
    // pixels above where she left off.**
    //
    // **THE OBSERVER IS THE FIX BECAUSE IT IS TIED TO THE THING THAT CHANGES**, rather than to a
    // count of frames or a timeout, which is the same guess with a number on it.
    //
    // >>> **AND IT MAY NEVER YANK A MEMBER WHO IS READING.** <<< This house has already paid for
    // that exact defect on the live-comments lane, where every arriving comment snapped the lane back
    // under a moving thumb. So it re-scrolls ONLY while she is still standing at the bottom — the
    // position the newest-message law is about. The moment she scrolls up to read, the observer
    // stops moving her and the thread stays exactly where she put it.
    // =============================================================================================
    // >>> **AND HELD THERE THROUGH A BOUNDED SETTLE WINDOW — HER ORDER, 2026-09-17.** <<<
    //
    // **MEASURED WITH A BROWSER OPEN AT 390px, opening a long conversation after a reload:**
    //
    //     when the first scroll ran ... document height 1,161px
    //     a moment later .............. document height 4,871px
    //     where she was left .......... 2,928px ABOVE her newest message
    //
    // The three passes above — the effect, one animation frame, and each image as it lands — all run
    // BEFORE the thread's own height settles on this path. **She opens a long conversation on her
    // phone and lands thousands of pixels above where she left off.**
    //
    // **A ResizeObserver ALONE WAS NOT ENOUGH AND THAT IS WHY THIS IS A LOOP.** It fires on the
    // thread's own size changing, so it cannot answer for anything that moves the PAGE afterwards.
    // Driven repeatedly, the observer-only repair passed one run and failed the next — it won a race
    // rather than ending it. **An intermittent fix is not a fix**, so the pin holds every frame for a
    // bounded window and the race has nowhere left to run.
    //
    // >>> **AND IT YIELDS THE INSTANT SHE TOUCHES THE PAGE — the live-comments lesson, paid for.** <<<
    // That lane snapped itself back under a moving thumb because its guard was a state DERIVED from
    // scrolling rather than the gesture itself. A derived guard is doubly wrong here: the document is
    // growing, so any "is she near the bottom" test balloons while she has not moved a finger and
    // switches the pin off exactly when it is needed. **Her wheel, her touch or her key ends it for
    // good** — one flag, checked before every single pin.
    //
    // The window is bounded so nothing here can hold a page for ever: after it, late images still
    // have their own listeners and a new message re-runs this effect from the top.
    // **AND THE WINDOW IS RE-ARMED BY ARRIVING CONTENT, NOT COUNTED FROM MOUNT.** Measured on the
    // same run: the thread still had ZERO rows 1.4 seconds after the conversation was opened, and
    // its 4,871px of height landed after that. A window counted from when this effect ran would
    // therefore expire before the thing it exists to catch had arrived — which is the observer-only
    // repair's failure in a different costume. **Every growth pushes the deadline out**, so the pin
    // lives exactly as long as the thread is still becoming itself and not one frame longer.
    const SETTLE_MS = 2500;
    let deadline = Date.now() + SETTLE_MS;
    let hersNow = false;
    let pinFrame = 0;
    const hers = () => { hersNow = true; };
    const pin = () => {
      if (hersNow) return;
      if (Date.now() > deadline) return;
      toNewest();
      pinFrame = window.requestAnimationFrame(pin);
    };
    for (const ev of ["wheel", "touchstart", "keydown"]) {
      window.addEventListener(ev, hers, { passive: true });
    }
    const grew = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
          if (hersNow) return;
          const wasDone = Date.now() > deadline;
          deadline = Date.now() + SETTLE_MS;
          // Restart the loop if it had already run out, so late content re-pins rather than landing
          // her wherever the last scroll left her.
          if (wasDone) pinFrame = window.requestAnimationFrame(pin);
        })
      : null;
    grew?.observe(el);
    pinFrame = window.requestAnimationFrame(pin);

    return () => {
      window.cancelAnimationFrame(again);
      window.cancelAnimationFrame(pinFrame);
      grew?.disconnect();
      for (const ev of ["wheel", "touchstart", "keydown"]) window.removeEventListener(ev, hers);
      pending.forEach((img) => img.removeEventListener("load", toNewest));
    };
  }, [messages, sending]);

  // R60 — **SHE TAPPED A NAMED CONVERSATION IN HER SIDEBAR AND IT OPENED EMPTY.** All three failure
  // paths here ended at a blank thread and none of them said a word: `data.messages || []`, the
  // `catch { /* leave empty */ }`, and the `.catch(() => null)` on the attachments. The clear STAYS
  // — she is switching threads and the old one must not sit under the new title — so the honest fix
  // is the SENTENCE, plus the paint guarded by POSITION (R52): the thread is only ever drawn from an
  // answer that arrived. U4: each file goes back beside its own message, through the one law now.
  async function openConversation(id: number) {
    setActiveId(id);
    setSidebarOpen(false);
    setEditingIndex(null);
    setMessages([]);
    const [read, att] = await Promise.all([
      readLalaThread<LalaPageMessage>(id),
      listLalaAttachments<LalaAttachment>(id),
    ]);
    const hist = read ? read.messages : null;
    free.take(read?.freeTurns);
    if (hist) setMessages(attachToMessages(hist, att));
    setThreadNote(lalaThreadNote(hist, att, LALA_THREAD_COPY));
    earlierAtt.current = att;
    setEarlier(read?.earlier ? "offer" : "none");
  }

  async function showEarlier() {
    if (activeId === null || earlier === "loading" || earlier === "none") return;
    const oldest = messages.find((m) => m.id)?.id;
    setEarlier("loading");
    const page = await readEarlierLalaMessages<LalaPageMessage>(activeId, oldest);
    if (!page) { setEarlier("failed"); return; }
    const next = prependEarlier(messages, attachToMessages(page.messages, earlierAtt.current));
    const added = next.length - messages.length;
    keepPlace.current = { height: document.documentElement.scrollHeight, y: window.scrollY };
    setMessages(next);
    setEditingIndex((i) => (i === null ? null : i + added));
    setEarlier(page.earlier ? "offer" : "none");
  }

  // New conversation just returns to the airy empty state; the thread is created on the
  // first message (so empty threads never clutter the sidebar).
  function newConversation() {
    setActiveId(null);
    setMessages([]);
    setEarlier("none");
    setThreadNote("");   // R60: a fresh thread is not the failed one she just left
    setInput("");
    setEditingIndex(null);
    setSidebarOpen(false);
  }

  function submitText(text: string, handoff: { type: string; kind: string; subject?: string } | null = null) {
    const t = text.trim();
    // A message can be just an attachment: "here, look at this".
    if ((!t && !files.pending.length) || sending) return;
    askLala("lala:chat", () => void doSend(t, handoff)); // ITEM 2: the DOOR she touched, never whether she PAYS — see freeTurnsDoorOpen
  }

  // HP1: a question asked from the home page arrives already asked. It is consumed ONCE (the ref
  // guards against a re-render asking it twice, which would charge the owner twice and show the
  // member a duplicate), and cleared from the parent so a later visit to this page is not
  // ambushed by an old question.
  const askedPending = useRef(false);
  useEffect(() => {
    const q = (pendingQuestion || "").trim();
    if (!q || askedPending.current) return;
    askedPending.current = true;
    const handoff = pendingHandoff;
    onPendingConsumed();
    submitText(q, handoff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuestion]);

  async function doSend(text: string, handoff: { type: string; kind: string; subject?: string } | null = null) {
    if (lalaSendState({ text, attachments: files.pending.length, inFlight: sendInFlightRef.current }) !== "send") return;
    sendInFlightRef.current = true;   // E5: this surface had no guard at all.
    try {
    let base = messages;
    if (editingIndex !== null) {
      base = messages.slice(0, editingIndex);
      setEditingIndex(null);
    }
    // R62 — **THE ROUND'S FINDING, AND IT COSTS A PAID TURN.** A refused create was not an error, it
    // was a null: her question went out anyway, Lala answered it, the owner was charged, and the
    // whole exchange was filed under NO NAMED THREAD — invisible in her sidebar, and identical on
    // screen to a member who has saved nothing. **HER WORDS STILL GO** (R57's ruling: throwing them
    // away is the one outcome worse than the silence) and the sentence is what changes.
    let convId = activeId;
    if (convId === null) {
      const made = await createLalaConversation(text.slice(0, 48), "");
      convId = made.ok ? made.id : null;
      if (convId !== null) setActiveId(convId);
      setThreadNote(lalaConversationNote(made, { signIn: LALA_THREAD_COPY.signIn, failed: LALA_THREAD_COPY.notSaved }));
    }
    const sentFiles = files.pending;
    const attachmentIds = files.ids;
    const afterId = newestStoredId(base);   // IT #300: the rows she already had, before her turn is drawn
    setMessages([...base, { role: "user", content: text, attachments: sentFiles }]);
    setInput("");
    files.clear();
    setSending(true);
    const controller = new AbortController();
    abortRef.current = controller;
    // R98: one send law for both surfaces; a stop is asked of the SIGNAL, never a name (R97).
    const answer = await sendToLala<DeliveredSeal, SealOption[], ChatCard[], ManualHandoff, Compilation>({
      message: text, conversationId: convId, attachmentIds, handoff: handoff || undefined,
      surface: "lala-page", signal: controller.signal,
    });
    // IT #300: a DROPPED request is not a refusal (see the drawer). A Stop is `aborted` and never waits.
    const dropped = answer.kind === "offline"
      ? await awaitDroppedReply(async () => (await readLalaThread<{ id?: string; role: string; content: string; cards?: ChatCard[]; manual?: ManualHandoff; compilation?: Compilation }>(convId))?.messages ?? null, text, afterId, { normalise: stripAttachNote })
      : null;
    const turn = dropped ? dropped.content : lalaSendTurn(answer);
    if (turn) setMessages((m) => [...m, dropped
      ? { role: "assistant", content: dropped.content, cards: dropped.cards, manual: dropped.manual, compilation: dropped.compilation }
      : { role: "assistant", content: turn, seal: answer.seal, sealOptions: answer.sealOptions, cards: answer.cards, manual: answer.manual, compilation: answer.compilation }]);
    // T7-5: the same shared box as the drawer, from the same server payload, on the same condition.
    if (answer.kind === "ok") { setTurnNotice(applyTurnPayload(answer.turns)); free.take(answer.freeTurns); raiseLockedDoor(answer.lock, undefined, { answered: true }); }
    // The sidebar is refreshed on anything that reached the server, not only on an answer: R62's
    // create runs BEFORE this send, so a refused turn can still have made a thread she must be able
    // to see. An abort reached nothing and changes nothing.
    if (answer.kind !== "aborted" && !free.wallFrom(answer.freeTurns)) await loadConversations();  // FREE-1: at the wall this read is 402 and T8-3 NAVIGATES her off it (see `wallFrom`).
    setSending(false); abortRef.current = null;
    } finally { sendInFlightRef.current = false; }   // E5: loadConversations can throw.
  }

  function stopGenerating() {
    abortRef.current?.abort();
  }

  function editLastMessage() {
    if (lastUserIndex < 0) return;
    setInput(messages[lastUserIndex].content);
    setEditingIndex(lastUserIndex);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submitText(input);
  }

  // R56: this one never AWAITED the write, so the `try` caught a synchronous throw and nothing
  // else — a rejected write was an unhandled rejection and the tick flipped anyway. Its flash was
  // also 1400ms where the other four were 1500: what a number typed five times does.
  async function copyMessage(text: string, index: number) {
    const state = copyState(await copyText(text));
    setCopiedIndex({ index, state });
    window.setTimeout(() => setCopiedIndex((c) => (c?.index === index ? null : c)), COPY_FLASH_MS);
  }

  // R38 — SHE CAN NAME HER OWN THREAD. This route and its client helper both existed and NOTHING
  // called either, so a saved conversation was titled with the first 48 characters of her first
  // message, verbatim and cut mid-word, permanently. The list is re-read from the server afterwards,
  // so what she sees is what is really stored.
  async function renameThread(id: number, title: string): Promise<string | null> {
    const stored = await renameConversation(id, title);
    await loadConversations();
    return stored;
  }

  // R35: a saved thread is every turn she paid for, and it asks first now. The list is re-read from
  // the server either way, so what she sees afterwards is what is really there.
  // R62: the payload read here was already honest; it goes through the one transport so this route
  // has ONE answer-reader rather than two thirds of one (R53's ruling on the same shape).
  async function removeConversation(id: number): Promise<boolean> {
    const answer = await deleteLalaConversation(id);
    await loadConversations();
    if (answer.ok) setTrashKey((k) => k + 1); // D9: it is now under Recently deleted, for 30 days
    if (answer.ok && activeId === id) newConversation();
    return answer.ok;
  }
  const [trashKey, setTrashKey] = useState(0);
  const resumedRef = useRef(false); // D10: resume her newest thread once per visit, never on every list refresh

  // R62: the sidebar's shape is a LAW (src/lib/lala-thread.ts), not a loop inside a two-dozen-hook
  // component where nothing could run it.
  const groups = groupConversations(conversations);

  // N2-3 (owner's mobile ruling): the attach, mic and Send controls sit in their OWN row directly under
  // the text box, so the box itself is clean and empty. The row is a real element rather than a CSS
  // reflow of loose children, which is what lets the box be styled as a plain field on a phone while
  // desktop keeps the single pill. Same markup both ways; only the CSS differs.
  const compose = (docked: boolean) => (
    <form className={`ask-lala-compose${docked ? " is-docked" : ""}`} onSubmit={onSubmit}>
      <AttachmentTray items={files.pending} onRemove={files.remove} uploading={files.uploading} error={files.error} />
      <LalaTurnNotice notice={turnNotice} freeTurns={free.payload} language={language} onUpgrade={APP_MODE ? undefined : () => onRequireMembership(() => {}, { feature: "lala:chat" })} />
      {/* W4-7: a growing textarea. The empty state and the thread state call this same function, and
          only one of them is ever mounted, so one ref serves both. Enter sends, Shift+Enter takes a
          new line. */}
      <textarea
        ref={(el) => { composeRef.current = el; applyLiveView(el, composeLimits); }}
        value={input}
        rows={1}
        disabled={composer.disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void onSubmit(e as unknown as FormEvent); } }}
        placeholder={composer.placeholder || (editingIndex !== null ? "Edit your message..." : "Ask Lala anything...")}
        aria-label="Message Lala"
      />
      <div className="ask-lala-compose__controls">
        <AttachButton onFiles={(f) => onRequireMembership(() => { void files.attach(f); })} disabled={files.uploading} />
        <MicButton setValue={setInput} field={composeRef} limits={composeLimits} />
        {sending ? (
          <button type="button" className="ask-lala-compose__stop" onClick={stopGenerating} aria-label="Stop generating">
            <Square size={16} />
          </button>
        ) : (
          <button className="ask-lala-compose__send" type="submit" disabled={composer.disabled} aria-label={editingIndex !== null ? "Resend" : "Send"}>
            {editingIndex !== null ? <Check size={18} /> : "Send"}
          </button>
        )}
      </div>
    </form>
  );

  return (
    <section className="page-shell ask-lala-page">
      <div className="ask-lala-page__backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className={`ask-lala-workspace${sidebarOpen ? " is-open" : ""}`}>
        <aside className="ask-lala-sidebar">
          <button className="ask-lala-sidebar__toggle" type="button" onClick={() => setSidebarOpen((o) => !o)} aria-label="Toggle conversations">
            <MessageCircle size={18} />
          </button>
          <button className="ask-lala-sidebar__new" type="button" onClick={newConversation} aria-label="New conversation">
            <span className="ask-lala-sidebar__plus">＋</span>
            <span className="ask-lala-sidebar__newlabel">New conversation</span>
          </button>
          <div className="ask-lala-sidebar__list">
            {conversations.length === 0 ? (
              <p className="ask-lala-sidebar__empty">Your saved conversations appear here.</p>
            ) : (
              groups.map((group) => (
                <div className="ask-lala-collection" key={group.name || "__unfiled"}>
                  <p className="ask-lala-collection__name">{group.name || "Unfiled"}</p>
                  {group.items.map((c) => (
                    <div key={c.id} className={c.id === activeId ? "ask-lala-convo is-active" : "ask-lala-convo"}>
                      <button type="button" className="ask-lala-convo__open" onClick={() => openConversation(c.id)}>
                        {c.title || "Untitled"}
                      </button>
                      <RenameControl kind="conversation" value={c.title || ""} what={c.title || "Untitled"} onRename={(t) => renameThread(c.id, t)} className="ask-lala-convo__rename-ask" />
                      <DeleteControl kind="conversation" what={c.title} className="ask-lala-convo__delete-ask" onDelete={() => removeConversation(c.id)} />
                    </div>
                  ))}
                </div>
              ))
            )}
            <LalaRecentlyDeleted refreshKey={trashKey} onRestored={async (rid) => { await loadConversations(); openConversation(rid); }} />
          </div>
        </aside>

        <div className={`ask-lala-main${talkWindowClass(free.payload)}`}>
          {/* R60: ABOVE the ternary, because a thread that did not arrive leaves `messages` empty and
              drops her into the EMPTY state — the greeting and the chips, which is exactly what a
              brand-new conversation looks like. That is the branch the sentence is needed on most. */}
          {threadNote ? <p className="reading-menu-note" role="status">{threadNote}</p> : null}
          {showEmpty ? (
            <div className="ask-lala-hello">
              <h1 className="ask-lala-hello__greeting">
                <span>Hi {firstName}.</span>
                <span>{ASK_LALA_GREETING_LINE_2}</span>
              </h1>
              <p className="ask-lala-hello__intro">{ASK_LALA_INTRO}</p>
              {/* N9-3 (owner 2026-07-27): the input and the Suggestions control live in ONE DOCK, which
                  on a phone is pinned to the bottom of the screen FROM THE START, not only after the
                  first message. On desktop the dock is `display: contents`, so it disappears from layout
                  and these children lay out exactly as they always have. One element, two behaviours, and
                  the empty state and the thread state now dock the SAME way instead of two ways. */}
              <div className="ask-lala-dock" ref={measureAskDock}>
                {compose(false)}
                {/* N2-3: on a phone the loose suggestion bubbles collapse into ONE "Suggestions" control
                    that opens a sheet of tappable suggestions; picking one SENDS it. Desktop keeps the
                    open row of chips. Both are rendered and the CSS shows the right one, so there is no
                    viewport guessing in JavaScript and no second copy of the suggestion list. */}
                <div className={suggestOpen ? "ask-lala-suggest is-open" : "ask-lala-suggest"}>
                  <button
                    type="button"
                    className="ask-lala-suggest__toggle"
                    aria-expanded={suggestOpen}
                    onClick={() => setSuggestOpen((v) => !v)}
                  >
                    Suggestions
                    <ChevronDown size={16} className="ask-lala-suggest__chev" aria-hidden="true" />
                  </button>
                  {suggestOpen ? (
                    <div className="ask-lala-suggest__list" role="menu">
                      {chips.map((c) => (
                        <button
                          key={c}
                          type="button"
                          role="menuitem"
                          className="ask-lala-suggest__item"
                          onClick={() => { setSuggestOpen(false); submitText(c); }}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="ask-lala-chips">
                {chips.map((c) => (
                  <button key={c} type="button" className="ask-lala-chip" onClick={() => submitText(c)}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {earlier !== "none" ? (
                <button type="button" className="ask-lala-earlier" onClick={() => void showEarlier()} disabled={earlier === "loading"}>
                  {earlier === "loading" ? LALA_THREAD_COPY.earlierLoading : earlier === "failed" ? LALA_THREAD_COPY.earlierFailed : LALA_THREAD_COPY.earlier}
                </button>
              ) : null}
              <div className="ask-lala-thread" ref={threadRef}>
                {messages.map((m, index) => {
                  // The saved text carries an "[attached: ...]" note for later turns; the
                  // thread shows the real files instead, so strip it from the bubble.
                  const text = m.role === "user" ? stripAttachNote(m.content) : stripMarkdown(m.content);
                  return (
                    <div key={index} className={m.role === "user" ? "ask-lala__row ask-lala__row--user" : "ask-lala__row ask-lala__row--lala"}>
                      {m.attachments?.length ? <MessageAttachments items={m.attachments} /> : null}
                      {text ? (
                        <div className={m.role === "user" ? "ask-lala__msg ask-lala__msg--user" : "ask-lala__msg ask-lala__msg--lala"}>
                          {text}
                        </div>
                      ) : null}
                      <div className="ask-lala__msg-actions">
                        {/* R56: an ICON-ONLY control has nowhere to put a word, so a failed copy
                            would be a tick that never appears and nothing else. The tick is drawn
                            only for a write that really landed, and a refusal says so in text
                            beside it — the accessible name carries the same answer (14-B5). */}
                        <button type="button" onClick={() => copyMessage(text, index)} aria-label={copyLabel(copiedIndex?.index === index ? copiedIndex.state : "idle", { ...copyWords(language, t), copy: t(language, "common.copyMessage") })}>
                          {copiedIndex?.index === index && copiedIndex.state === "copied" ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                        {copiedIndex?.index === index && copiedIndex.state === "failed"
                          ? <span className="ask-lala__copy-failed">{t(language, "common.copyFailed")}</span> : null}
                        {m.role === "user" && index === lastUserIndex && !sending ? (
                          <button type="button" onClick={editLastMessage} aria-label="Edit message">
                            <Pencil size={13} />
                          </button>
                        ) : null}
                      </div>
                      {m.cards?.length ? <ChatCards cards={m.cards} /> : null}
                {m.manual ? <ManualHandoffCard manual={m.manual} /> : null}{m.compilation ? <CompilationCard compilation={m.compilation} /> : null}
                      {sealsOf(m.seal).map((s) => <SealCard key={s.slug} seal={s} onDownload={(url) => { void downloadFromUrl(url); }} />)}
                      {m.sealOptions?.length ? <SealChoiceList options={m.sealOptions} onRequireMembership={onRequireMembership} language={language} /> : null}
                    </div>
                  );
                })}
                {sending ? (
                  <div className="ask-lala__row ask-lala__row--lala">
                    <div className="ask-lala__msg ask-lala__msg--lala ask-lala__msg--typing">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* N9-3: the SAME dock as the empty state, so the input sits in one place all the way
                  through a conversation instead of moving once the first message lands. */}
              <div className="ask-lala-dock" ref={measureAskDock}> {compose(true)}</div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// The Human Design room: a real bodygraph reading on the verified AstroAPI
// engine, to the same standard as every other room (letterhead, Save to My Room,
// PDF). Birth details flow through the shared CreateRoomForm (geocoded so the
// coordinates carry the timezone).
function HumanDesignPage({ chart, isMember, onRequireMembership, goHome, language }: { chart: BirthChartForm | null; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; goHome: () => void; language: string }) {
  const [form, setForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart, ...(chart || {}) }));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  const [compatBusy, setCompatBusy] = useState(false);
  const [compatStatus, setCompatStatus] = useState("");
  const [pickKind, setPickKind] = useState<"me" | "person" | "new">("me");
  // The compatibility partner is its own subject: a two-slot picker (saved circle or
  // someone new) with full birth details, so the block always has a real, working
  // generator (never dead furniture) even when the member has saved no one yet.
  const [partnerForm, setPartnerForm] = useState<BirthChartForm>(() => ({ ...initialBirthChart }));
  const update = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => setForm((f) => ({ ...f, [field]: value }));
  const updatePartner = <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => setPartnerForm((f) => ({ ...f, [field]: value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    onRequireMembership(() => { void run(); });
  }

  async function run() {
    if (busy) return;
    const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    setBusy(true);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = ["hd.stage1", "hd.stage2", "hd.stage3", "hd.stage4"].map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/human-design/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: fullName, chart: { ...form, name: fullName } }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "hd.roomSignIn")); return; }
      if (data?.requiresInput) { setStatus(data.message || t(language, "hd.needsBirth")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "hd.failed"));
        return;
      }
      setReport(data.report as AnyReport);
      setStatus("");
    } catch {
      setStatus(t(language, "hd.failed"));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  function submitCompat(event: FormEvent) {
    event.preventDefault();
    onRequireMembership(() => { void runCompat(); });
  }

  async function runCompat() {
    if (compatBusy) return;
    const fullName = combineName(form.firstName, form.middleName, form.lastName) || form.name;
    const partnerName = combineName(partnerForm.firstName, partnerForm.middleName, partnerForm.lastName) || partnerForm.name;
    if (!partnerName || !partnerForm.birthDate) {
      setCompatStatus(t(language, "hd.compat.needPartner"));
      return;
    }
    setCompatBusy(true);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = ["hd.compat.stage1", "hd.compat.stage2", "hd.compat.stage3", "hd.compat.stage4"].map((k) => t(language, k));
    let i = 0;
    setCompatStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setCompatStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/human-design/compatibility", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          chart: { ...form, name: fullName },
          // R2: `partnerForm` is a BirthChartForm and has never carried a `relationship` — no input on
          // this form collects one — so this read was always undefined and always sent "". The dead
          // read is gone; the server still fills the label from the person's own row when the partner
          // is chosen from her circle. Adding an input here is a design change and is her call.
          partner: { chart: { ...partnerForm, name: partnerName }, name: partnerName },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setCompatStatus(t(language, "hd.compat.roomSignIn")); return; }
      if (data?.requiresInput) { setCompatStatus(data.message || t(language, "hd.compat.needsBirth")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setCompatStatus(data?.message || t(language, "hd.compat.failed"));
        return;
      }
      setReport(data.report as AnyReport);
      setCompatStatus("");
    } catch {
      setCompatStatus(t(language, "hd.compat.failed"));
    } finally {
      window.clearInterval(timer);
      setCompatBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving");
    setSaveNote("");
    const type = (report as { type?: string }).type || "human-design";
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const title = subjectReadingTitle(name, type === "human-design-compat" ? "Human Design Compatibility" : "Human Design");
    const answer = await postReadingToRoom({ type, title, data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <section className="page-shell specialty-page human-design-page">
      <div className="human-design-backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>
      <div className="specialty-hero">
        <p className="eyebrow">Human Design room</p>
        <h1>{t(language, "hd.title")}</h1>
        <p>{t(language, "hd.lede")}</p>
      </div>

      <div className="human-design-form-wrap">
        <SubjectPicker memberChart={chart} language={language} onPick={(c, m) => { setForm({ ...initialBirthChart, ...c }); setPickKind(m.kind); }} />
        <CreateRoomForm birthChart={form} updateBirthChart={update} onSubmit={submit} language={language} submitLabel={t(language, busy ? "hd.stage1" : "hd.go")} nameHint={t(language, "hd.nameHint")} className="find-your-type__form" generating={busy} />
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </div>

      <div className="hd-compat-picker">
        <p className="eyebrow">{t(language, "hd.compat.eyebrow")}</p>
        <h2>{t(language, "hd.compat.title")}</h2>
        <p className="hd-compat-picker__lead">{t(language, "hd.compat.lede")}</p>
        <div className="hd-compat-form">
          <SubjectPicker
            language={language}
            memberChart={null}
            label={t(language, "hd.compat.picker")}
            onPick={(c) => setPartnerForm({ ...initialBirthChart, ...c })}
          />
          <CreateRoomForm
            birthChart={partnerForm}
            updateBirthChart={updatePartner}
            onSubmit={submitCompat}
            language={language}
            submitLabel={t(language, compatBusy ? "hd.compat.going" : "hd.compat.go")}
            nameHint={t(language, "hd.compat.nameHint")} generating={compatBusy}
          />
        </div>
        {compatStatus ? <p className={`find-your-type__status${compatBusy ? " is-live" : ""}`}>{compatStatus}</p> : null}
      </div>

      {report ? (
        <DeliveredReading
          report={report} room="human-design" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            {(report as { type?: string }).type !== "human-design-compat" ? <SaveSubjectOffer pickKind={pickKind} chart={form} language={language} /> : null}
          </>}
        />
      ) : null}

    </section>
  );
}

// R23: GeneratorPage moved to src/components/RoomPages.tsx with HomePage — the two ends of one
// routing decision, and the fallthrough is now CALLED by a gate instead of being whatever the
// router failed to branch on.

// The Magic Room: a room of workings. MR-ART — the owner's five images ARE the clickable tiles, each
// opening its working: the Petition generator, the Keys of Solomon, the animated Spellbook, the Oils
// experience, and the Books of Moses. No dead clicks: every tile is live.

// MR-B4 / MR-O3 — the ONE shared Magic Room popup overlay is `src/components/MagicOverlay.tsx` (R116:
// its scrim used to dismiss, over seven generators that each deliver a paid working, which is exactly
// what C2 forbids). Reuses .modal-backdrop (portal to <body>, above the nav).

function SpellBookPage({ goHome, isMember, onRequireMembership, language }: { goHome: () => void; isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  // OV2-7/OV2-8: a "Go to the Cauldron" from elsewhere in the house lands here. The flag is read as
  // the INITIAL view because on that path this page has only just mounted, so the event that
  // accompanies it went out before there was a listener to hear it.
  const [view, setView] = useState<MagicView>(() => (consumeCauldronRequest() ? "cauldron" : "tiles"));
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const close = () => setView("tiles");
  // The other half: already standing in the Magic Room when she asks for the pot.
  useEffect(() => {
    const open = () => { consumeCauldronRequest(); setView("cauldron"); };
    window.addEventListener(OPEN_CAULDRON_EVENT, open);
    return () => window.removeEventListener(OPEN_CAULDRON_EVENT, open);
  }, []);

  // The Spellbook is its own full-scene animated experience (the closed book grows toward the screen and
  // opens), so it keeps a Back control. Every other working (MR-O3) pops over the still-visible room.
  if (view === "spellbook") {
    return (
      <section className="page-shell magic-room-scene-page">
        <PageOutlineTitle page="spell-book" />
        <button className="back-button back-button--scene" type="button" onClick={close}>
          <ArrowLeft size={18} />
          Back to the Magic Room
        </button>
        <SpellbookExperience isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
      </section>
    );
  }

  return (
    <section className="page-shell magic-room-scene-page mr-room-page">
      <PageOutlineTitle page="spell-book" />
      <button className="back-button back-button--scene" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <MagicScene onOpen={setView} />

      {/* R7/R8 + ITEM 12: the room's edge controls are `src/components/MagicEdgeControls.tsx` now —
          hookless, so a gate CALLS them instead of grepping this file for three buttons. */}
      {view === "tiles" ? (
        <MagicEdgeControls isMember={isMember} onOpen={setView} onInstructions={() => setInstructionsOpen(true)} onRequireMembership={onRequireMembership} />
      ) : null}

      {instructionsOpen ? <MagicRoomInstructions onClose={() => setInstructionsOpen(false)} /> : null}

      {view !== "tiles" ? (
        <MagicOverlay label={magicOverlayLabel(view)} onClose={close}>
          {view === "petition" ? (
            <PetitionGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "keys" ? (
            <KeysGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "oils" ? (
            <OilsGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "moses" ? (
            <MosesGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "herbs" ? (
            <HerbsGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "notepaper" ? (
            <NotepaperGenerator isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          ) : view === "cabinet" ? (
            <FileCabinet language={language} />
          ) : (
            <CauldronExperience isMember={isMember} onRequireMembership={onRequireMembership} language={language} />
          )}
        </MagicOverlay>
      ) : null}
    </section>
  );
}

// ROADMAP ITEM 7 (2026-09-04): the Magic Room scene and `useElementSize` moved to
// `src/components/MagicScene.tsx`, bodies unchanged.
// MR-SB / MR-SB2 — the Spellbook. Browse Conjure spells by category, or make one for your situation.
// R216: the Conjure Spellbook — its four types, and `SpellbookExperience` itself — moved VERBATIM
// to `src/components/SpellbookExperience.tsx`, where a gate can IMPORT the room whose browse race
// this round repaired. `usePrefersReducedMotion` went to its own module, because the Magic Room
// scene above uses it too and a hook exported from a component file is a second thing to find.
// `SpellLayering` is re-exported from there: the oils generator's catalog offer carries one.

// MR-O2 — the Spell Your Oils GENERATOR. The member says what the oil is for and picks a tradition; Lala
// composes a complete, personalized conjure-oil working grounded ONLY in her materials (her oils method +
// the herb knowledge). Three modes: Hoodoo (rootwork oil), Hoodoo + Conjure (oil + a Greater Keys seal
// woven in), Conjure (the seal-centered working). Mirrors the Keys delivery (form -> gated report ->
// shelf/My Room/PDF/Cauldron); the conjure modes carry the real pentacle seal full-size.
// T7-4: what the oils generator is handed when her own sheet already covers the ask.
type CatalogOfferRecipe = {
  slug: string; title: string; purposeText: string; category: string;
  sealsCell: string; herbsCell: string;
  seals: Array<{ kind: string; name: string; imageUrl: string }>;
};
type CatalogOffer = { recipes: CatalogOfferRecipe[]; layering?: SpellLayering | null; note?: string };

function OilsGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [purpose, setPurpose] = useState("");
  const [tradition, setTradition] = useState<string>("hoodoo");
  const [targetName, setTargetName] = useState("");
  const [targetBirth, setTargetBirth] = useState("");
  const [showTarget, setShowTarget] = useState(false);
  // N7-4: what SHE calls her oil. Asked ONCE, here, and optional. Left blank it stays the honest
  // label "Conjure Oil" on the shelf; it never becomes a document title wearing her personal name.
  const [creationName, setCreationName] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  // T7-4: when her own candle sheet already carries an oil for this ask, the server offers HER
  // recipe first. Choice over verdict: take hers, or ask for one composed for this exact situation.
  const [offer, setOffer] = useState<CatalogOffer | null>(null);
  void isMember;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (purpose.trim().length < 4) { setStatus(t(language, "oils.needPurpose")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run(opts: { recipe?: string; compose?: boolean } = {}) {
    setBusy(true); setReport(null); setOffer(null); setSave("idle"); setSaveNote("");
    const stages = [t(language, "oils.stage.sitting"), t(language, "oils.stage.carrier"), t(language, tradition === "hoodoo" ? "oils.stage.spelling" : "oils.stage.seal"), t(language, "working.writing")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/oils", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose: purpose.trim(), tradition, targetName: targetName.trim(), targetBirth: targetBirth.trim(), creationName: cleanCreationName(creationName), ...opts }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "oils.signIn")); return; }
      // T7-4: her recipes come back BEFORE anything is composed. Nothing was generated to get here.
      if (data?.ok && data.offer?.recipes?.length) { setOffer(data.offer as CatalogOffer); setStatus(""); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "oils.failed"));
        return;
      }
      setReport(data.report as AnyReport); setStatus("");
    } catch {
      setStatus(t(language, "oils.failed"));
    } finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    // N7-4: the SAME title law the server used when it auto-shelved this oil, so an explicit save
    // can never rename her creation behind her back.
    const answer = await postReadingToRoom({ type: "oils-working", title: oilShelfName, data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  const sealUrl = (report as { seal?: { imageUrl?: string; slug?: string } } | null)?.seal?.imageUrl;
  const sealSlug = (report as { seal?: { slug?: string } } | null)?.seal?.slug;
  // N7-4: ONE name for this oil everywhere it is referred to — the shelf, My Room and the Cauldron.
  // Her name if she gave one (the server echoes it back on the report), otherwise the honest label.
  const oilShelfName = creationTitle("oils-working", (report as { creationName?: string } | null)?.creationName || creationName);

  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · Oils</p>
        <h1 className="dream-title"><Droplets size={26} /> {t(language, "oils.title")}</h1>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)}
            placeholder={t(language, "oils.placeholder")} rows={5} />
        </div>
        <div className="oil-traditions" role="radiogroup" aria-label={t(language, "oils.traditionsAria")}>
          {OIL_TRADITIONS.map((o) => (
            <button type="button" key={o.key} role="radio" aria-checked={tradition === o.key}
              className={`oil-tradition${tradition === o.key ? " is-active" : ""}`} onClick={() => setTradition(o.key)}>
              <span className="oil-tradition__label">{o.label}</span>
              <span className="oil-tradition__note">{o.note}</span>
            </button>
          ))}
        </div>
        {showTarget ? (
          <div className="petition-target">
            <p className="petition-target__hint">{t(language, "oils.target.hint")}</p>
            <div className="petition-target__fields">
              <input type="text" value={targetName} onChange={(e) => setTargetName(e.target.value)} placeholder={t(language, "petition.target.name")} aria-label={t(language, "petition.target.nameLabel")} />
              <input type="text" value={targetBirth} onChange={(e) => setTargetBirth(e.target.value)} placeholder={t(language, "petition.target.birth")} aria-label={t(language, "petition.target.birthLabel")} />
            </div>
            <button type="button" className="petition-target__toggle" onClick={() => { setShowTarget(false); setTargetName(""); setTargetBirth(""); }}>{t(language, "oils.target.off")}</button>
          </div>
        ) : (
          <button type="button" className="petition-target__toggle" onClick={() => setShowTarget(true)}>{t(language, "oils.target.on")}</button>
        )}
        {/* N7-4: NAME YOUR CREATION. Asked once, optional, and taken verbatim. Blank is a real
            answer: the oil goes to the shelf as "Conjure Oil", never as a title with her name on it. */}
        <div className="creation-name">
          <label className="creation-name__label" htmlFor="oil-creation-name">{t(language, "oils.name.label")}</label>
          <input id="oil-creation-name" className="creation-name__input" type="text" value={creationName}
            maxLength={MAX_CREATION_NAME} onChange={(e) => setCreationName(e.target.value)}
            placeholder={t(language, "oils.name.placeholder")} />
          <p className="creation-name__hint">{t(language, "oils.name.hint")}</p>
        </div>
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "oils.stage.spelling" : "oils.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {/* T7-4 — HER RECIPE FIRST. Choice over verdict: when her own candle sheet already carries an
          oil for this ask it is offered by name, with its seals and her herbs, and composing one for
          this exact situation stays one tap away. Nothing was generated to get here. */}
      {offer ? (
        <div className="catalog-offer">
          <p className="catalog-offer__lede">{offer.note || t(language, "oils.offer.lede")}</p>
          {offer.layering?.principle ? <p className="catalog-offer__layering">{offer.layering.principle}</p> : null}
          <ul className="catalog-offer__list">
            {offer.recipes.map((r) => (
              <li key={r.slug} className="catalog-offer__item">
                <div className="catalog-offer__head">
                  <span className="catalog-offer__title">{r.title}</span>
                  {r.purposeText ? <span className="catalog-offer__purpose">{r.purposeText}</span> : null}
                </div>
                {r.seals.some((s) => s.imageUrl) ? (
                  <div className="catalog-offer__seals">
                    {r.seals.filter((s) => s.imageUrl).map((s, i) => (
                      <img key={i} className="catalog-offer__seal" src={s.imageUrl} alt={s.name} loading="lazy" />
                    ))}
                  </div>
                ) : null}
                <p className="catalog-offer__cell"><strong>{t(language, "oils.offer.spelledWith")}</strong> {r.sealsCell}</p>
                <p className="catalog-offer__cell"><strong>{t(language, "oils.offer.herbs")}</strong> {r.herbsCell}</p>
                <button type="button" className="button button--primary" disabled={busy}
                  onClick={() => { void run({ recipe: r.slug }); }}>{t(language, "oils.offer.work", { title: r.title })}</button>
              </li>
            ))}
          </ul>
          <button type="button" className="button button--ghost catalog-offer__compose" disabled={busy}
            onClick={() => { void run({ compose: true }); }}>{t(language, "oils.offer.compose")}</button>
        </div>
      ) : null}

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            {/* >>> IT #198/#199: A BUTTON, NEVER AN ANCHOR. <<< A bare `<a download>` on a phone is a
                handoff to the OS, which gives the file to whatever app claims the type -- an
                astrology app, for her -- and the seal is gone. The ONE transport instead (R28/R29;
                `SealCard` learned this at #127). This one had `target="_blank"` on it too, which
                made it worse: opening the seal in another tab IS the handoff. */}
            {sealUrl ? (
              <button type="button" className="button button--ghost" onClick={() => { void downloadFromUrl(sealUrl as string); }}>{t(language, "working.downloadSeal")}</button>
            ) : null}
            {/* N7-4: the Cauldron's item list shows what SHE called it, and the compiled ritual
                reads it back off the report's creationName. */}
            <SendToCauldronButton language={language} kind="oils-working" title={oilShelfName} data={report} />
          </>}
        />
      ) : null}
    </>
  );
}

// MR-M — the Books of Moses generator. A working grounded in the Sixth and Seventh Books of Moses for the
// member's situation. Retrieval-grounded server-side; mirrors the Keys delivery (no seal image yet).
function MosesGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  void isMember;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (situation.trim().length < 8) { setStatus(t(language, "working.needSituation")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run() {
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = [t(language, "working.sitting"), t(language, "moses.stage.opening"), t(language, "moses.stage.finding"), t(language, "working.writingOut")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/moses", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "moses.signIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "working.failed"));
        return;
      }
      setReport(data.report as AnyReport); setStatus("");
    } catch {
      setStatus(t(language, "working.failed"));
    } finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "moses-working", title: subjectReadingTitle(name, "Books of Moses Working"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · The Books of Moses</p>
        <h1 className="dream-title">The Sixth and Seventh Books of Moses</h1>
        <p>{t(language, "moses.lede")}</p>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <textarea value={situation} onChange={(e) => setSituation(e.target.value)}
            placeholder={t(language, "moses.placeholder")} rows={6} />
        </div>
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "moses.going" : "moses.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            {/* N14-2: the plate itself, downloadable, exactly as the Keys card offers its pentacle. */}
            {(report as { sealImageUrl?: string }).sealImageUrl ? (
              // IT #319 (found in path): the last two bare seal anchors, IT #198/#199's trap. A button
              // through the ONE transport, which names the file and says how to save it in an app's browser.
              <button type="button" className="button button--ghost"
                onClick={() => { void downloadFromUrl((report as { sealImageUrl?: string }).sealImageUrl as string); }}>
                {t(language, "working.downloadSeal")}
              </button>
            ) : null}
            <SendToCauldronButton language={language} kind="moses-working" title="Books of Moses working" data={report} />
          </>}
        />
      ) : null}
    </>
  );
}

// MR-K — the Keys generator. A working grounded in the Greater Key of Solomon: the right pentacle for
// the member's situation, its real seal shown, and how to work it. Mirrors the petition delivery.
function KeysGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [situation, setSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  void isMember;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (situation.trim().length < 8) { setStatus(t(language, "working.needSituation")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run() {
    setBusy(true);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = [
      t(language, "working.sitting"),
      t(language, "keys.stage.turning"),
      t(language, "keys.stage.drawing"),
      t(language, "working.writing"),
    ];
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "keys.signIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "working.failed"));
        return;
      }
      setReport(data.report as AnyReport);
      setStatus("");
    } catch {
      setStatus(t(language, "working.failed"));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving");
    setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "keys-working", title: subjectReadingTitle(name, "Greater Keys of Solomon Working"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · The Greater Keys of Solomon</p>
        <h1 className="dream-title">The Greater Keys of Solomon</h1>
        <p>{t(language, "keys.lede")}</p>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <textarea
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            placeholder={t(language, "keys.placeholder")}
            rows={6}
          />
        </div>
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "keys.going" : "keys.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            {(report as { seal?: { imageUrl?: string; name?: string } }).seal?.imageUrl ? (
              // IT #319 (found in path): the same bare anchor, the same repair.
              <button type="button" className="button button--ghost"
                onClick={() => { void downloadFromUrl((report as { seal?: { imageUrl?: string } }).seal!.imageUrl as string); }}>
                {t(language, "working.downloadSeal")}
              </button>
            ) : null}
            <SendToCauldronButton language={language} kind="keys-working" title={`${(report as { seal?: { name?: string } }).seal?.name || "Greater Keys working"}`} data={report} />
          </>}
        />
      ) : null}
    </>
  );
}

// MR-H — the Herbs & Tools generator. Look up or ask about a herb, curio, or tool; grounded in
// Cunningham's + her materials server-side, biased to household items, covering the wider toolkit
// (threads, personal items, mojo bags). Mirrors the Keys/Moses delivery (form -> gated report -> shelf).
function HerbsGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  void isMember;
  // MR-R2: chips are AIMS now (what you need herbs FOR), not herb names.
  const chips = ["Money", "Healing", "Love", "Protection", "A court case", "Cleansing"];

  function submit(event: FormEvent) {
    event.preventDefault();
    if (topic.trim().length < 2) { setStatus(t(language, "herbs.needTopic")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run(q?: string) {
    const ask = (q ?? topic).trim();
    if (ask.length < 2) return;
    if (q) setTopic(q);
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = [t(language, "herbs.stage.reading"), t(language, "herbs.stage.turning"), t(language, "herbs.stage.gathering"), t(language, "working.writingOut")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/herbs", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aim: ask }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "herbs.signIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "herbs.failed"));
        return;
      }
      setReport(data.report as AnyReport); setStatus("");
    } catch {
      setStatus(t(language, "herbs.failed"));
    } finally { window.clearInterval(timer); setBusy(false); }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    const title = (report as { title?: string }).title || "Herbs & Tools";
    const answer = await postReadingToRoom({ type: "herb-working", title: subjectReadingTitle(title, "Herbs & Tools"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · The Book of Roots</p>
        <h1 className="dream-title">The Book of Roots</h1>
        <p>{t(language, "herbs.lede")}</p>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder={t(language, "herbs.placeholder")} />
        </div>
        <div className="herb-chips">
          {chips.map((c) => (
            <button type="button" key={c} className="herb-chip" onClick={() => onRequireMembership(() => { void run(c); })}>{c}</button>
          ))}
        </div>
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "herbs.going" : "herbs.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            <SendToCauldronButton language={language} kind="herb-working" title={(report as { title?: string }).title || "Herbs & Tools"} data={report} />
          </>}
        />
      ) : null}
    </>
  );
}

// MR-NP — THE NOTEPAPER. A blank house-styled paper where a member writes or pastes their OWN words:
// their own petition, incantation, or chant. Send to Cauldron (rides in verbatim) + Save to Shelf.
function NotepaperGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  void isMember;
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [note, setNote] = useState("");
  const noteReport = () => ({
    type: "note", kicker: "Your Notepaper", title: title.trim() || "My words",
    header: { name: title.trim() || "My words" },
    noteText: text, overview: `Your own words.`,
    sections: [{ heading: "Your words, as you wrote them", body: text }],
  });
  async function saveToShelf() {
    if (text.trim().length < 2) { setNote(t(language, "note.needText")); return; }
    setSave("saving"); setNote("");
    const answer = await postReadingToRoom({ type: "note", title: `${title.trim() || "My words"}`, data: noteReport() });
    setSave(saveReadingState(answer)); setNote(saveReadingNote(answer, { done: t(language, "note.savedNote"), signIn: t(language, "note.signIn"), failed: t(language, "common.saveFailed") }));
  }
  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · Your Notepaper</p>
        <h1 className="dream-title">{t(language, "note.title")}</h1>
        <p>{t(language, "note.lede")}</p>
      </div>
      <div className="notepaper">
        <input className="notepaper__title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(language, "note.titlePlaceholder")} />
        <textarea className="notepaper__body" value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={t(language, "note.bodyPlaceholder")} />
        <div className="report-actions result-destinations">
          <button type="button" className="button button--ghost" onClick={() => onRequireMembership(() => { void saveToShelf(); })} disabled={save === "saving"}>
            {t(language, save === "saved" ? "note.saved" : save === "saving" ? "result.saving" : "note.save")}
          </button>
          <SendToCauldronButton language={language} kind="note" title={title.trim() || "My words"} data={noteReport()} disabled={text.trim().length < 2} />
        </div>
        {note ? <p className="find-your-type__status">{note}</p> : null}
      </div>
    </>
  );
}

// R216: `SendToCauldronButton` moved VERBATIM to `src/components/SendToCauldronButton.tsx`.

// MR-P — the petition generator. Mirrors the Dream page's delivery (form -> gated report -> shelf
// three-destination). The petition is spoken-word + voice gated server-side before it is ever shown.
// MR-P2/R44 — the divine names a petition may be invoked in are the SERVER'S allow-list, imported
// rather than typed again here: what she may pick and what an unrecognised pick falls back to are one
// declaration in src/magicRoom.js, so this dropdown cannot offer a name the petition is not spoken in.
function PetitionGenerator({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; language: string }) {
  const [situation, setSituation] = useState("");
  const [divineName, setDivineName] = useState<string>("El Shaddai");
  // MR-P3: an OPTIONAL target turns the petition into her Cross Method paper (name crossed and covered).
  const [targetName, setTargetName] = useState("");
  const [targetBirth, setTargetBirth] = useState("");
  const [showTarget, setShowTarget] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  void isMember;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (situation.trim().length < 12) { setStatus(t(language, "petition.needSituation")); return; }
    onRequireMembership(() => { void run(); });
  }

  async function run() {
    setBusy(true);
    setReport(null);
    setSave("idle");
    setSaveNote("");
    const stages = ["working.sitting", "petition.stage.choosing", "petition.stage.speaking", "petition.stage.writing"].map((k) => t(language, k));
    let i = 0;
    setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const response = await fetch("/api/petition", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ situation: situation.trim(), divineName, targetName: targetName.trim(), targetBirth: targetBirth.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { setStatus(t(language, "petition.signIn")); return; }
      if (!response.ok || data?.ok === false || !data?.report) {
        setStatus(data?.message || t(language, "petition.failed"));
        return;
      }
      setReport(data.report as AnyReport);
      setStatus("");
    } catch {
      setStatus(t(language, "petition.failed"));
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function saveToRoom() {
    if (!report) return;
    setSave("saving");
    setSaveNote("");
    const name = (report as { header?: { name?: string } }).header?.name || "You";
    const answer = await postReadingToRoom({ type: "petition", title: subjectReadingTitle(name, "Petition"), data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, saveCopy(language)));
  }

  return (
    <>
      <div className="specialty-hero">
        <p className="eyebrow">Magic Room · Petition</p>
        <h1 className="dream-title">{t(language, "petition.title")}</h1>
        <p>{t(language, "petition.lede")}</p>
      </div>

      <form className="dream-form" onSubmit={submit}>
        <div className="dream-input">
          <textarea
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            placeholder={t(language, "petition.placeholder")}
            rows={6}
          />
        </div>
        <div className="petition-name">
          <label className="petition-name__label" htmlFor="petition-divine-name">{t(language, "petition.inTheNameOf")}</label>
          <select id="petition-divine-name" className="petition-name__select" value={divineName} onChange={(e) => setDivineName(e.target.value)}>
            {PETITION_DIVINE_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {/* MR-P3: her Cross Method — worked on a named person, the name crossed and covered by the command. */}
        {showTarget ? (
          <div className="petition-target">
            <p className="petition-target__hint">{t(language, "petition.target.hint")}</p>
            <div className="petition-target__fields">
              <input type="text" value={targetName} onChange={(e) => setTargetName(e.target.value)} placeholder={t(language, "petition.target.name")} aria-label={t(language, "petition.target.nameLabel")} />
              <input type="text" value={targetBirth} onChange={(e) => setTargetBirth(e.target.value)} placeholder={t(language, "petition.target.birth")} aria-label={t(language, "petition.target.birthLabel")} />
            </div>
            <button type="button" className="petition-target__toggle" onClick={() => { setShowTarget(false); setTargetName(""); setTargetBirth(""); }}>{t(language, "petition.target.off")}</button>
          </div>
        ) : (
          <button type="button" className="petition-target__toggle" onClick={() => setShowTarget(true)}>{t(language, "petition.target.on")}</button>
        )}
        <button className={`button button--primary dream-go${busy ? " is-generating" : ""}`} type="submit" disabled={busy}>
          {t(language, busy ? "petition.going" : "petition.go")}
        </button>
        {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
      </form>

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<>
            <AskLalaAboutButton report={report} kind="reading" />
            <SendToCauldronButton language={language} kind="petition" title="Petition" data={report} />
          </>}
        />
      ) : null}
    </>
  );
}

// W5-2 — ONE ANSWER TO "IS THIS BOOK ON HER SHELF", shared by every surface that offers the save.
// Her three books had no save anywhere because the capability lived inside the one reader that
// needed it first; four surfaces offer it now, and they read the same list to decide what to say.
// The toggle moves the chip FIRST and puts it back if the server refuses, so a tap on a cover
// answers instantly and never lies about what the database did.
// R8/BREACH-1: `useSavedShelf` moved to src/components/useSavedShelf.ts (three callers, no importer).


// Lala's Bookshelf: the family's own books, shown face-front (full covers) above
// the category shelves, each credited to its author.
// Lala's Bookshelf is the FIRST floating shelf: same vertical gold-chrome label as
// every other shelf (no longer a giant page title), her books stay face-front (covers)
// on a floating gold plank while every other shelf is spine-out.
// R27-3: FeaturedShelf moved to src/components/FeaturedShelf.tsx, carrying W5-2's decision — the save
// chip is behind `onSave && isSaved`, an OPTIONAL capability, which is the R26 shape one size down.

// ROADMAP ITEM 7 (2026-09-04): the page-image reader and the book shelf moved to
// `src/components/BookShelf.tsx`, bodies unchanged — the same seam `VideoShelf.tsx` left by, in
// the same area. `LibraryPage` below is their only caller.
// ROADMAP ITEM 7 (2026-09-04): the video player, card and shelf moved to
// `src/components/VideoShelf.tsx`, bodies unchanged. `LibraryPage` below is their only caller.
function LibraryPage({
  books,
  setActiveBook,
  onRequireMembership,
  goHome, language,
}: {
  books: Book[];
  setActiveBook: (book: Book) => void;
  isMember: boolean;
  onRequireMembership: (action: () => void, lock?: LockInfo) => void;
  goHome: () => void; language: string;
}) {
  const requireFeature = useRequireFeature();
  // W5-2: one saved-shelf answer for this whole page — both shelves read it, and so do both readers.
  const shelf = useSavedShelf();
  // T4-1/T4-2 — THE LIBRARY OPENS ON THE VIDEOS (owner's ruling). Books are one tap away, and the
  // view she is standing in survives a refresh, a deep link and the back button: it is written to
  // the URL as `?view=books`, so a link she sends someone opens where she was. Videos are the
  // default, so the plain `/library` URL carries no query at all.
  const [media, setMedia] = useState<"book" | "video">(() => {
    if (typeof window === "undefined") return "video";
    return new URLSearchParams(window.location.search).get("view") === "books" ? "book" : "video";
  });
  const showMedia = (next: "book" | "video") => {
    setMedia(next);
    if (typeof window === "undefined") return;
    const url = next === "book" ? `${window.location.pathname}?view=books` : window.location.pathname;
    window.history.replaceState({ ...(window.history.state || {}), libraryView: next }, "", url);
  };
  // The back button walks through history, which changes the URL without re-mounting this page, so
  // the view has to follow it rather than sit on the state it was left in.
  useEffect(() => {
    const onPop = () => setMedia(new URLSearchParams(window.location.search).get("view") === "books" ? "book" : "video");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [dbBooks, setDbBooks] = useState<ShelfBook[] | null>(null);
  const [selected, setSelected] = useState<ShelfBook | null>(null);
  const [page, setPage] = useState(0);
  // R85: `null` until the read lands (R39's third state). Starting at `[]` is what made a failed
  // read indistinguishable from a shelf with no books on it — and the band is drawn only when it is
  // non-empty, so what she actually got was NO SHELF AT ALL.
  const [featured, setFeatured] = useState<FeaturedBook[] | null>(null);
  const [featuredOpen, setFeaturedOpen] = useState<FeaturedBook | null>(null);
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState<{ slug: string; title: string; startAt: number } | null>(null);
  const [coverPeek, setCoverPeek] = useState<ShelfBook | null>(null); // Q6: full uncropped cover popup
  // T1-7: which videos cost money, and which of them she has already bought. Both answers come from
  // the server (/api/membership/tiers lists the products; entitlements lists HER purchases), so the
  // Library never decides on its own what she owns.
  const [oneTime, setOneTime] = useState<OneTimeProduct[]>([]);
  // T8-9: whether PLAYBACK is open to her, asked of the server's own answer rather than the blanket
  // isMember flag — the shelf is visible to everyone now, so the cards have to say which of them
  // she can actually watch. `library` is the feature the stream route charges.
  // R91: this shelf's reading of "we do not know" is UNCHANGED and conservative (the BUY face and the
  // padlock, as the note above rules). What changed is that the read re-asks, so it is temporary.
  const { purchases: ownedProducts = [], features: entitledFeatures = {} } = useEntitlements() ?? {};
  const canStreamVideo = Boolean(entitledFeatures.library);
  useEffect(() => {
    let active = true;
    void readTierCatalog<TierCard, OneTimeProduct>().then((answer) => {
      if (active && answer) setOneTime(answer.oneTime);
    });
    return () => { active = false; };
  }, []);
  const oneTimeFor = (slug: string) => oneTime.find((p) => p.videoSlug === slug) || null;
  const [videoProgress, setVideoProgress] = useState<Record<string, { positionSeconds: number; durationSeconds: number | null }>>({});

  // Continue Watching data: the member's saved positions, so the video cards show a
  // resume bar and the player picks up where they left off. Refreshed when the player
  // closes so a just-watched video reflects its new position immediately.
  // R55: the same read as My Room's, through the same law. **THE GUARD COMES BEFORE THE PAINT** —
  // the old fallback `{ videos: [] }` was itself an array, so it passed the `Array.isArray` check
  // and wiped every resume bar on every card.
  const loadVideoProgress = async () => {
    const list = await listWatching();
    if (!list) return;
    setVideoProgress(watchingMap(list));
  };
  useEffect(() => { if (media === "video") loadVideoProgress(); }, [media]);

  // Lala's Bookshelf: the family's own books, shown face-front above the shelves.
  useEffect(() => {
    let active = true;
    // R85: the law already answers `T[] | null` (R59). The state takes that answer whole rather than
    // dropping the `null` on the floor — R53's lesson: a law's answer is only worth what the caller
    // does with it.
    void listFeaturedBooks<FeaturedBook>().then((rows) => { if (active) setFeatured(rows); });
    return () => { active = false; };
  }, []);

  // Shelves render from the database when available, falling back to the in-code
  // catalog so the library always works.
  // T8-9: did the catalogue actually arrive? Kept apart from "it arrived and it was empty", so the
  // empty state can stop asserting a fact it does not have.
  // R94: `source` has THREE values and was read as a boolean, so "fallback" (a deliberate answer:
  // no database, use the in-code catalogue) and "error" (we could not ask) shared one `else` — and
  // that else substitutes 47 titles whose text lives in the database that just refused. The answer
  // is taken whole now (src/lib/library-shelf.ts), and the server no longer answers 200 on a throw.
  const [shelfLoad, setShelfLoad] = useState<"loading" | "db" | "fallback" | "error">("loading");
  useEffect(() => {
    let active = true;
    setShelfLoad("loading");
    void readLibraryShelf<Record<string, unknown>>(media === "video" ? "video" : "book").then((answer) => {
      if (!active) return;
      setShelfLoad(answer.kind);
      // R59: the same seven coercions were typed here and in My Room's saved shelf, 9,500 lines
      // apart. One normaliser (src/lib/shelves.ts), where a gate can run it.
      setDbBooks(answer.kind === "db" ? shelfBooksFrom(answer.books) : null);
    });
    return () => {
      active = false;
    };
  }, [media]);

  const fallback: ShelfBook[] = books.map((b) => ({
    slug: bookSlug(b.title),
    title: b.title,
    author: b.author ?? null,
    mediaType: b.type === "Video" ? "video" : "book",
    categories: [b.category],
    pageCount: 0,
  }));
  // From the DB, only show books that actually have text on the shelf — this hides
  // empty seed duplicates, not-yet-ingested stubs, and excluded titles. Videos and
  // the in-code fallback (no page data) are always shown.
  const usingDbData = dbBooks !== null;
  // R94: AN ERROR IS NOT A CATALOGUE. Held in the law by POSITION (R52) so no later spelling can put
  // the in-code substitution back in front of the guard.
  const allShelfBooks = shelfSource(shelfLoad, dbBooks, fallback).filter(
    (b) => b.mediaType === media && (media === "video" || !usingDbData || b.pageCount > 0),
  );
  // Real search across the catalog: title, author, and category.
  const q = search.trim().toLowerCase();
  // R3: the same words, searched inside the book TEXT. Only for books — a video has no pages — and
  // only from three characters up, which the hook enforces so no one-letter query reaches the DB.
  const textHits = useBookSearch(media === "book" ? search : "");
  const shelfBooks = q
    ? allShelfBooks.filter(
        (b) =>
          b.title.toLowerCase().includes(q) ||
          (b.author || "").toLowerCase().includes(q) ||
          b.categories.some((c) => c.toLowerCase().includes(q)),
      )
    : allShelfBooks;
  const { categories, rows } = buildShelves(shelfBooks);
  // R109: the two sentences this page says ABOUT its shelf rather than on it. Both are decisions, not
  // renderings, and both were wrong on the Video tab — the count called videos books, and the empty
  // state told her no videos had been added while her own search was what emptied the shelf.
  const emptyNote = libraryEmptyNote({ load: shelfLoad, media, term: search.trim(), passages: textHits.hits.length });
  const countNote = librarySearchCount(media, shelfBooks.length, search.trim());

  const scrollToShelf = (category: string) =>
    document.getElementById(shelfSlug(category))?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Opening a book to read is a gated action. **B1 — OPENING A VIDEO'S COVER IS NOT.** This one line
  // guarded the whole video branch, and the whole of what it guarded was `setCoverPeek(book)` — so
  // the tier gate fired on the CARD, before she had seen the cover, the runtime, the blurb or the
  // PRICE, and the Play button inside the popup was guarded by nothing at all. One step too early
  // and one step too shallow. FREE-1's ruling is that browsing is free and the gate moves to the
  // moment it costs something, and `VideoCard`'s own comment already said the card is never removed
  // and never disabled; this is that ruling reaching the one surface it had not.
  const openBook = (book: ShelfBook) => {
    if (book.mediaType === "video") {
      // Q6: show the full, uncropped cover first (the shelf thumbnail is cropped-to-fit);
      // play launches from that popup, where the gate now is.
      setCoverPeek(book);
      return;
    }
    requireFeature("library", () => {
      setSelected(book);
      setPage(0);
      const match = books.find((b) => bookSlug(b.title) === book.slug);
      if (match) setActiveBook(match);
    });
  };

  // B1: the one action BOTH faces of the play tap perform, so a member who joins from the purple box
  // resumes exactly the video she reached for (`requireFeature` remembers the action it refused).
  const playCoverPeek = () => {
    const b = coverPeek;
    if (!b) return;
    setCoverPeek(null);
    setPlaying({ slug: b.slug, title: b.title, startAt: videoProgress[b.slug]?.positionSeconds || 0 });
  };

  return (
    <section className="page-shell library-page">
      <PageOutlineTitle page="library" />
      {/* The page background IS the atmosphere: a full-bleed, fixed library image that
          shows everywhere — behind, between, and through the floating shelves. */}
      <div className="library-backdrop" aria-hidden="true" />
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className="library-layout">
        <section className="book-shelf" aria-label={t(language, "library.books")}>
          {/* Top bar: the Book/Video toggle (left) and the real search (right), clear of
              everything. Lala's Bookshelf is no longer a giant title here — it is the
              first floating shelf's vertical label. */}
          <div className="library-topbar">
            {/* T4-2: the toggle sits TOP CENTRE, on its own row, so the first thing she sees is
                which library she is in and how to reach the other one. The search keeps its own
                row beneath it. */}
            <div className="library-toggle" role="tablist" aria-label={t(language, "library.view")}>
              <button
                type="button"
                role="tab"
                aria-selected={media === "video"}
                className={media === "video" ? "library-toggle__btn library-toggle__btn--active" : "library-toggle__btn"}
                onClick={() => showMedia("video")}
              >
                {t(language, "library.tab.video")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={media === "book"}
                className={media === "book" ? "library-toggle__btn library-toggle__btn--active" : "library-toggle__btn"}
                onClick={() => showMedia("book")}
              >
                {t(language, "library.tab.book")}
              </button>
            </div>

            <label className="library-search">
              <Search size={16} className="library-search__icon" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(language, "library.search.placeholder")}
                aria-label={t(language, "library.search.label")}
              />
            </label>
          </div>

          {/* R85 — **THE SENTENCE STANDS WHERE THE SHELF WOULD HAVE STOOD, AND IT IS DRAWN ABOVE
              THE SHELF'S OWN CONDITION** (R65's ruling, held by POSITION): her three books simply
              were not there, and a band that is absent has no error state for her to notice. */}
          {media === "book" && featured === null && !q ? (
            <div className="library-empty library-empty--featured">{t(language, "library.featured.failed")}</div>
          ) : null}
          {media === "book" && featured !== null && featured.length > 0 && !q ? (
            <FeaturedShelf books={featured} onOpen={(b) => onRequireMembership(() => setFeaturedOpen(b))} isSaved={shelf.isSaved} onSave={shelf.toggle} language={language} />
          ) : null}

          {categories.length > 0 ? (
            <nav className="shelf-filter" aria-label={t(language, "library.jump")}>
              {categories.map((cluster) => (
                <button key={cluster.category} type="button" className="shelf-filter__chip" onClick={() => scrollToShelf(cluster.category)}>
                  <span className="shelf-filter__icon" aria-hidden="true">{CATEGORY_GLYPHS[cluster.category] || "✦"}</span>
                  {cluster.category}
                  <span className="shelf-filter__count">{cluster.books.length}</span>
                </button>
              ))}
            </nav>
          ) : null}

          {/* R3: this counts TITLES, and it now says so. It read "0 books for 'the ka'" directly
              above twenty-five real passages — a successful search wearing the word for failure. */}
          {countNote ? (
            <p className="library-search__note">{t(language, countNote.key, countNote.vars)}</p>
          ) : null}

          {/* R3 — AND THE SAME WORDS SEARCHED INSIDE THE PAGES. The shelf filter above matches
              titles, authors and categories; this matches the 28.8 million characters of book TEXT,
              which is what a member actually came looking for. Books only: a video has no pages. */}
          {media === "book" && q ? (
            <BookHitList
              state={textHits}
              term={search}
              showTitles
              language={language}
              onOpen={(hit) => requireFeature("library", () => {
                const book = allShelfBooks.find((b) => b.slug === hit.slug);
                if (!book) return;
                setSelected(book);
                setPage(Math.max(0, hit.page - 1));
              })}
            />
          ) : null}

          {/* R109: WHICH of the five sentences stands here is `libraryEmptyNote`
              (src/lib/library-empty.ts), where a gate RUNS every cell of the grid. T8-9's "we could
              not ask" and R3's "the shelf is filtered, not empty" were both already written; the
              ORDER was the defect, because the media arm sat above the search arms and R3's fix could
              therefore only ever reach books. `null` means DRAW NOTHING, and it is the still-loading
              answer this chain never had. */}
          {shelfBooks.length === 0 ? (
            emptyNote ? <div className="library-empty">{t(language, emptyNote.key, emptyNote.vars)}</div> : null
          ) : media === "video" ? (
            <div className="bookcase bookcase--video">
              {categories.map((cluster) => (
                <VideoShelf
                  key={cluster.category}
                  category={cluster.category}
                  language={language}
                  books={cluster.books}
                  progress={videoProgress}
                  onOpen={openBook}
                  canPlay={canStreamVideo}
                  productFor={oneTimeFor}
                  ownedKeys={ownedProducts}
                />
              ))}
            </div>
          ) : (
            <div className="bookcase">
              {rows.map((row, index) =>
                row.shared ? (
                  <div key={`shared-${index}`} className="shelf-shared">
                    {row.clusters.map((cluster) => (
                      <Shelf key={cluster.category} category={cluster.category} language={language} books={cluster.books} activeSlug={selected?.slug ?? null} onOpen={openBook} isSaved={shelf.isSaved} onSave={shelf.toggle} />
                    ))}
                  </div>
                ) : (
                  <Shelf key={row.clusters[0].category} category={row.clusters[0].category} language={language} books={row.clusters[0].books} activeSlug={selected?.slug ?? null} onOpen={openBook} isSaved={shelf.isSaved} onSave={shelf.toggle} />
                ),
              )}
            </div>
          )}
        </section>
      </div>

      {selected ? (
        <BookReaderModal book={selected} page={page} setPage={setPage} onClose={() => setSelected(null)} language={language} />
      ) : null}
      {featuredOpen ? (
        <PageImageReader book={featuredOpen} onClose={() => setFeaturedOpen(null)} language={language} />
      ) : null}
      {coverPeek ? (
        <VideoCoverModal
          book={coverPeek}
          language={language}
          product={oneTimeFor(coverPeek.slug)}
          owned={ownsVideoProduct(oneTimeFor(coverPeek.slug), ownedProducts)}
          canStream={canStreamVideo}
          onPlay={playCoverPeek}
          onLocked={() => requireFeature("library", playCoverPeek)}
          onClose={() => setCoverPeek(null)}
        />
      ) : null}
      {playing ? (
        <VideoPlayerModal
          slug={playing.slug}
          title={playing.title}
          language={language}
          startAt={playing.startAt}
          onClose={() => { setPlaying(null); loadVideoProgress(); }}
        />
      ) : null}
    </section>
  );
}

// R8/BREACH-1: the book reader moved to src/components/BookReaderModal.tsx, carrying `ReaderState`
// and R216-audited `useBookSearch`. R81 retired the reason they were held back; this went back.

export default App;
